import React, { useEffect, useState, useCallback, useRef } from 'react';
import MoveBidToJob from './MoveBidToJob';
import FieldwireLines from './FieldwireLines';
import TrenchCalc from './TrenchCalc';
import SendPointer from '../../components/SendPointer';
import AttachButton, { AttachThumb, isPdf } from '../../components/AttachButton';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { quoteFileName, jobDocFileName } from '../../utils/ui';
import AdminLayout from './AdminLayout';
import { ShopOrdersBoard } from './AdminShopOrders';
import { CreatePoModal } from './CreatePoModal';
import PoDetailModal from './PoDetailModal';
import PdfAnnotator from './PdfAnnotator';
import RateSheetModal from '../../components/RateSheetModal';
import { LogDeliveryModal } from './AdminMaterial';
import ActivityPills from '../../components/ActivityPills';
import JobBanner from './JobBanner';
import DocumentsPillow from './DocumentsPillow';
import OldJobWizard from './OldJobWizard';
import { BidFieldwirePillows, FieldwireTaskDrawer, FwTaskChooser } from './FieldwirePanel';
import JobHeaderPillow, { Field as JhField, inp as jhInp, lbl as jhLbl } from './JobInfoPillow';
import { tidyAddress } from '../../utils/address';
import { classifyBid, priceStructureLabel, workTypeLabel } from '../../utils/bidTypes';
import { escDeep } from '../../utils/escapeHtml';
import TmTile, { tmUnitOf } from './TmTile';
import { TicketDayLog } from './ExtraWorkCard';
import BidUpdatesPillow, { announceBidUpdates } from './BidUpdatesPillow';
import WorkflowPhotos from '../../components/WorkflowPhotos';
import JobLocationModal from '../../components/JobLocationModal';
import AddressPicker from '../../components/AddressPicker';
import MobileBottomNav from '../../components/MobileBottomNav';
import { DrillCardModal } from './AdminWorkflows';
import { useCategories } from '../../hooks/useCategories';
import { formatPhone } from '../../utils/phone';
import { openPdfFrom } from '../../utils/openPdf';
import PdfWindow from '../../components/PdfWindow';
import { ContactCheck, offerToSaveRecipients } from '../../components/ContactCheck';
import { findPlace } from '../../utils/placeSearch';
import { SubmittalBoard, SubmittalPillows, SubmittalActivity, SubmittalRemindWindow, SubmittalReleaseWindow, SubmittalVendorPanel, SubmittalParts, LayoutSwitch, useSubmittalLayout, stageOf as submittalStageOf, submittalMatches } from './SubmittalBoard';

// The office and the field open the same job at different addresses, so a pointer is
// built from wherever the sender is standing; a supervisor sent the office one is
// redirected to their own copy rather than bounced home (see FIELD_TWIN in App.js).
const itemJobPath = pid =>
  `${window.location.pathname.startsWith('/sup/') ? '/sup/jobs' : '/admin/jobs'}/${pid}`;

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// A cell that always holds a number can't be emptied — the change handler puts the 0
// straight back — so the first keystroke has to replace what's there rather than
// append to it. Without this you type 20 into a Profit % showing 0 and get 200.
const selectOnFocus = e => e.target.select();

export const STATUS_META = {
  potential: { label: 'Potential', color: '#7c3aed' },
  draft:     { label: 'Upcoming',  color: '#6b7280' },
  submitted: { label: 'Submitted', color: '#1d4ed8' },
  awarded:   { label: 'Awarded',   color: '#d97706' },
  lost:      { label: 'Lost',      color: '#9ca3af' },
};

// Also used by the Job Information tile, so the job page picks a location the same
// way the bid does rather than growing a second map.
export function LocationPickerModal({ value, onConfirm, onClose }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);
  const [search, setSearch] = useState(value || '');
  const [chosen, setChosen] = useState(value || '');
  const [chosenLatLng, setChosenLatLng] = useState(null);
  const [searching, setSearching] = useState(false);
  const [miss, setMiss] = useState('');   // the search found nothing, or found a simpler version (Oct 8 2026)

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    function initMap(L) {
      if (!mapRef.current || leafletRef.current) return;
      const map = L.map(mapRef.current).setView([39.5296, -119.8138], 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap contributors' }).addTo(map);
      const icon = L.icon({ iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png', iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41] });
      leafletRef.current = map;
      map.on('click', async e => {
        const { lat, lng } = e.latlng;
        if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
        else markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
        setChosenLatLng({ lat, lng });
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const d = await r.json();
          const addr = tidyAddress(d.display_name) || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          setChosen(addr); setSearch(addr);
        } catch { setChosen(`${lat.toFixed(5)}, ${lng.toFixed(5)}`); }
      });
      if (value) {
        findPlace(value).then(h => (h ? [{ lat: h.lat, lon: h.lng }] : [])).then(results => {
            if (results[0]) {
              const lat = parseFloat(results[0].lat), lng = parseFloat(results[0].lon);
              map.setView([lat, lng], 15);
              markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
            }
          }).catch(() => {});
      }
    }
    if (window.L) { initMap(window.L); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap(window.L);
    document.head.appendChild(script);
    return () => { if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; markerRef.current = null; } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function doSearch() {
    if (!search.trim() || !leafletRef.current) return;
    setSearching(true);
    try {
      // Tries the address as typed, then simpler versions (no building / suite / brackets) — see utils/placeSearch.
      const hit = await findPlace(search);
      const results = hit ? [{ lat: hit.lat, lon: hit.lng, display_name: hit.exact ? hit.display : search.trim() }] : [];
      setMiss(!hit ? 'Couldn’t find that on the map — try just the street and town, or click the map to drop the pin.' : hit.exact ? '' : `Found it as “${hit.query}” — check the pin, or click the map to move it.`);
      if (results[0]) {
        const L = window.L;
        const lat = parseFloat(results[0].lat), lng = parseFloat(results[0].lon);
        leafletRef.current.setView([lat, lng], 15);
        const icon = L.icon({ iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png', iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41] });
        if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
        else markerRef.current = L.marker([lat, lng], { icon }).addTo(leafletRef.current);
        setChosenLatLng({ lat, lng });
        setChosen(tidyAddress(results[0].display_name));
      }
    } finally { setSearching(false); }
  }

  return (
    // Above everything, including the job page's expanded pillow at 5100 — a map you
    // opened deliberately shouldn't come up behind the panel you opened it from.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 680, maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Pick Location</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="Search address…" style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
          <button onClick={doSearch} disabled={searching}
            style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {searching ? '…' : 'Search'}
          </button>
        </div>
        {miss && <div style={{ padding: '8px 16px', fontSize: 12.5, fontWeight: 600, color: miss.startsWith('Found') ? '#92400e' : '#b91c1c', background: miss.startsWith('Found') ? '#fffbeb' : '#fef2f2', borderBottom: '1px solid #e5e7eb' }}>{miss}</div>}
        <div ref={mapRef} style={{ flex: 1, minHeight: 380 }} />
        {chosen && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><path d="M6 1C4.07 1 2.5 2.57 2.5 4.5c0 2.625 3.5 6.5 3.5 6.5s3.5-3.875 3.5-6.5C9.5 2.57 7.93 1 6 1z" stroke="#1d4ed8" strokeWidth="1.2" fill="none"/><circle cx="6" cy="4.5" r="1.2" stroke="#1d4ed8" strokeWidth="1.2" fill="none"/></svg>
            <span style={{ flex: 1 }}>{chosen}</span>
          </div>
        )}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => { onConfirm({ address: chosen || search, lat: chosenLatLng?.lat ?? null, lng: chosenLatLng?.lng ?? null }); onClose(); }} disabled={!chosen && !search}
            style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Use This Location
          </button>
        </div>
      </div>
    </div>
  );
}

function newItem(sortOrder = 0) {
  return {
    _key: Math.random().toString(36).slice(2),
    id: null,
    sort_order: sortOrder,
    item_number: '',
    pay_item_number: '',
    description: '',
    quantity: 1,
    unit: 'EACH',
    material_cost: 0,
    material_escalation: 0,
    labor_hours: 0,
    labor_rate: null,
    dje_standard: 0,
    dje_takeoff: 0,
    overhead_material_pct: null,
    overhead_labor_pct: null,
    profit_pct: null,
    rounding: 0,
    notes: '',
    sub_items: [],
  };
}

// "Don't round this line." bid_items.rounding is REAL NOT NULL so None can't be
// null; a real exponent is only ever -2..4, so 99 is unambiguous. Must match the
// migration in server/db.js. Anything missing counts as None too, which keeps a
// half-migrated or hand-edited row from silently re-pricing itself.
// Organisational flags an estimator puts on a bid line. Deliberately unnamed — Ryan
// wants the crew to decide what each colour means on a given bid, so the app never
// asserts a meaning. Never affects price, order, or anything printed.
export const ITEM_FLAGS = [
  { key: 'red',    color: '#dc2626' },
  { key: 'yellow', color: '#eab308' },
  { key: 'green',  color: '#16a34a' },
  { key: 'blue',   color: '#2563eb' },
  { key: 'black',  color: '#111827' },
];
const flagColor = k => ITEM_FLAGS.find(f => f.key === k)?.color || null;

export const ROUND_NONE = 99;
const isRoundNone = exp => exp == null || exp >= ROUND_NONE;
// A unit price is money: with Round = None it is the worked-out price to the CENT, never 13.0001
// (Ryan, Nov 2 2026, job 2624: the page showed $13.00 and extended 506 × 13.0001 = $6,578.05 —
// "use the numbers in the circle"). The price shown is the price the line extends from.
const toCents = n => Math.round(n * 100) / 100;

// C and M (Ryan, Oct 21 2026): the quantity is the real count — 1,200 ft — and the price is per
// 100 (C) or per 1,000 (M), so the line extends at quantity ÷ 100 or ÷ 1,000. Every other unit is per one.
export const unitPer = unit => { const u = String(unit || '').trim().toUpperCase(); return u === 'C' ? 100 : u === 'M' ? 1000 : 1; };
// The quantity the price multiplies: the count, over 100 / 1,000 on a C / M line.
export const pricedQty = it => (it.quantity || 1) / unitPer(it && it.unit);

// Core calculation — every bid line's money comes through here (and the two below).
export function calcItem(item, bid) {
  const matOH   = (item.overhead_material_pct ?? bid.overhead_material_pct) / 100;
  const labOH   = (item.overhead_labor_pct   ?? bid.overhead_labor_pct)   / 100;
  const prof    = (item.profit_pct           ?? bid.profit_pct)           / 100;
  const tax     = bid.sales_tax_pct / 100;
  const sup     = bid.supervision_pct / 100;
  const rate    = item.labor_rate ?? bid.labor_rate; // per-line $/hr override (else the bid's rate)
  const qty     = item.quantity || 1;

  const matBase  = (item.material_cost || 0) + (item.material_escalation || 0);
  const matTax   = matBase * tax;
  const matTotal = matBase + matTax;

  const prodHours = item.labor_hours || 0;
  const supHours  = prodHours * sup;
  const laborCost = prodHours * rate;
  const supCost   = supHours  * rate;
  const laborTotal = laborCost + supCost;

  const dje      = (item.dje_standard || 0) + (item.dje_takeoff || 0);
  const prime    = matTotal + laborTotal + dje;
  const ohMat    = (matTotal + dje) * matOH;
  const ohLab    = laborTotal * labOH;
  const totalCost = prime + ohMat + ohLab;
  const profit   = totalCost * prof;
  const selling  = totalCost + profit;
  // Round column: the power of ten to round the unit price to, so 0 means "1" — the
  // whole dollar. It used to skip rounding whenever the exponent was 0, which made
  // "1" the one option in the list that did nothing. ROUND_NONE leaves the price
  // alone; existing lines were migrated to it so their prices didn't move (see db.js).
  const roundExp    = item.rounding;
  const roundFactor = Math.pow(10, roundExp ?? 0);
  // A unit price typed in on the job's Line Items tile is the price, full stop (Ryan, Oct 29 2026).
  const typed       = priceOverrideOf(item);
  const unitPrice   = typed != null ? typed : isRoundNone(roundExp) ? toCents(selling) : Math.round(selling / roundFactor) * roundFactor;
  const itemTotal   = unitPrice * qty / unitPer(item.unit);

  return { matBase, matTax, matTotal, prodHours, supHours, laborCost, supCost, laborTotal, dje, prime, ohMat, ohLab, totalCost, profit, selling, itemTotal, unitPrice };
}

// Sub-items are NOT rounded — they contribute their raw (unrounded) selling price.
// The PARENT line's rounding shapes the final unit price into a clean number, so
// the adjustment lives on the main line, not each sub. (No subs: the item rounds itself.)
// A line's sub-items whether they came parsed or straight from the server as JSON text
// (Chad, Oct 28 2026: the job's Line Items read the bid raw, so every line priced by its
// sub-items showed $0 there and its total came up short).
export const subItemsOf = item => {
  const s = item && item.sub_items;
  if (Array.isArray(s)) return s;
  if (typeof s === 'string' && s.trim()) { try { const p = JSON.parse(s); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};
// The unit price typed in on the job's Line Items tile, or null when the line is worked out as usual.
export const priceOverrideOf = item => {
  const v = item && item.price_override;
  return v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
};
export function itemEffectiveUnitPrice(item, bid) {
  const subs = subItemsOf(item);
  if (priceOverrideOf(item) != null) return priceOverrideOf(item);
  if (subs.length === 0) return calcItem(item, bid).unitPrice;
  // Each sub extends at its own priced quantity; the line's unit price is then per the line's own
  // unit (per 100 on a C line), so it divides by the line's priced quantity.
  const rawTotal = subs.reduce((s, sub) => s + calcItem(sub, bid).selling * pricedQty(sub), 0);
  const rawUnit = rawTotal / pricedQty(item);
  const roundExp = item.rounding;
  const factor = Math.pow(10, roundExp ?? 0);
  return isRoundNone(roundExp) ? toCents(rawUnit) : Math.round(rawUnit / factor) * factor;
}
export function itemEffectiveTotal(item, bid) {
  const subs = subItemsOf(item);
  if (subs.length === 0 || priceOverrideOf(item) != null) return calcItem(item, bid).itemTotal;
  return itemEffectiveUnitPrice(item, bid) * pricedQty(item);
}

// Editable dollar amount for a bid recipient (the GC's bid to the owner).
// Uses local state so typing always works, shows thousands separators when not
// focused, and only saves on blur. A plain text field (not type=number) avoids
// the spinner-only / value-stacking quirks.
function RecipientBidAmount({ value, disabled, onSave }) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(value != null ? String(value) : '');
  useEffect(() => { if (!focused) setText(value != null ? String(value) : ''); }, [value, focused]);
  const formatted = value != null && value !== '' ? Number(value).toLocaleString('en-US') : '';
  return (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#9ca3af', pointerEvents: 'none' }}>$</span>
      <input
        type="text" inputMode="decimal" disabled={disabled}
        value={focused ? text : formatted}
        placeholder="—"
        onFocus={() => setFocused(true)}
        onChange={e => setText(e.target.value.replace(/[^0-9.]/g, ''))}
        onBlur={() => { setFocused(false); if (String(text) !== String(value ?? '')) onSave(text); }}
        style={{ width: 130, border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 8px 5px 18px', fontSize: 13, background: disabled ? '#f9fafb' : '#fff' }}
      />
    </div>
  );
}

function exportBidToExcel(bid, items, recipient) {
  const wb = XLSX.utils.book_new();
  const pct = bid.profit_pct;
  const fmtD = (n) => n == null ? '' : Number(n).toFixed(2);

  // ── Sheet 1: Estimate Recap ───────────────────────────────────────────────
  const recapRows = [
    ['TITAN ELECTRIC', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['ESTIMATE RECAP', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    [],
    // When the sheet is exported FOR a recipient it carries that contractor's name,
    // so label it "Bid To:" — only the un-targeted export shows the job's Owner.
    ['Job Name:', bid.job_name, '', '', recipient ? 'Bid To:' : 'Owner:', recipient ? recipient.client_name : (bid.client_name || '')],
    ['Contact:', recipient ? (recipient.contact_name || '') : (bid.client_contact || ''), '', '', 'Email:', recipient ? (recipient.contact_email || '') : (bid.client_email || '')],
    ['Location:', bid.job_location || '', '', '', 'Bid Date:', bid.bid_date || ''],
    [],
    ['DEFAULT RATES', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['Labor Rate ($/hr)', bid.labor_rate, '', 'Supervision %', bid.supervision_pct, '', 'Sales Tax %', bid.sales_tax_pct],
    ['Overhead – Mat %', bid.overhead_material_pct, '', 'Overhead – Labor %', bid.overhead_labor_pct, '', 'Profit %', bid.profit_pct],
    [],
    // Header row
    [
      'Item #', 'Pay Item #', 'Description', 'Qty', 'Unit',
      'Material ($)', 'Escalation ($)', 'Mat+Tax ($)',
      'Prod Hrs', 'Sup Hrs', 'Labor ($)', 'Sup Labor ($)', 'Labor Total ($)',
      'Sub ($)', 'Sub T/O ($)', 'Total Sub ($)',
      'Prime Cost ($)', 'OH Mat ($)', 'OH Lab ($)', 'Total Cost ($)',
      'Profit ($)', 'Selling Price', 'Rounding ($)', 'Unit Price', 'Item Total ($)',
    ],
  ];

  items.forEach((item, idx) => {
    const c = calcItem(item, bid);
    recapRows.push([
      item.item_number || (idx + 1),
      item.pay_item_number || '',
      item.description || '',
      item.quantity ?? 1,
      item.unit || '',
      fmtD(item.material_cost),
      fmtD(item.material_escalation),
      fmtD(c.matTotal),
      fmtD(c.prodHours),
      fmtD(c.supHours),
      fmtD(c.laborCost),
      fmtD(c.supCost),
      fmtD(c.laborTotal),
      fmtD(item.dje_standard),
      fmtD(item.dje_takeoff),
      fmtD(c.dje),
      fmtD(c.prime),
      fmtD(c.ohMat),
      fmtD(c.ohLab),
      fmtD(c.totalCost),
      fmtD(c.profit),
      fmtD(c.selling),
      // Show what the dropdown shows, not the None sentinel.
      isRoundNone(item.rounding) ? 'None' : fmtD(Math.pow(10, item.rounding)),
      fmtD(c.unitPrice),
      fmtD(c.itemTotal),
    ]);
  });

  // Grand total row
  const grandTotal = items.reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
  const totalHrs = items.reduce((s, i) => s + (calcItem(i, bid).prodHours + calcItem(i, bid).supHours), 0);
  recapRows.push([]);
  recapRows.push(['', '', 'GRAND TOTAL', '', '', '', '', '', fmtD(totalHrs), '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', fmtD(grandTotal)]);

  const wsRecap = XLSX.utils.aoa_to_sheet(recapRows);

  // Column widths
  wsRecap['!cols'] = [
    {wch:8},{wch:10},{wch:32},{wch:6},{wch:6},
    {wch:12},{wch:12},{wch:12},
    {wch:9},{wch:9},{wch:12},{wch:12},{wch:13},
    {wch:11},{wch:11},{wch:12},
    {wch:13},{wch:11},{wch:11},{wch:13},
    {wch:11},{wch:13},{wch:11},{wch:12},{wch:14},
  ];

  XLSX.utils.book_append_sheet(wb, wsRecap, 'Estimate Recap');

  // ── Sheet 2: Summary ─────────────────────────────────────────────────────
  const summaryRows = [
    ['TITAN ELECTRIC – BID SUMMARY'],
    [],
    ['Job Name', bid.job_name],
    ['Owner', bid.client_name || ''],
    ['Location', bid.job_location || ''],
    ['Bid Date', bid.bid_date || ''],
    ['Status', bid.status],
    [],
    ['FINANCIALS', ''],
    ['Grand Total', fmtD(grandTotal)],
    ['Total Labor Hours (incl. supervision)', fmtD(totalHrs)],
    ['Profit %', pct + '%'],
    [],
    ['RATES', ''],
    ['Labor Rate ($/hr)', bid.labor_rate],
    ['Supervision %', bid.supervision_pct + '%'],
    ['Sales Tax %', bid.sales_tax_pct + '%'],
    ['Overhead – Materials %', bid.overhead_material_pct + '%'],
    ['Overhead – Labor %', bid.overhead_labor_pct + '%'],
    [],
    ['SCOPE', ''],
    [bid.scope || ''],
    [],
    ['EXCLUSIONS', ''],
    [bid.exclusions || ''],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
  wsSummary['!cols'] = [{wch: 36}, {wch: 24}];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  XLSX.writeFile(wb, quoteFileName(bid, recipient, 'xlsx'));
}

// The one line a lump-sum quote prints (Ryan, Oct 28 2026) — typed on the bid, else a plain default.
export const lumpDescOf = bid => (bid.quote_lump_desc && String(bid.quote_lump_desc).trim())
  || `${bid.job_name ? bid.job_name + ' — ' : ''}complete scope of work as described below, lump sum`;

// Everything the quote PDF says, worked out here with the same helpers the bid page
// uses (so the PDF matches the screen) and handed to the server already formatted —
// the server only draws. One place for the numbers; see POST /bids/:id/quote-pdf.
function buildQuoteData(bid, items, recipient, alternates = [], tmItems = []) {
  const to = recipient || { client_name: bid.client_name, contact_name: bid.client_contact, contact_email: bid.client_email, contact_phone: bid.client_phone };
  const fmtMoney = n => n == null ? '$0.00' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dateStr = bid.bid_date
    ? new Date(bid.bid_date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // A specific recipient gets only their own name + contact — not the owner's address/fax.
  const custAddr = recipient ? '' : [bid.client_address1, bid.client_address2].filter(Boolean).join(', ');
  const custFax = recipient ? '' : (bid.client_fax || '');

  const isTm = bid.pricing_type === 'tm';
  const isTmDrill = isTm && (bid.tm_drill || /Drilling/i.test(bid.bid_type || ''));
  const isTmGeneral = isTm && !isTmDrill;
  const tmBasisWord = bid.tm_rate_basis === 'hourly' ? 'Hour' : 'Day';

  const rows = [];
  // A T&M quote is its list of rate lines. Quantities are optional estimates: with
  // none, every line reads "per day / per hr" and the quote stays open-ended; with
  // some, an estimated total prints too. Older T&M bids with no lines fall back to the
  // drill rate + mobilization the bid itself carries, exactly as they printed before.
  const tmLines = isTm && Array.isArray(tmItems) ? tmItems : [];
  const tmAnyQty = tmLines.some(t => Number(t.quantity) > 0);
  const tmEstimate = tmLines.reduce((s, t) => s + (Number(t.quantity) || 0) * (Number(t.rate) || 0), 0);
  if (tmLines.length) {
    tmLines.forEach((t, i) => {
      const qty = Number(t.quantity) || 0;
      const unit = tmUnitOf(t);
      rows.push({ kind: 'line', num: String(i + 1), desc: t.name, qty: qty ? String(qty) : '—', qtyMuted: !qty, unit: unit.toUpperCase(),
        unitPrice: fmtMoney(t.rate), extended: qty ? fmtMoney(qty * (Number(t.rate) || 0)) : `per ${unit}`, extMuted: !qty });
    });
  } else if (isTmDrill) {
    rows.push({ kind: 'line', num: 'T&M', desc: `Drilling — ${bid.tm_drill || 'Drill'} (${tmBasisWord === 'Hour' ? 'Hourly' : 'Daily'} rate)`, qty: '—', qtyMuted: true, unit: tmBasisWord, unitPrice: fmtMoney(bid.tm_rate), extended: `per ${tmBasisWord.toLowerCase()}`, extMuted: true });
    rows.push({ kind: 'line', num: '', desc: 'Mobilization (round trip)', qty: '1', unit: 'EA', unitPrice: fmtMoney(bid.tm_mobilization), extended: fmtMoney(bid.tm_mobilization) });
  }
  const line = (item, idx) => ({ kind: 'line', num: String(item.item_number || (idx + 1)), desc: item.description || '', qty: String(item.quantity ?? 1), unit: item.unit || '',
    unitPrice: fmtMoney(itemEffectiveUnitPrice(item, bid)), extended: fmtMoney(itemEffectiveTotal(item, bid)) });

  // Alternates: the base and its lines first, then each alternate under its own heading
  // with its own subtotal (deductive shown as a minus), and the foot total is the base
  // with every alternate applied. Same rules as the on-screen summary.
  const alts = Array.isArray(alternates) ? alternates : [];
  const sumOf = list => list.reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
  const inGroup = gid => items.filter(i => (i.alternate_id || null) === gid);
  const baseItems = inGroup(null);
  const grandTotal = sumOf(items);
  const baseBidTotal = sumOf(baseItems);
  const alternatesNet = alts.reduce((s, a) => s + (a.kind === 'deductive' ? -sumOf(inGroup(a.id)) : sumOf(inGroup(a.id))), 0);
  const withAllAlternates = baseBidTotal + alternatesNet;

  // Lump sum on the quote (Ryan, Oct 28 2026): the base bid prints as ONE line at its total and
  // each alternate as one line at its subtotal; the bid sheet keeps every line.
  const lump = !!bid.quote_lump_sum && !isTm;
  const lumpRow = total => ({ kind: 'line', num: '1', desc: lumpDescOf(bid), qty: '1', unit: 'LS', unitPrice: fmtMoney(total), extended: fmtMoney(total) });
  if (tmLines.length) {
    // The rate lines are the whole quote — the hard-bid estimate stays off a T&M quote.
  } else if (!alts.length) {
    if (lump) rows.push(lumpRow(grandTotal));
    else items.forEach((it, i) => rows.push(line(it, i)));
  } else {
    rows.push({ kind: 'heading', label: 'Base Bid', note: '' });
    if (lump) { if (baseItems.length) rows.push(lumpRow(baseBidTotal)); }
    else baseItems.forEach((it, i) => rows.push(line(it, i)));
    rows.push({ kind: 'subtotal', label: 'BASE BID', value: fmtMoney(baseBidTotal), negative: false, strong: true });
    for (const a of alts) {
      const gi = inGroup(a.id);
      if (!gi.length) continue;
      const deduct = a.kind === 'deductive';
      rows.push({ kind: 'gap' });
      rows.push({ kind: 'heading', label: a.title, note: deduct ? 'Deductive — taking this alternate reduces the price by the amount below' : 'Additive — taking this alternate adds the amount below' });
      if (lump) rows.push({ ...lumpRow(sumOf(gi)), num: '', desc: `${a.title} — lump sum` });
      else gi.forEach((it, i) => rows.push(line(it, i)));
      rows.push({ kind: 'subtotal', label: `${a.title} subtotal`, value: fmtMoney(sumOf(gi)), negative: deduct, strong: false });
    }
  }

  let total;
  if (tmLines.length) {
    const nteNote = bid.tm_nte ? `, not to exceed ${fmtMoney(bid.tm_nte)}` : '';
    if (tmAnyQty) total = { kind: 'total', label: 'ESTIMATED T&M TOTAL', value: fmtMoney(tmEstimate), note: `Estimate only — billed on actual time and material used at the rates above${nteNote}.` };
    else if (bid.tm_nte) total = { kind: 'nte', value: fmtMoney(bid.tm_nte), note: 'Billed time & material at the rates above, not to exceed this amount.' };
    else total = { kind: 'tm_drill', note: 'Time & material — billed at the rates above on actual days, hours and materials used. Total varies with time on site.' };
  }
  else if (isTmDrill) total = { kind: 'tm_drill', note: `Time & material — billed on actual ${bid.tm_rate_basis === 'hourly' ? 'hours' : 'days'} worked plus one mobilization. Total varies with time on site.` };
  else if (isTmGeneral) total = { kind: 'nte', value: fmtMoney(bid.tm_nte), note: 'Billed time & material (labor + materials used), not to exceed this amount.' };
  else total = { kind: 'total', value: fmtMoney(alts.length ? withAllAlternates : grandTotal), note: alts.length ? 'Base bid with every alternate applied. Each alternate is priced above.' : '' };

  let drillIncluded = [], drillExcluded = [];
  if (bid.drill_scope_items) {
    try {
      const scopeItems = JSON.parse(bid.drill_scope_items).filter(it => it.status !== 'na');
      drillIncluded = scopeItems.filter(it => it.status === 'included').map(it => it.label);
      drillExcluded = scopeItems.filter(it => it.status === 'excluded').map(it => it.label);
    } catch {}
  }

  return {
    title: quoteFileName(bid, recipient, 'pdf'), recipientId: recipient?.id || null,
    jobName: bid.job_name || '', jobLocation: tidyAddress(bid.job_location) || '', dateStr, bidTime: bid.bid_time || '',
    to: { client_name: to.client_name || '', contact_name: to.contact_name || '', contact_email: to.contact_email || '', contact_phone: to.contact_phone || '' },
    custAddr, custFax, rows, total,
    // Loops scope (count + size) prints as the first line of the scope when set.
    scope: [bid.loop_count ? `${bid.loop_count} loop${Number(bid.loop_count) !== 1 ? 's' : ''}${bid.loop_size ? ` · ${bid.loop_size}` : ''}` : null, bid.scope || ''].filter(Boolean).join('\n'),
    exclusions: bid.exclusions || '', drillIncluded, drillExcluded,
    pmName: bid.bid_pm_name || '', pmEmail: bid.bid_pm_email || '', pmPhone: bid.bid_pm_phone || '',
  };
}

// Print = file. The quote is drawn on the server, filed in the job's documents ledger
// (Documents tile — "Quotes", one revision group per recipient), and opened in a tab
// under its real name. The tab is opened before the round trip so popup blockers
// don't eat it. If the server can't be reached the old in-browser print preview opens
// in that same tab instead, so a quote can always still go out.
async function exportQuotePdf(bid, items, recipient, alternates = [], { onError } = {}) {
  // Printing a quote with nothing marked included/excluded is usually a miss — ask first.
  if (!scopeChosen(bid) && !window.confirm(NO_EXCLUSIONS_MSG)) return;
  if (!addendaOk(bid)) return;
  const win = window.open('', '_blank');
  try {
    // A T&M quote prints its rate lines — fetched fresh so the PDF matches the tile.
    let tmItems = [];
    if (bid.pricing_type === 'tm') { try { const r = await apiFetch(`/bids/${bid.id}/tm-items`); tmItems = Array.isArray(r) ? r : []; } catch {} }
    const res = await fetch(`${API_BASE}/bids/${bid.id}/quote-pdf`, {
      method: 'POST', headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify(buildQuoteData(bid, items, recipient, alternates, tmItems)),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Quote PDF failed (${res.status})`);
    const ct = res.headers.get('content-type') || '';
    let url;
    if (ct.includes('application/json')) {
      const { document: doc } = await res.json();
      url = `${API_BASE}/documents/${doc.id}/file`;
    } else {
      url = URL.createObjectURL(await res.blob());
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
    if (win) win.location.href = url; else window.open(url, '_blank');
    window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { bidId: bid.id } }));
  } catch (e) {
    if (onError) onError(e.message || 'Could not build the quote PDF; opened the print preview instead.');
    generateBidQuotePDF(bid, items, recipient, alternates, win);
  }
}

function generateBidQuotePDF(bid, items, recipient, alternates = [], targetWin = null) {
  // Everything typed by a person is text on this page, never markup.
  bid = escDeep(bid); items = escDeep(items || []); recipient = recipient ? escDeep(recipient) : recipient; alternates = escDeep(alternates || []);
  const logoUrl = `${window.location.origin}/titan-logo.jpg`;
  const to = recipient || { client_name: bid.client_name, contact_name: bid.client_contact, contact_email: bid.client_email, contact_phone: bid.client_phone };

  const fmtMoney = n => n == null ? '$0.00' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dateStr = bid.bid_date
    ? new Date(bid.bid_date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // When exporting to a specific bid recipient, show only that recipient's name +
  // contact info — not the project owner's address/fax (which live on the bid).
  const custAddr = recipient ? '' : [bid.client_address1, bid.client_address2].filter(Boolean).join(', ');
  const custFax = recipient ? '' : (bid.client_fax || '');

  // Time & Material: drilling shows the rate + mobilization as line items (open-ended);
  // General shows a "not to exceed" price.
  const isTm = bid.pricing_type === 'tm';
  const isTmDrill = isTm && (bid.tm_drill || /Drilling/i.test(bid.bid_type || ''));
  const isTmGeneral = isTm && !isTmDrill;
  const tmBasisWord = bid.tm_rate_basis === 'hourly' ? 'Hour' : 'Day';
  const cell = (v, extra = '') => `<td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;${extra}">${v}</td>`;
  let tmRows = '';
  if (isTmDrill) {
    tmRows = `
      <tr>
        ${cell('T&M', 'text-align:center;color:#6b7280;')}
        ${cell(`Drilling — ${bid.tm_drill || 'Drill'} (${tmBasisWord === 'Hour' ? 'Hourly' : 'Daily'} rate)`)}
        ${cell('—', 'text-align:center;color:#9ca3af;')}
        ${cell(tmBasisWord, 'text-align:center;color:#6b7280;')}
        ${cell(fmtMoney(bid.tm_rate), 'text-align:right;')}
        ${cell(`per ${tmBasisWord.toLowerCase()}`, 'text-align:right;color:#6b7280;font-style:italic;')}
      </tr>
      <tr>
        ${cell('', 'text-align:center;color:#6b7280;')}
        ${cell('Mobilization (round trip)')}
        ${cell('1', 'text-align:center;')}
        ${cell('EA', 'text-align:center;color:#6b7280;')}
        ${cell(fmtMoney(bid.tm_mobilization), 'text-align:right;')}
        ${cell(fmtMoney(bid.tm_mobilization), 'text-align:right;font-weight:600;')}
      </tr>`;
  }

  const lineRow = (item, idx) => {
    const unitPrice = item._lump != null ? item._lump : itemEffectiveUnitPrice(item, bid);
    const extended = item._lump != null ? item._lump : itemEffectiveTotal(item, bid);
    return `
      <tr>
        <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;text-align:center;color:#6b7280;">${item.item_number || (idx + 1)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;">${item.description || ''}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${item.quantity ?? 1}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;text-align:center;color:#6b7280;">${item.unit || ''}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmtMoney(unitPrice)}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;">${fmtMoney(extended)}</td>
      </tr>`;
  };

  // ── Alternates ──────────────────────────────────────────────────────────────
  // A bid cut into groups prints as several smaller bids: the base and its lines,
  // then each alternate under its own heading with its own subtotal, and a summary
  // at the end so the owner can read the price of every combination they might take.
  //
  // A DEDUCTIVE group's lines are part of the base scope, so its subtotal is already
  // inside the base figure — which is exactly why the summary shows it as a minus.
  const alts = Array.isArray(alternates) ? alternates : [];
  const sumOf = list => list.reduce((s, i) => s + (i._lump != null ? i._lump : (itemEffectiveTotal(i, bid) || 0)), 0);
  const inGroup = id => items.filter(i => (i.alternate_id || null) === id);
  const baseItems = inGroup(null);
  const grandTotal = items.reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
  // The base bid is the first block and nothing else — what goes on the bid form.
  // Alternates sit below it with their own subtotals, additive or deductive, and the
  // total at the foot is the base with all of them applied.
  const baseBidTotal = sumOf(baseItems);
  const alternatesNet = alts.reduce((s, a) => s + (a.kind === 'deductive' ? -sumOf(inGroup(a.id)) : sumOf(inGroup(a.id))), 0);
  const withAllAlternates = baseBidTotal + alternatesNet;

  const groupHeadingRow = (label, note) => `
      <tr><td colspan="6" style="padding:10px 10px 6px;border-bottom:2px solid #1e3a5f;">
        <span style="font-size:12.5px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:.5px;">${label}</span>
        ${note ? `<span style="font-size:11px;color:#6b7280;margin-left:8px;">${note}</span>` : ''}
      </td></tr>`;
  const groupSubtotalRow = (label, value, negative, strong) => `
      <tr><td colspan="5" style="padding:${strong ? '10px' : '8px'} 10px;text-align:right;font-size:${strong ? 13 : 12}px;font-weight:${strong ? 800 : 700};color:${strong ? '#1e3a5f' : '#374151'};background:${strong ? '#eef2f7' : '#f9fafb'};${strong ? 'border-top:2px solid #1e3a5f;' : ''}">${label}</td>
        <td style="padding:${strong ? '10px' : '8px'} 10px;text-align:right;font-size:${strong ? 15 : 13}px;font-weight:800;background:${strong ? '#eef2f7' : '#f9fafb'};white-space:nowrap;${strong ? 'border-top:2px solid #1e3a5f;' : ''}${negative ? 'color:#b91c1c;' : ''}">${negative ? `(${fmtMoney(value)})` : fmtMoney(value)}</td></tr>`;

  // A group heading and its first line live in their own <tbody> that can't be split,
  // so a heading can never print at the foot of a page with its items overleaf.
  // `break-inside: avoid` on a small tbody is honoured far more reliably than
  // `break-after: avoid` on a single row. The rest of the group flows normally,
  // which matters when a group runs longer than a page.
  const groupBlock = (heading, note, gi, subtotalLabel, deduct, strong) => {
    const [first, ...rest] = gi;
    return `<tbody style="break-inside:avoid;page-break-inside:avoid;">`
      + groupHeadingRow(heading, note)
      + (first ? lineRow(first, 0) : '')
      + `</tbody><tbody>`
      + rest.map(lineRow).join('')
      + groupSubtotalRow(subtotalLabel, sumOf(gi), deduct, strong)
      + `</tbody>`;
  };

  // Lump sum on the quote (Ryan, Oct 28 2026): the base bid as one line at its total.
  const lump = !!bid.quote_lump_sum && !isTm;
  const lumpOf = (total, desc = lumpDescOf(bid), num = '1') => [{ item_number: num, description: desc, quantity: 1, unit: 'LS', _lump: total }];
  let itemRows;
  if (!alts.length) {
    itemRows = `<tbody>${(lump ? lumpOf(grandTotal) : items).map(lineRow).join('')}</tbody>`;
  } else {
    itemRows = groupBlock('Base Bid', '', lump ? (baseItems.length ? lumpOf(baseBidTotal) : []) : baseItems, 'BASE BID', false, true)
      + alts.map(a => {
          const gi = inGroup(a.id);
          if (!gi.length) return '';
          const deduct = a.kind === 'deductive';
          return `<tbody><tr><td colspan="6" style="height:14px;"></td></tr></tbody>`
            + groupBlock(a.title, deduct
                ? 'Deductive — taking this alternate reduces the price by the amount below'
                : 'Additive — taking this alternate adds the amount below',
                lump ? lumpOf(sumOf(gi), `${a.title} — lump sum`, ' ') : gi, `${a.title} subtotal`, deduct, false);
        }).join('');
  }
  const rows = (tmRows ? `<tbody>${tmRows}</tbody>` : '') + itemRows;


  // Build drill scope checklist rows for PDF (skip N/A items)
  let drillScopeBlock = '';
  if (bid.drill_scope_items) {
    const scopeItems = JSON.parse(bid.drill_scope_items).filter(it => it.status !== 'na');
    if (scopeItems.length > 0) {
      const included = scopeItems.filter(it => it.status === 'included');
      const excluded = scopeItems.filter(it => it.status === 'excluded');
      drillScopeBlock = `
        <div style="margin-top:24px;padding:20px;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;margin-bottom:12px;">Inclusions &amp; Exclusions</div>
          ${included.length > 0 ? `<div style="margin-bottom:${excluded.length > 0 ? 14 : 0}px;"><div style="font-size:11px;font-weight:700;color:#16a34a;margin-bottom:6px;">INCLUDED</div><ul style="margin:0;padding-left:16px;column-count:4;column-gap:16px;">${included.map(it => `<li style="font-size:12px;line-height:1.3;margin-bottom:3px;break-inside:avoid;-webkit-column-break-inside:avoid;">${it.label}</li>`).join('')}</ul></div>` : ''}
          ${excluded.length > 0 ? `<div><div style="font-size:11px;font-weight:700;color:#dc2626;margin-bottom:6px;">EXCLUDED</div><ul style="margin:0;padding-left:16px;column-count:4;column-gap:16px;">${excluded.map(it => `<li style="font-size:12px;line-height:1.3;margin-bottom:3px;break-inside:avoid;-webkit-column-break-inside:avoid;">${it.label}</li>`).join('')}</ul></div>` : ''}
        </div>`;
    }
  }

  const scopeBlock = (bid.scope || bid.exclusions || drillScopeBlock) ? `
    <div class="avoid" style="margin-top:18px;padding:16px 18px;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
      ${bid.scope ? `<div style="margin-bottom:${bid.exclusions || drillScopeBlock ? 14 : 0}px;"><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;margin-bottom:6px;">Scope of Work</div><div style="font-size:13px;line-height:1.5;white-space:pre-wrap;">${bid.scope}</div></div>` : ''}
      ${bid.exclusions ? `<div style="margin-bottom:${drillScopeBlock ? 16 : 0}px;"><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;margin-bottom:6px;">Additional Comments</div><div style="font-size:13px;line-height:1.6;white-space:pre-wrap;">${bid.exclusions}</div></div>` : ''}
    </div>${drillScopeBlock}` : '';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${quoteFileName(bid, recipient)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { font-family: Arial, sans-serif; font-size: 12.5px; color: #111827; background: #e9edf2; }
    /* Render the preview inside a real Letter-sized page so what you see on screen
       is exactly what saves to PDF (no reflow between preview and print). */
    .toolbar { width: 8.5in; max-width: 96%; margin: 14px auto 0; text-align: right; }
    .sheet { width: 8.5in; min-height: 11in; padding: 0.5in 0.55in; margin: 12px auto 28px; background: #fff; box-shadow: 0 2px 14px rgba(0,0,0,0.18); }
    .avoid { break-inside: avoid; -webkit-column-break-inside: avoid; }
    /* Margins belong to the PAGE, not to the sheet. Padding on a block only applies
       once at its start and once at its end, so a 0.5in pad plus a zero page margin
       gave page 1 a top margin, the last page a bottom margin, and every page in
       between nothing at all — a long quote ran to the paper edge from page 2 on. */
    @page { size: letter; margin: 0.5in 0.55in; }
    @media print {
      html, body { background: #fff; }
      .no-print { display: none !important; }
      /* The page margin does the spacing now, so the sheet adds none of its own. */
      .sheet { width: auto; min-height: 0; margin: 0; padding: 0; box-shadow: none; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* Carry the column headings — and the job they belong to — onto every page.
         Page 3 of a line-item list is meaningless without them. */
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      /* Don't let a line item be sliced in half by a page break. */
      tr, li { break-inside: avoid; page-break-inside: avoid; }
      /* A heading stranded at the foot of a page with its rows overleaf. */
      h1, h2, h3, .keep-next { break-after: avoid; page-break-after: avoid; }
    }
  </style>
</head>
<body>

  <!-- Print button (hidden when printing) -->
  <div class="toolbar no-print">
    <button onclick="window.print()" style="padding:10px 24px;background:#1e3a5f;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;">
      🖨 Print / Save as PDF
    </button>
  </div>

  <div class="sheet"><div id="fit">

  <!-- Header — logo only; Titan's company details live in the footer -->
  <div style="padding-bottom:12px;border-bottom:3px solid #1e3a5f;margin-bottom:16px;">
    <img src="${logoUrl}" alt="Titan Electrical Contracting" style="height:60px;object-fit:contain;" onerror="this.style.display='none'" />
  </div>

  <!-- Quote label + project name -->
  <div style="margin-bottom:14px;">
    <div style="font-size:22px;font-weight:700;color:#1e3a5f;letter-spacing:-0.5px;">BID PROPOSAL</div>
    <div style="font-size:17px;font-weight:700;color:#111827;margin-top:3px;">${bid.job_name || '—'}</div>
    ${bid.job_location ? `<div style="font-size:12px;color:#6b7280;">${tidyAddress(bid.job_location)}</div>` : ''}
  </div>

  <!-- Bid Prepared For — single consolidated customer block -->
  <div style="background:#f9fafb;border-radius:8px;border-left:4px solid #f59e0b;padding:14px 18px;margin-bottom:16px;">
    <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;margin-bottom:9px;">Bid Prepared For</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:5px 28px;font-size:13px;color:#111827;line-height:1.4;">
      <div><span style="color:#6b7280;">Contractor:</span> <strong>${to.client_name || '—'}</strong></div>
      <div><span style="color:#6b7280;">Date:</span> ${dateStr}</div>
      ${to.contact_name ? `<div><span style="color:#6b7280;">Contact:</span> ${to.contact_name}</div>` : '<div></div>'}
      ${bid.bid_time ? `<div><span style="color:#6b7280;">Time:</span> ${bid.bid_time}</div>` : '<div></div>'}
      ${custAddr ? `<div><span style="color:#6b7280;">Address:</span> ${custAddr}</div>` : '<div></div>'}
      ${to.contact_phone ? `<div><span style="color:#6b7280;">Phone:</span> ${formatPhone(to.contact_phone)}</div>` : '<div></div>'}
      ${to.contact_email ? `<div><span style="color:#6b7280;">Email:</span> ${to.contact_email}</div>` : ''}
      ${custFax ? `<div><span style="color:#6b7280;">Fax:</span> ${custFax}</div>` : ''}
    </div>
  </div>

  <!-- Line Items Table (drilling T&M shows rate+mob rows; General T&M has none) -->
  ${rows.trim() ? `
  <table style="width:100%;border-collapse:collapse;font-size:13px;">
    <thead>
      <!-- Repeats at the top of every printed page along with the column headings,
           so page three of a long quote still says which job it belongs to and who
           it was priced for. On page one it simply reads as the table's caption. -->
      <tr>
        <th colspan="6" style="padding:5px 10px;text-align:left;font-weight:600;font-size:10.5px;color:#4b5563;background:#f3f4f6;border-bottom:1px solid #d1d5db;text-transform:uppercase;letter-spacing:.4px;">
          ${bid.job_name || 'Quote'}${to?.client_name ? ` &nbsp;·&nbsp; ${to.client_name}` : ''} &nbsp;·&nbsp; ${dateStr}
        </th>
      </tr>
      <tr style="background:#1e3a5f;color:#fff;">
        <th style="padding:8px 10px;text-align:center;font-weight:600;width:60px;">Item #</th>
        <th style="padding:8px 10px;text-align:left;font-weight:600;">Description</th>
        <th style="padding:8px 10px;text-align:center;font-weight:600;width:60px;">Qty</th>
        <th style="padding:8px 10px;text-align:center;font-weight:600;width:60px;">Unit</th>
        <th style="padding:8px 10px;text-align:right;font-weight:600;width:110px;">Unit Price</th>
        <th style="padding:8px 10px;text-align:right;font-weight:600;width:120px;">Extended</th>
      </tr>
    </thead>
    ${rows}
  </table>` : ''}

  <!-- Total: T&M drilling note / General not-to-exceed / fixed total -->
  ${isTmDrill ? `
  <div class="avoid" style="display:flex;justify-content:flex-end;margin-top:12px;">
    <div style="min-width:340px;padding:11px 16px;background:#faf5ff;border:1.5px solid #ddd6fe;border-radius:8px;text-align:right;font-size:12px;color:#6b7280;">
      Time &amp; material — billed on actual ${bid.tm_rate_basis === 'hourly' ? 'hours' : 'days'} worked plus one mobilization. Total varies with time on site.
    </div>
  </div>` : isTmGeneral ? `
  <div class="avoid" style="display:flex;justify-content:flex-end;margin-top:12px;">
    <div style="min-width:300px;">
      <div style="display:flex;justify-content:space-between;padding:11px 16px;background:#1e3a5f;border-radius:8px;color:#fff;">
        <span style="font-size:16px;font-weight:700;">NOT TO EXCEED</span>
        <span style="font-size:18px;font-weight:700;">${fmtMoney(bid.tm_nte)}</span>
      </div>
      <div style="margin-top:6px;text-align:right;font-size:11px;color:#9ca3af;">Billed time &amp; material (labor + materials used), not to exceed this amount.</div>
    </div>
  </div>` : `
  <div class="avoid" style="display:flex;justify-content:flex-end;margin-top:12px;">
    <div style="min-width:280px;">
      <div style="display:flex;justify-content:space-between;padding:11px 16px;background:#1e3a5f;border-radius:8px;color:#fff;">
        <span style="font-size:16px;font-weight:700;">TOTAL BID PRICE</span>
        <span style="font-size:18px;font-weight:700;">${fmtMoney(alts.length ? withAllAlternates : grandTotal)}</span>
      </div>
      ${alts.length ? '<div style="margin-top:6px;text-align:right;font-size:11px;color:#9ca3af;">Base bid with every alternate applied. Each alternate is priced above.</div>' : ''}
    </div>
  </div>`}

  ${scopeBlock}

  <div style="margin-top:16px;text-align:right;font-size:11px;color:#9ca3af;">This proposal is valid for 30 days from the date above.</div>

  <!-- Signature block -->
  <div class="avoid" style="margin-top:20px;display:grid;grid-template-columns:1fr 1fr;gap:40px;">
    <div>
      <div style="border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280;">Authorized Signature – Titan Electrical Contracting, Inc.</div>
    </div>
    <div>
      <div style="border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280;">Accepted by – ${to.client_name || 'Client'}</div>
    </div>
  </div>

  <!-- Company footer — Titan contact info + licenses -->
  <div class="avoid" style="margin-top:22px;padding-top:12px;border-top:3px solid #1e3a5f;display:flex;justify-content:space-between;align-items:flex-start;gap:20px;flex-wrap:wrap;">
    <div style="font-size:12px;color:#6b7280;line-height:1.5;">
      <div style="font-size:14px;font-weight:700;color:#1e3a5f;">TITAN ELECTRICAL CONTRACTING, INC.</div>
      <div>Suite 100, 5450 Mill Street, Reno, NV 89502</div>
      <div>Phone: (775) 857-4500</div>
      <div style="margin-top:3px;">NV Licenses &nbsp;·&nbsp; Class A #90896 &nbsp;·&nbsp; Class B #69843 &nbsp;·&nbsp; Class C #69814</div>
    </div>
    <div style="text-align:right;font-size:12px;color:#6b7280;line-height:1.5;">
      ${bid.bid_pm_name ? `<div style="font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#9ca3af;font-weight:700;">Project Manager</div><div style="font-size:13px;font-weight:600;color:#1e3a5f;">${bid.bid_pm_name}</div>` : ''}
      ${bid.bid_pm_email ? `<div>${bid.bid_pm_email}</div>` : ''}
      ${bid.bid_pm_phone ? `<div>${formatPhone(bid.bid_pm_phone)}</div>` : ''}
    </div>
  </div>

  </div></div><!-- /#fit /.sheet -->

  <script>
    // Auto shrink-to-fit: if the content runs just past one page, scale it down so
    // it lands on a single page. If it's well over (multi-page), leave it alone.
    window.addEventListener('load', function () {
      try {
        var fit = document.getElementById('fit');
        if (!fit) return;
        var pageH = 10 * 96; // usable height: 11in page − 0.5in top/bottom padding
        var h = fit.scrollHeight;
        if (h > pageH + 4) {
          var scale = pageH / h;
          if (scale >= 0.78) fit.style.zoom = scale; // "close enough" — one page
        }
      } catch (e) {}
    });
  </script>

</body>
</html>`;

  const win = targetWin || window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

// How the bid-only facts read on the Job Information card in bid mode.
const fmtTime12 = t => { if (!t) return ''; const [h, m] = String(t).split(':'); const hh = ((+h + 11) % 12) + 1; return `${hh}:${m} ${+h >= 12 ? 'PM' : 'AM'}`; };
function bidWhenText(bid) {
  if (!bid?.bid_date) return bid?.bid_time ? fmtTime12(bid.bid_time) : null;
  const d = new Date(bid.bid_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return bid.bid_time ? `${d} · ${fmtTime12(bid.bid_time)}` : d;
}
function preBidText(bid) {
  if (!bid?.pre_bid) return bid?.job_type === 'Prime' ? 'None' : null;
  const when = bid.pre_bid_datetime ? new Date(bid.pre_bid_datetime).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'date not set';
  return `${bid.pre_bid_mandatory ? 'Mandatory · ' : ''}${when}`;
}
function fmt$(n) {
  if (n == null || isNaN(n)) return '$—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n == null) return '—';
  return Number(n).toFixed(3) + '%';
}

// ── Award Modal ───────────────────────────────────────────────────────────────
const WORKFLOW_OPTIONS = [
  { key: 'vertical_drilling',   label: 'Vertical Drilling',   icon: '⬇️' },
  { key: 'horizontal_drilling', label: 'Directional Drilling', icon: '➡️' },
  { key: 'loops',               label: 'Loops',               icon: '🔁' },
  { key: 'nve',                 label: 'NVE',                 icon: '⚡' },
  { key: 'usa_digs',            label: 'USA Digs',            icon: '🗺️' },
  { key: 'shop',                label: 'Shop',                icon: '🔧' },
  { key: 'shop_order',          label: 'Shop Orders',         icon: '📦' },
  { key: 'submittals',          label: 'Submittals',          icon: '📋' },
  { key: 'extra_work',          label: 'Extra Work',          icon: '➕' },
];
// Zone 1 = office workflows; Zone 2 = field workflows. A workflow's zone is
// locked by its type, so each zone's "+ Add" button offers only its own types.
const OFFICE_WORKFLOW_TYPES = ['contract', 'submittals', 'subcontracts', 'payroll', 'change_orders'];
const OFFICE_WORKFLOW_OPTIONS = [
  { key: 'contract',      label: 'Contract',      icon: '📄' },
  { key: 'submittals',    label: 'Submittals',    icon: '📋' },
  { key: 'subcontracts',  label: 'Subcontracts',  icon: '🤝' },
  { key: 'payroll',       label: 'Payroll',       icon: '💵' },
  { key: 'change_orders', label: 'Change Orders', icon: '📝' },
];
const FIELD_WORKFLOW_OPTIONS = WORKFLOW_OPTIONS.filter(o => !OFFICE_WORKFLOW_TYPES.includes(o.key));
// Every workflow category, field + office, for the imported-project setup picker.
const ALL_WORKFLOW_OPTIONS = [...FIELD_WORKFLOW_OPTIONS, ...OFFICE_WORKFLOW_OPTIONS];

// The name block on the award screen: the rule's suggestion, the checks, and the box
// the PM can accept or edit. Re-asks the server whenever the customer changes.
function JobNameBlock({ bid, jobName, setJobName, hint, setHint, customer }) {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true; setLoading(true);
    apiFetch(`/bids/${bid.id}/name-suggestion?customer=${encodeURIComponent(customer || '')}&name=${encodeURIComponent(bid.job_name || '')}`)
      .then(h => { if (!alive) return; setHint(h); if (h?.suggested && (!jobName || jobName === bid.job_name)) setJobName(h.suggested); })
      .catch(() => { if (alive) setHint({ suggested: '', warnings: ['Could not check the name right now — give it a good one by hand.'] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [bid.id, customer]); // eslint-disable-line react-hooks/exhaustive-deps
  const matches = hint?.suggested && jobName.trim() === hint.suggested;
  // A work-order number, or a job number with a dash after it — not "106 Line Spider".
  const numberInName = /^\s*(?:WO\d{4,6}|2\d{3}\s*[-–—:·])/i.test(jobName);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: 10, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Job name</label>
        <span style={{ fontSize: 11, color: '#6b7280' }}>Customer - what the work is{hint?.far ? ' - town' : ''}{loading ? ' · checking…' : ''}</span>
      </div>
      <input value={jobName} onChange={e => setJobName(e.target.value)} placeholder="e.g. Q&D Construction - RTC Bike Network"
        style={{ width: '100%', border: `1.5px solid ${numberInName ? '#f59e0b' : matches ? '#16a34a' : '#cbd5e1'}`, borderRadius: 7, padding: '9px 12px', fontSize: 15, fontWeight: 700, color: '#111827', background: '#fff', boxSizing: 'border-box' }} />
      {hint?.suggested && !matches && (
        <button type="button" onClick={() => setJobName(hint.suggested)}
          style={{ alignSelf: 'flex-start', background: 'none', border: 'none', fontSize: 11.5, color: '#16a34a', cursor: 'pointer', padding: 0, fontWeight: 700 }}>
          Use suggested: {hint.suggested}
        </button>
      )}
      {matches && <div style={{ fontSize: 11.5, color: '#15803d', fontWeight: 700 }}>✓ Follows the naming rule{hint?.town && hint?.far ? ` · ${hint.town} is about ${hint.minutes} minutes out` : hint?.minutes != null ? ` · about ${hint.minutes} minutes from the shop` : ''}</div>}
      {numberInName && <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>⚠ Leave the job number out of the name — it has its own field above.</div>}
      {(hint?.warnings || []).map((w, i) => <div key={i} style={{ fontSize: 11.5, color: '#92400e' }}>• {w}</div>)}
    </div>
  );
}

function AwardModal({ bid, items: currentItems = [], alternates = [], employees, onClose, onAward }) {
  const [step, setStep] = useState(1);
  const [moving, setMoving] = useState(false);   // Make Change to Existing Job (Ryan, Oct 22 2026)
  const [division, setDivision] = useState(null);
  const [nextNumber, setNextNumber] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  // Is the number about to be used already somebody's? Checked as it is typed (Ryan,
  // Sep 30 2026: no duplicate job or work order numbers); the server refuses it too.
  const [taken, setTaken] = useState(null);
  useEffect(() => {
    const n = (jobNumber || nextNumber || '').trim();
    if (!n) { setTaken(null); return; }
    let live = true;
    const t = setTimeout(() => {
      apiFetch(`/projects/number-check?number=${encodeURIComponent(n)}`)
        .then(r => { if (live) setTaken(r && r.taken ? { number: n, ...r } : null); })
        .catch(() => { if (live) setTaken(null); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [jobNumber, nextNumber]);
  const lastSuggested = useRef('');   // the suggestion currently in the box, if the PM has not overtyped it
  // October to December (Pat, Sep 25 2026): ask whether the work starts this year or
  // next, because that decides the number series — 26xx / WO26… or 27xx / WO27….
  const thisYear = new Date().getFullYear();
  const askYear = new Date().getMonth() >= 9;
  const [startYear, setStartYear] = useState(askYear ? null : thisYear);
  // Only a year the PM actually gave — a button press or a typed start date — can be
  // held against the number. Before October the year is only assumed for the
  // suggestion, and a 27xx number typed in September is the PM's call (Pat, Sep 28
  // 2026: "it said it had a 26 start date but I can't see where it is getting that").
  const [yearSaid, setYearSaid] = useState(false);
  // The job's name (Pat, Sep 25 2026): suggested by the naming rule — Customer - Scope,
  // plus the town when the site is far from the shop — checked, and editable.
  const [jobName, setJobName] = useState(bid.job_name || '');
  const [nameHint, setNameHint] = useState(null);   // { suggested, warnings, far, town, minutes }
  const [form, setForm] = useState({ pm_id: bid.bid_pm_id || '', tsheets_jobcode_id: '' });   // no dates here — the Schedule tiles own them (Oct 1 2026)
  // Pre-check drilling workflows based on bid type
  const isDrillBid = bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling';
  // Pre-fill from the workflows chosen on the bid card; fall back to the bid type.
  const bidWorkflowKeys = (() => {
    try { const a = bid.bid_workflows ? JSON.parse(bid.bid_workflows) : []; if (Array.isArray(a) && a.length) return a; } catch {}
    const arr = [];
    if (bid.bid_type === 'Vertical Drilling') arr.push('vertical_drilling');
    if (bid.bid_type === 'Directional Drilling') arr.push('horizontal_drilling');
    // A Loops work type has no bid_type of its own — it defaults the workflow directly.
    if (bid.work_type === 'loops') arr.push('loops');
    return arr;
  })();
  const [workflows] = useState(() => new Set(bidWorkflowKeys));   // straight from the bid — no checklist (Oct 6 2026)
  const [customWorkflow, setCustomWorkflow] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [awardedById, setAwardedById] = useState('');
  // The quote this job was won on, and how far the bid has drifted from each one.
  const [quoteCheck, setQuoteCheck] = useState(null);
  const [usedQuoteId, setUsedQuoteId] = useState('');
  const [usedQuoteDocId, setUsedQuoteDocId] = useState('');   // a quote PDF from the bid's Documents (no recipient)

  // What each recipient's quote actually adds up to, and the PDF of it. Priced with
  // the SNAPSHOT's own bid settings (tax, overhead, profit, labour rate) where there
  // is one — pricing a saved quote with today's percentages would report a number
  // that was never quoted. Falls back to the live bid when nothing was recorded.
  const quoteDocFor = React.useCallback(rid => {
    const r = recipients.find(x => x.id === rid);
    let snap = null;
    try { snap = r?.quote_snapshot ? (typeof r.quote_snapshot === 'string' ? JSON.parse(r.quote_snapshot) : r.quote_snapshot) : null; } catch {}
    if (snap && Array.isArray(snap.items)) {
      const b = { ...bid, ...snap.bid };
      const its = snap.items.map((it, i) => ({
        ...it, _key: `snap-${i}`,
        sub_items: it.sub_items ? (typeof it.sub_items === 'string' ? JSON.parse(it.sub_items) : it.sub_items) : [],
      }));
      return { bid: b, items: its, recipient: r, fromSnapshot: true };
    }
    return { bid, items: currentItems, recipient: r, fromSnapshot: false };
  }, [recipients, bid, currentItems]);

  const quoteTotal = React.useCallback(rid => {
    const d = quoteDocFor(rid);
    try { return d.items.reduce((s, it) => s + itemEffectiveTotal(it, d.bid), 0); } catch { return null; }
  }, [quoteDocFor]);

  function openQuotePdf(rid) {
    const d = quoteDocFor(rid);
    if (!d.recipient) return;
    exportQuotePdf(d.bid, d.items, d.recipient, alternates);
  }
  const pms = employees.filter(e => ['Admin', 'ProjectManager', 'OfficeStaff'].includes(e.role));
  function pmName(e) { return e.firstName ? `${e.firstName} ${e.lastName}` : `${e.first_name} ${e.last_name}`; }

  // Fetch bid recipients on open. Pre-select the GC marked as low bidder so the
  // award auto-fills the GC on the new job (per the recipients checklist).
  useEffect(() => {
    apiFetch(`/bids/${bid.id}/recipients`).then(recs => {
      setRecipients(recs);
      const low = Array.isArray(recs) && recs.find(r => r.low_bidder);
      if (low) setAwardedById(prev => prev || low.id);
      // The recipient is the owner and there is no list to choose from → it is the owner.
      else if (bid.recipient_is_owner && bid.client_name && Array.isArray(recs) && recs.length === 0) setAwardedById(prev => prev || bid.client_name);
    }).catch(() => {});
  }, [bid.id]);

  // Load the quote comparison the first time the confirm step is opened.
  useEffect(() => {
    if (step !== 4 || quoteCheck) return;
    apiFetch(`/bids/${bid.id}/quote-check`)
      .then(d => {
        setQuoteCheck(d);
        const list = d.recipients || [];
        // Default to the obvious one: the low bidder, else the only quote actually
        // sent, else the only recipient. Never guess between several.
        const pick = list.find(r => r.low_bidder)
          || (list.filter(r => r.sent_at).length === 1 ? list.find(r => r.sent_at) : null)
          || (list.length === 1 ? list[0] : null);
        if (pick) setUsedQuoteId(prev => prev || pick.recipient_id);
        // No recipients: the newest quote PDF filed on the bid is the obvious one.
        else if (!list.length && (d.filed_quotes || []).length) setUsedQuoteDocId(prev => prev || d.filed_quotes[0].doc_id);
      })
      .catch(() => setQuoteCheck({ recipients: [] }));
  }, [step, bid.id, quoteCheck]);

  // Fetch the next job number when the division — and, late in the year, the start
  // year — is chosen. Until the year is answered there is no number to suggest.
  useEffect(() => {
    if (!division || !startYear) return;
    apiFetch(`/bids/next-number?division=${division}&year=${startYear}`)
      .then(r => {
        setNextNumber(r.number);
        // Only replace a number the PM has not touched — an empty box or the previous
        // suggestion. A typed 2704 stays 2704 when the start date changes the year.
        setJobNumber(prev => (!prev || prev === lastSuggested.current ? r.number : prev));
        lastSuggested.current = r.number;
      })
      .catch(() => {});
  }, [division, startYear]);
  // A start date typed on this step answers the question by itself — any month of the
  // year, so a job that starts next January gets next year's series suggested.
  // The series the typed number belongs to, so a mismatch with the answer can be flagged.
  const seriesOf = n => { const m = String(n || '').match(/^(?:WO)?(\d\d)/i); return m ? 2000 + Number(m[1]) : null; };

  async function submit() {
    setSaving(true); setErr('');
    const allWorkflows = [...workflows];
    if (customWorkflow.trim()) allWorkflows.push(customWorkflow.trim());
    const rec = recipients.find(r => r.id === awardedById);
    try {
      const result = await apiFetch(`/bids/${bid.id}/award`, {
        method: 'POST',
        body: JSON.stringify({
          ...form, division, job_number: jobNumber || nextNumber, job_name: jobName, workflows: allWorkflows,
          used_quote_recipient_id: usedQuoteId || null,
          used_quote_doc_id: !usedQuoteId && usedQuoteDocId ? usedQuoteDocId : null,
          awarded_by_name: rec ? `${rec.contact_name || ''} (${rec.client_name})`.trim() : awardedById || null,
          awarded_by_email: rec?.contact_email || null,
          awarded_by_phone: rec?.contact_phone || null,
        }),
      });
      onAward(result);
    } catch (ex) {
      // This file's apiFetch throws the raw body as the message; read the server's words out of it.
      let msg = ex.message, code = null, existing = null;
      try { const j = JSON.parse(ex.message); if (j && j.error) { msg = j.error; code = j.code || null; existing = j.existing || null; } } catch {}
      setErr(msg);
      // The number is already a job — offer to open that one instead of making another (WO26088, Oct 7 2026).
      if (code === 'number_taken') {
        if (existing && existing.id && window.confirm(`${msg}

Open ${existing.project_number || 'that job'} instead?`)) window.location.assign(`/admin/jobs/${existing.id}`);
        else if (!existing) window.alert(msg);
      }
    } finally { setSaving(false); }
  }

  const yy = new Date().getFullYear().toString().slice(2);
  const stepLabels = ['Job Type', 'Details', 'Workflows', 'Confirm Quote'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: 540, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ background: '#0f2d1f', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Award Bid</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{bid.job_name}</div>
            {bid.client_name && <div style={{ fontSize: 12, color: '#a7f3d0', marginTop: 2 }}>{bid.client_name}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6ee7b7', lineHeight: 1 }}>×</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          {/* No Workflows step (Pat, Oct 6 2026): the job gets the bid's workflows; the PM adds
              the rest the first time they open the job. */}
          {[['Job Type', 1], ['Details', 2], ['Confirm Quote', 4]].map(([label, n], i) => (
            <div key={n} style={{ flex: 1, padding: '9px 0', textAlign: 'center', fontSize: 11, fontWeight: 600,
              color: step === n ? '#16a34a' : step > n ? '#374151' : '#9ca3af',
              borderBottom: step === n ? '2px solid #16a34a' : '2px solid transparent' }}>
              {step > n ? '✓ ' : `${i + 1}. `}{label}
            </div>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>

        {moving && <MoveBidToJob bid={bid} onBack={() => setMoving(false)} onClose={onClose} />}

        {/* ── Step 1: Choose division ── */}
        {step === 1 && !moving && (
          <div style={{ padding: 24 }}>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: '#374151', fontWeight: 500 }}>What type of job is this?</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { key: 'large', label: 'Large Job', sub: 'Project', icon: '🏗️', desc: 'Full project workflow with sub-jobs and detailed tracking.', fmt: `${yy}001` },
                { key: 'small', label: 'Small Job', sub: 'Work Order', icon: '🔧', desc: 'Streamlined workflow, typically under $100,000.', fmt: `WO${yy}001` },
              ].map(opt => (
                <button key={opt.key} type="button" onClick={() => { setDivision(opt.key); setStep(2); }}
                  style={{ textAlign: 'left', padding: '18px 16px', borderRadius: 12, cursor: 'pointer', background: '#fff',
                    border: `2px solid #e5e7eb`, transition: 'border-color .15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#16a34a'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{opt.icon}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{opt.label}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{opt.sub}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 10 }}>{opt.desc}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Number format: <strong style={{ color: '#374151' }}>{opt.fmt}…</strong></div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <button type="button" onClick={() => setMoving(true)} title="The GC wants this as a change order or work order on a job we already have"
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px dashed #16a34a', background: '#f0fdf4', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>↪ Make Change to Existing Job</button>
              <button type="button" onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Step 2: Details + confirm job number ── */}
        {step === 2 && (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Awarding recipient */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Who is awarding this job to us?</label>
              {recipients.length > 0 ? (
                <select value={awardedById} onChange={e => setAwardedById(e.target.value)}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }}>
                  <option value="">— Select recipient —</option>
                  {recipients.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.client_name}{r.contact_name ? ` — ${r.contact_name}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={awardedById} onChange={e => setAwardedById(e.target.value)}
                  placeholder="No recipients on file — type name"
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
              )}
              {awardedById && recipients.find(r => r.id === awardedById)?.contact_email && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  {recipients.find(r => r.id === awardedById).contact_email}
                  {recipients.find(r => r.id === awardedById).contact_phone ? ` · ${formatPhone(recipients.find(r => r.id === awardedById).contact_phone)}` : ''}
                </div>
              )}
            </div>

            {askYear && (
              <div style={{ background: startYear ? '#f8fafc' : '#fffbeb', border: `1.5px solid ${startYear ? '#cbd5e1' : '#f59e0b'}`, borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111827' }}>When does this work start?</div>
                <div style={{ fontSize: 11.5, color: '#6b7280' }}>It picks the number series: {thisYear} is {String(thisYear).slice(2)}xx / WO{String(thisYear).slice(2)}…, {thisYear + 1} is {String(thisYear + 1).slice(2)}xx / WO{String(thisYear + 1).slice(2)}….</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[thisYear, thisYear + 1].map(y => (
                    <button key={y} type="button" onClick={() => { setStartYear(y); setYearSaid(true); }}
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: `2px solid ${startYear === y ? '#16a34a' : '#e5e7eb'}`, background: startYear === y ? '#f0fdf4' : '#fff', color: startYear === y ? '#14532d' : '#374151', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                      {y === thisYear ? `This year (${y})` : `Next year (${y})`}
                    </button>
                  ))}
                </div>
                {!startYear && <div style={{ fontSize: 11.5, color: '#92400e', fontWeight: 700 }}>Pick one and the number fills in.</div>}
              </div>
            )}

            {/* Job number — confirmed & editable */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>{division === 'small' ? '🔧' : '🏗️'}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5 }}>{division === 'small' ? 'Work Order Number' : 'Project Number'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Auto-generated — override if needed</div>
                </div>
              </div>
              <input
                value={jobNumber}
                onChange={e => setJobNumber(e.target.value)}
                placeholder={nextNumber ? nextNumber : 'e.g. WO26901'}
                style={{ width: '100%', border: `1.5px solid ${taken ? '#dc2626' : '#16a34a'}`, borderRadius: 7, padding: '9px 12px', fontSize: 18, fontWeight: 800, color: taken ? '#b91c1c' : '#14532d', letterSpacing: 1, background: taken ? '#fef2f2' : '#fff', boxSizing: 'border-box' }}
              />
              {taken && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c', fontWeight: 800 }}>⛔ {taken.error}</div>
              )}
              {yearSaid && startYear && jobNumber && seriesOf(jobNumber) && seriesOf(jobNumber) !== startYear && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>⚠ {jobNumber} is in the {String(seriesOf(jobNumber)).slice(2)}xx series, but {form.start_date ? 'the start date' : 'you said the work'} is in {startYear}{nextNumber ? ` — the next ${String(startYear).slice(2)}xx number is ${nextNumber}` : ''}.</div>
              )}
              {!yearSaid && jobNumber && seriesOf(jobNumber) === thisYear + 1 && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: '#6b7280' }}>{jobNumber} is a {thisYear + 1} number. Fine if the work starts next year — a start date below confirms it.</div>
              )}
              {nextNumber && !jobNumber && (
                <button type="button" onClick={() => setJobNumber(nextNumber)}
                  style={{ marginTop: 4, alignSelf: 'flex-start', background: 'none', border: 'none', fontSize: 11, color: '#16a34a', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                  Use suggested: {nextNumber}
                </button>
              )}
            </div>

            <JobNameBlock bid={bid} jobName={jobName} setJobName={setJobName} hint={nameHint} setHint={setNameHint}
              customer={(recipients.find(r => r.id === awardedById) || {}).client_name || bid.client_name || ''} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Project Manager</label>
              <select value={form.pm_id} onChange={e => setForm(f => ({ ...f, pm_id: e.target.value }))}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }}>
                <option value="">— Select PM —</option>
                {pms.map(e => <option key={e.id} value={e.id}>{pmName(e)}</option>)}
              </select>
            </div>

            {/* No start or completion date here (Ryan, Oct 1 2026): when the job runs is set
                on the Schedule tiles once it is a job, and that is what every report reads. */}
            <div style={{ fontSize: 11.5, color: '#6b7280', background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
              📅 Dates come from the Schedule tiles once the job exists — Big Picture for the dirt and electrical lines, the card's start and working days for drilling, loops and work orders.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>TSheets Job Code ID</label>
              <input value={form.tsheets_jobcode_id} onChange={e => setForm(f => ({ ...f, tsheets_jobcode_id: e.target.value }))}
                placeholder="Optional — can be added later"
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
            </div>

            {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 4 }}>
              <button type="button" onClick={() => setStep(1)} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>← Back</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="button" onClick={() => { if (taken) { window.alert(taken.error); return; } if (!jobNumber.trim() && nextNumber) setJobNumber(nextNumber); setStep(4); }}
                  disabled={!jobNumber.trim() && !nextNumber}
                  title={taken ? taken.error : undefined}
                  style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: taken ? '#9ca3af' : '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (jobNumber.trim() || nextNumber) ? 1 : 0.5 }}>
                  Next: Workflows →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — confirm which quote this job was won on, and lock it.
            Until now "View Used Quote PDF" re-rendered live data, so the quote you
            saw after an award was whatever the bid said that day. This is where a
            person looks at what actually went out and says yes. */}
        {step === 4 && (
          <div style={{ padding: '18px 22px 20px', overflowY: 'auto' }}>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 4, fontWeight: 700 }}>Which quote won this job?</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
              This gets locked to the job — from here, “View Used Quote PDF” shows this one and stops following later edits to the bid.
            </div>

            {quoteCheck === null && <div style={{ fontSize: 13, color: '#9ca3af', padding: '12px 0' }}>Checking the quotes…</div>}

            {/* No recipients, but quote PDFs filed on the bid (exported straight from the bid, or uploaded):
                pick the one this job was won on — that PDF becomes "View Used Quote PDF" (Ryan, Oct 14 2026). */}
            {quoteCheck && quoteCheck.recipients.length === 0 && (quoteCheck.filed_quotes || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Quotes saved on this bid — pick the one that won it:</div>
                {quoteCheck.filed_quotes.map(q => {
                  const on = usedQuoteDocId === q.doc_id;
                  return (
                    <label key={q.doc_id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1.5px solid ${on ? '#16a34a' : '#e5e7eb'}`, background: on ? '#f0fdf4' : '#fff', borderRadius: 10, padding: '9px 11px', cursor: 'pointer' }}>
                      <input type="radio" checked={on} onChange={() => setUsedQuoteDocId(q.doc_id)} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.title}</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{q.to ? `To ${q.to} · ` : ''}{new Date(q.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{q.revision > 1 ? ` · rev ${q.revision}` : ''}{q.printed ? '' : ' · uploaded'}</span>
                      </span>
                      <a href={`${API_BASE}/documents/${q.doc_id}/file`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>Open ↗</a>
                    </label>
                  );
                })}
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: '#6b7280', padding: '2px 4px', cursor: 'pointer' }}>
                  <input type="radio" checked={!usedQuoteDocId} onChange={() => setUsedQuoteDocId('')} /> None of these — award without a recorded quote
                </label>
              </div>
            )}
            {quoteCheck && quoteCheck.recipients.length === 0 && !(quoteCheck.filed_quotes || []).length && (
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '11px 13px', fontSize: 12.5, color: '#7c2d12', lineHeight: 1.5, marginBottom: 14 }}>
                <strong>No quote recipients on this bid.</strong> There's nothing to lock, so the job will be awarded without a recorded quote. You can still add a recipient and export a quote afterwards.
              </div>
            )}

            {quoteCheck && quoteCheck.recipients.map(r => {
              const picked = usedQuoteId === r.recipient_id;
              const drift = r.changes || [];
              return (
                <div key={r.recipient_id} onClick={() => setUsedQuoteId(r.recipient_id)}
                  style={{ border: `2px solid ${picked ? '#16a34a' : '#e5e7eb'}`, background: picked ? '#f0fdf4' : '#fff',
                    borderRadius: 10, padding: '11px 13px', marginBottom: 10, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: '#111827', flex: 1 }}>{r.client_name}</span>
                    {r.low_bidder && <span style={{ fontSize: 10, fontWeight: 800, color: '#15803d', background: '#dcfce7', borderRadius: 4, padding: '2px 6px' }}>LOW BIDDER</span>}
                    {picked && <span style={{ fontSize: 12, fontWeight: 800, color: '#16a34a' }}>✓</span>}
                  </div>
                  {/* The number is what people actually recognise a quote by — the one
                      thing that makes "is this the one we won on?" answerable at a
                      glance. Priced from the snapshot's own settings, so it's the
                      figure that was quoted, not today's arithmetic. */}
                  {(() => {
                    const total = quoteTotal(r.recipient_id);
                    return total == null ? null : (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 5 }}>
                        <span style={{ fontSize: 20, fontWeight: 800, color: picked ? '#15803d' : '#111827' }}>
                          {'$' + Math.round(total).toLocaleString('en-US')}
                        </span>
                        <button onClick={e => { e.stopPropagation(); openQuotePdf(r.recipient_id); }}
                          title={r.has_snapshot ? 'Open the quote exactly as it was sent' : 'Open this quote — built from the bid as it stands today'}
                          style={{ background: 'none', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1d4ed8', fontSize: 11, fontWeight: 700, padding: '3px 9px', cursor: 'pointer' }}>
                          📄 View the PDF
                        </button>
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 3 }}>
                    {r.contact_name ? `${r.contact_name} · ` : ''}
                    {r.sent_at ? `quoted ${new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'never marked as sent'}
                    {r.has_snapshot ? ` · ${r.item_count} lines recorded` : ''}
                  </div>

                  {!r.has_snapshot && (
                    <div style={{ marginTop: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '8px 10px', fontSize: 11.5, color: '#92400e', lineHeight: 1.5 }}>
                      No record of what this quote said — it went out before the app started keeping them. Picking it captures the bid <strong>as it is today</strong>, which may not match what was sent.
                    </div>
                  )}

                  {r.has_snapshot && drift.length === 0 && (
                    <div style={{ marginTop: 8, fontSize: 11.5, color: '#15803d', fontWeight: 700 }}>
                      ✓ The bid still matches this quote exactly.
                    </div>
                  )}

                  {r.has_snapshot && drift.length > 0 && (
                    <div style={{ marginTop: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 11px' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: '#b91c1c', marginBottom: 5 }}>
                        ⚠ The bid has changed in {drift.length} place{drift.length === 1 ? '' : 's'} since this went out
                      </div>
                      {/* What the drift is worth. "3 lines changed" doesn't tell you
                          whether to care; "the bid is $4,180 higher than this quote"
                          does. */}
                      {(() => {
                        const quoted = quoteTotal(r.recipient_id);
                        let nowTotal = null;
                        try { nowTotal = currentItems.reduce((s, it) => s + itemEffectiveTotal(it, bid), 0); } catch {}
                        if (quoted == null || nowTotal == null) return null;
                        const diff = Math.round(nowTotal - quoted);
                        if (diff === 0) return (
                          <div style={{ fontSize: 11.5, color: '#7f1d1d', marginBottom: 5 }}>The total is the same either way — {'$' + Math.round(quoted).toLocaleString('en-US')}.</div>
                        );
                        return (
                          <div style={{ fontSize: 11.5, color: '#7f1d1d', marginBottom: 5 }}>
                            Quoted <strong>{'$' + Math.round(quoted).toLocaleString('en-US')}</strong>; the bid now comes to{' '}
                            <strong>{'$' + Math.round(nowTotal).toLocaleString('en-US')}</strong>{' '}
                            <span style={{ fontWeight: 800 }}>({diff > 0 ? '+' : '−'}{'$' + Math.abs(diff).toLocaleString('en-US')})</span>.
                          </div>
                        );
                      })()}
                      <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                        {drift.slice(0, 12).map((c, i) => (
                          <div key={i} style={{ fontSize: 11, color: '#7f1d1d', lineHeight: 1.5, marginBottom: 2 }}>
                            <strong>{c.kind === 'changed' ? 'Changed' : c.kind === 'added' ? 'Added' : 'Removed'}</strong>
                            {' · '}{String(c.description || '').slice(0, 40)}
                            {c.kind === 'changed' && c.fields && (
                              <span style={{ color: '#991b1b' }}> — {c.fields.map(f => `${f.replace(/_/g, ' ')}: ${c.from[f] ?? '—'} → ${c.to[f] ?? '—'}`).join(', ')}</span>
                            )}
                          </div>
                        ))}
                        {drift.length > 12 && <div style={{ fontSize: 11, color: '#991b1b', fontStyle: 'italic' }}>…and {drift.length - 12} more</div>}
                      </div>
                      <div style={{ fontSize: 11, color: '#7f1d1d', marginTop: 6 }}>
                        You can still lock this quote — it records what was sent, not what the bid says now.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 12px', color: '#b91c1c', fontSize: 12.5, marginTop: 6 }}>{err}</div>}

            {/* What comes over: the bid's workflows, nothing else (Pat, Oct 6 2026). */}
            <div style={{ fontSize: 12.5, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
              <b>Workflows from the bid:</b> {[...workflows].length ? [...workflows].map(k => ([...WORKFLOW_OPTIONS, ...OFFICE_WORKFLOW_OPTIONS].find(o => o.key === k) || { label: k }).label).join(', ') : 'none'}
              <span style={{ color: '#9ca3af' }}> — the PM adds the rest the first time they open the job.</span>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 10 }}>
              <button type="button" onClick={() => setStep(2)} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>← Back</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="button" onClick={submit} disabled={saving || (quoteCheck && quoteCheck.recipients.length > 0 && !usedQuoteId)}
                  title={quoteCheck && quoteCheck.recipients.length > 0 && !usedQuoteId ? 'Pick the quote this job was won on' : ''}
                  style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                    background: (saving || (quoteCheck && quoteCheck.recipients.length > 0 && !usedQuoteId)) ? '#d1d5db' : '#16a34a',
                    color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Creating…' : `Confirm & Create ${division === 'small' ? 'Work Order' : 'Project'} →`}
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
}

// ── Hole Schedule constants & helpers ────────────────────────────────────────
const DRILL_RATES = { Volvo: 3500, Ella: 5000, Harold: 7000 };
const DIAMETERS = [18, 24, 30, 36, 42, 48, 54, 60, 66, 72];
// Directional (horizontal) bores also come in the smaller conduit sizes (2"–24"
// in 2" increments), so that bid type gets those plus the larger sizes.
const DIAMETERS_DIRECTIONAL = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 30, 36, 42, 48, 54, 60, 66, 72];
const SOIL_OPTIONS = ['Good', 'Cobble', 'Rock', 'Urban Area'];

function computeHoleItems(schedule, bid) {
  if (!Array.isArray(schedule)) return [];
  return schedule
    .filter(r => r.qty > 0 && r.diameter && r.holes_per_day > 0 && r.drill)
    .map(r => {
      const qty = parseFloat(r.qty);
      const days = Math.ceil(qty / r.holes_per_day);
      const drillCost = days * (DRILL_RATES[r.drill] || 0);
      const costPerHole = qty > 0 ? drillCost / qty : 0;
      return {
        _key: `hole-${r.id}`,
        id: null,
        auto_source: 'hole_schedule',
        sort_order: -9000,
        item_number: '',
        pay_item_number: '',
        description: `${r.soil || 'Unspecified'} – ${r.diameter}" Dia. × ${r.depth || '?'} ${bid.bid_type === 'Directional Drilling' ? 'Length' : 'Depth'}`,
        quantity: qty,
        unit: 'EA',
        material_cost: 0,
        material_escalation: 0,
        labor_hours: 0,
        dje_standard: costPerHole,
        dje_takeoff: 0,
        overhead_material_pct: 0,
        overhead_labor_pct: 0,
        profit_pct: 0,
        rounding: 0,
        notes: '',
      };
    });
}

function HoleScheduleTable({ value, isLocked, onChange, bidType }) {
  const rows = Array.isArray(value) ? value : [];
  const diameters = bidType === 'Directional Drilling' ? DIAMETERS_DIRECTIONAL : DIAMETERS;

  function addRow() {
    onChange([...rows, { id: Date.now().toString(), qty: '', diameter: '', depth: '', soil: '', drill: '', holes_per_day: '' }]);
  }
  function updateRow(id, field, val) {
    onChange(rows.map(r => r.id === id ? { ...r, [field]: val } : r));
  }
  function removeRow(id) {
    onChange(rows.filter(r => r.id !== id));
  }

  const th = { padding: '8px 10px', fontWeight: 700, fontSize: 11, color: '#fff', textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', verticalAlign: 'middle' };
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 8px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const sel = { ...inp, background: '#fff', cursor: 'pointer' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#4a1d50', textTransform: 'uppercase', letterSpacing: 0.5 }}>Hole Schedule</div>
        {!isLocked && (
          <button onClick={addRow}
            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#4a1d50', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + Add Hole Size
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: 13, background: '#f9fafb', borderRadius: 8, border: '1px dashed #e5e7eb' }}>
          No holes added yet.{!isLocked && ' Click "+ Add Hole Size" to begin.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 780 }}>
            <thead>
              <tr style={{ background: '#4a1d50' }}>
                <th style={th}>QTY</th>
                <th style={th}>Diameter</th>
                <th style={th}>{bidType === 'Directional Drilling' ? 'Length' : 'Depth'}</th>
                <th style={th}>Soil Condition</th>
                <th style={th}>Drill</th>
                <th style={th}>Holes/Day</th>
                <th style={{ ...th, textAlign: 'center' }}>Days</th>
                <th style={{ ...th, textAlign: 'right' }}>Drill Cost</th>
                {!isLocked && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const qty = parseFloat(r.qty) || 0;
                const hpd = parseFloat(r.holes_per_day) || 0;
                const days = qty > 0 && hpd > 0 ? Math.ceil(qty / hpd) : null;
                const cost = days ? days * (DRILL_RATES[r.drill] || 0) : null;
                return (
                  <tr key={r.id} style={{ background: idx % 2 === 0 ? '#fff' : '#faf5ff', borderBottom: '1px solid #ede9fe' }}>
                    <td style={td}>
                      <input type="number" min="1" value={r.qty} disabled={isLocked}
                        onChange={e => updateRow(r.id, 'qty', e.target.value)}
                        style={{ ...inp, width: 60 }} />
                    </td>
                    <td style={td}>
                      <select value={r.diameter} disabled={isLocked} onChange={e => updateRow(r.id, 'diameter', e.target.value)} style={{ ...sel, width: 90 }}>
                        <option value="">—</option>
                        {diameters.map(d => <option key={d} value={d}>{d}"</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <input type="text" value={r.depth} disabled={isLocked} placeholder='e.g. 20&apos;'
                        onChange={e => updateRow(r.id, 'depth', e.target.value)}
                        style={{ ...inp, width: 80 }} />
                    </td>
                    <td style={td}>
                      <select value={r.soil} disabled={isLocked} onChange={e => updateRow(r.id, 'soil', e.target.value)} style={{ ...sel, width: 120 }}>
                        <option value="">—</option>
                        {SOIL_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <select value={r.drill} disabled={isLocked} onChange={e => updateRow(r.id, 'drill', e.target.value)} style={{ ...sel, width: 170 }}>
                        <option value="">—</option>
                        <option value="Volvo">Volvo ($3,500/day)</option>
                        <option value="Ella">Ella ($5,000/day)</option>
                        <option value="Harold">Harold ($7,000/day)</option>
                      </select>
                    </td>
                    <td style={td}>
                      <input type="number" min="1" value={r.holes_per_day} disabled={isLocked}
                        onChange={e => updateRow(r.id, 'holes_per_day', e.target.value)}
                        style={{ ...inp, width: 70 }} />
                    </td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 600, color: days ? '#4a1d50' : '#9ca3af' }}>
                      {days ?? '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: cost ? '#064e3b' : '#9ca3af' }}>
                      {cost ? `$${cost.toLocaleString()}` : '—'}
                    </td>
                    {!isLocked && (
                      <td style={td}>
                        <button onClick={() => removeRow(r.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '0 4px' }}>×</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Pre-Bid Meeting Block ─────────────────────────────────────────────────────
// Category taxonomy is managed in Settings → Categories and read via
// useCategories() inside each component that needs it (Vendor Quotes, Submittals).

export function VendorQuotesSection({ bid, isLocked, show, onToggle, attachments, onQuotesChange, onFileUpload, onFileRemove, getToken, API_BASE, bidId, projectId, onPOCreated, showToast, readFirst = false }) {
  const { material: DEFAULT_MATERIAL_QUOTES, sub: DEFAULT_SUB_QUOTES } = useCategories();
  // readFirst (the bid page's tile): open to a card of the quotes — vendor, amount,
  // status, files, PO — and only show the working columns after ✎ Edit.
  const [editing, setEditing] = React.useState(false);
  const readMode = readFirst && !editing;
  function parseItems(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.length) return [];
      // Migrate old flat format (had 'enabled' field)
      if ('enabled' in parsed[0]) {
        return parsed.filter(q => q.enabled).map(q => ({
          id: q.id, name: q.name, category: q.category,
          quotes: [{ id: `mig-${q.id}`, vendorId: null, vendorName: q.notes || '', status: q.status || 'Pending', amount: null, notes: '', files: q.files || [] }]
        }));
      }
      return parsed;
    } catch { return []; }
  }

  const [items, setItems] = React.useState(() => parseItems(bid.vendor_quotes));
  const [vendors, setVendors] = React.useState([]);
  const [wizard, setWizard] = React.useState(null);
  const [uploading, setUploading] = React.useState(null);
  const [poLookup, setPoLookup] = React.useState({}); // po_id -> po_number (covers POs made before we stored the number on the quote)
  const [markupAtt, setMarkupAtt] = React.useState(null); // vendor-quote PDF open in the markup tool
  const dropRefs = React.useRef({});

  const isPdf = att => (att.mime_type || '').includes('pdf') || /\.pdf$/i.test(att.original_name || '');

  // "+ Create PO" on a Used quote opens the normal PO window, filled in from the quote, and
  // it stays open until saved or cancelled; the quote's file goes onto the PO. It used to
  // write the PO on the click with nothing to review and no file (Bart, Oct 2 2026).
  const [poFor, setPoFor] = React.useState(null);       // { itemId, quoteId, vendor, description, cost, notes, files }
  const [poVendors, setPoVendors] = React.useState(null);
  const [poJobLabel, setPoJobLabel] = React.useState('');
  function openPoFor(it, q) {
    if (!poJobLabel && projectId) apiFetch(`/projects/${projectId}`).then(p => { if (p) { const num = String(p.project_number || '').trim(), nm = String(p.name || '').trim(); setPoJobLabel(num && nm.startsWith(num) ? nm : [num, nm].filter(Boolean).join(' — ')); } }).catch(() => {});
    if (!poVendors) apiFetch('/vendors').then(v => setPoVendors(Array.isArray(v) ? v : [])).catch(() => setPoVendors([]));
    setPoFor({ itemId: it.id, quoteId: q.id, vendor: q.vendorName || '', cost: q.amount ?? null, notes: q.notes || '',
      description: [it.name || it.category || 'Material', q.vendorName ? `per ${q.vendorName} quote` : null].filter(Boolean).join(' — '),
      files: attachments.filter(a => (q.files || []).includes(a.id)) });
  }

  async function saveMarkup(att, annotations) {
    await apiFetch(`/bids/${bidId}/attachments/${att.id}/annotations`, { method: 'PATCH', body: JSON.stringify({ annotations }) });
    att.annotations = JSON.stringify(annotations); // keep the in-memory copy fresh so re-opening shows saved marks
  }

  React.useEffect(() => { setItems(parseItems(bid.vendor_quotes)); }, [bid.vendor_quotes]);
  React.useEffect(() => {
    fetch(`${API_BASE}/vendors/active`, { headers: { 'x-worker-token': getToken() } })
      .then(r => r.json()).then(setVendors).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (!projectId) return;
    fetch(`${API_BASE}/projects/${projectId}/purchase-orders`, { headers: { 'x-worker-token': getToken() } })
      .then(r => r.json()).then(pos => {
        if (!Array.isArray(pos)) return;
        const map = {};
        pos.forEach(p => { map[p.id] = p.po_number; });
        setPoLookup(map);
      }).catch(() => {});
  }, [projectId]);

  const poNumberFor = q => q.po_number || poLookup[q.po_id] || null;

  function update(next) { setItems(next); onQuotesChange(next); }

  function setQuoteField(itemId, quoteId, field, value) {
    update(items.map(it => it.id !== itemId ? it : {
      ...it, quotes: it.quotes.map(q => q.id !== quoteId ? q : { ...q, [field]: value })
    }));
  }

  function removeQuote(itemId, quoteId) {
    update(items.map(it => it.id !== itemId ? it : { ...it, quotes: it.quotes.filter(q => q.id !== quoteId) })
      .filter(it => it.quotes.length > 0));
  }

  function openWizard(prefillItemId) {
    if (prefillItemId) {
      const it = items.find(i => i.id === prefillItemId);
      setWizard({ isNewItem: false, category: it.category, itemId: prefillItemId, itemName: it.name, vendorId: '', vendorName: '' });
    } else {
      setWizard({ isNewItem: true, category: '', itemId: '', itemName: '', isCustom: false, vendorId: '', vendorName: '' });
    }
  }

  function confirmWizard() {
    const w = wizard;
    const vName = w.vendorId ? (vendors.find(v => v.id === w.vendorId)?.name || '') : w.vendorName.trim();
    const newQ = { id: `vq-${Date.now()}`, vendorId: w.vendorId || null, vendorName: vName, status: 'Pending', amount: null, notes: '', files: [] };
    if (!w.isNewItem) {
      update(items.map(it => it.id === w.itemId ? { ...it, quotes: [...it.quotes, newQ] } : it));
    } else {
      const existing = items.find(it => it.name === w.itemName && it.category === w.category);
      if (existing) {
        update(items.map(it => it.id === existing.id ? { ...it, quotes: [...it.quotes, newQ] } : it));
      } else {
        update([...items, { id: `item-${Date.now()}`, name: w.itemName, category: w.category, quotes: [newQ] }]);
      }
    }
    setWizard(null);
  }

  const wizardValid = wizard && wizard.category && wizard.itemName.trim() && (wizard.vendorId || wizard.vendorName.trim());
  const itemOptions = wizard?.category === 'Material' ? DEFAULT_MATERIAL_QUOTES : wizard?.category === 'Sub' ? DEFAULT_SUB_QUOTES : [];
  const SC = { Pending: { bg: '#fffbeb', color: '#d97706' }, Received: { bg: '#ecfdf5', color: '#059669' }, Used: { bg: '#d1fae5', color: '#065f46' }, 'N/A': { bg: '#f3f4f6', color: '#6b7280' } };

  async function handleFileDrop(e, quoteId) {
    e.preventDefault();
    if (dropRefs.current[quoteId]) dropRefs.current[quoteId].style.background = '';
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    setUploading(quoteId);
    try { for (const f of files) await onFileUpload(f, quoteId, items); } finally { setUploading(null); }
  }
  async function handleFileInput(e, quoteId) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(quoteId);
    try { for (const f of files) await onFileUpload(f, quoteId, items); } finally { setUploading(null); }
  }

  function renderCategory(category, label) {
    const catItems = items.filter(it => it.category === category);
    return (
      <div style={{ flex: 1, minWidth: 280 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #e5e7eb' }}>{label}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {catItems.map(it => {
            const usedQ = it.quotes.find(q => q.status === 'Used');
            return (
              <div key={it.id} style={{ border: usedQ ? '2px solid #6ee7b7' : '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                {/* Item header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: usedQ ? '#ecfdf5' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontWeight: 700, fontSize: 13, flex: 1, color: usedQ ? '#065f46' : '#111827' }}>{it.name}</span>
                  {usedQ && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#065f46', whiteSpace: 'nowrap' }}>
                      ✓ {usedQ.vendorName || '—'} · {category === 'Sub' ? 'Sub' : 'Material'}{usedQ.amount != null ? ` · $${Number(usedQ.amount).toLocaleString()}` : ''}
                    </span>
                  )}
                  {!isLocked && (
                    <button onClick={() => openWizard(it.id)}
                      style={{ padding: '3px 9px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 11, fontWeight: 600, color: '#374151', cursor: 'pointer', flexShrink: 0 }}>
                      + Quote
                    </button>
                  )}
                </div>
                {/* Vendor quote cards stacked */}
                {it.quotes.map((q, qi) => {
                  const sc = SC[q.status] || SC['Pending'];
                  const isUsed = q.status === 'Used';
                  const qFiles = attachments.filter(a => (q.files || []).includes(a.id));
                  const inputId = `vq-file-${q.id}`;
                  return (
                    <div key={q.id} style={{ borderTop: qi > 0 ? '1px solid #e5e7eb' : 'none', background: isUsed ? '#f0fdf4' : '#fff', padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ flex: 1, fontWeight: 600, fontSize: 12, color: isUsed ? '#065f46' : '#374151' }}>
                          {q.vendorName || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No vendor</span>}
                        </span>
                        <select value={q.status} disabled={isLocked}
                          onChange={e => setQuoteField(it.id, q.id, 'status', e.target.value)}
                          style={{ border: 'none', borderRadius: 12, padding: '3px 8px', fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color, cursor: isLocked ? 'default' : 'pointer' }}>
                          <option>Pending</option><option>Received</option><option>Used</option><option>N/A</option>
                        </select>
                        {isUsed && projectId && (
                          q.po_id ? (
                            <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              ✓ PO {poNumberFor(q) ? `#${poNumberFor(q)}` : 'Created'}
                            </span>
                          ) : (
                            <button onClick={() => openPoFor(it, q)}
                              style={{ fontSize: 10, background: '#92400e', color: '#fff', border: 'none', borderRadius: 10, padding: '3px 10px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              + Create PO
                            </button>
                          )
                        )}
                        {!isLocked && (
                          <button onClick={() => removeQuote(it.id, q.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
                        )}
                      </div>
                      {(() => {
                        // Amount is enterable on every quote (not just Used) so all
                        // vendors' prices can be compared; the cheapest gets a badge.
                        const amts = it.quotes.map(x => x.amount).filter(v => v != null && !isNaN(v));
                        const lowest = amts.length ? Math.min(...amts) : null;
                        const isLowest = q.amount != null && lowest != null && Number(q.amount) === lowest && amts.length > 1;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={{ fontSize: 12, color: isUsed ? '#065f46' : '#6b7280', fontWeight: 700 }}>$</span>
                            <input type="number" value={q.amount ?? ''} disabled={isLocked}
                              placeholder="Quote amount"
                              onChange={e => setQuoteField(it.id, q.id, 'amount', e.target.value ? parseFloat(e.target.value) : null)}
                              style={{ flex: 1, border: `1.5px solid ${isUsed ? '#6ee7b7' : '#d1d5db'}`, borderRadius: 6, padding: '5px 8px', fontSize: 13, fontWeight: 700, color: isUsed ? '#065f46' : '#374151', background: isUsed ? '#f0fdf4' : '#fff' }} />
                            {isLowest && <span title="Lowest quote in this package" style={{ fontSize: 10, fontWeight: 800, color: '#15803d', background: '#dcfce7', padding: '2px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>LOWEST</span>}
                          </div>
                        );
                      })()}
                      <input value={q.notes} disabled={isLocked} placeholder="Notes…"
                        onChange={e => setQuoteField(it.id, q.id, 'notes', e.target.value)}
                        style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', fontSize: 11, background: isLocked ? '#f9fafb' : '#fff', boxSizing: 'border-box' }} />
                      {!isLocked && (
                        <div ref={el => dropRefs.current[q.id] = el}
                          onClick={() => document.getElementById(inputId)?.click()}
                          onDragOver={e => { e.preventDefault(); if (dropRefs.current[q.id]) dropRefs.current[q.id].style.background = '#f0f9ff'; }}
                          onDragLeave={e => { if (dropRefs.current[q.id]) dropRefs.current[q.id].style.background = ''; }}
                          onDrop={e => handleFileDrop(e, q.id)}
                          style={{ marginTop: 6, border: '1.5px dashed #d1d5db', borderRadius: 6, padding: '6px', textAlign: 'center', cursor: 'pointer', color: '#9ca3af', fontSize: 10, transition: 'background 0.15s' }}>
                          {uploading === q.id ? 'Uploading…' : 'Drop file or click to upload'}
                          <input id={inputId} type="file" multiple style={{ display: 'none' }} onChange={e => handleFileInput(e, q.id)} />
                        </div>
                      )}
                      {qFiles.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 5 }}>
                          {qFiles.map(att => (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', background: '#f3f4f6', borderRadius: 4 }}>
                              <span style={{ flex: 1, fontSize: 10, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_name}</span>
                              {att.annotations && att.annotations !== '[]' && <span title="Has markup" style={{ fontSize: 10, color: '#e11d48' }}>✏️</span>}
                              <a href={`${API_BASE}/bids/${bidId}/attachments/${att.id}/download?token=${getToken()}`} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#6366f1', textDecoration: 'none' }}>View</a>
                              {isPdf(att) && <button onClick={() => setMarkupAtt(att)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#e11d48', fontWeight: 700, padding: 0 }}>Markup</button>}
                              {!isLocked && <button onClick={() => onFileRemove(att.id, q.id, items)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, padding: 0 }}>×</button>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {catItems.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>No {label.toLowerCase()} yet — click "+ Add Quote" above to get started.</div>
          )}
        </div>
      </div>
    );
  }

  const totalQuotes = items.reduce((s, it) => s + it.quotes.length, 0);
  const usedCount = items.reduce((s, it) => s + it.quotes.filter(q => q.status === 'Used').length, 0);
  const poNumbers = [...new Set(items.flatMap(it => it.quotes).filter(q => q.po_id).map(poNumberFor).filter(Boolean))];

  return (
    <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <div onClick={onToggle} style={{ background: '#92400e', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12 2H2a1 1 0 00-1 1v8a1 1 0 001 1h10a1 1 0 001-1V3a1 1 0 00-1-1z" stroke="#fff" strokeWidth="1.3" fill="none"/><path d="M1 5h12" stroke="#fff" strokeWidth="1.2"/><path d="M5 9h4" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/></svg>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Vendor Quotes{totalQuotes > 0 ? ` (${totalQuotes} quote${totalQuotes !== 1 ? 's' : ''}${usedCount > 0 ? `, ${usedCount} used` : ''})` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={e => e.stopPropagation()}>
          {poNumbers.length > 0 && (
            <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: 10, fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>
              {poNumbers.length === 1 ? `PO #${poNumbers[0]}` : `POs ${poNumbers.map(n => `#${n}`).join(', ')}`}
            </span>
          )}
          {show && !isLocked && !readMode && (
            <button onClick={() => openWizard(null)}
              style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#fff', color: '#92400e', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              + Add Quote
            </button>
          )}
          {show && readFirst && !isLocked && (
            <button onClick={() => setEditing(e => !e)}
              style={{ padding: '4px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.5)', background: editing ? '#fff' : 'transparent', color: editing ? '#92400e' : '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
              {editing ? 'Done' : '✎ Edit'}
            </button>
          )}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" onClick={onToggle} style={{ transform: show ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><path d="M2 4l4 4 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
      </div>
      {show && readMode && (() => {
        const lblS = { fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 };
        const statusTint = s => s === 'Used' ? '#15803d' : s === 'Received' ? '#1d4ed8' : s === 'Declined' ? '#b91c1c' : '#a16207';
        const money = n => n == null || n === '' ? null : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const block = (cat, label) => {
          const list = items.filter(it => it.category === cat);
          return (
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <div style={lblS}>{label}</div>
              {list.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>None yet</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {list.map(it => (
                  <div key={it.id}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111', marginBottom: 3 }}>{it.name}</div>
                    {it.quotes.length === 0 && <div style={{ fontSize: 12.5, color: '#9ca3af', paddingLeft: 10 }}>No quotes yet</div>}
                    {it.quotes.map(q => {
                      const files = (q.files || []).map(fid => attachments.find(a => a.id === fid)).filter(Boolean);
                      const po = q.po_id ? poNumberFor(q) : null;
                      return (
                        <div key={q.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0 4px 10px', borderLeft: `2px solid ${statusTint(q.status)}33`, fontSize: 13, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, color: '#111' }}>{q.vendorName || 'Vendor TBD'}</span>
                          <span style={{ fontWeight: 800, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{money(q.amount) || <span style={{ color: '#9ca3af', fontWeight: 500 }}>no price yet</span>}</span>
                          <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: `${statusTint(q.status)}14`, color: statusTint(q.status) }}>{q.status || 'Pending'}</span>
                          {po && <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#15803d' }}>PO #{po}</span>}
                          {files.map(f => (
                            <a key={f.id} href={`${API_BASE}/bids/${bidId}/attachments/${f.id}/download?token=${getToken()}`} target="_blank" rel="noreferrer"
                              style={{ fontSize: 12, color: '#1d4ed8', textDecoration: 'none', fontWeight: 600 }}>📄 {f.original_name}</a>
                          ))}
                          {q.notes && <span style={{ flexBasis: '100%', fontSize: 12, color: '#6b7280' }}>{q.notes}</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        };
        return (
          <div style={{ padding: 20, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {block('Material', 'Material Quotes')}
            <div style={{ width: 1, background: '#e5e7eb', alignSelf: 'stretch' }} />
            {block('Sub', 'Subcontractor Quotes')}
            {totalQuotes === 0 && !isLocked && (
              <div style={{ flexBasis: '100%', fontSize: 12.5, color: '#9ca3af' }}>Press ✎ Edit to add a quote, attach the vendor's PDF, or mark one as used.</div>
            )}
          </div>
        );
      })()}
      {show && !readMode && (
        <div style={{ padding: 20, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {renderCategory('Material', 'Material Quotes')}
          <div style={{ width: 1, background: '#e5e7eb', alignSelf: 'stretch' }} />
          {renderCategory('Sub', 'Subcontractor Quotes')}
        </div>
      )}

      {/* Write a PO from a quote */}
      <CreatePoModal
        open={!!poFor}
        onClose={() => setPoFor(null)}
        projectId={projectId}
        projectLabel={poJobLabel}
        vendors={poVendors || []}
        defaultVendor={poFor?.vendor}
        defaultDescription={poFor?.description}
        defaultCost={poFor?.cost}
        defaultNotes={poFor?.notes}
        quoteAttachments={poFor?.files}
        fromQuote
        onCreated={po => {
          const pf = poFor; if (!pf || !po) return;
          update(items.map(iit => iit.id !== pf.itemId ? iit : { ...iit, quotes: iit.quotes.map(qq => qq.id !== pf.quoteId ? qq : { ...qq, po_id: po.id, po_number: po.po_number }) }));
          setPoLookup(m => ({ ...m, [po.id]: po.po_number }));
          if (onPOCreated) onPOCreated(po);
        }}
      />

      {/* Vendor-quote PDF markup */}
      {markupAtt && (
        <PdfAnnotator
          url={`${API_BASE}/bids/${bidId}/attachments/${markupAtt.id}/download`}
          token={getToken()}
          title={markupAtt.original_name}
          downloadName={markupAtt.original_name}
          initialAnnotations={markupAtt.annotations}
          onSave={({ strokes }) => saveMarkup(markupAtt, strokes)}
          onClose={() => setMarkupAtt(null)}
        />
      )}

      {/* Add Quote Wizard */}
      {wizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setWizard(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, color: '#1f2937' }}>
              {wizard.isNewItem ? 'Add Vendor Quote' : `Add Quote — ${wizard.itemName}`}
            </div>

            {/* Category */}
            {wizard.isNewItem && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Type</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['Material', 'Sub'].map(cat => (
                    <button key={cat} onClick={() => setWizard(w => ({ ...w, category: cat, itemName: '', isCustom: false }))}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: wizard.category === cat ? 'none' : '1.5px solid #e5e7eb', background: wizard.category === cat ? (cat === 'Material' ? '#1e3a5f' : '#4a1d50') : '#f9fafb', color: wizard.category === cat ? '#fff' : '#374151', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                      {cat === 'Sub' ? 'Subcontractor' : 'Material'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Item picker */}
            {wizard.isNewItem && wizard.category && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Item</div>
                <select value={wizard.isCustom ? '__custom__' : wizard.itemName}
                  onChange={e => { const v = e.target.value; setWizard(w => ({ ...w, isCustom: v === '__custom__', itemName: v === '__custom__' ? '' : v })); }}
                  style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, marginBottom: wizard.isCustom ? 8 : 0, boxSizing: 'border-box' }}>
                  <option value="">— Select item —</option>
                  {itemOptions.map(n => <option key={n} value={n}>{n}</option>)}
                  <option value="__custom__">Custom item…</option>
                </select>
                {wizard.isCustom && (
                  <input autoFocus value={wizard.itemName} onChange={e => setWizard(w => ({ ...w, itemName: e.target.value }))}
                    placeholder="Enter item name"
                    style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' }} />
                )}
              </div>
            )}

            {/* Vendor picker — shown once item is chosen */}
            {(wizard.itemName.trim() || !wizard.isNewItem) && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Vendor</div>
                <select value={wizard.vendorId}
                  onChange={e => setWizard(w => ({ ...w, vendorId: e.target.value, vendorName: '' }))}
                  style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, marginBottom: !wizard.vendorId ? 8 : 0, boxSizing: 'border-box' }}>
                  <option value="">Other / type name…</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                {!wizard.vendorId && (
                  <input value={wizard.vendorName} onChange={e => setWizard(w => ({ ...w, vendorName: e.target.value }))}
                    placeholder="Vendor name"
                    style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' }} />
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setWizard(null)}
                style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmWizard} disabled={!wizardValid}
                style={{ flex: 2, padding: '9px', borderRadius: 8, border: 'none', background: wizardValid ? '#92400e' : '#d1d5db', color: '#fff', fontSize: 13, fontWeight: 700, cursor: wizardValid ? 'pointer' : 'default' }}>
                Add Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The Parts Directory, opened as a window rather than a dropdown.
//
// It used to be a 320px popover showing eight results in 12px type, which is a hard
// way to find a part when the directory holds hundreds. Same job, given room: a real
// search box, the categories as filters, and as many results as the screen will hold,
// two across on a desktop.
//
// Takes options in one shape — { key, label, sub, category, price, original } — so
// the bid table and T&M billing pick from the same window.
// tabs (optional): [{ key, label, title, options, emptyText, onManage }] — one window, several
// lists (Ryan, Oct 22 2026: the blue + picks Material, Equipment, Labor, Common Prices or a
// Bid Assembly). onPick then gets (original, tabKey).
export function PartsPickerModal({ title: title0 = 'Parts Directory', subtitle, options: options0 = [], onPick: onPick0, onClose, emptyText: empty0 = 'Nothing in the directory yet.', onManage: manage0, initialQuery = '', tabs = null }) {
  const [tab, setTab] = React.useState(tabs && tabs.length ? tabs[0].key : null);
  const active = tabs ? (tabs.find(t => t.key === tab) || tabs[0]) : null;
  const options = active ? (active.options || []) : options0;
  const title = active ? (active.title || active.label) : title0;
  const emptyText = active ? (active.emptyText || 'Nothing here yet.') : empty0;
  const onManage = active ? active.onManage : manage0;
  const onPick = o => (active ? onPick0(o, active.key) : onPick0(o));
  // Opened from a line that already says something? Start the search there.
  const [q, setQ] = React.useState(initialQuery);
  const [cat, setCat] = React.useState('');
  // Another tab is another list: the line's own words were a search of the first one, so it starts clear.
  const firstTab = React.useRef(true);
  React.useEffect(() => { setCat(''); if (firstTab.current) { firstTab.current = false; return; } setQ(''); }, [tab]);
  const inputRef = React.useRef(null);
  React.useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);
  React.useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cats = [...new Set(options.map(o => o.category).filter(Boolean))].sort();
  const ql = q.trim().toLowerCase();
  const shown = options.filter(o =>
    (!cat || o.category === cat) &&
    (!ql || ((o.label || '') + ' ' + (o.sub || '') + ' ' + (o.category || '')).toLowerCase().includes(ql)));

  const money = n => (n == null || n === '') ? '' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const chip = on => ({
    padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    border: on ? '1.5px solid #4f46e5' : '1.5px solid #e5e7eb', background: on ? '#eef2ff' : '#fff', color: on ? '#4f46e5' : '#6b7280',
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 7000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 780, maxWidth: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: '#4f46e5', color: '#fff', padding: '13px 18px', borderRadius: '14px 14px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>📦 {title}</div>
            <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
              {subtitle || (shown.length === options.length
                ? options.length + ' item' + (options.length === 1 ? '' : 's')
                : shown.length + ' of ' + options.length)}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 20, lineHeight: 1, width: 36, height: 36, borderRadius: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
        </div>

        <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #f1f5f9' }}>
          {tabs && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {tabs.map(t => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  style={{ padding: '6px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 800, cursor: 'pointer', border: 'none',
                    background: t.key === active.key ? '#4f46e5' : '#eef2ff', color: t.key === active.key ? '#fff' : '#4f46e5' }}>
                  {t.label}{t.options ? <span style={{ opacity: 0.7, fontWeight: 700 }}> {t.options.length}</span> : null}
                </button>
              ))}
            </div>
          )}
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && shown.length) { onPick(shown[0].original !== undefined ? shown[0].original : shown[0]); } }}
            placeholder="Search by description, category…"
            style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #d1d5db', borderRadius: 10, padding: '11px 14px', fontSize: 15, outline: 'none' }} />
          {cats.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setCat('')} style={chip(!cat)}>All</button>
              {cats.map(c => <button key={c} onClick={() => setCat(c === cat ? '' : c)} style={chip(cat === c)}>{c}</button>)}
            </div>
          )}
        </div>

        <div style={{ overflowY: 'auto', padding: 12, background: '#f8fafc', flex: 1 }}>
          {shown.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
              {options.length ? 'Nothing matches “' + q.trim() + '”.' : emptyText}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 8 }}>
              {shown.map(o => (
                <button key={o.key} onClick={() => onPick(o.original !== undefined ? o.original : o)}
                  style={{ textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '11px 13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.borderColor = '#c7d2fe'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{o.label}</div>
                    {o.sub && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.sub}</div>}
                  </div>
                  {o.price != null && o.price !== '' && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--orange)', whiteSpace: 'nowrap' }}>{money(o.price)}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: '9px 16px', borderTop: '1px solid #f1f5f9', fontSize: 11.5, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>Enter picks the first match · Esc closes</span>
          {/* Adding and editing parts still lives in the directory proper. */}
          {onManage && (
            <button onClick={onManage}
              style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}>
              Manage directory →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Parts Directory ───────────────────────────────────────────────────────────
function PartsDirectoryModal({ API_BASE, getToken, onClose, onSelect }) {
  const [parts, setParts] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [editId, setEditId] = React.useState(null);
  const [form, setForm] = React.useState({ description: '', unit: 'EACH', material_cost: '', labor_hours: '', category: '' });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API_BASE}/parts`, { headers: { 'x-worker-token': getToken() } })
      .then(r => r.json()).then(setParts).catch(() => {});
  }, []);

  const filtered = parts.filter(p => !search.trim() || p.description.toLowerCase().includes(search.toLowerCase()) || (p.category || '').toLowerCase().includes(search.toLowerCase()));
  const categories = [...new Set(parts.map(p => p.category).filter(Boolean))].sort();

  function startAdd() { setEditId('new'); setForm({ description: '', unit: 'EACH', material_cost: '', labor_hours: '', category: '' }); }
  function startEdit(p) { setEditId(p.id); setForm({ description: p.description, unit: p.unit, material_cost: p.material_cost, labor_hours: p.labor_hours, category: p.category || '' }); }
  function cancelEdit() { setEditId(null); }

  async function save() {
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      const isNew = editId === 'new';
      const url = isNew ? `${API_BASE}/parts` : `${API_BASE}/parts/${editId}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PATCH', headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() }, body: JSON.stringify(form) });
      if (res.ok) {
        const saved = await res.json();
        setParts(ps => isNew ? [...ps, saved] : ps.map(p => p.id === saved.id ? saved : p));
        setEditId(null);
      }
    } finally { setSaving(false); }
  }

  async function deletePart(id) {
    if (!window.confirm('Remove this part from the directory?')) return;
    await fetch(`${API_BASE}/parts/${id}`, { method: 'DELETE', headers: { 'x-worker-token': getToken() } });
    setParts(ps => ps.filter(p => p.id !== id));
  }

  const fmt$ = n => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
  const inputSt = { border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 760, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: '#1e3a5f', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6, flex: 1 }}>📦 Parts Directory</span>
          <button onClick={startAdd} style={{ padding: '5px 14px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add Part</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parts…"
            style={{ ...inputSt, width: '100%' }} autoFocus />
        </div>

        {/* Add / Edit form */}
        {editId && (
          <div style={{ padding: '14px 20px', background: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#0369a1', marginBottom: 10, textTransform: 'uppercase' }}>{editId === 'new' ? 'New Part' : 'Edit Part'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8 }}>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>DESCRIPTION</div>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Part description" style={inputSt} /></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>UNIT</div>
                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} style={inputSt}>
                  {['EACH','LF','SF','CY','LS','HR','TON','GAL','C','M'].map(u => <option key={u}>{u}</option>)}
                </select></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>MATERIAL COST</div>
                <input type="text" inputMode="decimal" value={form.material_cost} onChange={e => setForm(f => ({ ...f, material_cost: e.target.value }))} placeholder="0.00" style={inputSt} /></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>LABOR HRS</div>
                <input type="text" inputMode="decimal" value={form.labor_hours} onChange={e => setForm(f => ({ ...f, labor_hours: e.target.value }))} placeholder="0.00" style={inputSt} /></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>CATEGORY</div>
                <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Conduit" list="part-cats" style={inputSt} />
                <datalist id="part-cats">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={cancelEdit} style={{ padding: '6px 16px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving || !form.description.trim()}
                style={{ padding: '6px 16px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save Part'}
              </button>
            </div>
          </div>
        )}

        {/* Parts table */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
              <tr>
                {['Description', 'Unit', 'Mat. Cost', 'Labor Hrs', 'Category', ''].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Mat. Cost' || h === 'Labor Hrs' ? 'right' : 'left', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', cursor: onSelect ? 'pointer' : 'default' }}
                  onClick={() => onSelect && onSelect(p)}>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: '#111827' }}>{p.description}</td>
                  <td style={{ padding: '9px 12px', color: '#6b7280' }}>{p.unit}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--orange)', fontWeight: 600 }}>{fmt$(p.material_cost)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{p.labor_hours}h</td>
                  <td style={{ padding: '9px 12px' }}>{p.category ? <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>{p.category}</span> : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => startEdit(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 12, marginRight: 8 }}>Edit</button>
                    <button onClick={() => deletePart(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12 }}>Delete</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>No parts found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {onSelect && (
          <div style={{ padding: '10px 20px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 12, color: '#6b7280', borderRadius: '0 0 14px 14px' }}>
            Click a row to apply it to the line item
          </div>
        )}
      </div>
    </div>
  );
}

// Grouped-number input for bid figures: shows the value with thousands commas
// when idle, the raw number while editing, and auto-widens so large numbers stay
// fully readable in the pill. Money uses 2 decimals; quantity uses 0–4 as typed.
// The units a line can carry, as a dropdown (Ryan, Oct 21 2026 — line items had EACH / EA / Each,
// LF / LINFT / LNFT, SF / SQFT… typed by hand). A line already carrying something else keeps it
// (it's shown as its own choice); "Other…" types a new one.
export const BID_UNITS = ['EACH', 'EA', 'LS', 'LF', 'SF', 'SY', 'CY', 'HR', 'DAY', 'MO', 'FA', 'TON', 'C', 'M'];
// C and M read as what they mean (Ryan, Oct 21 2026). Type the real count (1,200 ft) and the price
// per 100 / per 1,000 — the line extends at quantity ÷ 100 / ÷ 1,000 (unitPer, above).
const UNIT_LABEL = { C: 'C — per 100', M: 'M — per 1,000' };
export function UnitSelect({ value, onChange, disabled, style }) {
  const v = String(value || '').trim();
  const known = BID_UNITS.includes(v);
  return (
    <select value={v} disabled={disabled}
      onClick={e => e.stopPropagation()}
      onChange={e => {
        e.stopPropagation();
        if (e.target.value === '__other') {
          const typed = (window.prompt('Unit for this line (e.g. GAL, PER DAY, EA/MO):', known ? '' : v) || '').trim().toUpperCase();
          if (typed) onChange(typed);
          return;
        }
        onChange(e.target.value);
      }}
      style={{ ...style, padding: '4px 2px', cursor: disabled ? 'default' : 'pointer' }}>
      {!v && <option value="">—</option>}
      {v && !known && <option value={v}>{v}</option>}
      {BID_UNITS.map(u => <option key={u} value={u}>{UNIT_LABEL[u] || u}</option>)}
      <option value="__other">Other…</option>
    </select>
  );
}

function MoneyInput({ value, onCommit, disabled, baseStyle, minDecimals = 2, maxDecimals = 2, align = 'right', minWidth = 90 }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const num = Number(value) || 0;
  const formatted = num ? num.toLocaleString('en-US', { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals }) : '';
  const shown = editing ? draft : formatted;
  const width = Math.min(220, Math.max(minWidth, (shown.length + 3) * 8));
  return (
    <input
      type="text" inputMode="decimal" value={shown} disabled={disabled}
      onClick={e => e.stopPropagation()}
      onFocus={e => { setEditing(true); setDraft(num ? String(num) : ''); const t = e.target; requestAnimationFrame(() => t.select()); }}
      onChange={e => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
      onBlur={() => { setEditing(false); onCommit(parseFloat(draft) || 0); }}
      style={{ ...baseStyle, width, textAlign: align }}
    />
  );
}

// Equipment-on-job card for Zone 2: pieces on site now + cumulative days-on-job
// (from the move log) + drill hours, with a per-piece breakdown.
// Unified T&M billing on any Time & Material job — labor, materials (parts +
// stock), and equipment/trucks in one box, organized for the bill.
// Editable Time & Material price, shown on the Job Description of any T&M bid.
// General → a Not-to-Exceed price. Drilling → drill / basis / rate / mobilization
// (pre-fills from saved Drill Rates when you pick a drill). Saves via PATCH /bids/:id/tm.
const TM_PRICE_DRILLS = ['Harold', 'Ella', 'Volvo'];
function TMPriceBlock({ bid, bidId, locked, onSaved }) {
  const isDrill = !!(bid.tm_drill || /Drilling/i.test(bid.bid_type || ''));
  const [nte, setNte] = useState(bid.tm_nte ?? '');
  const [drill, setDrill] = useState(bid.tm_drill || '');
  const [basis, setBasis] = useState(bid.tm_rate_basis || 'daily');
  // Both rates, because a drilling job is worked both ways — a full day on the hole,
  // then four hours to finish the last one. Either can be left blank; the field simply
  // can't log that unit until it's priced. Falls back to the single rate a bid carried
  // before there were two, so an existing job opens showing what it has always said.
  const [dayRate, setDayRate] = useState(bid.tm_daily_rate ?? (bid.tm_rate_basis !== 'hourly' ? bid.tm_rate : null) ?? '');
  const [hourRate, setHourRate] = useState(bid.tm_hourly_rate ?? (bid.tm_rate_basis === 'hourly' ? bid.tm_rate : null) ?? '');
  const [mob, setMob] = useState(bid.tm_mobilization ?? '');
  const [rates, setRates] = useState([]);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (isDrill) apiFetch('/drill-rates').then(r => setRates(Array.isArray(r) ? r : [])).catch(() => {});
  }, [isDrill]);

  async function save(patch) {
    try {
      const updated = await apiFetch(`/bids/${bidId}/tm`, { method: 'PATCH', body: JSON.stringify(patch) });
      if (onSaved && updated) onSaved(updated);
      setFlash(true); setTimeout(() => setFlash(false), 1500);
    } catch {}
  }

  // Picking a drill pulls both of its saved rates at once — the Drill Rates table has
  // carried a daily and an hourly figure per rig all along; only one was ever read.
  function pickDrill(d) {
    setDrill(d);
    const row = rates.find(r => r.drill === d);
    let nextDay = dayRate === '' ? null : Number(dayRate);
    let nextHour = hourRate === '' ? null : Number(hourRate);
    let nextMob = mob === '' ? null : Number(mob);
    if (row) {
      nextDay = row.daily_rate; nextHour = row.hourly_rate; nextMob = row.mobilization;
      setDayRate(nextDay ?? ''); setHourRate(nextHour ?? ''); setMob(nextMob ?? '');
    }
    save({ drill: d, rate_basis: basis, daily_rate: nextDay, hourly_rate: nextHour, mobilization: nextMob });
  }
  // Only decides which rate the printed quote leads with. Neither rate is touched —
  // switching the quote to hourly must not quietly delete the day rate the job is
  // already being billed at.
  function pickBasis(b) { setBasis(b); save({ rate_basis: b }); }

  const lbl = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' };
  const inp = { border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: locked ? '#f9fafb' : '#fff' };

  return (
    <div style={{ gridColumn: '1 / -1', border: '1px solid #ede9fe', background: '#faf5ff', borderRadius: 10, padding: 14, marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Time &amp; Material Price
        </span>
        {flash && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>}
      </div>
      {!isDrill ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 260 }}>
          <label style={lbl}>Not to Exceed ($)</label>
          <input type="number" step="any" value={nte} disabled={locked}
            placeholder="e.g. 15000"
            onChange={e => setNte(e.target.value)}
            onBlur={() => save({ nte: nte })}
            style={inp} />
          <span style={{ fontSize: 11, color: '#6b7280' }}>Shown as the NOT-TO-EXCEED total on the quote.</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={lbl}>Drill</label>
            <select value={drill} disabled={locked} onChange={e => pickDrill(e.target.value)} style={inp}>
              <option value="">— Select drill —</option>
              {TM_PRICE_DRILLS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={lbl}>Day Rate ($/day)</label>
            <input type="number" step="any" value={dayRate} disabled={locked}
              placeholder="e.g. 7500"
              onChange={e => setDayRate(e.target.value)}
              onBlur={() => save({ daily_rate: dayRate })} style={inp} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={lbl}>Hour Rate ($/hr)</label>
            <input type="number" step="any" value={hourRate} disabled={locked}
              placeholder="e.g. 700"
              onChange={e => setHourRate(e.target.value)}
              onBlur={() => save({ hourly_rate: hourRate })} style={inp} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={lbl}>Mobilization ($)</label>
            <input type="number" step="any" value={mob} disabled={locked}
              onChange={e => setMob(e.target.value)}
              onBlur={() => save({ mobilization: mob })} style={inp} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={lbl}>Quote Shows</label>
            <select value={basis} disabled={locked} onChange={e => pickBasis(e.target.value)} style={inp}>
              <option value="daily">Day rate</option>
              <option value="hourly">Hour rate</option>
            </select>
          </div>
          {/* Said out loud, because "why can't the field log hours" is exactly the
              question this block answers. */}
          <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: '#6b7280', lineHeight: 1.5 }}>
            Fill in both and the field can log a day or an hour on this job, each billed at its
            own rate. Leave one blank and that unit simply isn&rsquo;t offered.
            <strong> Quote Shows</strong> only picks which rate the printed quote leads with.
          </div>
        </div>
      )}
    </div>
  );
}

export function TMBillingSection({ projectId, bid }) {
  const [open, setOpen] = useState(true);
  const [entries, setEntries] = useState([]);
  const [parts, setParts] = useState([]);
  const [stock, setStock] = useState([]);
  const [equip, setEquip] = useState([]);
  const [kind, setKind] = useState('labor');
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ work_date: today, description: '', quantity: '', unit: '', rate: '', ref_type: null, ref_id: null });
  const [pickQ, setPickQ] = useState('');
  const [pickOpen, setPickOpen] = useState(false);
  const [browse, setBrowse] = useState(false);   // the full parts window
  const [saving, setSaving] = useState(false);

  const reload = () => apiFetch(`/projects/${projectId}/tm-billing`).then(r => setEntries(Array.isArray(r) ? r : [])).catch(() => {});
  useEffect(() => {
    reload();
    apiFetch('/parts').then(r => setParts(Array.isArray(r) ? r : [])).catch(() => {});
    apiFetch('/stock-items').then(r => setStock(Array.isArray(r) ? r : [])).catch(() => {});
    apiFetch('/equipment').then(r => setStock2(r)).catch(() => {});
    function setStock2(r) { setEquip(Array.isArray(r) ? r : []); }
  }, [projectId]); // eslint-disable-line

  const money = n => (n == null || n === '') ? '' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const num = v => (v === '' || v == null) ? null : Number(v);

  // Options for the picker, by kind.
  const materialOpts = [
    ...parts.map(p => ({ key: 'part:' + p.id, label: p.description, category: p.category || 'Parts', sub: [p.category, p.unit].filter(Boolean).join(' · '), unit: (p.unit || 'ea'), rate: p.material_cost, ref_type: 'part', ref_id: p.id })),
    ...stock.map(s => ({ key: 'stock:' + s.id, label: s.name, category: 'Stock', sub: ['Stock', s.unit, s.qty_on_hand != null ? `${s.qty_on_hand} on hand` : ''].filter(Boolean).join(' · '), unit: (s.unit || 'ea'), rate: (s.part_cost != null ? s.part_cost : s.unit_cost), ref_type: 'stock', ref_id: s.id })),
  ];
  const equipOpts = equip.map(e => ({ key: e.id, category: e.category || '', label: `${e.asset_number ? e.asset_number + ' · ' : ''}${e.name}`, sub: [e.category, e.billing_rate != null ? `$${e.billing_rate}/${e.meter_unit || e.rate_unit || 'day'}` : ''].filter(Boolean).join(' · '), unit: (e.rate_unit || e.meter_unit || 'day'), rate: e.billing_rate, ref_type: 'equipment', ref_id: e.id }));
  const opts = kind === 'material' ? materialOpts : kind === 'equipment' ? equipOpts : [];
  const matches = pickQ.trim() ? opts.filter(o => o.label.toLowerCase().includes(pickQ.trim().toLowerCase())).slice(0, 30) : opts.slice(0, 30);

  function chooseKind(k) { setKind(k); setF({ work_date: f.work_date, description: '', quantity: '', unit: '', rate: '', ref_type: null, ref_id: null }); setPickQ(''); setPickOpen(false); }
  function pick(o) { setF(v => ({ ...v, description: o.label, unit: o.unit || '', rate: o.rate != null ? o.rate : '', ref_type: o.ref_type, ref_id: o.ref_id })); setPickQ(o.label); setPickOpen(false); }

  const amount = (num(f.quantity) != null && num(f.rate) != null) ? Math.round(num(f.quantity) * num(f.rate) * 100) / 100 : null;
  const canAdd = f.description.trim() && num(f.quantity) > 0;

  async function add() {
    if (!canAdd || saving) return;
    setSaving(true);
    try {
      await apiFetch(`/projects/${projectId}/tm-billing`, { method: 'POST', body: JSON.stringify({
        kind, work_date: f.work_date, description: f.description.trim(), quantity: num(f.quantity),
        unit: f.unit || null, rate: num(f.rate), amount, ref_type: f.ref_type, ref_id: f.ref_id,
      }) });
      setF({ work_date: f.work_date, description: '', quantity: '', unit: '', rate: '', ref_type: null, ref_id: null }); setPickQ('');
      reload();
    } catch { alert('Could not save. Please try again.'); }
    setSaving(false);
  }
  function del(id) { apiFetch(`/projects/${projectId}/tm-billing/${id}`, { method: 'DELETE' }).then(reload).catch(() => {}); }

  const kindMeta = { labor: { label: 'Labor', color: '#1e3a5f' }, material: { label: 'Material', color: '#4338ca' }, equipment: { label: 'Equipment', color: '#b45309' } };
  const byKind = k => entries.filter(e => e.kind === k);
  const sub = k => byKind(k).reduce((s, e) => s + (e.amount || 0), 0);
  const grand = entries.reduce((s, e) => s + (e.amount || 0), 0);
  const nte = num(bid?.tm_nte);

  const lbl = { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };
  const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ background: '#7c3aed', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>⏱️ T&amp;M Billing ({money(grand) || '$0.00'}{nte ? ` of ${money(nte)}` : ''})</span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: 16 }}>
          {/* Add entry */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {[['labor', '👷 Labor'], ['material', '🧱 Material'], ['equipment', '🚚 Equipment']].map(([k, l]) => (
              <button key={k} onClick={() => chooseKind(k)}
                style={{ padding: '7px 14px', borderRadius: 20, border: kind === k ? `2px solid ${kindMeta[k].color}` : '1.5px solid #e5e7eb', background: kind === k ? '#f5f3ff' : '#fff', color: kind === k ? kindMeta[k].color : '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
            ))}
          </div>

          <div style={{ background: '#faf5ff', border: '1px solid #ede9fe', borderRadius: 10, padding: 12, marginBottom: 14 }}>
            {kind === 'labor' ? (
              <div style={{ marginBottom: 8 }}><div style={lbl}>Who / what</div><input value={f.description} onChange={e => setF(v => ({ ...v, description: e.target.value }))} placeholder="e.g. Cody — install" style={{ ...inp, marginTop: 4 }} /></div>
            ) : (
              <div style={{ marginBottom: 8, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={lbl}>{kind === 'material' ? 'Material (parts + stock)' : 'Equipment / truck'}</div>
                  <button type="button" onClick={() => setBrowse(true)} title={kind === 'material' ? 'Browse the parts directory' : 'Browse equipment'}
                    style={{ width: 20, height: 20, borderRadius: 10, border: 'none', background: '#e0e7ff', color: '#4f46e5', fontSize: 14, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: 0 }}>+</button>
                </div>
                <input value={pickQ} onChange={e => { setPickQ(e.target.value); setPickOpen(true); setF(v => ({ ...v, ref_type: null, ref_id: null })); }} onFocus={() => setPickOpen(true)}
                  placeholder={kind === 'material' ? 'Search parts or stock…' : 'Search equipment…'} style={{ ...inp, marginTop: 4 }} />
                {pickOpen && matches.length > 0 && (
                  <div style={{ position: 'absolute', zIndex: 30, left: 0, right: 0, top: '100%', marginTop: 2, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto' }}>
                    {matches.map(o => (
                      <div key={o.key} onClick={() => pick(o)} style={{ padding: '8px 11px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{o.label}</div>
                        {o.sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{o.sub}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {browse && (
              <PartsPickerModal
                title={kind === 'material' ? 'Parts & Stock' : 'Equipment'}
                subtitle={kind === 'material' ? 'Pick what went on the job — fills the description, unit and rate.' : 'Pick the machine or truck — fills the description, unit and rate.'}
                options={opts.map(o => ({ ...o, price: o.rate, original: o }))}
                onPick={o => { pick(o); setBrowse(false); }}
                onClose={() => setBrowse(false)}
                emptyText={kind === 'material' ? 'Nothing in the parts directory or stock yet.' : 'No equipment on file yet.'} />
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ width: 140 }}><div style={lbl}>Date</div><input type="date" value={f.work_date} onChange={e => setF(v => ({ ...v, work_date: e.target.value }))} style={{ ...inp, marginTop: 4 }} /></div>
              <div style={{ flex: '1 1 70px' }}><div style={lbl}>{kind === 'labor' ? 'Hours' : 'Qty'}</div><input type="number" inputMode="decimal" value={f.quantity} onChange={e => setF(v => ({ ...v, quantity: e.target.value }))} style={{ ...inp, marginTop: 4 }} /></div>
              <div style={{ width: 70 }}><div style={lbl}>Unit</div><input value={f.unit} onChange={e => setF(v => ({ ...v, unit: e.target.value }))} placeholder={kind === 'labor' ? 'hr' : 'ea'} style={{ ...inp, marginTop: 4 }} /></div>
              <div style={{ flex: '1 1 90px' }}><div style={lbl}>Rate $</div><input type="number" inputMode="decimal" value={f.rate} onChange={e => setF(v => ({ ...v, rate: e.target.value }))} placeholder="0" style={{ ...inp, marginTop: 4 }} /></div>
              <div style={{ flex: '1 1 90px' }}><div style={lbl}>Amount</div><div style={{ ...inp, marginTop: 4, background: '#f3f4f6', color: '#111827', fontWeight: 700 }}>{amount != null ? money(amount) : '—'}</div></div>
            </div>
            <button onClick={add} disabled={!canAdd || saving} style={{ marginTop: 10, width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: canAdd ? '#7c3aed' : '#cbd5e1', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: canAdd ? 'pointer' : 'default' }}>
              {saving ? 'Adding…' : `+ Add ${kindMeta[kind].label}`}
            </button>
            {kind === 'material' && f.ref_type === 'stock' && <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>Draws this qty out of stock on the Stock Material tab.</div>}
          </div>

          {/* Entries grouped by kind */}
          {['labor', 'material', 'equipment'].map(k => {
            const list = byKind(k);
            if (!list.length) return null;
            return (
              <div key={k} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', ...lbl, color: kindMeta[k].color, marginBottom: 6 }}>
                  <span>{kindMeta[k].label}</span><span>{money(sub(k))}</span>
                </div>
                {list.map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#111827' }}>{e.description}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtBillDate(e.work_date)} · {e.actor_name}</div>
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 120 }}>
                      <div style={{ fontSize: 12.5, color: '#374151' }}>{e.quantity != null ? `${e.quantity}${e.unit ? ' ' + e.unit : ''}` : ''}{e.rate != null ? ` × $${e.rate}` : ''}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{money(e.amount)}</div>
                    </div>
                    <button onClick={() => del(e.id)} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            );
          })}
          {entries.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '8px 0' }}>Nothing logged yet — add labor, materials, or equipment above.</div>}

          {/* Grand total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 10, borderTop: '2px solid #ede9fe' }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: '#7c3aed' }}>Total {nte ? <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>of {money(nte)} NTE</span> : null}</span>
            <span style={{ fontSize: 17, fontWeight: 800, color: nte && grand > nte ? '#b91c1c' : '#7c3aed' }}>{money(grand)}</span>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Drill days/hours (if any) are on the drilling card — the final bill combines both.</div>
        </div>
      )}
    </div>
  );
}
const fmtBillDate = d => { if (!d) return ''; const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d); return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

export function EquipmentJobCard({ projectId }) {
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  React.useEffect(() => { apiFetch(`/projects/${projectId}/equipment-summary`).then(setData).catch(() => {}); }, [projectId]);
  const onNow = data?.onNow || 0, days = data?.totalDays || 0, hours = data?.totalHours || 0;
  // 'YYYY-MM-DD' -> 'Jul 10'
  const fmtDay = s => { if (!s) return ''; const d = new Date(s + 'T12:00:00'); return isNaN(d) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
  const tripLabel = t => `${fmtDay(t.from)} → ${t.current ? 'present' : fmtDay(t.to)} · ${t.days} day${t.days !== 1 ? 's' : ''}`;
  const term = q.trim().toLowerCase();
  const pieces = (data?.pieces || []).filter(p => {
    if (!term) return true;
    const hay = [p.assetNumber, p.name, p.category, ...(p.trips || []).flatMap(t => [t.from, t.to, tripLabel(t)])]
      .filter(Boolean).join(' ').toLowerCase();
    return hay.includes(term);
  });
  return (
    <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ background: '#475569', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>🔧 Equipment ({onNow} on job{days ? ` · ${days} eq-days` : ''}{hours ? ` · ${hours}h` : ''})</span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
            <div style={{ padding: 16 }}>
              {!data ? <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
                : (data.pieces || []).length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No equipment has been on this job yet.</div>
                : <>
                {data.pieces.length > 4 && (
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by equipment number or date…"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 10 }} />
                )}
                {pieces.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No matches for “{q}”.</div>
                : pieces.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 8px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>
                        {p.assetNumber ? <span style={{ color: '#1e3a5f' }}>{p.assetNumber} </span> : null}{p.name}
                      </div>
                      {p.category && <div style={{ fontSize: 11.5, color: '#9ca3af' }}>{p.category}</div>}
                      {(p.trips || []).length > 0 && (
                        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {p.trips.map((t, i) => (
                            <div key={i} style={{ fontSize: 11.5, color: t.current ? '#15803d' : '#6b7280' }}>{tripLabel(t)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    {p.onNow && <span style={{ fontSize: 10, fontWeight: 800, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>ON SITE</span>}
                    <div style={{ textAlign: 'right', minWidth: 70 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{p.days} day{p.days !== 1 ? 's' : ''}</div>
                      {p.hours ? <div style={{ fontSize: 11.5, color: '#6b7280' }}>{p.hours} hrs</div> : null}
                    </div>
                  </div>
                ))}
                </>}
              <div style={{ fontSize: 11, color: '#9ca3af', padding: '10px 8px 4px' }}>Dates &amp; days come from the equipment move log — keep moves logged (on &amp; off) for accurate job costing.</div>
            </div>
      )}
    </div>
  );
}

export function PreBidBlock({ bid, isLocked, updateBid, updateBidFields }) {
  const hasPreBid = !!bid.pre_bid;
  const isMandatory = !!bid.pre_bid_mandatory;
  const blockBg = isMandatory ? '#fef2f2' : '#f0f4ff';
  const blockBorder = isMandatory ? '#fca5a5' : '#c7d5f8';
  const headerColor = isMandatory ? '#991b1b' : '#1e3a5f';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, gridColumn: 'span 2', background: blockBg, border: `1.5px solid ${blockBorder}`, borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: headerColor, textTransform: 'uppercase', letterSpacing: '.5px' }}>
        Pre-Bid Meeting
        {isMandatory && <span style={{ marginLeft: 8, background: '#b91c1c', color: '#fff', borderRadius: 4, padding: '1px 7px', fontSize: 10 }}>MANDATORY TO BID</span>}
      </div>

      {/* 1. Is there a pre-bid? */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isLocked ? 'default' : 'pointer' }}>
        <input type="checkbox" checked={hasPreBid} disabled={isLocked}
          onChange={e => {
            if (e.target.checked) {
              updateBid('pre_bid', 1);
            } else {
              updateBidFields({ pre_bid: 0, pre_bid_mandatory: 0, pre_bid_datetime: null });
            }
          }}
          style={{ width: 16, height: 16, accentColor: '#1e3a5f', cursor: isLocked ? 'default' : 'pointer' }} />
        <span style={{ fontSize: 13, color: hasPreBid ? headerColor : '#6b7280', fontWeight: hasPreBid ? 600 : 400 }}>
          Is there a pre-bid meeting?
        </span>
      </label>

      {hasPreBid && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 24, borderLeft: `2px solid ${blockBorder}`, marginLeft: 8 }}>
          {/* 2. Is it mandatory? */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isLocked ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={isMandatory} disabled={isLocked}
              onChange={e => updateBid('pre_bid_mandatory', e.target.checked ? 1 : 0)}
              style={{ width: 16, height: 16, accentColor: '#b91c1c', cursor: isLocked ? 'default' : 'pointer' }} />
            <span style={{ fontSize: 13, color: isMandatory ? '#b91c1c' : '#6b7280', fontWeight: isMandatory ? 700 : 400 }}>
              Is attendance mandatory to bid?
            </span>
          </label>

          {/* 3. Date & Time */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Date &amp; Time</label>
            <input type="datetime-local" value={bid.pre_bid_datetime || ''} disabled={isLocked}
              onChange={e => updateBid('pre_bid_datetime', e.target.value || null)}
              style={{ border: `1px solid ${blockBorder}`, borderRadius: 7, padding: '7px 10px', fontSize: 13, background: isLocked ? '#f9fafb' : '#fff', maxWidth: 240 }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI Extraction Review Modal ────────────────────────────────────────────────
export function ExtractModal({ items: initialItems, onClose, onAdd, fromQuote }) {
  const [items, setItems] = useState(initialItems.map((it, i) => ({ ...it, _sel: true, _key: i })));

  function toggle(key) { setItems(it => it.map(x => x._key === key ? { ...x, _sel: !x._sel } : x)); }
  function update(key, field, val) { setItems(it => it.map(x => x._key === key ? { ...x, [field]: val } : x)); }
  function setAll(sel) { setItems(it => it.map(x => ({ ...x, _sel: sel }))); }

  const selected = items.filter(x => x._sel);
  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 860, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{fromQuote ? 'Lines from the old quote' : 'AI Extracted Line Items'}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {fromQuote
                ? 'Prices come in exactly as quoted — no tax, overhead or profit added on top. Check them against the quote before adding.'
                : 'Review and edit before adding to bid. Uncheck any items to skip.'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <button onClick={() => setAll(true)} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #7c3aed', background: '#f5f3ff', color: '#7c3aed', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Select all</button>
              <button onClick={() => setAll(false)} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Select none</button>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{selected.length} of {items.length} selected</span>
            </div>
            {items.some(it => it.price_estimated) && (
              <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ background: '#f59e0b', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 4, letterSpacing: 0.4 }}>EST</span>
                {items.filter(it => it.price_estimated).length} material cost{items.filter(it => it.price_estimated).length !== 1 ? 's' : ''} estimated from a similar price-book item — please verify.
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 0 8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ width: 32, padding: '8px 12px', textAlign: 'center' }}>
                  <input type="checkbox" checked={allSelected} onChange={() => setAll(!allSelected)} title={allSelected ? 'Select none' : 'Select all'} style={{ cursor: 'pointer' }} />
                </th>
                <th style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Description</th>
                <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#6b7280', width: 70 }}>Qty</th>
                <th style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', width: 60 }}>Unit</th>
                <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#6b7280', width: 100 }}>{fromQuote ? 'Price/unit' : 'Mat $/unit'}</th>
                {!fromQuote && <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#6b7280', width: 80 }}>Labor Hrs</th>}
                {!fromQuote && <th style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Notes</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it._key} style={{ borderBottom: '1px solid #f3f4f6', opacity: it._sel ? 1 : 0.4 }}>
                  <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                    <input type="checkbox" checked={it._sel} onChange={() => toggle(it._key)} />
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <input value={it.description} onChange={e => update(it._key, 'description', e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 5, padding: '4px 6px', fontSize: 12 }} />
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <input type="number" value={it.quantity} onChange={e => update(it._key, 'quantity', parseFloat(e.target.value) || 0)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 5, padding: '4px 6px', fontSize: 12, textAlign: 'right' }} />
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <input value={it.unit} onChange={e => update(it._key, 'unit', e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 5, padding: '4px 6px', fontSize: 12 }} />
                  </td>
                  {/* A quote's money lives in dje_standard — the one cost input the bid
                      maths passes through untouched by tax, overhead and profit. */}
                  {fromQuote ? (
                    <td style={{ padding: '4px 8px' }}>
                      <input type="number" value={it.dje_standard} onChange={e => update(it._key, 'dje_standard', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${Number(it.dje_standard) > 0 ? '#e5e7eb' : '#fca5a5'}`, borderRadius: 5, padding: '4px 6px', fontSize: 12, textAlign: 'right', background: Number(it.dje_standard) > 0 ? '#fff' : '#fef2f2' }}
                        title={Number(it.dje_standard) > 0 ? 'The price as quoted' : 'No price read for this line — type it in or uncheck the line'} />
                    </td>
                  ) : (
                  <td style={{ padding: '4px 8px' }}>
                    <div style={{ position: 'relative' }}>
                      <input type="number" value={it.material_cost} onChange={e => update(it._key, 'material_cost', parseFloat(e.target.value) || 0)}
                        title={it.price_estimated ? `Estimated from price book: "${it.price_ref_desc || ''}" — please verify` : (it.price_source === 'price_book' ? 'Matched from price book' : '')}
                        style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${it.price_estimated ? '#f59e0b' : '#e5e7eb'}`, borderRadius: 5, padding: '4px 6px', fontSize: 12, textAlign: 'right', background: it.price_estimated ? '#fffbeb' : '#fff' }} />
                      {it.price_estimated && (
                        <span title={`Estimated from: ${it.price_ref_desc || 'a similar item'}`}
                          style={{ position: 'absolute', top: -7, right: -4, background: '#f59e0b', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 4, letterSpacing: 0.4, pointerEvents: 'none' }}>EST</span>
                      )}
                    </div>
                  </td>
                  )}
                  {!fromQuote && (
                  <td style={{ padding: '4px 8px' }}>
                    <input type="number" value={it.labor_hours} onChange={e => update(it._key, 'labor_hours', parseFloat(e.target.value) || 0)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 5, padding: '4px 6px', fontSize: 12, textAlign: 'right' }} />
                  </td>
                  )}
                  {!fromQuote && (
                  <td style={{ padding: '4px 8px' }}>
                    <input value={it.notes || ''} onChange={e => update(it._key, 'notes', e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 5, padding: '4px 6px', fontSize: 12 }} />
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{selected.length} of {items.length} items selected</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => onAdd(selected)} disabled={selected.length === 0}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: selected.length === 0 ? '#d1d5db' : '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600, cursor: selected.length === 0 ? 'default' : 'pointer' }}>
              Add {selected.length} Item{selected.length !== 1 ? 's' : ''} to Bid
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Drill Scope Checklist ─────────────────────────────────────────────────────

const DEFAULT_DRILL_SCOPE = [
  { id: 'mobilization',   label: 'Mobilization' },
  { id: 'potholing',      label: 'Pot Holing' },
  { id: 'permanent_pothole_repair', label: 'Permanent Repair of Pot Holes' },
  { id: 'bore_pits',      label: 'Bore Pits Excavation & Backfill' },
  { id: 'directional_equipment', label: 'Directional Drilling Equipment' },
  { id: 'jack_bore_equipment', label: 'Jack and Bore Equipment' },
  { id: 'lift_equipment', label: 'Equipment Necessary to Lift Boring Equipment into Place' },
  { id: 'hard_rock_hammering', label: 'Hard Rock or Down Hole Hammering' },
  { id: 'casing',         label: 'Casing' },
  { id: 'conduits',       label: 'Conduits' },
  { id: 'conduit_in_casing', label: 'Installation of Conduits in Casing' },
  { id: 'void_fill',      label: 'Filling of Void Between Conduit and Casing' },
  { id: 'welding',        label: 'Welding' },
  { id: 'fusing',         label: 'Fusing' },
  { id: 'const_water',    label: 'Construction Water' },
  { id: 'mud_disposal',   label: 'Mud Disposal' },
  { id: 'mud_recycling',  label: 'Mud Recycling' },
  { id: 'mud_removal_local', label: 'Mud Removal to Local Site' },
  { id: 'rr_flagging',    label: 'Rail Road Flagging, Observers, or Special Trainings / MSHA Training' },
  { id: 'traffic_control', label: 'Traffic Control or Flaggers' },
  { id: 'erosion',        label: 'Erosion Control' },
  { id: 'bmp_swppp',      label: 'BMPs and SWPPPs' },
  { id: 'landscape_repair', label: 'Landscape & Repair Upon Completion of Bore' },
  { id: 'permits',        label: 'Permits' },
  { id: 'performance_bonds', label: 'Performance Bonds' },
  { id: 'night_work',     label: 'Night Work' },
  { id: 'weekend_work',   label: 'Weekend Work' },
  { id: 'overtime',       label: 'Overtime Work' },
  { id: 'staging',        label: 'Staging Areas' },
  { id: 'private_utility', label: 'Private Utility Marking' },
  { id: 'unmarked_util',   label: 'Costs Associated with Unmarked Utilities' },
];

// Generic inclusions/exclusions for non-drilling bids (traffic signal, lighting, etc.).
const DEFAULT_GENERAL_SCOPE = [
  { id: 'mobilization',       label: 'Mobilization' },
  { id: 'permits_fees_bonds', label: 'Permits, Fees and Bonds' },
  { id: 'performance_bonds',  label: 'Performance Bonds' },
  { id: 'traffic_control',    label: 'Traffic Control or Flaggers' },
  { id: 'trenching',          label: 'Trenching, Excavation & Backfill' },
  { id: 'concrete_ac_cut',    label: 'Concrete or AC Cutting or Patching' },
  { id: 'material_testing',   label: 'Material Testing' },
  { id: 'surveying',          label: 'Surveying' },
  { id: 'striping_signage',   label: 'Striping or Permanent Signage' },
  { id: 'erosion',            label: 'Erosion Control' },
  { id: 'bmp_swppp',          label: 'BMPs and SWPPPs' },
  { id: 'private_utility',    label: 'Private Utility Marking' },
  { id: 'unmarked_util',      label: 'Costs Associated with Unmarked Utilities' },
  { id: 'landscape_repair',   label: 'Landscape & Repair Upon Completion' },
  { id: 'night_work',         label: 'Night Work' },
  { id: 'weekend_work',       label: 'Weekend Work' },
  { id: 'overtime',           label: 'Overtime Work' },
  { id: 'staging',            label: 'Staging Areas' },
  { id: 'sales_tax',          label: 'Sales Tax' },
  { id: 'prevailing_wage',    label: 'Prevailing Wage' },
  { id: 'as_builts',          label: 'As-Builts / Record Drawings' },
  { id: 'warranty',           label: 'Warranty' },
];

// Has anyone actually marked what's in and what's out on this bid? Drives the red
// Exclusions cue on the Line Items / T&M tiles and the "print anyway?" check when a
// quote goes out with none chosen (Ryan, Sep 2026).
export function scopeChosen(bid) {
  let items = null;
  try { items = bid?.drill_scope_items ? JSON.parse(bid.drill_scope_items) : null; } catch { items = null; }
  return Array.isArray(items) && items.some(it => it && (it.status === 'included' || it.status === 'excluded'));
}
// A public bid's quote: addendums are the thing that gets missed (Pat, Oct 9 2026).
const ADDENDA_MSG = 'This is a PUBLIC bid.\n\nDid you verify the addendums — every one issued is acknowledged and its changes are in the quote?\n\nOK if they are, Cancel to go back and check.';
const addendaOk = bid => String(bid?.job_sector || '') !== 'Public' || window.confirm(ADDENDA_MSG);
const NO_EXCLUSIONS_MSG = 'No inclusions or exclusions are marked on this bid, so the quote will go out without that section.\n\nIs that right? OK to continue, Cancel to go back and mark them under Exclusions.';

function initScopeItems(saved, defaults = DEFAULT_DRILL_SCOPE) {
  const base = defaults.map(d => ({
    ...d, status: 'na', custom: false,
    ...(saved ? (saved.find(s => s.id === d.id) || {}) : {}),
  }));
  const baseIds = new Set(base.map(b => b.id));
  // carry over any saved items not in this base (custom items + items from a different default set)
  const extras = saved ? saved.filter(s => !baseIds.has(s.id)) : [];
  return [...base, ...extras];
}

function DrillScopeChecklist({ value, isLocked, onChange, defaults = DEFAULT_DRILL_SCOPE }) {
  const [items, setItems] = React.useState(() => initScopeItems(value, defaults));
  const [newLabel, setNewLabel] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(true); // starts collapsed — the list is long

  React.useEffect(() => {
    setItems(initScopeItems(value, defaults));
  }, [JSON.stringify(value), defaults]);

  function setStatus(id, status) {
    const next = items.map(it => it.id === id ? { ...it, status } : it);
    setItems(next);
    onChange(next);
  }

  function addCustom() {
    if (!newLabel.trim()) return;
    const next = [...items, { id: 'custom_' + Date.now(), label: newLabel.trim(), status: 'included', custom: true }];
    setItems(next);
    onChange(next);
    setNewLabel('');
    setAdding(false);
  }

  function removeCustom(id) {
    const next = items.filter(it => it.id !== id);
    setItems(next);
    onChange(next);
  }

  const STATUS_OPTS = [
    { val: 'included', label: 'Included', color: '#16a34a', bg: '#dcfce7' },
    { val: 'excluded', label: 'Excluded', color: '#dc2626', bg: '#fee2e2' },
    { val: 'na',       label: 'N/A',      color: '#9ca3af', bg: '#f3f4f6' },
  ];

  return (
    <div>
      <div onClick={() => setCollapsed(c => !c)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', marginBottom: collapsed ? 0 : 10 }}>
        {(() => {
          const nIn = items.filter(i => i.status === 'included').length, nEx = items.filter(i => i.status === 'excluded').length;
          // Red until something is marked — a quote with no inclusions/exclusions is usually a miss.
          const none = nIn + nEx === 0;
          return (
            <div style={{ fontSize: 11, fontWeight: 700, color: none ? '#dc2626' : '#4a1d50', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {none ? '⚠ ' : ''}Inclusions and Exclusions
              <span style={{ marginLeft: 8, color: none ? '#dc2626' : '#9ca3af', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                {none ? (isLocked ? 'none marked' : 'none marked yet — open and mark what’s in and out') : `(${nIn} incl · ${nEx} excl)`}
              </span>
            </div>
          );
        })()}
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{collapsed ? '▼' : '▲'}</span>
      </div>
      {!collapsed && (<>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        {items.map((item, idx) => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
            <div style={{ flex: 1, fontSize: 13, color: '#374151' }}>{item.label}</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {STATUS_OPTS.map(opt => (
                <button key={opt.val} onClick={() => !isLocked && setStatus(item.id, opt.val)}
                  disabled={isLocked}
                  style={{
                    padding: '3px 10px', borderRadius: 5, border: 'none', fontSize: 11, fontWeight: 600, cursor: isLocked ? 'default' : 'pointer',
                    background: item.status === opt.val ? opt.bg : '#f3f4f6',
                    color: item.status === opt.val ? opt.color : '#9ca3af',
                    outline: item.status === opt.val ? `2px solid ${opt.color}` : '2px solid transparent',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {item.custom && !isLocked && (
              <button onClick={() => removeCustom(item.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
            )}
          </div>
        ))}
      </div>

      {!isLocked && (
        <div style={{ marginTop: 8 }}>
          {adding ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input autoFocus value={newLabel} onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustom(); if (e.key === 'Escape') setAdding(false); }}
                placeholder="Custom item label…"
                style={{ flex: 1, border: '1px solid #a78bfa', borderRadius: 7, padding: '6px 10px', fontSize: 13, outline: 'none' }} />
              <button onClick={addCustom} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#4a1d50', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Add</button>
              <button onClick={() => setAdding(false)} style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setAdding(true)}
              style={{ padding: '6px 14px', borderRadius: 7, border: '1px dashed #a78bfa', background: '#faf5ff', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              + Add Custom Item
            </button>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}

// ── Rate Sheet Panel ──────────────────────────────────────────────────────────
const RATE_CATEGORIES = ['Labor', 'Truck', 'Equipment'];

function RateSheetPanel({ onAdd }) {
  const [rates] = React.useState(() => {
    try {
      // rates are fetched from server; for now use inline from price_reference
      return null;
    } catch { return null; }
  });
  const [serverRates, setServerRates] = React.useState(null);

  React.useEffect(() => {
    apiFetch('/rate-sheet').then(r => setServerRates(r.items)).catch(() => {});
  }, []);

  const items = serverRates || [];
  const grouped = RATE_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = items.filter(r => r.category === cat);
    return acc;
  }, {});

  return (
    <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', background: '#faf5ff' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        2026 Labor & Equipment Rates — Click to add line item
      </div>
      {items.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: 12 }}>Loading rates…</div>
      ) : (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {RATE_CATEGORIES.map(cat => (
            <div key={cat}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{cat}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {grouped[cat].map(rate => (
                  <button key={rate.description} onClick={() => onAdd(rate)}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #ddd6fe', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                    <span>{rate.description}</span>
                    <span style={{ color: '#7c3aed', fontWeight: 600 }}>${rate.rate}/hr</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
// ── NVE Constants ─────────────────────────────────────────────────────────────
const NVE_STATUSES = [
  { key: 'application_in',           label: 'Application In',                color: '#6b7280', bg: '#f3f4f6' },
  { key: 'construction_ready',       label: 'Construction Ready',            color: '#1d4ed8', bg: '#eff6ff' },
  { key: 'trench_sand_tape',         label: 'Trench, Sand & Tape Complete',  color: '#7c3aed', bg: '#f5f3ff' },
  { key: 'called_in_final',          label: 'Called In for Final',           color: '#d97706', bg: '#fffbeb' },
  { key: 'finaled',                  label: 'Finaled',                       color: '#059669', bg: '#ecfdf5' },
  { key: 'meter_set',                label: 'Meter Set / Complete',          color: '#15803d', bg: '#dcfce7' },
];
const NVE_SERVICE_SIZES = ['Overhead', 'Underground', 'Pedestal', 'CT Cabinet', 'Temporary', 'Sub-Meter'];
const NVE_AMPS = ['100', '200', '320', '400', '800', '1200'];
const NVE_VOLTAGES = ['120/240V Single Phase', '3 Phase 208V', '3 Phase 480V'];
const NVE_PHOTO_TYPES = [
  { key: 'before_install', label: 'Before Install' },
  { key: 'after_install',  label: 'After Install'  },
  { key: 'address_sign',   label: 'Address Sign'   },
];

// ── Shop Tab (equipment moving to/from this job) ────────────────────────────
export function ShopTab({ projectId, sjId, isAdminZone, API_BASE, getToken, showToast }) {
  const [moves, setMoves] = React.useState([]);
  const [equipment, setEquipment] = React.useState([]);
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ equipment_id: '', fromValue: 'shop', driver: '', moved_at: new Date().toISOString().slice(0, 10), status: 'completed', notes: '' });

  function apiFetch(path, opts = {}) { return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))); }
  function reload() { apiFetch(`/equipment-movements?projectId=${projectId}`).then(setMoves).catch(() => {}); }
  React.useEffect(() => {
    Promise.all([apiFetch(`/equipment-movements?projectId=${projectId}`).catch(() => []), apiFetch('/equipment').catch(() => []), apiFetch('/projects').catch(() => [])])
      .then(([m, e, p]) => { setMoves(Array.isArray(m) ? m : []); setEquipment(Array.isArray(e) ? e : []); setJobs(Array.isArray(p) ? p : []); }).finally(() => setLoading(false));
  }, [projectId]);

  const jobLabel = j => `${j.project_number || ''} — ${j.name || ''}`.replace(/^ — | — $/g, '').trim() || j.project_number || 'Job';
  const thisJob = jobs.find(j => j.id === projectId);
  const thisLabel = thisJob ? jobLabel(thisJob) : 'This Job';
  const fromOptions = [{ value: 'shop', label: 'Shop / Yard' }, ...jobs.filter(j => j.id !== projectId).map(j => ({ value: j.id, label: jobLabel(j) }))];
  function locFromValue(v) { if (v === 'shop' || !v) return { label: 'Shop / Yard', project_id: null }; const j = jobs.find(x => x.id === v); return { label: j ? jobLabel(j) : v, project_id: v }; }

  async function add() {
    if (!form.equipment_id) { if (showToast) showToast('Pick a piece of equipment', 'error'); return; }
    const from = locFromValue(form.fromValue);
    const body = { equipment_id: form.equipment_id, from_label: from.label, from_project_id: from.project_id, to_label: thisLabel, to_project_id: projectId, driver: form.driver, moved_at: form.moved_at, status: form.status, notes: form.notes };
    try { await apiFetch('/equipment-movements', { method: 'POST', body: JSON.stringify(body) }); reload(); setAdding(false); setForm(f => ({ ...f, equipment_id: '', driver: '', notes: '' })); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function del(id) { if (!window.confirm('Delete this movement?')) return; try { await apiFetch(`/equipment-movements/${id}`, { method: 'DELETE' }); setMoves(prev => prev.filter(x => x.id !== id)); } catch {} }
  async function complete(m) { try { await apiFetch(`/equipment-movements/${m.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'completed' }) }); reload(); } catch {} }

  const fmtDate = d => { if (!d) return '—'; const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d); return isNaN(dt) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 8px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const lbl = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };
  if (loading) return <div style={{ padding: 12, color: '#9ca3af', fontSize: 13 }}>Loading…</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Equipment moving to &amp; from this job. Full history is on the <strong>Shop</strong> tab in the main menu.</div>
      {moves.length === 0 && !adding && <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 10 }}>No equipment movements for this job yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {moves.map(m => { const done = m.status === 'completed'; return (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>🔧 {m.equipment_name}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{m.from_label} → {m.to_label}{m.driver ? ` · ${m.driver}` : ''}</div>
            </div>
            <span style={{ fontSize: 11, color: '#6b7280' }}>{fmtDate(m.moved_at)}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: done ? '#15803d' : '#1d4ed8', background: done ? '#f0fdf4' : '#eff6ff', padding: '2px 8px', borderRadius: 10 }}>{done ? 'Completed' : 'Scheduled'}</span>
            {isAdminZone && !done && <button onClick={() => complete(m)} style={{ background: 'none', border: 'none', color: '#15803d', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✓ Done</button>}
            {isAdminZone && <button onClick={() => del(m.id)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 15, cursor: 'pointer' }}>×</button>}
          </div>
        ); })}
      </div>
      {isAdminZone && (adding ? (
        <div style={{ marginTop: 10, padding: 12, border: '1px dashed #cbd5e1', borderRadius: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Equipment</div><select value={form.equipment_id} onChange={e => setForm(f => ({ ...f, equipment_id: e.target.value }))} style={inp}><option value="">— Select —</option>{equipment.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select></div>
          <div><div style={lbl}>From</div><select value={form.fromValue} onChange={e => setForm(f => ({ ...f, fromValue: e.target.value }))} style={inp}>{fromOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          <div><div style={lbl}>To</div><input value={thisLabel} disabled style={{ ...inp, background: '#f3f4f6' }} /></div>
          <div><div style={lbl}>Driver</div><input value={form.driver} onChange={e => setForm(f => ({ ...f, driver: e.target.value }))} style={inp} /></div>
          <div><div style={lbl}>Date</div><input type="date" value={form.moved_at} onChange={e => setForm(f => ({ ...f, moved_at: e.target.value }))} style={inp} /></div>
          <div><div style={lbl}>Status</div><select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inp}><option value="scheduled">Scheduled</option><option value="completed">Completed</option></select></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={add} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: '#475569', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Log</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ marginTop: 10, padding: '8px 16px', borderRadius: 8, border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Log Movement to this job</button>
      ))}
      {sjId && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#4a1d50', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Equipment Hours</div>
          <EquipmentHoursTab sjId={sjId} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
        </div>
      )}
    </div>
  );
}

// ── Equipment Hours Tab (billable machine time; rate pulled from Equipment) ─
// Field crews log date/equipment/hours from the PWA; the rate lives once on
// each machine's equipment record. Office roles see rate + billable $; the
// server omits those for field/shop, so this table just hides what it lacks.
export function EquipmentHoursTab({ sjId, isAdminZone, API_BASE, getToken, showToast }) {
  const [items, setItems] = React.useState([]);
  const [drills, setDrills] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState({ equipment_id: '', work_date: new Date().toISOString().slice(0, 10), hours: '', note: '' });

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
  }
  function reload() { apiFetch(`/sub-jobs/${sjId}/drill-hours`).then(d => setItems(Array.isArray(d) ? d : [])).catch(() => {}); }
  React.useEffect(() => {
    Promise.all([apiFetch(`/sub-jobs/${sjId}/drill-hours`).catch(() => []), apiFetch('/drill-options').catch(() => [])])
      .then(([h, d]) => { setItems(Array.isArray(h) ? h : []); setDrills(Array.isArray(d) ? d : []); }).finally(() => setLoading(false));
  }, [sjId]);

  const totalHours = items.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  const hasRates = items.some(r => r.billable != null);
  const totalBillable = items.reduce((s, r) => s + (Number(r.billable) || 0), 0);

  async function add() {
    if (!draft.equipment_id) { showToast && showToast('Pick equipment', 'error'); return; }
    if (!draft.hours) { showToast && showToast('Enter hours', 'error'); return; }
    const drill = drills.find(d => d.id === draft.equipment_id);
    try {
      await apiFetch(`/sub-jobs/${sjId}/drill-hours`, { method: 'POST', body: JSON.stringify({ equipment_id: draft.equipment_id, drill_name: drill ? drill.name : '', work_date: draft.work_date, hours: draft.hours, note: draft.note }) });
      reload(); setAdding(false); setDraft(d => ({ ...d, equipment_id: '', hours: '', note: '' }));
    } catch (e) { showToast && showToast(e.message || 'Failed', 'error'); }
  }
  async function del(id) { if (!window.confirm('Delete this entry?')) return; try { await apiFetch(`/sub-jobs/${sjId}/drill-hours/${id}`, { method: 'DELETE' }); setItems(prev => prev.filter(x => x.id !== id)); } catch {} }

  const fmtDate = d => { if (!d) return '—'; const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d); return isNaN(dt) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
  const money = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 8px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const lbl = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };
  const th = { padding: '7px 10px', fontWeight: 700, fontSize: 11, color: '#fff', textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4, background: '#4a1d50', whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6' };
  if (loading) return <div style={{ padding: 12, color: '#9ca3af', fontSize: 13 }}>Loading…</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Billable equipment time for this job. Set each machine's rate once in <strong>Settings → Equipment</strong>; it's pulled in here automatically.</div>
      {items.length === 0 && !adding && <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 10 }}>No equipment hours logged yet.</div>}
      {items.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Equipment</th><th style={{ ...th, textAlign: 'right' }}>Hours</th>
              {hasRates && <th style={{ ...th, textAlign: 'right' }}>Rate</th>}
              {hasRates && <th style={{ ...th, textAlign: 'right' }}>Billable</th>}
              <th style={th}>Note</th><th style={th} />
            </tr></thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#faf5ff' }}>
                  <td style={td}>{fmtDate(r.work_date)}</td>
                  <td style={td}>{r.drill_name || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{r.hours ?? '—'}</td>
                  {hasRates && <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{r.billing_rate != null ? `${money(r.billing_rate)}/${r.rate_unit === 'day' ? 'day' : 'hr'}` : '—'}</td>}
                  {hasRates && <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{r.billable != null ? money(r.billable) : '—'}</td>}
                  <td style={{ ...td, color: '#6b7280' }}>{r.note || ''}</td>
                  <td style={td}>{isAdminZone && <button onClick={() => del(r.id)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 15, cursor: 'pointer' }}>×</button>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={2}>Total</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{totalHours}</td>
              {hasRates && <td style={td} />}
              {hasRates && <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{money(totalBillable)}</td>}
              <td style={td} colSpan={2} />
            </tr></tfoot>
          </table>
        </div>
      )}
      {isAdminZone && (adding ? (
        <div style={{ padding: 12, border: '1px dashed #cbd5e1', borderRadius: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><div style={lbl}>Date</div><input type="date" value={draft.work_date} onChange={e => setDraft(d => ({ ...d, work_date: e.target.value }))} style={inp} /></div>
          <div><div style={lbl}>Hours</div><input type="number" step="0.5" value={draft.hours} onChange={e => setDraft(d => ({ ...d, hours: e.target.value }))} style={inp} placeholder="0" /></div>
          <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Equipment</div><select value={draft.equipment_id} onChange={e => setDraft(d => ({ ...d, equipment_id: e.target.value }))} style={inp}><option value="">— Select —</option>{drills.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
          <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Note</div><input value={draft.note} onChange={e => setDraft(d => ({ ...d, note: e.target.value }))} style={inp} placeholder="Optional" /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={add} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: '#4a1d50', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Log Hours</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px dashed #cbd5e1', background: '#faf5ff', color: '#4a1d50', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Log Equipment Hours</button>
      ))}
    </div>
  );
}

// ── Payroll Tab (certified payroll reports per pay period) ──────────────────
const PAY_STATUS_META = {
  due:       { label: 'Due',       color: '#b45309', bg: '#fffbeb' },
  submitted: { label: 'Submitted', color: '#1d4ed8', bg: '#eff6ff' },
  accepted:  { label: 'Accepted',  color: '#15803d', bg: '#f0fdf4' },
};
const PAY_STATUS_OPTS = [['due', 'Due'], ['submitted', 'Submitted'], ['accepted', 'Accepted']];

export function PayrollTab({ sjId, isAdminZone, API_BASE, getToken, showToast }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState({ period_ending: '', report_number: '', status: 'due' });

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
  }
  React.useEffect(() => { apiFetch(`/sub-jobs/${sjId}/payroll-items`).then(setItems).catch(() => {}).finally(() => setLoading(false)); }, [sjId]);

  async function add() {
    if (!draft.period_ending) { if (showToast) showToast('Period-ending date is required', 'error'); return; }
    try { const it = await apiFetch(`/sub-jobs/${sjId}/payroll-items`, { method: 'POST', body: JSON.stringify(draft) }); setItems(prev => [...prev, it]); setDraft({ period_ending: '', report_number: '', status: 'due' }); setAdding(false); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function patch(id, fields) {
    setItems(prev => prev.map(x => x.id === id ? { ...x, ...fields } : x));
    try { const up = await apiFetch(`/sub-jobs/${sjId}/payroll-items/${id}`, { method: 'PATCH', body: JSON.stringify(fields) }); setItems(prev => prev.map(x => x.id === id ? up : x)); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function del(id) {
    if (!window.confirm('Remove this payroll report?')) return;
    try { await apiFetch(`/sub-jobs/${sjId}/payroll-items/${id}`, { method: 'DELETE' }); setItems(prev => prev.filter(x => x.id !== id)); } catch {}
  }
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 8px', fontSize: 13, boxSizing: 'border-box' };
  const lbl = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };
  if (loading) return <div style={{ padding: 12, color: '#9ca3af', fontSize: 13 }}>Loading…</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Certified payroll reports — one per pay period.</div>
      {items.length === 0 && !adding && <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 10 }}>No payroll reports yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(it => {
          const sm = PAY_STATUS_META[it.status] || PAY_STATUS_META.due;
          return (
            <div key={it.id} style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '10px 12px', border: '1px solid #e5e7eb', borderLeft: `4px solid ${sm.color}`, borderRadius: 8, background: '#fff', flexWrap: 'wrap' }}>
              <div><div style={lbl}>Period Ending</div><input type="date" value={it.period_ending ? String(it.period_ending).slice(0, 10) : ''} disabled={!isAdminZone} onChange={e => patch(it.id, { period_ending: e.target.value })} style={inp} /></div>
              <div><div style={lbl}>Report #</div><input defaultValue={it.report_number || ''} disabled={!isAdminZone} onBlur={e => { if (e.target.value !== (it.report_number || '')) patch(it.id, { report_number: e.target.value }); }} placeholder="#" style={{ ...inp, width: 90 }} /></div>
              <div><div style={lbl}>Submitted</div><input type="date" value={it.submitted_date ? String(it.submitted_date).slice(0, 10) : ''} disabled={!isAdminZone} onChange={e => patch(it.id, { submitted_date: e.target.value })} style={inp} /></div>
              <div><div style={lbl}>Status</div><select value={it.status} disabled={!isAdminZone} onChange={e => patch(it.id, { status: e.target.value })} style={{ ...inp, color: sm.color, fontWeight: 700, background: sm.bg }}>{PAY_STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              {isAdminZone && <button onClick={() => del(it.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9ca3af', fontSize: 16, cursor: 'pointer' }}>×</button>}
            </div>
          );
        })}
      </div>
      {isAdminZone && (adding ? (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 10, padding: '10px 12px', border: '1px dashed #cbd5e1', borderRadius: 8, flexWrap: 'wrap' }}>
          <div><div style={lbl}>Period Ending *</div><input type="date" value={draft.period_ending} onChange={e => setDraft(d => ({ ...d, period_ending: e.target.value }))} style={inp} /></div>
          <div><div style={lbl}>Report #</div><input value={draft.report_number} onChange={e => setDraft(d => ({ ...d, report_number: e.target.value }))} style={{ ...inp, width: 90 }} /></div>
          <div><div style={lbl}>Status</div><select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))} style={inp}>{PAY_STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <button onClick={add} style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: '#a16207', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add</button>
          <button onClick={() => setAdding(false)} style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ marginTop: 10, padding: '8px 16px', borderRadius: 8, border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add Payroll Report</button>
      ))}
    </div>
  );
}

// USA (811) dig-ticket statuses. Yellow for called-in-not-active and expiring
// soon, green for active, red for expired — matching the requested tab colors.
const USA_STATUS_META = {
  // Grey: a ticket nobody has called in yet is a to-do, not a warning.
  not_called_in: { label: 'Not Called In', color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db', dot: '#9ca3af' },
  called_in:     { label: 'Called In',     color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
  active:        { label: 'Active',        color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', dot: '#22c55e' },
  expiring_soon: { label: 'Expiring Soon', color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
  expired:       { label: 'Expired',       color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444' },
  // Grey, deliberately: a finished hole is not a warning and shouldn't read as one.
  dug:           { label: 'Dug',           color: '#57534e', bg: '#f5f5f4', border: '#e7e5e4', dot: '#a8a29e' },
};
const USA_ROLLUP_ORDER = { expired: 5, expiring_soon: 4, not_called_in: 3, called_in: 2, active: 1, dug: 0 };

export function UsaDigsTab({ sjId, isAdminZone, API_BASE, getToken, showToast }) {
  const [items, setItems] = React.useState([]);
  const [photos, setPhotos] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [drafts, setDrafts] = React.useState({});
  const [uploading, setUploading] = React.useState(null);
  const [uploadingPdf, setUploadingPdf] = React.useState(null);
  const [lightbox, setLightbox] = React.useState(null); // enlarged photo url
  const geoRef = React.useRef({});

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
  }
  function loadPhotos(itemId) {
    apiFetch(`/sub-jobs/${sjId}/usa-digs/${itemId}/photos`).then(ps => setPhotos(p => ({ ...p, [itemId]: ps }))).catch(() => {});
  }
  React.useEffect(() => {
    apiFetch(`/sub-jobs/${sjId}/usa-digs`).then(its => { setItems(its); its.forEach(it => loadPhotos(it.id)); })
      .catch(e => { if (showToast) showToast(e.message, 'error'); }).finally(() => setLoading(false));
  }, [sjId]);

  async function addItem() {
    try {
      const it = await apiFetch(`/sub-jobs/${sjId}/usa-digs`, { method: 'POST', body: JSON.stringify({ usa_number: '', called_in_at: '' }) });
      setItems(prev => [...prev, it]);
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  function setDraft(itemId, field, value) { setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] || {}), [field]: value } })); }
  async function saveField(item, field, value) {
    try {
      const upd = await apiFetch(`/sub-jobs/${sjId}/usa-digs/${item.id}`, { method: 'PATCH', body: JSON.stringify({ [field]: value }) });
      setItems(prev => prev.map(x => x.id === item.id ? upd : x));
      setDrafts(d => { const n = { ...d }; if (n[item.id]) delete n[item.id][field]; return n; });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  // The server stamps the time and the name, so two people can't disagree about when.
  async function markDug(item, dug) {
    try {
      const upd = await apiFetch(`/sub-jobs/${sjId}/usa-digs/${item.id}/dug`, { method: 'POST', body: JSON.stringify({ dug }) });
      setItems(prev => prev.map(x => x.id === item.id ? upd : x));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function deleteItem(itemId) {
    if (!window.confirm('Remove this USA ticket?')) return;
    try { await apiFetch(`/sub-jobs/${sjId}/usa-digs/${itemId}`, { method: 'DELETE' }); setItems(prev => prev.filter(x => x.id !== itemId)); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  function requestGeoThenCamera(itemId) {
    geoRef.current[itemId] = null;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => { geoRef.current[itemId] = { lat: pos.coords.latitude, lng: pos.coords.longitude }; },
        () => { geoRef.current[itemId] = null; },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
    }
    const input = document.getElementById(`usa-photo-${itemId}`);
    if (input) input.click();
  }
  async function reverseGeocode(lat, lng) {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&zoom=18&lat=${lat}&lon=${lng}`);
      const j = await r.json(); const a = (j && j.address) || {};
      const parts = [[a.house_number, a.road].filter(Boolean).join(' '), a.city || a.town || a.village, a.state].filter(Boolean);
      return parts.join(', ') || (j && j.display_name) || null;
    } catch { return null; }
  }
  async function onPhotoSelected(itemId, file) {
    if (!file) return;
    setUploading(itemId);
    try {
      const fd = new FormData(); fd.append('file', file);
      const geo = geoRef.current[itemId];
      if (geo) { fd.append('lat', geo.lat); fd.append('lng', geo.lng); const loc = await reverseGeocode(geo.lat, geo.lng); if (loc) fd.append('location', loc); }
      const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/usa-digs/${itemId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error('Upload failed');
      loadPhotos(itemId);
      const its = await apiFetch(`/sub-jobs/${sjId}/usa-digs`); setItems(its);
      if (!geo && showToast) showToast('Photo added — GPS unavailable, so no location was captured', 'info');
    } catch (e) { if (showToast) showToast(e.message || 'Upload failed', 'error'); }
    setUploading(null);
  }
  async function deletePhoto(itemId, photoId) {
    try { await apiFetch(`/sub-jobs/${sjId}/usa-digs/${itemId}/photos/${photoId}`, { method: 'DELETE' });
      setPhotos(p => ({ ...p, [itemId]: (p[itemId] || []).filter(x => x.id !== photoId) })); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // The official USA locate-ticket PDF — one per ticket.
  async function onPdfSelected(itemId, file) {
    if (!file) return;
    setUploadingPdf(itemId);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/usa-digs/${itemId}/pdf`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error('Upload failed');
      const upd = await r.json();
      setItems(prev => prev.map(x => x.id === itemId ? upd : x));
    } catch (e) { if (showToast) showToast(e.message || 'Upload failed', 'error'); }
    setUploadingPdf(null);
  }
  async function removePdf(itemId) {
    if (!window.confirm('Remove the attached USA PDF?')) return;
    try { const upd = await apiFetch(`/sub-jobs/${sjId}/usa-digs/${itemId}/pdf`, { method: 'DELETE' });
      setItems(prev => prev.map(x => x.id === itemId ? upd : x)); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  const photoUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
  const rollup = items.reduce((worst, it) => (!worst || (USA_ROLLUP_ORDER[it.status] || 0) > (USA_ROLLUP_ORDER[worst] || 0)) ? it.status : worst, null);
  const rollupMeta = rollup ? USA_STATUS_META[rollup] : null;
  const inputSt = { border: '1px solid #e5e7eb', borderRadius: 7, padding: '9px 10px', fontSize: 14, boxSizing: 'border-box', width: '100%', maxWidth: '100%' };
  const fmtDT = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  const toLocalInput = iso => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d)) return ''; const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

  if (loading) return <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading USA tickets…</div>;

  return (
    <div>
      {rollupMeta && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, marginBottom: 12, background: rollupMeta.bg, border: `1px solid ${rollupMeta.border}` }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: rollupMeta.dot }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: rollupMeta.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{rollupMeta.label}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>· {items.length} ticket{items.length !== 1 ? 's' : ''}</span>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No USA tickets yet — add one for each dig alert.</div>}
        {items.map(item => {
          const sm = USA_STATUS_META[item.status] || USA_STATUS_META.not_called_in;
          const draft = drafts[item.id] || {};
          const itemPhotos = photos[item.id] || [];
          return (
            <div key={item.id} style={{ border: `1px solid ${sm.border}`, borderLeft: `4px solid ${sm.dot}`, borderRadius: 10, padding: 14, background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: sm.color, background: sm.bg, padding: '3px 9px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>{sm.label}</span>
                <span style={{ flex: 1 }} />
                {/* Said at the hole, by whoever dug it. After this the ticket stops
                    counting down — which is the whole point of saying so.

                    Filled and solid on purpose. A pale tinted pill sat next to the pale
                    tinted status pill and read as a second status, so nobody pressed it.
                    Nothing else in this row is filled, and the label is an instruction
                    rather than a noun. */}
                <button onClick={() => markDug(item, !item.dug_at)}
                  title={item.dug_at ? 'Mark this ticket not dug yet' : 'The digging on this ticket is finished'}
                  style={{ padding: item.dug_at ? '5px 11px' : '7px 14px', borderRadius: 8,
                    fontSize: item.dug_at ? 12 : 12.5, fontWeight: 800, cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    border: item.dug_at ? '1px solid #d6d3d1' : 'none',
                    background: item.dug_at ? '#fff' : '#15803d',
                    color: item.dug_at ? '#57534e' : '#fff',
                    boxShadow: item.dug_at ? 'none' : '0 1px 2px rgba(0,0,0,0.18)' }}>
                  {item.dug_at ? '✓ Dug — press to undo' : '👉 Press when digging complete'}
                </button>
                <button onClick={() => deleteItem(item.id)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 16, cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>USA Number</label>
                  <input value={draft.usa_number !== undefined ? draft.usa_number : (item.usa_number || '')}
                    onChange={e => setDraft(item.id, 'usa_number', e.target.value)}
                    onBlur={e => { if (e.target.value !== (item.usa_number || '')) saveField(item, 'usa_number', e.target.value); }}
                    placeholder="Ticket #" style={inputSt} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Called In (date &amp; time)</label>
                    <button type="button" onClick={() => saveField(item, 'called_in_at', new Date().toISOString())}
                      style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Now</button>
                  </div>
                  <input type="datetime-local" value={toLocalInput(item.called_in_at)}
                    onChange={e => saveField(item, 'called_in_at', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    style={inputSt} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
                <span>Active: <strong style={{ color: '#374151' }}>{fmtDT(item.active_at)}</strong></span>
                <span>Expires: <strong style={{ color: '#374151' }}>{fmtDT(item.expires_at)}</strong></span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Location</label>
                {item.location ? (
                  <div style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>📍 {item.location}
                    {item.lat != null && <a href={`https://www.google.com/maps?q=${item.lat},${item.lng}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#1d4ed8' }}>map</a>}</div>
                ) : item.lat != null ? (
                  <div style={{ fontSize: 13, color: '#374151' }}>📍 {Number(item.lat).toFixed(5)}, {Number(item.lng).toFixed(5)} <a href={`https://www.google.com/maps?q=${item.lat},${item.lng}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#1d4ed8' }}>map</a></div>
                ) : (
                  <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No location yet — add a photo to capture GPS.</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {itemPhotos.map(ph => (
                  <div key={ph.id} style={{ position: 'relative' }}>
                    <img src={photoUrl(ph.filename)} alt="USA ticket photo" onClick={() => setLightbox(photoUrl(ph.filename))}
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'zoom-in' }} />
                    <button onClick={() => deletePhoto(item.id, ph.id)} title="Remove photo"
                      style={{ position: 'absolute', top: -7, right: -7, background: '#ef4444', color: '#fff', border: '2px solid #fff', borderRadius: '50%', width: 22, height: 22, fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>
                ))}
                <input id={`usa-photo-${item.id}`} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { onPhotoSelected(item.id, e.target.files[0]); e.target.value = ''; }} />
                <button onClick={() => requestGeoThenCamera(item.id)} disabled={uploading === item.id}
                  style={{ width: 64, height: 64, borderRadius: 8, border: '1.5px dashed #cbd5e1', background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  {uploading === item.id ? '…' : <>📷<span>Photo</span></>}
                </button>
              </div>
              {/* Official USA (811) locate-ticket PDF */}
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>USA Ticket PDF</label>
                {item.ticket_pdf_filename ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {item.ticket_pdf_name || 'USA ticket.pdf'}</span>
                    <a href={photoUrl(item.ticket_pdf_filename)} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>View</a>
                    <button onClick={() => removePdf(item.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                  </div>
                ) : (
                  // Drag the ticket PDF (or a photo of it) straight onto here, or click to browse.
                  <AttachButton accept="both" icon="📄" disabled={uploadingPdf === item.id}
                    label={uploadingPdf === item.id ? 'Uploading…' : 'Drag & drop the USA ticket here, or click to attach'}
                    style={{ width: '100%', marginTop: 4, padding: '15px 12px', fontSize: 13, borderRadius: 8 }}
                    onFiles={fs => onPdfSelected(item.id, fs[0])} />
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <input value={draft.notes !== undefined ? draft.notes : (item.notes || '')}
                  onChange={e => setDraft(item.id, 'notes', e.target.value)}
                  onBlur={e => { if (e.target.value !== (item.notes || '')) saveField(item, 'notes', e.target.value); }}
                  placeholder="Notes…" style={{ ...inputSt, width: '100%' }} />
              </div>
            </div>
          );
        })}
      </div>
      <button onClick={addItem} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        + Add USA Ticket
      </button>

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <img src={lightbox} alt="USA ticket photo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
          <button onClick={() => setLightbox(null)}
            style={{ position: 'fixed', top: 'max(env(safe-area-inset-top), 16px)', right: 16, background: 'rgba(0,0,0,0.5)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.6)', borderRadius: '50%', width: 40, height: 40, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      )}
    </div>
  );
}

export function NVETab({ sjId, isAdminZone, API_BASE, getToken, showToast }) {
  const [meters, setMeters] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedMeters, setExpandedMeters] = React.useState({});
  const [meterPhotos, setMeterPhotos] = React.useState({}); // meterId -> []
  const [uploadingPhoto, setUploadingPhoto] = React.useState({}); // meterId-type -> bool
  const [addingMeter, setAddingMeter] = React.useState(false);
  // One Fieldwire task per meter (Ryan, Oct 2 2026): which meters are on their way over,
  // which task is open in the drawer, and the job's Fieldwire picture (statuses, linked?).
  const [sendingFw, setSendingFw] = React.useState({});
  const [fwOpen, setFwOpen] = React.useState(null);
  const [fwOv, setFwOv] = React.useState(null);
  const [fwPick, setFwPick] = React.useState(null);   // the meter whose "attach or create?" chooser is open
  const [savingMeter, setSavingMeter] = React.useState({});
  const [meterDrafts, setMeterDrafts] = React.useState({}); // meterId -> field overrides
  const [lightbox, setLightbox] = React.useState(null); // { url, type }
  const [mapModal, setMapModal] = React.useState(null); // meterId

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, {
      ...opts,
      // A JSON body must say so, or the server reads an empty body and changes nothing while answering
      // OK (Ryan, Nov 2 2026: unticking a document for the package looked saved but every file still
      // printed). FormData bodies are not strings and keep their own type.
      headers: { 'x-worker-token': getToken(), ...(typeof opts.body === 'string' ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) },
    }).then(async r => {
      if (r.ok) return r.json();
      const text = await r.text();
      let msg;
      try { msg = JSON.parse(text)?.error || text; } catch { msg = `Server error ${r.status}`; }
      return Promise.reject(new Error(msg));
    });
  }

  React.useEffect(() => {
    apiFetch(`/sub-jobs/${sjId}/nve-meters`).then(ms => {
      setMeters(ms);
      const exp = {}; const drafts = {};
      ms.forEach(m => { exp[m.id] = true; drafts[m.id] = {}; });
      setExpandedMeters(exp);
      setMeterDrafts(drafts);
      // Load photos for each meter
      Promise.all(ms.map(m =>
        apiFetch(`/sub-jobs/${sjId}/nve-meters/${m.id}/photos`).then(photos => [m.id, photos])
      )).then(pairs => {
        const mp = {};
        pairs.forEach(([id, photos]) => { mp[id] = photos; });
        setMeterPhotos(mp);
      });
    }).catch(console.error).finally(() => setLoading(false));
  }, [sjId]);

  // The job's Fieldwire picture, once there is a meter to read the job off (the first meter
  // added gets its Fieldwire button straight away).
  const fwProjectId = meters[0] && meters[0].project_id;
  React.useEffect(() => {
    if (!fwProjectId) return;
    apiFetch(`/projects/${fwProjectId}/fieldwire`).then(setFwOv).catch(() => setFwOv(null));
  }, [fwProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  function draft(meterId) { return meterDrafts[meterId] || {}; }
  function fieldVal(meter, field) {
    const d = draft(meter.id);
    return d[field] !== undefined ? d[field] : (meter[field] ?? '');
  }
  function setField(meterId, field, value) {
    setMeterDrafts(d => ({ ...d, [meterId]: { ...d[meterId], [field]: value } }));
  }

  async function saveMeter(meter) {
    const changes = draft(meter.id);
    if (Object.keys(changes).length === 0) return;
    setSavingMeter(s => ({ ...s, [meter.id]: true }));
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meter.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      setMeters(ms => ms.map(m => m.id === meter.id ? updated : m));
      setMeterDrafts(d => ({ ...d, [meter.id]: {} }));
      if (showToast) showToast('Meter saved');
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setSavingMeter(s => ({ ...s, [meter.id]: false }));
  }

  async function addMeter() {
    setAddingMeter(true);
    try {
      const m = await apiFetch(`/sub-jobs/${sjId}/nve-meters`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setMeters(ms => [...ms, m]);
      setExpandedMeters(e => ({ ...e, [m.id]: true }));
      setMeterDrafts(d => ({ ...d, [m.id]: {} }));
      setMeterPhotos(p => ({ ...p, [m.id]: [] }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setAddingMeter(false);
  }

  async function deleteMeter(meterId) {
    if (!window.confirm('Delete this meter? All photos will be lost.')) return;
    try {
      await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meterId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setMeters(ms => ms.filter(m => m.id !== meterId));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  async function setStatus(meter, statusKey) {
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meter.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: statusKey }),
      });
      setMeters(ms => ms.map(m => m.id === meter.id ? updated : m));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  async function uploadPhoto(meterId, photoType, file) {
    const key = `${meterId}-${photoType}`;
    setUploadingPhoto(u => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('photo_type', photoType);
      const res = await fetch(`${API_BASE}/sub-jobs/${sjId}/nve-meters/${meterId}/photos`, {
        method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      const photo = await res.json();
      setMeterPhotos(p => ({ ...p, [meterId]: [...(p[meterId] || []), photo] }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setUploadingPhoto(u => ({ ...u, [key]: false }));
  }

  async function deletePhoto(meterId, photoId) {
    try {
      await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meterId}/photos/${photoId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setMeterPhotos(p => ({ ...p, [meterId]: (p[meterId] || []).filter(x => x.id !== photoId) }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Send one meter over as its own Fieldwire task; the row then shows the task's number
  // and status and opens it in the drawer.
  async function sendMeterToFieldwire(meter) {
    setSendingFw(s => ({ ...s, [meter.id]: true }));
    try {
      const r = await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meter.id}/fieldwire`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const fresh = await apiFetch(`/sub-jobs/${sjId}/nve-meters`);
      setMeters(fresh);
      if (showToast) showToast(`Fieldwire task #${r.task?.number || '?'} made for ${r.task?.name || 'the meter'}`);
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
    setSendingFw(s => ({ ...s, [meter.id]: false }));
  }
  async function attachMeterToFieldwire(meter, taskId) {
    setSendingFw(s => ({ ...s, [meter.id]: true }));
    try {
      const r = await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meter.id}/fieldwire/attach`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ task_id: taskId }) });
      setMeters(await apiFetch(`/sub-jobs/${sjId}/nve-meters`));
      if (showToast) showToast(`Meter put on Fieldwire task #${r.task?.number || '?'}`);
      setFwPick(null);
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
    setSendingFw(s => ({ ...s, [meter.id]: false }));
  }
  async function unlinkMeterFromFieldwire(meter) {
    if (!window.confirm('Take this meter off its Fieldwire task? The task stays in Fieldwire.')) return;
    try {
      await apiFetch(`/sub-jobs/${sjId}/nve-meters/${meter.id}/fieldwire`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setMeters(await apiFetch(`/sub-jobs/${sjId}/nve-meters`));
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
  }
  async function sendAllMetersToFieldwire() {
    for (const m of meters.filter(x => !x.fw_task_id)) await sendMeterToFieldwire(m);   // one after the other — Fieldwire numbers them in order
  }

  function photoUrl(filename) { return `${API_BASE.replace('/api', '')}/uploads/${filename}`; }

  const statusIdx = key => NVE_STATUSES.findIndex(s => s.key === key);

  if (loading) return <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading NVE data…</div>;

  // The Plan Sets drop zone that used to sit here came off (Ryan, Oct 2 2026): the card's
  // Drawings pillow holds the sheets (Fieldwire) and any uploaded drawings now.
  const fwLinked = !!(fwOv && fwOv.configured && fwOv.linked);
  const fwMissing = meters.filter(m => !m.fw_task_id);
  const projectId = meters[0] && meters[0].project_id;

  return (
    <div>
      {/* ── One Fieldwire task per meter (Ryan, Oct 2 2026) ── */}
      {isAdminZone && fwLinked && meters.length > 1 && fwMissing.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '9px 12px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff' }}>
          <span style={{ fontSize: 12.5, color: '#1e40af', fontWeight: 700, flex: 1 }}>📍 {fwMissing.length === meters.length ? 'None of these meters are in Fieldwire yet.' : `${fwMissing.length} of ${meters.length} meters are not in Fieldwire yet.`}</span>
          <button onClick={sendAllMetersToFieldwire} disabled={Object.values(sendingFw).some(Boolean)}
            style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: '#1e40af', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {Object.values(sendingFw).some(Boolean) ? 'Sending…' : 'One task per meter'}
          </button>
        </div>
      )}

      {/* ── Meters ─────────────────────────────────────────────── */}
      {meters.map((meter, mIdx) => {
        const isComplete = meter.status === 'meter_set';
        const curStatusIdx = statusIdx(meter.status);
        const photos = meterPhotos[meter.id] || [];
        const isExpanded = expandedMeters[meter.id] !== false;
        const hasDraft = Object.keys(draft(meter.id)).length > 0;

        return (
          <div key={meter.id} className="admin-card" style={{ marginBottom: 14, overflow: 'hidden', border: isComplete ? '2px solid #16a34a' : '1px solid #e5e7eb' }}>
            {/* Meter header */}
            <div onClick={() => setExpandedMeters(e => ({ ...e, [meter.id]: !isExpanded }))}
              style={{ background: isComplete ? '#dcfce7' : '#f9fafb', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {isComplete && <span style={{ fontSize: 16 }}>✅</span>}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: isComplete ? '#15803d' : '#111827' }}>
                    {meter.address || `Meter ${mIdx + 1}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                    {NVE_STATUSES.find(s => s.key === meter.status)?.label || meter.status}
                    {meter.amps && ` · ${meter.amps}A`}
                    {meter.voltage && ` · ${meter.voltage}`}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                {/* Its Fieldwire task: number + status, opens in the drawer; or the button to make one. */}
                {meter.fw_task_id ? (<>
                  <button onClick={() => setFwOpen(meter.fw_task_id)} title="Open the Fieldwire task"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${(meter.fw_task && meter.fw_task.color) || '#bfdbfe'}`, borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, color: '#1e40af', cursor: 'pointer' }}>
                    📍 {meter.fw_task && meter.fw_task.number ? '#' + meter.fw_task.number : 'Fieldwire'}
                    {meter.fw_task && meter.fw_task.status ? <span style={{ color: meter.fw_task.color || '#6b7280' }}>· {meter.fw_task.status}</span> : null}
                  </button>
                  {isAdminZone && <button onClick={() => unlinkMeterFromFieldwire(meter)} title="Take this meter off that task (the task stays in Fieldwire)"
                    style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0, cursor: 'pointer' }}>⨯</button>}
                </>) : (isAdminZone && fwLinked && (
                  <button onClick={() => setFwPick(meter)} disabled={!!sendingFw[meter.id]} title="Put this meter on a Fieldwire task — one it already has, or a new one"
                    style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 5, color: '#1e40af', fontSize: 11, padding: '2px 8px', cursor: 'pointer', fontWeight: 700 }}>
                    {sendingFw[meter.id] ? 'Sending…' : '📍 Fieldwire task'}
                  </button>
                ))}
                {isAdminZone && (
                  <button onClick={() => deleteMeter(meter.id)}
                    style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 5, color: '#ef4444', fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                    Delete
                  </button>
                )}
                <span style={{ fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}
                  onClick={() => setExpandedMeters(e => ({ ...e, [meter.id]: !isExpanded }))}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>
            </div>

            {isExpanded && (() => {
              // "Underground only" stopped being a checkbox on Oct 3 2026 (Ryan): a job with NO
              // meters is underground only — so a meter that exists is a meter, with everything.
              const ug = false;
              const statusOpts = NVE_STATUSES;
              return (
              <div style={{ padding: 16 }}>

                {/* Status stepper */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Status</div>
                  <select
                    value={meter.status || 'application_in'}
                    disabled={!isAdminZone}
                    onChange={e => setStatus(meter, e.target.value)}
                    style={{
                      border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13,
                      fontWeight: 600, cursor: isAdminZone ? 'pointer' : 'default',
                      color: NVE_STATUSES.find(s => s.key === (meter.status || 'application_in'))?.color || '#374151',
                      background: NVE_STATUSES.find(s => s.key === (meter.status || 'application_in'))?.bg || '#f9fafb',
                      width: '100%', maxWidth: 300,
                    }}>
                    {statusOpts.map(s => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Fields grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  {!ug && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Address</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={fieldVal(meter, 'address')} onChange={e => setField(meter.id, 'address', e.target.value)}
                        placeholder="123 Main St, Las Vegas, NV"
                        style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                      <button onClick={() => setMapModal(meter.id)}
                        title="Set map location"
                        style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 16 }}>
                        📍
                      </button>
                    </div>
                    {meter.lat && meter.lng && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>📍 {parseFloat(meter.lat).toFixed(5)}, {parseFloat(meter.lng).toFixed(5)}</div>
                    )}
                  </div>
                  )}
                  {!ug && [
                    ['Service Size', 'service_size', NVE_SERVICE_SIZES],
                    ['Amps', 'amps', NVE_AMPS],
                    ['Voltage', 'voltage', NVE_VOLTAGES],
                  ].map(([label, field, opts]) => (
                    <div key={field}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{label}</div>
                      <select value={fieldVal(meter, field)} onChange={e => setField(meter.id, field, e.target.value)}
                        style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: '#fff' }}>
                        <option value="">— Select —</option>
                        {opts.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Permit #</div>
                    <input value={fieldVal(meter, 'permit_number')} onChange={e => setField(meter.id, 'permit_number', e.target.value)}
                      placeholder="Permit number"
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Permit Date</div>
                    <input type="date" value={fieldVal(meter, 'permit_date')} onChange={e => setField(meter.id, 'permit_date', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  </div>
                  {!ug && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8 }}>
                    <input type="checkbox" id={`city-ok-${meter.id}`}
                      checked={!!(fieldVal(meter, 'city_ok') == 1 || fieldVal(meter, 'city_ok') === true)}
                      onChange={e => setField(meter.id, 'city_ok', e.target.checked ? 1 : 0)}
                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }} />
                    <label htmlFor={`city-ok-${meter.id}`} style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>City OK</label>
                  </div>
                  )}
                  {!ug && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>City OK Date</div>
                    <input type="date" value={fieldVal(meter, 'city_ok_date')} onChange={e => setField(meter.id, 'city_ok_date', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  </div>
                  )}
                </div>

                {hasDraft && (
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => saveMeter(meter)} disabled={savingMeter[meter.id]}
                      style={{ padding: '7px 18px', borderRadius: 8, background: '#1e3a5f', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: savingMeter[meter.id] ? 0.6 : 1 }}>
                      {savingMeter[meter.id] ? 'Saving…' : 'Save Meter'}
                    </button>
                  </div>
                )}

                {/* Photos */}
                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>Photos</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {NVE_PHOTO_TYPES.map(pt => {
                      const typePhotos = photos.filter(p => p.photo_type === pt.key);
                      const upKey = `${meter.id}-${pt.key}`;
                      return (
                        <div key={pt.key}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 6, textAlign: 'center' }}>{pt.label}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 6 }}>
                            {typePhotos.map(p => (
                              <AttachThumb key={p.id} size={70} src={photoUrl(p.filename)} name={p.original_name || p.filename}
                                href={photoUrl(p.filename)} onRemove={isAdminZone ? () => deletePhoto(meter.id, p.id) : undefined} />
                            ))}
                          </div>
                          <AttachButton multiple icon="📷" label={uploadingPhoto[upKey] ? 'Uploading…' : '+ Photo / File'}
                            disabled={!!uploadingPhoto[upKey]} style={{ width: '100%', padding: '6px', fontSize: 11 }}
                            onFiles={fs => fs.forEach(f => uploadPhoto(meter.id, pt.key, f))} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              );
            })()}
          </div>
        );
      })}

      <button onClick={addMeter} disabled={addingMeter}
        style={{ width: '100%', padding: '10px', borderRadius: 8, border: '2px dashed #d1d5db', background: 'transparent', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: addingMeter ? 0.6 : 1 }}>
        {addingMeter ? 'Adding…' : '+ Add Meter'}
      </button>

      {/* Map modal */}
      {mapModal && (() => {
        const meter = meters.find(m => m.id === mapModal);
        if (!meter) return null;
        return (
          <LocationPickerModal
            value={meter.address || ''}
            onClose={() => setMapModal(null)}
            onConfirm={({ address, lat, lng }) => {
              setField(meter.id, 'address', address);
              setField(meter.id, 'lat', lat);
              setField(meter.id, 'lng', lng);
              setMapModal(null);
            }}
          />
        );
      })()}

      {/* Photo lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>{lightbox.label} — click anywhere to close</div>
          <img src={lightbox.url} alt="Photo" style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 10, objectFit: 'contain' }} />
        </div>
      )}

      {/* "Attach to an existing task, or create a new one?" (Ryan, Oct 3 2026) */}
      {fwPick && projectId && (
        <NveMeterFwChooser projectId={projectId} meter={fwPick} meters={meters} apiFetch={apiFetch} busy={!!sendingFw[fwPick.id]}
          onCreate={() => { const m = fwPick; setFwPick(null); sendMeterToFieldwire(m); }}
          onAttach={taskId => attachMeterToFieldwire(fwPick, taskId)}
          onClose={() => setFwPick(null)} />
      )}
      {/* A meter's Fieldwire task, in the app's own drawer. */}
      {fwOpen && projectId && (
        <FieldwireTaskDrawer projectId={projectId} taskId={fwOpen} statuses={(fwOv && fwOv.statuses) || []} canEdit={!!isAdminZone}
          onClose={() => setFwOpen(null)} onChanged={() => apiFetch(`/sub-jobs/${sjId}/nve-meters`).then(setMeters).catch(() => {})} />
      )}
    </div>
  );
}

// The little window behind a meter's "📍 Fieldwire task" button (Ryan, Oct 3 2026): put the
// meter on a task the crew already made, or make a new one. The existing list is the job's
// open Fieldwire tasks, searchable, with the ones another meter already sits on greyed out.
function NveMeterFwChooser({ projectId, meter, meters, apiFetch, busy, onCreate, onAttach, onClose }) {
  const [mode, setMode] = React.useState(null);     // null | 'existing'
  const [tasks, setTasks] = React.useState(null);
  const [q, setQ] = React.useState('');
  React.useEffect(() => {
    if (mode !== 'existing' || tasks) return;
    apiFetch(`/projects/${projectId}/fieldwire/tasks`).then(r => setTasks(Array.isArray(r) ? r : [])).catch(() => setTasks([]));
  }, [mode, tasks, projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onOther = {}; meters.forEach(m => { if (m.fw_task_id && m.id !== meter.id) onOther[m.fw_task_id] = m.address || 'another meter'; });
  const needle = q.trim().toLowerCase();
  const shown = (tasks || []).filter(t => !needle || `#${t.sequence_number} ${t.name || ''} ${t.team_name || ''} ${t.sheet_name || ''}`.toLowerCase().includes(needle));
  const isDone = t => /^(completed|verified)$/i.test(t.status_name || '');
  const big = { padding: '14px 16px', borderRadius: 10, border: '1.5px solid #bfdbfe', background: '#eff6ff', color: '#1e3a8a', fontSize: 14, fontWeight: 800, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 6300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f1f1', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>📍 Fieldwire task for {meter.address || 'this meter'}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>One task per meter — put it on one the crew already made, or make a new one.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {mode !== 'existing' ? (<>
            <button onClick={() => setMode('existing')} style={big}>🔗 Attach to an existing task<div style={{ fontSize: 12, fontWeight: 500, color: '#3b82f6', marginTop: 2 }}>Pick from the tasks already on this job in Fieldwire.</div></button>
            <button onClick={onCreate} disabled={busy} style={{ ...big, border: '1.5px solid #1e40af', background: '#1e40af', color: '#fff' }}>{busy ? 'Making it…' : '＋ Create a new task'}<div style={{ fontSize: 12, fontWeight: 500, color: '#bfdbfe', marginTop: 2 }}>Named with the meter's address and the PID, under the NVE category, details in the first comment.</div></button>
          </>) : (<>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => setMode(null)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>‹ Back</button>
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search by number, name, category or sheet…" style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13.5 }} />
            </div>
            {tasks === null ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Reading the job's Fieldwire tasks…</div>
              : shown.length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>{tasks.length ? 'Nothing matches.' : 'No tasks on this job in Fieldwire yet — create a new one.'}</div>
              : shown.map(t => { const taken = onOther[t.id]; return (
                <button key={t.id} disabled={!!taken || busy} onClick={() => onAttach(t.id)} title={taken ? `Already on ${taken}` : 'Put the meter on this task'}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', background: taken ? '#f9fafb' : '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: '8px 10px', cursor: taken ? 'default' : 'pointer', font: 'inherit', opacity: taken ? 0.55 : 1 }}>
                  <span style={{ minWidth: 40, fontSize: 12, fontWeight: 900, color: t.team_color || '#6b7280' }}>#{t.sequence_number}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name || `Pin #${t.sequence_number}`}</span>
                    <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>{[t.team_name, t.sheet_name, t.owner_name, taken ? `already on ${taken}` : null].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: isDone(t) ? '#15803d' : (t.status_color || '#6b7280'), whiteSpace: 'nowrap' }}>{t.status_name || ''}</span>
                </button>); })}
          </>)}
        </div>
      </div>
    </div>
  );
}

const WORKFLOW_COLORS = {
  Conduit:    { color: '#1e3a5f', label: 'Conduit' },
  Wire:       { color: '#064e3b', label: 'Wire' },
  Trenching:  { color: '#7c2d12', label: 'Trenching' },
  Boring:     { color: '#4a1d50', label: 'Boring' },
  Inspection: { color: '#1e4d3a', label: 'Inspection' },
  Concrete:   { color: '#92400e', label: 'Concrete' },
  Grounding:  { color: '#1e3a5f', label: 'Grounding' },
  Demo:         { color: '#7f1d1d', label: 'Demo' },
  contract:     { color: '#1e3a5f', label: 'Contract', icon: '📄' },
  shop:         { color: '#475569', label: 'Shop', icon: '🔧' },
  shop_order:   { color: '#475569', label: 'Shop Orders', icon: '📦' },
  submittals:   { color: '#1e3a5f', label: 'Submittals', icon: '📋' },
  subcontracts: { color: '#4c1d95', label: 'Subcontracts', icon: '🤝' },
  payroll:      { color: '#a16207', label: 'Payroll', icon: '💵' },
  change_orders:{ color: '#0f4c35', label: 'Change Orders', icon: '📝' },
  extra_work:   { color: '#b45309', label: 'Extra Work', icon: '➕' },
  Custom:       { color: '#374151', label: 'Custom' },
};

// ── Submittals Tab ────────────────────────────────────────────────────────────
const SUBMITTAL_STATUSES = [
  { key: 'not_submitted',            label: 'Not Submitted',           color: '#6b7280', bg: '#f3f4f6' },
  // Requested = we asked the vendor for the submittal documents (Ryan, Oct 2 2026).
  { key: 'requested',                label: 'Requested',               color: '#b45309', bg: '#fffbeb' },
  { key: 'submitted',                label: 'Submitted',               color: '#1d4ed8', bg: '#eff6ff' },
  // 'told_low' is a BID status (the GC told us we were low) and never belonged to a
  // submittal — it slipped into this list and showed up in the dropdown (Chad, Sep 2026).
  { key: 'revise_resubmit',          label: 'Revise & Resubmit',       color: '#b91c1c', bg: '#fef2f2' },
  { key: 'approved_with_conditions', label: 'Approved w/ Conditions',  color: '#b45309', bg: '#fffbeb' },
  { key: 'approved',                 label: 'Approved · Holding for Release', color: '#15803d', bg: '#f0fdf4' },
  // Bought as needed — never released or tracked (Pat, Oct 7 2026).
  { key: 'approved_no_action',       label: 'Approved · No Further Action', color: '#4d7c0f', bg: '#f7fee7' },
];

// Shop deliveries for one submittal item, shown in the Procurement tab: the
// logged receipts (with packing-slip/package photos) + a Log Delivery button so
// a PM can receive/override right where they work.
function SubmittalReceiving({ item, projectName }) {
  const [receipts, setReceipts] = React.useState([]);
  const [showLog, setShowLog] = React.useState(false);
  const [lightbox, setLightbox] = React.useState(null);
  const purl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
  const load = React.useCallback(() => { apiFetch(`/submittal-items/${item.id}/receipts`).then(r => setReceipts(Array.isArray(r) ? r : [])).catch(() => {}); }, [item.id, item.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const split = (item.parts || []).length > 0;
  const letterOf = id => { const pt = (item.parts || []).find(x => x.id === id); return pt ? pt.letter : null; };
  React.useEffect(() => { load(); }, [load]);
  return (
    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Shop Deliveries ({receipts.length})</div>
        {split ? <span style={{ fontSize: 11.5, color: '#6b7280' }}>Receive each part above</span>
          : <button onClick={() => setShowLog(true)} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: '#065f46', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📦 Receive Delivery</button>}
      </div>
      {receipts.length === 0 ? <div style={{ fontSize: 12, color: '#9ca3af' }}>No deliveries logged yet.</div> : receipts.map(r => (
        <div key={r.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{letterOf(r.submittal_part_id) && <b style={{ color: '#6d28d9' }}>Part {letterOf(r.submittal_part_id)} · </b>}{r.fully_received ? 'Full delivery' : 'Partial'}{r.carrier ? ` · ${r.carrier}` : ''}{r.received_by_name ? ` · ${r.received_by_name}` : ''}{r.received_at ? ` · ${new Date(r.received_at).toLocaleDateString()}` : ''}</div>
          {r.note && <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{r.note}</div>}
          {r.photos && r.photos.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {r.photos.map(ph => (
                <div key={ph.id} style={{ position: 'relative' }}>
                  <img src={purl(ph.filename)} alt={ph.kind} onClick={() => setLightbox(purl(ph.filename))} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer' }} />
                  <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 8, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.6)', padding: '1px 4px', borderRadius: 5 }}>{ph.kind === 'packing_slip' ? 'Slip' : ph.kind === 'package' ? 'Pkg' : 'Photo'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {showLog && <LogDeliveryModal item={{ id: item.id, category: item.category, spec_number: item.spec_number, project_name: projectName }} onClose={() => setShowLog(false)} onDone={() => { setShowLog(false); load(); }} />}
      {lightbox && <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}><img src={lightbox} alt="" style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 10 }} /></div>}
    </div>
  );
}

export function SubmittalsTab({ sjId, projectId, project, bidItems, isAdminZone, addressee, addresseeOptions = [], API_BASE, getToken, showToast, openItemId }) {
  const { all: SUBMITTAL_CATEGORY_OPTIONS } = useCategories();
  const [statusFilter, setStatusFilter] = React.useState(null); // top-bar filter by status
  const [subQuery, setSubQuery] = React.useState('');            // search box on the board (Pat, Oct 7 2026)
  const [items, setItems]         = React.useState([]);
  const [docs, setDocs]           = React.useState({});       // itemId -> []
  const [rounds, setRounds]       = React.useState({});       // itemId -> []
  const [shipLog, setShipLog]     = React.useState({});       // itemId -> []
  const [expanded, setExpanded]   = React.useState({});
  // Cards (Ryan, Oct 2 2026): the submittals are a board of cards moving left to right;
  // one card open at a time in a window. The window's body is the same Details / Round
  // History / Procurement this screen always had.
  const [openCard, setOpenCard]   = React.useState(null);     // item id
  const [layout, setLayout]       = useSubmittalLayout();     // 'board' | 'list'
  const [remindFor, setRemindFor] = React.useState(null);     // { item, party: 'reviewer' | 'vendor', intent: 'status' | 'request' }
  const [releaseFor, setReleaseFor] = React.useState(null);   // item
  const [receivePart, setReceivePart] = React.useState(null); // { item, part } — logging a delivery against one release part
  const [previewFor, setPreviewFor] = React.useState(null);   // item
  // The package draft with highlighter and pen (Ryan, Nov 2 2026): { item, strokes } while open.
  const [markupFor, setMarkupFor] = React.useState(null);
  async function openMarkup(item) {
    try {
      const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/submittals/${item.id}/markup`, { headers: { 'x-worker-token': getToken() } }).then(x => x.json());
      setMarkupFor({ item, strokes: (r && Array.isArray(r.strokes)) ? r.strokes : [] });
    } catch { setMarkupFor({ item, strokes: [] }); }
  }
  async function saveMarkupFor(item, strokes) {
    const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/submittals/${item.id}/markup`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() }, body: JSON.stringify({ strokes }) }).then(x => x.json());
    if (r && r.error) throw new Error(r.error);
  }
  const [actBump, setActBump]     = React.useState(0);
  // Sent straight to one submittal — open that card.
  React.useEffect(() => {
    if (!openItemId) return;
    if (items.some(i => i.id === openItemId)) setOpenCard(openItemId);
  }, [openItemId, items.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = React.useState({});       // itemId -> 'details'|'history'|'procurement'
  const [drafts, setDrafts]       = React.useState({});
  const [saving, setSaving]       = React.useState({});
  const [loading, setLoading]     = React.useState(true);
  const [adding, setAdding]       = React.useState(false);
  const [lightbox, setLightbox]   = React.useState(null);
  const [showRoundForm, setShowRoundForm] = React.useState({});
  const [roundDraft, setRoundDraft]       = React.useState({});
  const [partsPicker, setPartsPicker]     = React.useState(null); // itemId when open
  const [allParts, setAllParts]           = React.useState([]);
  const [partsSearch, setPartsSearch]     = React.useState('');
  const [showBidPanel, setShowBidPanel]   = React.useState(false);  // "Create from Bid Items" panel open
  const [bidSel, setBidSel]               = React.useState({});     // bid-item index -> selected
  const [assignCat, setAssignCat]         = React.useState('');     // category to drop selected bid items into
  const [bulkAdding, setBulkAdding]       = React.useState(false);
  const [dragActive, setDragActive]       = React.useState(false);  // a bid item is currently being dragged
  const [dropCardId, setDropCardId]       = React.useState(null);   // submittal card currently hovered while dragging
  const [fileDragId, setFileDragId]       = React.useState(null);   // submittal card currently hovered while dragging a PDF file in from the OS
  // Emailing a package: which item's composer is open and what's in it; `mailStatus`
  // says whether the server can send and as whom.
  const [pkgEmail, setPkgEmail]           = React.useState(null);   // { itemId, to, cc, subject, message, busy, err }
  const [mailStatus, setMailStatus]       = React.useState(null);
  React.useEffect(() => { apiFetch('/mail/status').then(setMailStatus).catch(() => setMailStatus({ configured: false })); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, {
      ...opts,
      // A JSON body must say so, or the server reads an empty body and changes nothing while answering
      // OK (Ryan, Nov 2 2026: unticking a document for the package looked saved but every file still
      // printed). FormData bodies are not strings and keep their own type.
      headers: { 'x-worker-token': getToken(), ...(typeof opts.body === 'string' ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) },
    }).then(async r => {
      if (r.ok) return r.json();
      const text = await r.text();
      let msg; try { msg = JSON.parse(text)?.error || text; } catch { msg = `Server error ${r.status}`; }
      return Promise.reject(new Error(msg));
    });
  }

  React.useEffect(() => {
    apiFetch(`/sub-jobs/${sjId}/submittals`).then(async its => {
      setItems(its);
      // Seed the revision picker from what's saved, so "Rev 2" shows on return.
      setSubmittalRev(its.reduce((m, it) => { if (it.revision) m[it.id] = String(it.revision); return m; }, {}));
      setLoading(false);
      const results = await Promise.all(its.map(it => Promise.all([
        apiFetch(`/sub-jobs/${sjId}/submittals/${it.id}/documents`).catch(() => []),
        apiFetch(`/sub-jobs/${sjId}/submittals/${it.id}/rounds`).catch(() => []),
        apiFetch(`/sub-jobs/${sjId}/submittals/${it.id}/ship-log`).catch(() => []),
      ]).then(([d, r, s]) => ({ id: it.id, d, r, s }))));
      const d = {}, r = {}, s = {};
      results.forEach(x => { d[x.id] = x.d; r[x.id] = x.r; s[x.id] = x.s; });
      setDocs(d); setRounds(r); setShipLog(s);
    }).catch(e => { setLoading(false); if (showToast) showToast(e.message, 'error'); });
  }, [sjId]);

  const fv = (item, field) => drafts[item.id]?.[field] !== undefined ? drafts[item.id][field] : (item[field] ?? '');
  const setField = (id, field, val) => setDrafts(d => ({ ...d, [id]: { ...(d[id]||{}), [field]: val } }));
  const hasDraft = id => drafts[id] && Object.keys(drafts[id]).length > 0;
  const fileUrl  = fn => `${API_BASE.replace('/api','')}/uploads/${fn}`;
  const statusMeta = key => SUBMITTAL_STATUSES.find(s => s.key === key) || SUBMITTAL_STATUSES[0];
  const tab = id => activeTab[id] || 'details';

  async function addItem() {
    setAdding(true);
    try {
      // Auto-address a brand-new item to the right party: the awarding prime
      // when we're a sub, the owner when we're the prime. Stays editable.
      const seed = { category: 'New Item' };
      if (addressee?.to) { seed.submitted_to = addressee.to; seed.submitted_to_email = addressee.email || ''; }
      const it = await apiFetch(`/sub-jobs/${sjId}/submittals`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(seed) });
      setItems(prev => [...prev, it]);
      setDocs(d => ({ ...d, [it.id]: [] }));
      setRounds(r => ({ ...r, [it.id]: [] }));
      setShipLog(s => ({ ...s, [it.id]: [] }));
      setExpanded(e => ({ ...e, [it.id]: true }));
      setOpenCard(it.id);
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setAdding(false);
  }

  // ── Bid line items grouped under a category package ──
  const parseLineItems = item => {
    try { const a = typeof item.line_items === 'string' ? JSON.parse(item.line_items) : item.line_items; return Array.isArray(a) ? a : []; }
    catch { return []; }
  };
  const liKey = b => `${b?.item_number ?? ''}|${(b?.description || '').trim().toLowerCase()}`;
  const normBidItem = b => ({ description: (b.description || '').trim(), item_number: b.item_number ?? null, pay_item_number: b.pay_item_number ?? null, quantity: b.quantity ?? null, unit: b.unit || null });
  function mergeLineItems(existing, incoming) {
    const seen = new Set(existing.map(liKey));
    const merged = [...existing];
    incoming.forEach(b => { const k = liKey(b); if (!seen.has(k)) { seen.add(k); merged.push(normBidItem(b)); } });
    return merged;
  }

  // Save a submittal's line-item list to the server and update local state.
  async function patchLineItems(item, nextList) {
    const updated = await apiFetch(`/sub-jobs/${sjId}/submittals/${item.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line_items: nextList }),
    });
    setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    return updated;
  }

  // Drop / add bid items onto one existing category card.
  async function addBidItemsToSubmittal(item, bidArr) {
    const clean = (bidArr || []).filter(b => (b?.description || '').trim());
    if (!clean.length) return;
    try {
      const next = mergeLineItems(parseLineItems(item), clean);
      await patchLineItems(item, next);
      if (showToast) showToast(`Added ${clean.length} to ${item.category}`, 'success');
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Add bid items to a category by NAME — reuse an existing card with that
  // category or create a new package. Used by the "Add selected to…" control.
  async function addBidItemsToCategory(bidArr, categoryName) {
    const clean = (bidArr || []).filter(b => (b?.description || '').trim());
    const cat = (categoryName || '').trim();
    if (!clean.length || !cat) return;
    setBulkAdding(true);
    try {
      const existing = items.find(i => (i.category || '').trim().toLowerCase() === cat.toLowerCase());
      if (existing) {
        await addBidItemsToSubmittal(existing, clean);
      } else {
        const it = await apiFetch(`/sub-jobs/${sjId}/submittals`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category: cat, category_source: 'bid_item', line_items: clean.map(normBidItem) }),
        });
        setItems(prev => [...prev, it]);
        setDocs(d => ({ ...d, [it.id]: [] })); setRounds(r => ({ ...r, [it.id]: [] })); setShipLog(s => ({ ...s, [it.id]: [] }));
        setExpanded(e => ({ ...e, [it.id]: true }));
        if (showToast) showToast(`Created “${cat}” with ${clean.length} item${clean.length !== 1 ? 's' : ''}`, 'success');
      }
      setBidSel({});
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setBulkAdding(false);
  }

  async function removeLineItem(item, key) {
    try { await patchLineItems(item, parseLineItems(item).filter(b => liKey(b) !== key)); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Create an empty category package (no bid items yet) to drag items into.
  async function createEmptyCategory(cat) {
    if (items.some(i => (i.category || '').trim().toLowerCase() === cat.trim().toLowerCase())) {
      if (showToast) showToast(`“${cat}” already exists`, 'info');
      return;
    }
    try {
      const it = await apiFetch(`/sub-jobs/${sjId}/submittals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: cat, category_source: 'bid_item' }),
      });
      setItems(prev => [...prev, it]);
      setDocs(d => ({ ...d, [it.id]: [] })); setRounds(r => ({ ...r, [it.id]: [] })); setShipLog(s => ({ ...s, [it.id]: [] }));
      setExpanded(e => ({ ...e, [it.id]: true }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  async function saveItem(item, override) {
    // `override` lets a one-tap action (e.g. "Address to prime") save specific
    // fields immediately without waiting for the async draft state to flush.
    const payload = override ? { ...(drafts[item.id] || {}), ...override } : drafts[item.id];
    if (!override && !hasDraft(item.id)) return;
    setSaving(s => ({ ...s, [item.id]: true }));
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/submittals/${item.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      setItems(prev => prev.map(i => i.id === item.id ? updated : i));
      setDrafts(d => { const n = { ...d }; delete n[item.id]; return n; });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setSaving(s => ({ ...s, [item.id]: false }));
  }

  async function deleteItem(itemId) {
    if (!window.confirm('Remove this submittal item?')) return;
    try {
      await apiFetch(`/sub-jobs/${sjId}/submittals/${itemId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  async function uploadDoc(itemId, file) {
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', 'submittal');
    const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/submittals/${itemId}/documents`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
    if (!r.ok) { if (showToast) showToast('Upload failed', 'error'); return; }
    const doc = await r.json();
    setDocs(d => ({ ...d, [itemId]: [...(d[itemId]||[]), doc] }));
  }

  // In the package or not (Ryan, Oct 21 2026). Saved on the document, so the download and the email agree.
  async function toggleDocInPackage(itemId, doc, on) {
    setDocs(d => ({ ...d, [itemId]: (d[itemId] || []).map(x => (x.id === doc.id ? { ...x, include_in_package: on ? 1 : 0 } : x)) }));
    try { await apiFetch(`/sub-jobs/${sjId}/submittals/${itemId}/documents/${doc.id}`, { method: 'PATCH', body: JSON.stringify({ include_in_package: on }) }); }
    catch (e) {
      setDocs(d => ({ ...d, [itemId]: (d[itemId] || []).map(x => (x.id === doc.id ? { ...x, include_in_package: on ? 0 : 1 } : x)) }));
      if (showToast) showToast(e?.error || e?.message || 'Could not save that.', 'error');
    }
  }
  async function deleteDoc(itemId, docId) {
    try {
      await apiFetch(`/sub-jobs/${sjId}/submittals/${itemId}/documents/${docId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setDocs(d => ({ ...d, [itemId]: (d[itemId]||[]).filter(x => x.id !== docId) }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  async function openPartsPicker(itemId) {
    setPartsSearch('');
    setPartsPicker(itemId);
    if (allParts.length === 0) {
      try {
        const parts = await apiFetch('/parts');
        setAllParts(parts);
      } catch {}
    }
  }

  async function pullCutSheet(itemId, part) {
    try {
      const doc = await apiFetch(`/sub-jobs/${sjId}/submittals/${itemId}/documents/from-parts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parts_id: part.id }),
      });
      setDocs(d => ({ ...d, [itemId]: [...(d[itemId]||[]), doc] }));
      setPartsPicker(null);
      if (showToast) showToast(`Added: ${part.description}`, 'success');
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Save the revision number on the submittal itself. Optimistic — the picker moves
  // at once — and written straight to the item so it's there on the next visit and
  // wherever the item is drawn.
  async function saveRevision(itemId, value) {
    setSubmittalRev(r => ({ ...r, [itemId]: value }));
    const revision = value ? Number(value) : null;
    setItems(list => list.map(it => it.id === itemId ? { ...it, revision } : it));
    try {
      await apiFetch(`/sub-jobs/${sjId}/submittals/${itemId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revision }),
      });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  async function submitRound(itemId) {
    const rd = roundDraft[itemId] || {};
    const fd = new FormData();
    Object.entries(rd).forEach(([k, v]) => { if (k !== 'markupFile' && v != null) fd.append(k, v); });
    if (rd.markupFile) fd.append('markup', rd.markupFile);
    try {
      const round = await fetch(`${API_BASE}/sub-jobs/${sjId}/submittals/${itemId}/rounds`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd }).then(r => r.json());
      setRounds(prev => ({ ...prev, [itemId]: [...(prev[itemId]||[]), round] }));
      // Refresh item status
      const updated = await apiFetch(`/sub-jobs/${sjId}/submittals`);
      setItems(updated);
      setShowRoundForm(f => ({ ...f, [itemId]: false }));
      setRoundDraft(d => { const n = { ...d }; delete n[itemId]; return n; });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Download the generated submittal package. Fetches with the auth header (a
  // plain <a> link can't send headers, which is why the old version 401'd) and
  // saves the returned PDF as a file — keeping the token out of the URL.
  const [generatingPdf, setGeneratingPdf] = useState(null);
  // "rev2 when it matters" — set deliberately before printing rather than counted
  // automatically, so a revision means a document that actually supersedes an
  // earlier one, not just that somebody hit print twice.
  const [submittalRev, setSubmittalRev] = useState({});   // itemId -> 2,3…
  async function downloadSubmittalPackage(item) {
    setGeneratingPdf(item.id);
    try {
      // Flush any unsaved edits first (e.g. a just-typed "Submitted To" contact).
      // The server renders the PDF from the SAVED record, so without this the
      // cover sheet would show the old/empty value.
      if (hasDraft(item.id)) await saveItem(item);
      const res = await fetch(`${API_BASE}/sub-jobs/${sjId}/submittals/${item.id}/generate-pdf`, {
        headers: { 'x-worker-token': getToken() },
      });
      if (!res.ok) {
        let msg = 'Failed to generate PDF';
        try { const j = await res.json(); msg = j.error || msg; } catch {}
        throw new Error(msg);
      }
      // The server flags anything it couldn't merge — a missing file, or a PDF it
      // can't read (vendors send password-protected ones). Without this the package
      // downloads looking fine and the cover sheet is the only clue.
      const skippedRaw = res.headers.get('X-Submittal-Skipped');
      if (skippedRaw && showToast) {
        try {
          const list = JSON.parse(decodeURIComponent(skippedRaw));
          showToast(`${list.length} attachment${list.length === 1 ? '' : 's'} could not be included: ${list.map(s => s.name).join(', ')}. See the note on the cover sheet.`, 'error');
        } catch { showToast('Some attachments could not be included — see the cover sheet.', 'error'); }
      }
      const blob = await res.blob();
      // Build the filename from the job number + category (the server's own
      // Content-Disposition filename isn't readable cross-origin). Matches the
      // "[job#] - [category] Submittal.pdf" convention.
      const filename = jobDocFileName({ job: project, type: 'Submittal', number: item.titan_number, description: item.category, rev: submittalRev[item.id], ext: 'pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (showToast) showToast(e.message || 'Failed to generate PDF', 'error');
    } finally {
      setGeneratingPdf(null);
    }
  }

  // Email the package straight to the reviewer. The server builds the same PDF the
  // download does, attaches it, sends it from the sender's own mailbox, and logs the
  // submission round — so the history tab shows it went out without anyone typing it.
  function openPackageEmail(item) {
    const to = item.submitted_to_email || project?.awarded_by_email || '';
    const who = item.submitted_to || project?.awarded_by_name || '';
    const first = who ? String(who).trim().split(/\s+/)[0] : '';
    const nn = item.titan_number != null ? String(item.titan_number).padStart(2, '0') + ' ' : '';
    setPkgEmail({
      itemId: item.id, to, cc: '', sentToName: who,
      // Job names often already start with the number ("2624 - NDOT 4548"), so only
      // prepend it when they don't.
      subject: `Submittal ${nn}— ${item.category || ''} · ${String(project?.name || '').startsWith(String(project?.project_number || ' ')) ? '' : (project?.project_number || '') + ' '}${project?.name || ''}`.replace(/\s+/g, ' ').trim(),
      message: `Hello${first ? ' ' + first : ''},\n\nPlease find our submittal package for ${item.category || 'this item'} on ${project?.project_number || 'the job'} attached for your review. Let us know if you have any questions.\n\nThank you,`,
      busy: false, err: '',
    });
  }
  async function sendPackageEmail(item) {
    if (!pkgEmail || pkgEmail.busy) return;
    setPkgEmail(p => ({ ...p, busy: true, err: '' }));
    try {
      if (hasDraft(item.id)) await saveItem(item);
      const r = await apiFetch(`/sub-jobs/${sjId}/submittals/${item.id}/email`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: pkgEmail.to, cc: pkgEmail.cc, subject: pkgEmail.subject, message: pkgEmail.message, sent_to_name: pkgEmail.sentToName }) });
      if (r.skipped && r.skipped.length && showToast) showToast(`Sent, but ${r.skipped.length} attachment${r.skipped.length === 1 ? '' : 's'} could not be included: ${r.skipped.map(s => s.name).join(', ')}.`, 'error');
      else if (r.warning && showToast) showToast(`Package emailed to ${pkgEmail.to}. ${r.warning}`, 'error');
      else if (showToast) showToast(`Package emailed to ${pkgEmail.to}${r.from ? ' from ' + r.from : ''}`);
      offerToSaveRecipients({ to: pkgEmail.to, cc: pkgEmail.cc, clientName: project?.awarded_by_company || project?.client_name || null });   // in the Customer List? (Oct 6 2026)
      const updated = await apiFetch(`/sub-jobs/${sjId}/submittals`);
      if (Array.isArray(updated)) setItems(updated);
      apiFetch(`/sub-jobs/${sjId}/submittals/${item.id}/rounds`).then(rs => setRounds(x => ({ ...x, [item.id]: Array.isArray(rs) ? rs : [] }))).catch(() => {});
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId } }));
      setPkgEmail(null);
    } catch (e) {
      setPkgEmail(p => p ? { ...p, busy: false, err: e.message || 'The email didn’t go.' } : p);
    }
  }

  // Category options: the same list as the Vendor Quotes "Item" dropdown, plus
  // this bid's actual line-item descriptions as extra quick-picks. Deduped.
  const categoryOptions = Array.from(new Set([
    ...SUBMITTAL_CATEGORY_OPTIONS,
    ...(bidItems || []).map(b => b.description || b.name || '').filter(Boolean),
  ])).map(label => ({ label }));

  // ── Card pillows ──
  // The date pillow: Approved → release it; otherwise open the card where that date lives.
  function cardDate(it) {
    const st = submittalStageOf(it);
    // Split into release parts: each part releases on its own, on the Procurement tab.
    if ((it.parts || []).length) { setActiveTab(t => ({ ...t, [it.id]: 'procurement' })); setOpenCard(it.id); return; }
    if (st === 'approved') { setReleaseFor(it); return; }
    setActiveTab(t => ({ ...t, [it.id]: st === 'to_submit' ? 'details' : (st === 'released' || st === 'received' || st === 'requested') ? 'procurement' : 'history' }));
    setOpenCard(it.id);
  }
  // The second pillow: a reminder to whoever it was emailed to, or a status update (the
  // round form, already set to what comes next).
  function cardAction(it, kind) {
    if (kind === 'remind') { setRemindFor({ item: it, party: 'reviewer', intent: 'status' }); return; }
    if (kind === 'vendor') { setRemindFor({ item: it, party: 'vendor', intent: 'status' }); return; }
    if (kind === 'request') { setRemindFor({ item: it, party: 'vendor', intent: 'request' }); return; }
    const st = submittalStageOf(it);
    setActiveTab(t => ({ ...t, [it.id]: 'history' }));
    setRoundDraft(d => ({ ...d, [it.id]: { action: st === 'review' ? 'response' : 'submit' } }));
    setShowRoundForm(fm => ({ ...fm, [it.id]: true }));
    setOpenCard(it.id);
  }
  // A reminder went out or a release was saved: take the server's copy of the card.
  function cardRefreshed(updated) {
    if (updated && updated.id) setItems(prev => prev.map(i => (i.id === updated.id ? updated : i)));
    setActBump(n => n + 1);
    window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId } }));
  }

  if (loading) return <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading submittals…</div>;

  const BID_DND = 'application/x-bid-item';
  const onBidDragStart = (e, b) => { e.dataTransfer.setData(BID_DND, JSON.stringify(b)); e.dataTransfer.setData('text/plain', b.description || ''); e.dataTransfer.effectAllowed = 'copy'; setDragActive(true); };
  const onBidDragEnd = () => { setDragActive(false); setDropCardId(null); };
  const readBidDrop = e => { try { const raw = e.dataTransfer.getData(BID_DND); return raw ? JSON.parse(raw) : null; } catch { return null; } };
  const isBidDrag = e => Array.from(e.dataTransfer.types || []).includes(BID_DND);
  // An OS file drag (a PDF pulled in from the desktop/Finder) shows up as a 'Files' type.
  const isFileDrag = e => Array.from(e.dataTransfer.types || []).includes('Files');
  const bidList = (bidItems || []).filter(b => (b?.description || '').trim());
  const selectedBidItems = bidList.filter((_, i) => bidSel[i]);
  const usedKeys = new Set(items.flatMap(parseLineItems).map(liKey)); // bid items already placed in some category

  // Status roll-up for the summary bar.
  const subCounts = SUBMITTAL_STATUSES.reduce((a, s) => ({ ...a, [s.key]: 0 }), {});
  items.forEach(i => { const k = i.status || 'not_submitted'; subCounts[k] = (subCounts[k] || 0) + 1; });
  const approvedTotal = (subCounts.approved || 0) + (subCounts.approved_with_conditions || 0) + (subCounts.approved_no_action || 0);
  const visibleItems = (statusFilter ? items.filter(i => (i.status || 'not_submitted') === statusFilter) : items).filter(i => submittalMatches(i, subQuery));

  return (
    <div>
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={lightbox} alt="" style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 18, right: 22, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Parts Directory Picker */}
      {partsPicker && (
        <div onClick={() => setPartsPicker(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 580, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ background: '#1e3a5f', borderRadius: '12px 12px 0 0', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, flex: 1 }}>📦 Pull Cut Sheet from Parts Directory</span>
              <button onClick={() => setPartsPicker(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <input autoFocus value={partsSearch} onChange={e => setPartsSearch(e.target.value)}
                placeholder="Search parts…"
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {allParts
                .filter(p => p.cut_sheet && (!partsSearch.trim() || p.description.toLowerCase().includes(partsSearch.toLowerCase()) || (p.category||'').toLowerCase().includes(partsSearch.toLowerCase())))
                .map(p => (
                  <div key={p.id} onClick={() => pullCutSheet(partsPicker, p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <span style={{ fontSize: 20 }}>📄</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{p.description}</div>
                      {p.category && <div style={{ fontSize: 11, color: '#6b7280' }}>{p.category}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>+ Add</span>
                  </div>
                ))}
              {allParts.filter(p => p.cut_sheet).length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  No parts have cut sheets yet. Upload PDFs in the Parts Directory.
                </div>
              )}
              {allParts.filter(p => p.cut_sheet && (!partsSearch.trim() || p.description.toLowerCase().includes(partsSearch.toLowerCase()))).length === 0 && allParts.filter(p => p.cut_sheet).length > 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No matches found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Who submittals are addressed to — the awarding prime when we're a sub,
          the owner when we're the prime. New items auto-fill this; existing items
          get a one-tap "Address to …" button. */}
      {addressee?.to && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f5f7ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12.5, color: '#374151' }}>
          <span>📮</span>
          <span>Addressing submittals to the <strong>{addressee.role === 'prime' ? 'prime contractor' : 'owner'}</strong>: <strong>{addressee.display}</strong>{addressee.email ? ` · ${addressee.email}` : ''}</span>
        </div>
      )}

      {/* Group this bid's line items into category packages — drag a row onto a
          category card below, or check items and add them to a category here. */}
      {isAdminZone && bidList.length > 0 && (
        <div style={{ border: '1px solid #c7d2fe', borderRadius: 10, marginBottom: 14, overflow: 'hidden', background: '#f5f7ff' }}>
          <div onClick={() => setShowBidPanel(v => !v)}
            style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: '#eef2ff', borderBottom: showBidPanel ? '1px solid #c7d2fe' : 'none' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#3730a3', flex: 1 }}>🧱 Bid Items <span style={{ fontWeight: 500, color: '#6366f1' }}>({usedKeys.size}/{bidList.length} grouped)</span></span>
            <span style={{ fontSize: 11, color: '#6366f1' }}>{showBidPanel ? '▲' : '▼'}</span>
          </div>
          {showBidPanel && (
            <div style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4338ca', cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox"
                    checked={selectedBidItems.length === bidList.length && bidList.length > 0}
                    ref={el => { if (el) el.indeterminate = selectedBidItems.length > 0 && selectedBidItems.length < bidList.length; }}
                    onChange={e => { const all = {}; if (e.target.checked) bidList.forEach((_, i) => all[i] = true); setBidSel(all); }}
                    style={{ width: 15, height: 15 }} />
                  Select all
                </label>
                {selectedBidItems.length > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 240 }}>
                    <span style={{ fontSize: 12, color: '#4338ca', fontWeight: 600 }}>Add {selectedBidItems.length} to</span>
                    <input list="submittal-cat-list" value={assignCat} onChange={e => setAssignCat(e.target.value)}
                      placeholder="category…"
                      style={{ flex: 1, minWidth: 120, border: '1px solid #c7d2fe', borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
                    <datalist id="submittal-cat-list">
                      {categoryOptions.map((o, i) => <option key={i} value={o.label} />)}
                    </datalist>
                    <button onClick={() => { if (assignCat.trim()) { addBidItemsToCategory(selectedBidItems, assignCat); setAssignCat(''); } }}
                      disabled={bulkAdding || !assignCat.trim()}
                      style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: assignCat.trim() ? '#4f46e5' : '#c7d2fe', color: '#fff', fontSize: 12, fontWeight: 700, cursor: assignCat.trim() ? 'pointer' : 'default' }}>
                      {bulkAdding ? 'Adding…' : 'Add'}
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: '#6b7280' }}>Drag a row onto a category card below, or check items and add them to a category.</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                {bidList.map((b, i) => {
                  const used = usedKeys.has(liKey(b));
                  return (
                    <div key={i} draggable onDragStart={e => onBidDragStart(e, b)} onDragEnd={onBidDragEnd}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: bidSel[i] ? '#e0e7ff' : (used ? '#f0fdf4' : '#fff'), border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'grab', opacity: used && !bidSel[i] ? 0.7 : 1 }}>
                      <input type="checkbox" checked={!!bidSel[i]} onChange={e => setBidSel(s => ({ ...s, [i]: e.target.checked }))} style={{ width: 15, height: 15 }} />
                      <span title="Drag to a category card" style={{ color: '#a5b4fc', fontSize: 13, cursor: 'grab' }}>⠿</span>
                      {b.item_number != null && String(b.item_number) !== '' && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', fontFamily: 'monospace', background: '#eef2ff', borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>#{b.item_number}</span>
                      )}
                      <span style={{ flex: 1, fontSize: 13, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.description}</span>
                      {used && <span title="Already in a category" style={{ fontSize: 11, color: '#16a34a', flexShrink: 0 }}>✓</span>}
                      {b.quantity != null && <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{b.quantity}{b.unit ? ` ${b.unit}` : ''}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick-add empty category packages to drag items into */}
      {isAdminZone && bidList.length > 0 && items.length === 0 && (
        <div style={{ marginBottom: 14, padding: 12, border: '1px dashed #c7d2fe', borderRadius: 10, background: '#fbfcff' }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Start a category package, then drag bid items into it:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SUBMITTAL_CATEGORY_OPTIONS.map(c => (
              <button key={c} onClick={() => createEmptyCategory(c)}
                style={{ padding: '4px 10px', borderRadius: 14, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                + {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status summary bar — progress + tappable status filters */}
      {items.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{approvedTotal} of {items.length} approved</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{Math.round((approvedTotal / items.length) * 100)}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 6, background: '#f3f4f6', overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ height: '100%', width: `${Math.round((approvedTotal / items.length) * 100)}%`, background: '#15803d', borderRadius: 6 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => setStatusFilter(null)}
              style={{ padding: '4px 11px', borderRadius: 20, border: statusFilter === null ? '1.5px solid #111827' : '1px solid #e5e7eb', background: statusFilter === null ? '#111827' : '#fff', color: statusFilter === null ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              All {items.length}
            </button>
            {SUBMITTAL_STATUSES.filter(s => subCounts[s.key] > 0).map(s => {
              const on = statusFilter === s.key;
              return (
                <button key={s.key} onClick={() => setStatusFilter(on ? null : s.key)}
                  style={{ padding: '4px 11px', borderRadius: 20, border: `1.5px solid ${on ? s.color : s.bg}`, background: on ? s.color : s.bg, color: on ? '#fff' : s.color, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {s.label} {subCounts[s.key]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* The board: one column per stage, cards moving left to right. */}
      {items.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Cards move left to right on their own as rounds are logged and dates go in. Click a card to open it.</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <input value={subQuery} onChange={e => setSubQuery(e.target.value)} placeholder="🔍 Search submittals…"
                style={{ padding: '6px 26px 6px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 999, width: 210, boxSizing: 'border-box' }} />
              {subQuery && <button onClick={() => setSubQuery('')} title="Clear search" style={{ position: 'absolute', right: 6, width: 18, height: 18, borderRadius: 999, border: 'none', background: '#e5e7eb', color: '#6b7280', fontSize: 12, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>}
            </span>
            <LayoutSwitch layout={layout} onPick={setLayout} />
          </span>
        </div>
      )}
      {subQuery.trim() && visibleItems.length === 0 && <div style={{ fontSize: 13, color: '#6b7280', padding: '6px 2px 10px' }}>No submittals match “{subQuery.trim()}”.</div>}
      <SubmittalBoard items={visibleItems} layout={layout}
        onOpen={it => setOpenCard(it.id)}
        onDate={cardDate} onAction={cardAction} onPreview={setPreviewFor}
        highlightId={dropCardId || fileDragId}
        cardProps={isAdminZone ? (it => ({
          onDragOver: e => {
            if (isBidDrag(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (dropCardId !== it.id) setDropCardId(it.id); }
            else if (isFileDrag(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (fileDragId !== it.id) setFileDragId(it.id); }
          },
          onDragLeave: e => { if (!e.currentTarget.contains(e.relatedTarget)) { if (dropCardId === it.id) setDropCardId(null); if (fileDragId === it.id) setFileDragId(null); } },
          onDrop: e => {
            const b = readBidDrop(e); setDropCardId(null); setFileDragId(null); setDragActive(false);
            if (b) { e.preventDefault(); addBidItemsToSubmittal(it, [b]); return; }
            const files = Array.from(e.dataTransfer.files || []).filter(fl => fl.type === 'application/pdf' || /\.pdf$/i.test(fl.name));
            if (files.length) { e.preventDefault(); files.forEach(fl => uploadDoc(it.id, fl)); }
          },
        })) : undefined} />
      {remindFor && <SubmittalRemindWindow sjId={sjId} item={remindFor.item} party={remindFor.party} intent={remindFor.intent} project={project} showToast={showToast} onClose={() => setRemindFor(null)} onSent={cardRefreshed} />}
      {releaseFor && <SubmittalReleaseWindow sjId={sjId} item={releaseFor} showToast={showToast} onClose={() => setReleaseFor(null)} onReleased={cardRefreshed} />}
      {receivePart && <LogDeliveryModal item={{ id: receivePart.item.id, part_id: receivePart.part.id, part_items: receivePart.part.line_items || [], category: `${receivePart.item.category || 'Material'} — Part ${receivePart.part.letter}${receivePart.part.description ? ': ' + receivePart.part.description : ''}`, spec_number: receivePart.item.spec_number, project_name: project?.name }}
        z={7000} onClose={() => setReceivePart(null)}
        onDone={() => { const id = receivePart.item.id; setReceivePart(null); apiFetch(`/sub-jobs/${sjId}/submittals`).then(its => cardRefreshed((its || []).find(x => x.id === id))).catch(() => {}); }} />}
      {markupFor && (
        <PdfAnnotator
          url={`${API_BASE}/sub-jobs/${sjId}/submittals/${markupFor.item.id}/generate-pdf?preview=1`}
          token={getToken()}
          title={`Submittal #${String(markupFor.item.titan_number ?? '').padStart(2, '0')} — ${markupFor.item.category || ''} (draft)`}
          hint="This is the package as it will print. Highlight or pen on any page, Save, then Print."
          initialAnnotations={markupFor.strokes}
          onSave={({ strokes }) => saveMarkupFor(markupFor.item, strokes)}
          onPrint={() => downloadSubmittalPackage(markupFor.item)}
          printLabel="🖨 Print the package"
          onClose={() => setMarkupFor(null)}
        />
      )}
      {previewFor && <PdfWindow fetchPath={`/sub-jobs/${sjId}/submittals/${previewFor.id}/generate-pdf?preview=1`} title={`Submittal #${String(previewFor.titan_number).padStart(2, '0')} — ${previewFor.category || ''}`}
        fileName={jobDocFileName({ job: project, type: 'Submittal', number: previewFor.titan_number, description: previewFor.category, rev: submittalRev[previewFor.id] || previewFor.revision, ext: 'pdf' })}
        onClose={() => setPreviewFor(null)} />}

      {items.filter(i => i.id === openCard).map(item => {
        const isOpen  = true; // the card window is this item, open
        const sm      = statusMeta(item.status);
        const itemDocs   = docs[item.id]   || [];
        const itemRounds = rounds[item.id] || [];
        const itemShipLog= shipLog[item.id]|| [];
        const currentTab = tab(item.id);
        const bic = item.ball_in_court === 'titan' ? '🏠 Titan Electric' : '🏛️ Engineer / Owner';
        const bicColor = item.ball_in_court === 'titan' ? '#1d4ed8' : '#b45309';
        const lineItems = parseLineItems(item);
        const isDropTarget = dropCardId === item.id;
        const isFileTarget = fileDragId === item.id;

        return (
          <div key={item.id} onClick={() => setOpenCard(null)} style={{ position: 'fixed', inset: 0, zIndex: 6000, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 980, maxWidth: '100%', background: '#fff', borderRadius: 14, boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
          <div id={`sub-${item.id}`}
            onDragOver={isAdminZone ? (e => {
              if (isBidDrag(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (dropCardId !== item.id) setDropCardId(item.id); }
              else if (isFileDrag(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (fileDragId !== item.id) setFileDragId(item.id); }
            }) : undefined}
            onDragLeave={isAdminZone ? (e => { if (!e.currentTarget.contains(e.relatedTarget)) { if (dropCardId === item.id) setDropCardId(null); if (fileDragId === item.id) setFileDragId(null); } }) : undefined}
            onDrop={isAdminZone ? (e => {
              const b = readBidDrop(e); setDropCardId(null); setFileDragId(null); setDragActive(false);
              if (b) { e.preventDefault(); addBidItemsToSubmittal(item, [b]); return; }
              // A PDF dropped straight onto the row attaches it — no need to open the card first.
              const files = Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
              if (files.length) { e.preventDefault(); files.forEach(f => uploadDoc(item.id, f)); }
            }) : undefined}
            style={{ border: isDropTarget ? '2px solid #4f46e5' : isFileTarget ? '2px solid #2563eb' : (dragActive ? `2px dashed ${sm.color}66` : `2px solid ${sm.color}33`), boxShadow: (isDropTarget || isFileTarget) ? '0 0 0 3px #c7d2fe' : 'none', borderRadius: 10, marginBottom: 14, overflow: 'hidden', background: isDropTarget ? '#f5f7ff' : isFileTarget ? '#eff6ff' : '#fff', transition: 'box-shadow 0.12s' }}>
            {dragActive && (
              <div style={{ padding: '4px 14px', fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', textAlign: 'center' }}>
                ⬇ Drop a bid item here to add it to “{item.category}”
              </div>
            )}
            {isFileTarget && (
              <div style={{ padding: '4px 14px', fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', textAlign: 'center' }}>
                📄 Drop PDF here to attach to “{item.category}”
              </div>
            )}

            {/* Header */}
            <div
              style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: sm.bg, borderBottom: isOpen ? `1px solid ${sm.color}33` : 'none' }}>
              {/* This one submittal, sent on its own. */}
              <span onClick={e => e.stopPropagation()} style={{ display: 'flex', order: 2 }}>
                <SendPointer path={`${itemJobPath(projectId)}?pillow=submittals&item=${item.id}`}
                  label={`${project?.project_number || ''} ${item.category || 'Submittal'}`.trim()} />
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#374151', background: '#e5e7eb', borderRadius: 4, padding: '1px 7px', fontFamily: 'monospace' }}>
                    #{String(item.titan_number).padStart(2,'0')}
                  </span>
                  {item.spec_number && <span style={{ fontSize: 11, color: '#6b7280' }}>Spec {item.spec_number}</span>}
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>{item.category}</span>
                  {item.revision > 1 && (
                    <span title={`Revision ${item.revision}`} style={{ fontSize: 11, fontWeight: 800, color: '#5b21b6', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '1px 8px' }}>Rev {item.revision}</span>
                  )}
                  <span style={{ fontSize: 11, fontWeight: 700, color: sm.color, background: sm.bg, border: `1px solid ${sm.color}44`, borderRadius: 10, padding: '1px 8px' }}>{sm.label}</span>
                </div>
                {!isOpen && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: bicColor }}>{bic}</span>
                    {lineItems.length > 0 && <span style={{ fontSize: 11, color: '#6b7280' }}>{lineItems.length} bid item{lineItems.length !== 1 ? 's' : ''}</span>}
                    {itemDocs.length > 0 && <span style={{ fontSize: 11, color: '#6b7280' }}>{itemDocs.length} doc{itemDocs.length !== 1 ? 's' : ''}</span>}
                    {item.ship_date && <span style={{ fontSize: 11, color: '#6b7280' }}>🚢 {item.ship_date}</span>}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {isAdminZone && <button onClick={e => { e.stopPropagation(); deleteItem(item.id); }} style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>}
                <button onClick={e => { e.stopPropagation(); setOpenCard(null); }} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
              </div>
            </div>

            {isOpen && (
              <div>
                {/* The four pillows — the same as on the board card. */}
                <div style={{ padding: '12px 16px 12px', borderBottom: '1px solid #e5e7eb' }}>
                  <SubmittalPillows wide it={item} onDate={cardDate} onAction={cardAction} onPreview={setPreviewFor} />
                </div>
                {/* Sub-tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                  {[['details','📋 Details'],['history','🔄 Round History'],['procurement','📦 Procurement']].map(([key,label]) => (
                    <button key={key} onClick={() => setActiveTab(t => ({ ...t, [item.id]: key }))}
                      style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: currentTab === key ? `2px solid #1d4ed8` : '2px solid transparent', color: currentTab === key ? '#1d4ed8' : '#6b7280', fontWeight: currentTab === key ? 700 : 500, fontSize: 12, cursor: 'pointer' }}>
                      {label}
                    </button>
                  ))}
                </div>

                <div style={{ padding: 16 }}>

                  {/* ── DETAILS TAB ── */}
                  {currentTab === 'details' && (<>

                    {/* Ball in court */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Ball in Court:</span>
                      {/* Pass the new value as an override. saveItem reads the payload
                          from `drafts`, and a handler created on THIS render closes over
                          the drafts as they were BEFORE setField — so the change never
                          reached the server, and with nothing else pending it saved
                          nothing at all. The override argument exists for exactly this. */}
                      <button onClick={() => { setField(item.id, 'ball_in_court', 'titan'); saveItem(item, { ball_in_court: 'titan' }); }}
                        style={{ padding: '5px 14px', borderRadius: 20, border: `2px solid ${item.ball_in_court === 'titan' ? '#1d4ed8' : '#e5e7eb'}`, background: item.ball_in_court === 'titan' ? '#eff6ff' : '#fff', color: item.ball_in_court === 'titan' ? '#1d4ed8' : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        🏠 Titan Electric
                      </button>
                      <button onClick={() => { setField(item.id, 'ball_in_court', 'engineer'); saveItem(item, { ball_in_court: 'engineer' }); }}
                        style={{ padding: '5px 14px', borderRadius: 20, border: `2px solid ${item.ball_in_court === 'engineer' ? '#b45309' : '#e5e7eb'}`, background: item.ball_in_court === 'engineer' ? '#fffbeb' : '#fff', color: item.ball_in_court === 'engineer' ? '#b45309' : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        🏛️ Engineer / Owner
                      </button>
                    </div>

                    {/* Category & numbers */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Category</div>
                        <input list={`cat-list-${item.id}`} value={fv(item,'category')} onChange={e => setField(item.id,'category',e.target.value)} onBlur={() => saveItem(item)}
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                        <datalist id={`cat-list-${item.id}`}>
                          {categoryOptions.map((o,i) => <option key={i} value={o.label} />)}
                        </datalist>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Spec / Bid # (optional)</div>
                        <input value={fv(item,'spec_number')} onChange={e => setField(item.id,'spec_number',e.target.value)} onBlur={() => saveItem(item)}
                          placeholder="e.g. 16050-1"
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                      </div>
                    </div>

                    {/* Submitted to */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Submitted To (Name)</div>
                          {addressee?.to && fv(item,'submitted_to') !== addressee.to && (
                            <button type="button"
                              onClick={() => { setField(item.id, 'submitted_to', addressee.to); setField(item.id, 'submitted_to_email', addressee.email || ''); saveItem(item, { submitted_to: addressee.to, submitted_to_email: addressee.email || '' }); }}
                              style={{ fontSize: 10.5, fontWeight: 700, color: '#1e3a5f', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>
                              ↳ Address to {addressee.role === 'prime' ? 'prime' : 'owner'}
                            </button>
                          )}
                        </div>
                        {addresseeOptions.length > 0 && (
                          <select value=""
                            onChange={e => { const o = addresseeOptions.find(x => x.key === e.target.value); if (o) { setField(item.id, 'submitted_to', o.to); setField(item.id, 'submitted_to_email', o.email || ''); saveItem(item, { submitted_to: o.to, submitted_to_email: o.email || '' }); } }}
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #c7d2fe', background: '#f8faff', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#1e3a5f', fontWeight: 600, marginBottom: 6, cursor: 'pointer' }}>
                            <option value="">↳ Change recipient…</option>
                            {addresseeOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                          </select>
                        )}
                        <input value={fv(item,'submitted_to')} onChange={e => setField(item.id,'submitted_to',e.target.value)} onBlur={() => saveItem(item)}
                          placeholder="Engineer or Owner name"
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Email</div>
                        <input type="email" value={fv(item,'submitted_to_email')} onChange={e => setField(item.id,'submitted_to_email',e.target.value)} onBlur={() => saveItem(item)}
                          placeholder="engineer@firm.com"
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                      </div>
                    </div>

                    {/* Status */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Status</div>
                      {(() => { const sm = statusMeta(fv(item,'status') || 'not_submitted'); return (
                        <select value={fv(item,'status') || 'not_submitted'} onChange={e => { setField(item.id, 'status', e.target.value); saveItem(item, { status: e.target.value }); }}
                          style={{ border: `2px solid ${sm.color}`, borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, color: sm.color, background: sm.bg, cursor: 'pointer', minWidth: 220 }}>
                          {SUBMITTAL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      ); })()}
                    </div>

                    {/* Notes */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Notes</div>
                      <textarea value={fv(item,'notes')} onChange={e => setField(item.id,'notes',e.target.value)} onBlur={() => saveItem(item)}
                        placeholder="General notes about this submittal…" rows={3}
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
                    </div>

                    {/* Include attachment list toggle */}
                    <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id={`att-${item.id}`} checked={!!Number(fv(item,'include_attachment_list') ?? 1)}
                        onChange={e => { setField(item.id,'include_attachment_list', e.target.checked ? 1 : 0); saveItem(item, { include_attachment_list: e.target.checked ? 1 : 0 }); }}
                        style={{ width: 15, height: 15 }} />
                      <label htmlFor={`att-${item.id}`} style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>Include attachment list on cover sheet</label>
                    </div>

                    {(saving[item.id] || hasDraft(item.id)) && (
                      <div style={{ marginBottom: 16, fontSize: 12, fontWeight: 600, color: saving[item.id] ? '#6b7280' : '#9ca3af' }}>
                        {saving[item.id] ? 'Saving…' : 'Saves automatically'}
                      </div>
                    )}

                    {/* Included bid line items (grouped under this category) */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Bid Items in this Package{lineItems.length > 0 ? ` (${lineItems.length})` : ''}
                      </div>
                      {lineItems.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#9ca3af', border: '1px dashed #d1d5db', borderRadius: 6, padding: '10px 12px' }}>
                          No bid items yet — drag rows from the <strong>Bid Items</strong> panel above onto this card, or check items there and add them to “{item.category}”.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {lineItems.map((b, li) => (
                            <div key={li} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                              {b.item_number != null && String(b.item_number) !== '' && (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', fontFamily: 'monospace', background: '#eef2ff', borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>#{b.item_number}</span>
                              )}
                              <span style={{ flex: 1, fontSize: 13, color: '#111' }}>{b.description}</span>
                              {b.quantity != null && <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{b.quantity}{b.unit ? ` ${b.unit}` : ''}</span>}
                              {isAdminZone && (
                                <button onClick={() => removeLineItem(item, liKey(b))} title="Remove from this package"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Documents */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Submittal Documents <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: '#9ca3af' }}>· ☑ = in the PDF package</span></div>
                      {itemDocs.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                          {itemDocs.map(doc => (
                            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb', opacity: Number(doc.include_in_package ?? 1) === 0 ? 0.6 : 1 }}>
                              {/* Ticked = in the printed / emailed package. */}
                              <input type="checkbox" checked={Number(doc.include_in_package ?? 1) !== 0} disabled={!isAdminZone}
                                onChange={e => toggleDocInPackage(item.id, doc, e.target.checked)}
                                title="Include this file in the PDF package" style={{ width: 17, height: 17, cursor: isAdminZone ? 'pointer' : 'default', flexShrink: 0 }} />
                              <span style={{ fontSize: 18 }}>📄</span>
                              <a href={fileUrl(doc.filename)} target="_blank" rel="noreferrer"
                                style={{ flex: 1, fontSize: 13, color: '#1d4ed8', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {doc.original_name}
                              </a>
                              <span style={{ fontSize: 10, color: '#9ca3af', background: '#e5e7eb', borderRadius: 4, padding: '1px 6px' }}>{doc.source === 'parts_directory' ? 'Parts Dir.' : 'Upload'}</span>
                              {isAdminZone && <button onClick={() => deleteDoc(item.id, doc.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 16, cursor: 'pointer' }}>×</button>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* One big, forgiving drop target — drag a PDF anywhere onto it, or click to
                          browse. Far easier to hit than the old small button, and it can't be missed. */}
                      <AttachButton multiple accept="pdf" variant="dashed" icon="📄"
                        label={itemDocs.length ? 'Drag & drop another PDF here, or click to upload' : 'Drag & drop a PDF here, or click to upload'}
                        style={{ width: '100%', padding: '20px 14px', fontSize: 14, borderRadius: 10, marginBottom: 8 }}
                        onFiles={fs => fs.forEach(f => uploadDoc(item.id, f))} />
                      <button onClick={() => openPartsPicker(item.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f0fdf4', border: '1px dashed #86efac', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                        📦 Pull from Parts Directory
                      </button>
                    </div>

                    {/* Generate PDF button */}
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                      {/* See the whole package first and mark it up — highlighter and pen — before it prints (Ryan, Nov 2 2026). */}
                      <button onClick={() => openMarkup(item)} title="See the package as it will print, highlight and mark it up, then print it with the marks"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 8, padding: '8px 16px', background: '#fff', color: '#1e3a5f', border: '1.5px solid #1e3a5f', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        ✏️ Draft &amp; mark up
                      </button>
                      <button onClick={() => downloadSubmittalPackage(item)} disabled={generatingPdf === item.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 18px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: generatingPdf === item.id ? 'default' : 'pointer', opacity: generatingPdf === item.id ? 0.6 : 1 }}>
                        {generatingPdf === item.id ? '⏳ Generating…' : '📥 Generate PDF Package'}
                      </button>
                      {/* Or send it straight to the reviewer — same package, attached and logged. */}
                      <button onClick={() => mailStatus?.configured ? (pkgEmail?.itemId === item.id ? setPkgEmail(null) : openPackageEmail(item)) : showToast && showToast('Email isn’t set up on the server yet.', 'error')}
                        title={mailStatus?.configured ? `Sends from ${mailStatus.from}` : 'Email isn’t set up on the server yet'}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8, padding: '8px 16px', background: mailStatus?.configured ? '#15803d' : '#e5e7eb', color: mailStatus?.configured ? '#fff' : '#9ca3af', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        ✉️ Email Package
                      </button>
                      {pkgEmail?.itemId === item.id && (
                        <div style={{ marginTop: 12, border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.4 }}>Email this package — from {mailStatus?.from || 'the company mailbox'}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <AddressPicker value={pkgEmail.to} onChange={v => setPkgEmail(p => ({ ...p, to: v }))} placeholder="To — the reviewer; pick from the job or type" projectId={projectId}
                              inputStyle={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                            <AddressPicker value={pkgEmail.cc} onChange={v => setPkgEmail(p => ({ ...p, cc: v }))} placeholder="Cc (optional)" projectId={projectId}
                              inputStyle={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                          </div>
                          <input value={pkgEmail.subject} onChange={e => setPkgEmail(p => ({ ...p, subject: e.target.value }))} placeholder="Subject"
                            style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                          <textarea value={pkgEmail.message} rows={5} onChange={e => setPkgEmail(p => ({ ...p, message: e.target.value }))}
                            style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                          <div style={{ fontSize: 11.5, color: '#6b7280' }}>Attached: the package PDF (cover sheet + {(docs[item.id] || []).filter(d => d.doc_type === 'submittal' && Number(d.include_in_package ?? 1) !== 0).length} document{(docs[item.id] || []).filter(d => d.doc_type === 'submittal' && Number(d.include_in_package ?? 1) !== 0).length === 1 ? '' : 's'}) — filed on the Documents tile, and a "submitted" round is logged on this item.</div>
                          {pkgEmail.err && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{pkgEmail.err}</div>}
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setPkgEmail(null)} disabled={pkgEmail.busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                            {/* The package exactly as it will be attached (cover sheet + documents). */}
                            <button onClick={() => openPdfFrom(`/sub-jobs/${sjId}/submittals/${item.id}/generate-pdf`).catch(e => setPkgEmail(p => p ? { ...p, err: e?.message || 'Could not open the preview.' } : p))}
                              disabled={pkgEmail.busy} title="Open the package PDF that will be attached" style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>👁 Preview PDF</button>
                            <button onClick={() => sendPackageEmail(item)} disabled={pkgEmail.busy || !pkgEmail.to.trim()}
                              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: pkgEmail.to.trim() ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: pkgEmail.to.trim() ? 'pointer' : 'default' }}>
                              {pkgEmail.busy ? 'Sending…' : '✉️ Send package'}
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Revision — saved on the submittal, not just the filename, so
                          it survives a reload and shows as a badge on the card. */}
                      <select value={submittalRev[item.id] || ''} onChange={e => saveRevision(item.id, e.target.value)}
                        title="Mark this as a revision of an earlier submittal — this sticks"
                        style={{ marginLeft: 10, border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 8px', fontSize: 12, background: '#fff' }}>
                        <option value="">First issue</option>
                        {[2, 3, 4, 5, 6].map(v => <option key={v} value={v}>rev{v}</option>)}
                      </select>
                      <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 10 }}>
                        Saves as <b style={{ color: '#6b7280' }}>{jobDocFileName({ job: project, type: 'Submittal', number: item.titan_number, description: item.category, rev: submittalRev[item.id], ext: 'pdf' })}</b>
                      </span>
                    </div>
                  </>)}

                  {/* ── ROUND HISTORY TAB ── */}
                  {currentTab === 'history' && (<>
                    <div style={{ marginBottom: 14 }}>
                      {itemRounds.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 14 }}>No submissions recorded yet.</div>}
                      {itemRounds.map(rd => {
                        const isSubmit = rd.action === 'submit';
                        return (
                          <div key={rd.id} style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 10, background: isSubmit ? '#eff6ff' : '#fafafa' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, fontWeight: 800, color: '#374151', background: '#e5e7eb', borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace' }}>Round {rd.round_number}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: isSubmit ? '#1d4ed8' : '#374151' }}>{isSubmit ? '📤 Submitted' : '📥 Response Received'}</span>
                              </div>
                              <span style={{ fontSize: 11, color: '#9ca3af' }}>{rd.sent_date || rd.response_date || new Date(rd.created_at).toLocaleDateString()}</span>
                            </div>
                            {rd.sent_to && <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>To: <strong>{rd.sent_to}</strong>{rd.sent_to_email ? ` (${rd.sent_to_email})` : ''}</div>}
                            {rd.response_status && (
                              <div style={{ marginBottom: 4 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: statusMeta(rd.response_status)?.color || '#374151', background: statusMeta(rd.response_status)?.bg || '#f3f4f6', borderRadius: 10, padding: '2px 8px' }}>
                                  {statusMeta(rd.response_status)?.label || rd.response_status}
                                </span>
                              </div>
                            )}
                            {rd.response_notes && <div style={{ fontSize: 12, color: '#374151', marginTop: 6, fontStyle: 'italic' }}>"{rd.response_notes}"</div>}
                            {rd.markup_filename && (
                              <div style={{ marginTop: 8 }}>
                                <a href={fileUrl(rd.markup_filename)} target="_blank" rel="noreferrer"
                                  style={{ fontSize: 12, color: '#1d4ed8', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  📎 {rd.markup_original || 'Engineer Markup'}
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* New round form */}
                    {isAdminZone && !showRoundForm[item.id] && (
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={() => { setRoundDraft(d => ({ ...d, [item.id]: { action: 'submit' } })); setShowRoundForm(f => ({ ...f, [item.id]: true })); }}
                          style={{ padding: '7px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📤 Log Submission</button>
                        <button onClick={() => { setRoundDraft(d => ({ ...d, [item.id]: { action: 'response' } })); setShowRoundForm(f => ({ ...f, [item.id]: true })); }}
                          style={{ padding: '7px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📥 Log Response</button>
                      </div>
                    )}

                    {isAdminZone && showRoundForm[item.id] && (() => {
                      const rd = roundDraft[item.id] || {};
                      const setRd = (field, val) => setRoundDraft(d => ({ ...d, [item.id]: { ...(d[item.id]||{}), [field]: val } }));
                      const isSubmitAction = rd.action === 'submit';
                      return (
                        <div style={{ padding: 14, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', marginTop: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>{isSubmitAction ? '📤 Log Submission' : '📥 Log Engineer Response'}</div>
                          {isSubmitAction ? (<>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                              <div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Sent To</div>
                                <input value={rd.sent_to || item.submitted_to || ''} onChange={e => setRd('sent_to', e.target.value)}
                                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Date Sent</div>
                                <input type="date" value={rd.sent_date || ''} onChange={e => setRd('sent_date', e.target.value)}
                                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                              </div>
                            </div>
                          </>) : (<>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                              <div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Response Date</div>
                                <input type="date" value={rd.response_date || ''} onChange={e => setRd('response_date', e.target.value)}
                                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Result</div>
                                <select value={rd.response_status || ''} onChange={e => setRd('response_status', e.target.value)}
                                  style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}>
                                  <option value="">— Select —</option>
                                  {SUBMITTAL_STATUSES.filter(s => s.key !== 'not_submitted' && s.key !== 'submitted').map(s => (
                                    <option key={s.key} value={s.key}>{s.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Engineer Comments</div>
                              <textarea value={rd.response_notes || ''} onChange={e => setRd('response_notes', e.target.value)}
                                rows={3} placeholder="Any conditions, comments, or notes from the engineer…"
                                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13, resize: 'vertical' }} />
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Engineer Markup PDF (optional)</div>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: '#f3f4f6', border: '1px dashed #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                                {rd.markupFile ? `✓ ${rd.markupFile.name}` : '+ Upload Markup'}
                                <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => setRd('markupFile', e.target.files[0])} />
                              </label>
                            </div>
                          </>)}
                          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button onClick={() => submitRound(item.id)}
                              style={{ padding: '7px 18px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                            <button onClick={() => setShowRoundForm(f => ({ ...f, [item.id]: false }))}
                              style={{ padding: '7px 14px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                          </div>
                        </div>
                      );
                    })()}
                  </>)}

                  {/* ── PROCUREMENT TAB ── */}
                  {currentTab === 'procurement' && (<>
                    {/* Who it comes from and which PO buys it — the vendor contact is who the
                        status requests go to (Ryan, Oct 2 2026). Saves as it is changed. */}
                    {/* Where it stands after approval — and the way to change it, right here (Pat, Oct 7 2026:
                        "on the procurement page I don't see how to change from approved to Hold For Release"). */}
                    {(() => {
                      if ((item.parts || []).length) return null;   // split: each part has its own (below)
                      const st = fv(item, 'status') || 'not_submitted';
                      const approved = ['approved', 'approved_with_conditions', 'approved_no_action'].includes(st);
                      const released = !!item.released_date;
                      const here = !approved ? 'pending' : st === 'approved_no_action' ? 'no_action' : released ? 'released' : 'holding';
                      const setTo = async (key) => {
                        if (!isAdminZone || saving[item.id]) return;
                        if (key === 'release') { setReleaseFor(item); return; }
                        const patch = key === 'no_action' ? { status: 'approved_no_action' } : { status: st === 'approved_with_conditions' ? st : 'approved' };
                        if (released && key === 'holding') {
                          if (!window.confirm('Take it back to Holding for Release?\n\nThe release date comes off. Any PO already released stays Released.')) return;
                          patch.released_date = null;
                        }
                        setField(item.id, 'status', patch.status);
                        await saveItem(item, patch);
                      };
                      const opt = (key, label, sub, color, bg) => {
                        const on = here === key;
                        return (
                          <button key={key} type="button" disabled={!isAdminZone || !approved} onClick={() => setTo(key)}
                            style={{ flex: '1 1 150px', textAlign: 'left', padding: '9px 12px', borderRadius: 10, cursor: isAdminZone && approved ? 'pointer' : 'default',
                              border: `2px solid ${on ? color : '#e5e7eb'}`, background: on ? bg : '#fff', opacity: approved ? 1 : 0.55 }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: on ? color : '#374151' }}>{on ? '● ' : '○ '}{label}</div>
                            <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>{sub}</div>
                          </button>
                        );
                      };
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>After approval</div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {opt('holding', 'Approved · Holding for Release', 'Approved — waiting to release it to the manufacturer', '#15803d', '#f0fdf4')}
                            {opt('release', released ? '🚀 Released ' + (item.released_date || '') : '🚀 Release…', released ? 'Released to the manufacturer' : 'Pick the date and the POs to release with it', '#6d28d9', '#f5f3ff')}
                            {opt('no_action', 'Approved · No Further Action', 'Bought as needed — nothing to release or track', '#4d7c0f', '#f7fee7')}
                          </div>
                          {!approved && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>These open up once the submittal is approved.</div>}
                        </div>
                      );
                    })()}
                    <SubmittalVendorPanel sjId={sjId} item={item} projectId={projectId} canEdit={isAdminZone} showToast={showToast}
                      onChanged={cardRefreshed} onAskVendor={intent => setRemindFor({ item, party: 'vendor', intent })} />
                    {/* Release parts A, B, C… — each approved, released, shipped and received on its own (Ryan, Oct 26 2026). */}
                    <SubmittalParts sjId={sjId} item={item} canEdit={isAdminZone} showToast={showToast} onChanged={cardRefreshed}
                      onReceive={part => setReceivePart({ item, part })} />
                    {!(item.parts || []).length && (<>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Released to Manufacturer</div>
                        <input type="date" value={fv(item,'released_date')} onChange={e => setField(item.id,'released_date',e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                          Ship Date
                          {item.original_ship_date && item.ship_date && item.ship_date !== item.original_ship_date && (
                            <span style={{ marginLeft: 6, fontSize: 10, color: '#b45309', fontWeight: 700 }}>⚠️ Slipped</span>
                          )}
                        </div>
                        <input type="date" value={fv(item,'ship_date')} onChange={e => setField(item.id,'ship_date',e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${item.original_ship_date && item.ship_date && item.ship_date !== item.original_ship_date ? '#f59e0b' : '#e5e7eb'}`, borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                        {item.original_ship_date && item.ship_date !== item.original_ship_date && (
                          <div style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>Original: {item.original_ship_date}</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Received Date</div>
                        <input type="date" value={fv(item,'received_date')} onChange={e => setField(item.id,'received_date',e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${item.received_date ? '#16a34a' : '#e5e7eb'}`, borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                      </div>
                    </div>

                    {/* Procurement status visual */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16 }}>
                      {[['Released',!!item.released_date],['Shipped',!!item.ship_date],['Received',!!item.received_date]].map(([label, done], i, arr) => (
                        <React.Fragment key={label}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: done ? '#16a34a' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                              {done ? '✓' : '○'}
                            </div>
                            <span style={{ fontSize: 10, color: done ? '#16a34a' : '#9ca3af', fontWeight: done ? 700 : 400 }}>{label}</span>
                          </div>
                          {i < arr.length - 1 && <div style={{ flex: 1, height: 2, background: done ? '#16a34a' : '#e5e7eb', margin: '0 4px', marginBottom: 16 }} />}
                        </React.Fragment>
                      ))}
                    </div>

                    {hasDraft(item.id) && (
                      <button onClick={() => saveItem(item)} disabled={saving[item.id]}
                        style={{ marginBottom: 16, padding: '7px 20px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: saving[item.id] ? 0.6 : 1 }}>
                        {saving[item.id] ? 'Saving…' : 'Save Dates'}
                      </button>
                    )}
                    </>)}

                    {/* Ship date change log */}
                    {itemShipLog.length > 0 && (
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Ship Date Change Log</div>
                        {itemShipLog.map(log => (
                          <div key={log.id} style={{ display: 'flex', gap: 10, fontSize: 12, color: '#374151', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                            <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleDateString()}</span>
                            {log.part_letter && <span style={{ fontWeight: 800, color: '#6d28d9' }}>Part {log.part_letter}</span>}
                            <span>{log.old_date || '—'} → <strong>{log.new_date}</strong></span>
                            {log.note && <span style={{ color: '#6b7280', fontStyle: 'italic' }}>"{log.note}"</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    <SubmittalReceiving item={item} projectName={project?.name} />
                  </>)}

                </div>
              </div>
            )}
          </div>
          <SubmittalActivity itemId={item.id} bump={`${item.updated_at}|${itemRounds.length}|${actBump}`} />
          </div>
          </div>
        );
      })}

      {isAdminZone && (
        <button onClick={addItem} disabled={adding}
          style={{ width: '100%', padding: 10, border: '2px dashed #d1d5db', borderRadius: 8, background: 'none', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
          {adding ? 'Adding…' : '+ New Category / Submittal'}
        </button>
      )}
    </div>
  );
}

// ── Loops Tab ─────────────────────────────────────────────────────────────────
const LOOP_DIRECTIONS = ['EB', 'WB', 'NB', 'SB'];
const LOOP_TYPE_META = {
  gate:    { label: 'Gate',    color: '#1d4ed8', bg: '#eff6ff' },
  traffic: { label: 'Traffic', color: '#059669', bg: '#ecfdf5' },
  speed:   { label: 'Counter', color: '#d97706', bg: '#fffbeb' },
};

export function LoopsTab({ sjId, isAdminZone, API_BASE, getToken, showToast }) {
  // On a phone the Fieldwire task is just its pin and number (its status a coloured dot), so the
  // Left Turn / Thru / Advance boxes have room to read and type in (Pat, Oct 8 2026).
  const [narrow, setNarrow] = React.useState(() => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches);
  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 640px)'); const on = () => setNarrow(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  const [locations, setLocations]       = React.useState([]);
  const [locationPhotos, setLocationPhotos] = React.useState({});
  const [gateSizes, setGateSizes]       = React.useState({}); // locId -> [{id,qty,size}]
  const [trafficDirs, setTrafficDirs]   = React.useState({}); // locId -> [{id,direction,qty_left_turn,qty_thru,qty_advance}]
  // A Fieldwire task per direction row (Pat, Oct 6 2026): the job's link, the row being linked, the task open in the drawer.
  const [fwLink, setFwLink]             = React.useState(null);   // { project_id, linked, statuses }
  const [fwPick, setFwPick]             = React.useState(null);   // { loc, row }
  const [fwOpen, setFwOpen]             = React.useState(null);   // task id
  const [fwBusy, setFwBusy]             = React.useState({});
  const [expanded, setExpanded]         = React.useState({});
  const [drafts, setDrafts]             = React.useState({});
  const [saving, setSaving]             = React.useState({});
  const [addingLoc, setAddingLoc]       = React.useState(false);
  const [lightbox, setLightbox]         = React.useState(null);
  const [mapModal, setMapModal]         = React.useState(null);
  const [rowMap, setRowMap]             = React.useState(null);   // { loc, row } — a counter spot's pin
  const [loading, setLoading]           = React.useState(true);

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, {
      ...opts,
      // A JSON body must say so, or the server reads an empty body and changes nothing while answering
      // OK (Ryan, Nov 2 2026: unticking a document for the package looked saved but every file still
      // printed). FormData bodies are not strings and keep their own type.
      headers: { 'x-worker-token': getToken(), ...(typeof opts.body === 'string' ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) },
    }).then(async r => {
      if (r.ok) return r.json();
      const text = await r.text();
      let msg; try { msg = JSON.parse(text)?.error || text; } catch { msg = `Server error ${r.status}`; }
      return Promise.reject(new Error(msg));
    });
  }

  React.useEffect(() => {
    apiFetch(`/sub-jobs/${sjId}/loop-locations`).then(locs => {
      setLocations(locs);
      setLoading(false);
      // Load photos, gate sizes, traffic dirs for all locations in parallel
      Promise.all(locs.flatMap(l => [
        apiFetch(`/sub-jobs/${sjId}/loop-locations/${l.id}/photos`).then(p => ({ type: 'photos', id: l.id, data: p })),
        apiFetch(`/sub-jobs/${sjId}/loop-locations/${l.id}/gate-sizes`).then(p => ({ type: 'gate', id: l.id, data: p })).catch(() => ({ type: 'gate', id: l.id, data: [] })),
        apiFetch(`/sub-jobs/${sjId}/loop-locations/${l.id}/traffic-dirs`).then(p => ({ type: 'traffic', id: l.id, data: p })).catch(() => ({ type: 'traffic', id: l.id, data: [] })),
      ])).then(results => {
        const photos = {}, gate = {}, traffic = {};
        results.forEach(r => {
          if (r.type === 'photos')   photos[r.id]   = r.data;
          if (r.type === 'gate')     gate[r.id]     = r.data;
          if (r.type === 'traffic')  traffic[r.id]  = r.data;
        });
        setLocationPhotos(photos);
        setGateSizes(gate);
        setTrafficDirs(traffic);
      });
    }).catch(e => { setLoading(false); if (showToast) showToast(e.message, 'error'); });
  }, [sjId]);

  const fv = (loc, field) => drafts[loc.id]?.[field] !== undefined ? drafts[loc.id][field] : (loc[field] ?? '');
  const setField = (locId, field, value) => setDrafts(d => ({ ...d, [locId]: { ...(d[locId] || {}), [field]: value } }));
  const hasDraft = locId => drafts[locId] && Object.keys(drafts[locId]).length > 0;
  const photoUrl = filename => `${API_BASE.replace('/api', '')}/uploads/${filename}`;
  const locLabel = loc => (drafts[loc.id]?.street1 ?? loc.street1) || 'New Location';

  // Bid total for discrepancy check
  // Loops done in Fieldwire at a location: each direction / spot whose task reads Completed or Verified counts
  // its loops; a counter with no spots counts all of them when its own task is done (Pat, Oct 9 2026).
  const fwIsDone = st => /complet|verif/i.test(String(st || ''));
  const fwDoneFor = loc => {
    const rows = trafficDirs[loc.id] || [];
    const counter = (fv(loc, 'loop_type') || loc.loop_type) === 'speed';
    const qty = r => (counter ? Number(r.qty_thru) || 0 : (Number(r.qty_left_turn) || 0) + (Number(r.qty_thru) || 0) + (Number(r.qty_advance) || 0));
    const linked = rows.filter(r => r.fw_task);
    if (linked.length) return { linked: linked.length, done: linked.filter(r => fwIsDone(r.fw_task.status)).reduce((n, r) => n + qty(r), 0) };
    if (loc.fw_task) return { linked: 1, done: fwIsDone(loc.fw_task.status) ? bidTotal(loc) : 0 };
    return { linked: 0, done: 0 };
  };
  function bidTotal(loc) {
    const loopType = fv(loc, 'loop_type') || loc.loop_type || 'traffic';
    if (loopType === 'gate')    return (gateSizes[loc.id] || []).reduce((s, r) => s + (Number(r.qty) || 0), 0);
    if (loopType === 'speed')   return (trafficDirs[loc.id] || []).length ? (trafficDirs[loc.id] || []).reduce((s, r) => s + (Number(r.qty_thru) || 0), 0) : (Number(fv(loc, 'qty_thru')) || 0);
    if (loopType === 'traffic') return (trafficDirs[loc.id] || []).reduce((s, r) => s + (Number(r.qty_left_turn)||0) + (Number(r.qty_thru)||0) + (Number(r.qty_advance)||0), 0);
    return 0;
  }

  async function saveLoc(loc) {
    if (!hasDraft(loc.id)) return;
    setSaving(s => ({ ...s, [loc.id]: true }));
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/loop-locations/${loc.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(drafts[loc.id]),
      });
      setLocations(ls => ls.map(l => l.id === loc.id ? updated : l));
      setDrafts(d => { const n = { ...d }; delete n[loc.id]; return n; });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setSaving(s => ({ ...s, [loc.id]: false }));
  }

  async function addLocation() {
    setAddingLoc(true);
    try {
      const loc = await apiFetch(`/sub-jobs/${sjId}/loop-locations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setLocations(ls => [...ls, loc]);
      setGateSizes(g => ({ ...g, [loc.id]: [] }));
      setTrafficDirs(t => ({ ...t, [loc.id]: [] }));
      setLocationPhotos(p => ({ ...p, [loc.id]: [] }));
      setExpanded(e => ({ ...e, [loc.id]: true }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
    setAddingLoc(false);
  }

  async function deleteLoc(locId) {
    if (!window.confirm('Remove this location? All data will be lost.')) return;
    try {
      await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setLocations(ls => ls.filter(l => l.id !== locId));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Gate size rows
  async function addGateSize(locId) {
    try {
      const row = await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/gate-sizes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ qty: 1, size: "4'x12'" }) });
      setGateSizes(g => ({ ...g, [locId]: [...(g[locId] || []), row] }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function updateGateSize(locId, rowId, field, value) {
    setGateSizes(g => ({ ...g, [locId]: (g[locId] || []).map(r => r.id === rowId ? { ...r, [field]: value } : r) }));
    try {
      await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/gate-sizes/${rowId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function deleteGateSize(locId, rowId) {
    try {
      await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/gate-sizes/${rowId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setGateSizes(g => ({ ...g, [locId]: (g[locId] || []).filter(r => r.id !== rowId) }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Each direction is a pillow that can be moved up or down, dragged (on a computer), or grouped by
  // direction in one tap — EB next to EB (Pat, Oct 9 2026). The order is saved on the server.
  const [dragDir, setDragDir] = React.useState(null);   // { locId, id } being dragged
  async function saveDirOrder(locId, rows) {
    setTrafficDirs(t => ({ ...t, [locId]: rows }));
    try { await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs/order`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: rows.map(r => r.id) }) }); }
    catch (e) { if (showToast) showToast(e.message || 'Could not save the order', 'error'); }
  }
  function moveDir(locId, rowId, toIndex) {
    const rows = [...(trafficDirs[locId] || [])];
    const from = rows.findIndex(r => r.id === rowId);
    if (from < 0 || toIndex < 0 || toIndex >= rows.length || toIndex === from) return;
    const [r] = rows.splice(from, 1); rows.splice(toIndex, 0, r);
    saveDirOrder(locId, rows);
  }
  function groupDirs(locId) {
    const rank = d => { const i = LOOP_DIRECTIONS.indexOf(d); return i < 0 ? 99 : i; };
    const rows = (trafficDirs[locId] || []).map((r, i) => ({ r, i })).sort((a, b) => rank(a.r.direction) - rank(b.r.direction) || a.i - b.i).map(x => x.r);
    saveDirOrder(locId, rows);
  }

  // Traffic direction rows
  async function addTrafficDir(locId) {
    const usedDirs = (trafficDirs[locId] || []).map(r => r.direction);
    const nextDir = LOOP_DIRECTIONS.find(d => !usedDirs.includes(d)) || 'EB';
    try {
      const row = await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ direction: nextDir, qty_left_turn: 0, qty_thru: 0, qty_advance: 0 }) });
      setTrafficDirs(t => ({ ...t, [locId]: [...(t[locId] || []), row] }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  // A counter spot (Pat, Oct 7 2026: "same as the traffic signal") — a row with its own place,
  // direction (blank to start) and thru count.
  async function addCounterSpot(locId) {
    try {
      const row = await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ direction: '', qty_left_turn: 0, qty_thru: 0, qty_advance: 0 }) });
      setTrafficDirs(t => ({ ...t, [locId]: [...(t[locId] || []), row] }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function updateTrafficDir(locId, rowId, field, value) {
    setTrafficDirs(t => ({ ...t, [locId]: (t[locId] || []).map(r => r.id === rowId ? { ...r, [field]: value } : r) }));
    try {
      await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs/${rowId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }
  async function deleteTrafficDir(locId, rowId) {
    try {
      await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs/${rowId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setTrafficDirs(t => ({ ...t, [locId]: (t[locId] || []).filter(r => r.id !== rowId) }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // ── Fieldwire task per direction row ──
  React.useEffect(() => { apiFetch(`/sub-jobs/${sjId}/fieldwire-link`).then(setFwLink).catch(() => setFwLink(null)); }, [sjId]); // eslint-disable-line react-hooks/exhaustive-deps
  const reloadDirs = locId => apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs`).then(rows => setTrafficDirs(t => ({ ...t, [locId]: rows }))).catch(() => {});
  const dirPath = (locId, rowId) => `/sub-jobs/${sjId}/loop-locations/${locId}/traffic-dirs/${rowId}/fieldwire`;
  async function sendDirToFieldwire(loc, row, pin = {}) {
    setFwBusy(b => ({ ...b, [row.id]: true }));
    try {
      const r = await apiFetch(dirPath(loc.id, row.id), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pin || {}) });
      await reloadDirs(loc.id);
      if (showToast) showToast(`Fieldwire task #${r.task?.number || '?'} made: ${r.task?.name || row.direction || 'loops'}`);
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
    setFwBusy(b => ({ ...b, [row.id]: false }));
  }
  async function attachDirToFieldwire(loc, row, taskId) {
    setFwBusy(b => ({ ...b, [row.id]: true }));
    try {
      const r = await apiFetch(dirPath(loc.id, row.id) + '/attach', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ task_id: taskId }) });
      await reloadDirs(loc.id);
      setFwPick(null);
      if (showToast) showToast(`${row.direction || 'Row'} put on Fieldwire task #${r.task?.number || '?'}`);
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
    setFwBusy(b => ({ ...b, [row.id]: false }));
  }
  async function unlinkDirFromFieldwire(loc, row) {
    if (!window.confirm(`Take ${row.direction || 'this row'} off its Fieldwire task? The task stays in Fieldwire.`)) return;
    try { await apiFetch(dirPath(loc.id, row.id), { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' }); await reloadDirs(loc.id); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // ── A COUNTER location's Fieldwire task (Pat, Oct 7 2026) — on the location, it has no direction rows ──
  const reloadLocs = () => apiFetch(`/sub-jobs/${sjId}/loop-locations`).then(rows => setLocations(prev => rows.map(r => ({ ...(prev.find(p => p.id === r.id) || {}), ...r })))).catch(() => {});
  const counterPath = locId => `/sub-jobs/${sjId}/loop-locations/${locId}/fieldwire`;
  async function sendCounterToFieldwire(loc, pin = {}) {
    setFwBusy(b => ({ ...b, [loc.id]: true }));
    try {
      const r = await apiFetch(counterPath(loc.id), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pin || {}) });
      await reloadLocs();
      if (showToast) showToast(`Fieldwire task #${r.task?.number || '?'} made: ${r.task?.name || 'counter'}`);
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
    setFwBusy(b => ({ ...b, [loc.id]: false }));
  }
  async function attachCounterToFieldwire(loc, taskId) {
    setFwBusy(b => ({ ...b, [loc.id]: true }));
    try {
      const r = await apiFetch(counterPath(loc.id) + '/attach', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ task_id: taskId }) });
      await reloadLocs();
      setFwPick(null);
      if (showToast) showToast(`Counter put on Fieldwire task #${r.task?.number || '?'}`);
    } catch (e) { if (showToast) showToast(e.message, 'error'); else window.alert(e.message); }
    setFwBusy(b => ({ ...b, [loc.id]: false }));
  }
  async function unlinkCounterFromFieldwire(loc) {
    if (!window.confirm('Take this counter off its Fieldwire task? The task stays in Fieldwire.')) return;
    try { await apiFetch(counterPath(loc.id), { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' }); await reloadLocs(); }
    catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  // Photos
  async function uploadPhoto(locId, file) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('photo_type', 'general');
    const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/loop-locations/${locId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
    if (!r.ok) { if (showToast) showToast('Upload failed', 'error'); return; }
    const photo = await r.json();
    setLocationPhotos(p => ({ ...p, [locId]: [...(p[locId] || []), photo] }));
  }
  async function deletePhoto(locId, photoId) {
    try {
      await apiFetch(`/sub-jobs/${sjId}/loop-locations/${locId}/photos/${photoId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setLocationPhotos(p => ({ ...p, [locId]: (p[locId] || []).filter(x => x.id !== photoId) }));
    } catch (e) { if (showToast) showToast(e.message, 'error'); }
  }

  if (loading) return <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading loop locations…</div>;

  return (
    <div>
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={lightbox} alt="" style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 18, right: 22, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer' }}>✕</button>
        </div>
      )}
      {mapModal && (
        <LocationPickerModal
          value={(() => { const loc = locations.find(l => l.id === mapModal); return drafts[mapModal]?.street1 ?? loc?.street1 ?? ''; })()}
          onConfirm={({ lat, lng, address }) => {
            const locId = mapModal;
            const loc = locations.find(l => l.id === locId);
            if (lat != null) setField(locId, 'lat', lat);
            if (lng != null) setField(locId, 'lng', lng);
            if (address && loc && !loc.street1 && !(drafts[locId]?.street1)) setField(locId, 'street1', address);
            setMapModal(null);
          }}
          onClose={() => setMapModal(null)}
        />
      )}
      {rowMap && (
        <LocationPickerModal
          value={rowMap.row.spot_label || ''}
          onConfirm={({ lat, lng, address }) => {
            const { loc, row } = rowMap;
            if (lat != null) updateTrafficDir(loc.id, row.id, 'lat', lat);
            if (lng != null) updateTrafficDir(loc.id, row.id, 'lng', lng);
            if (address && !row.spot_label) updateTrafficDir(loc.id, row.id, 'spot_label', address);
            setRowMap(null);
          }}
          onClose={() => setRowMap(null)}
        />
      )}
      {/* A direction's Fieldwire task: link to one the crew already made, or make a new one (Pat, Oct 6 2026). */}
      {fwPick && fwLink && fwLink.project_id && (
        <FwTaskChooser projectId={fwLink.project_id}
          heading={`${[fwPick.loc.street1, fwPick.loc.street2].filter(Boolean).join(' & ') || 'Loops'} · ${fwPick.row ? `${fwPick.row.spot_label ? fwPick.row.spot_label + ' · ' : ''}${fwPick.row.direction || '—'}` : `${fv(fwPick.loc, 'directions') || ''} counter`.trim()}`}
          sub={fwPick.row ? 'Put this direction on a task the crew already made, or make a new one.' : 'Put this counter on a task the crew already made, or make a new one.'}
          createHint={`Makes “${[fwPick.loc.street1, fwPick.loc.street2].filter(Boolean).join(' & ') || 'Loops'}${fwPick.row ? (fwPick.row.direction ? ' ' + fwPick.row.direction : '') : ` ${fv(fwPick.loc, 'directions') || ''} counter`.replace(/\s+/g, ' ')}” in the Loops category, with the quantities and the map pin in a comment.`}
          busy={!!fwBusy[fwPick.row ? fwPick.row.id : fwPick.loc.id]}
          onCreate={pin => { const { loc, row } = fwPick; setFwPick(null); if (row) sendDirToFieldwire(loc, row, pin); else sendCounterToFieldwire(loc, pin); }}
          onAttach={taskId => (fwPick.row ? attachDirToFieldwire(fwPick.loc, fwPick.row, taskId) : attachCounterToFieldwire(fwPick.loc, taskId))}
          onClose={() => setFwPick(null)} />
      )}
      {fwOpen && fwLink && fwLink.project_id && (
        <FieldwireTaskDrawer projectId={fwLink.project_id} taskId={fwOpen} statuses={fwLink.statuses || []} canEdit={!!isAdminZone}
          onClose={() => setFwOpen(null)} onChanged={() => { Object.keys(trafficDirs).forEach(id => reloadDirs(id)); reloadLocs(); }} />
      )}

      {(() => {
        // Done in Fieldwire, across the card (Pat, Oct 9 2026): loops whose task reads Completed / Verified.
        const sum = locations.reduce((a, l) => { const d = fwDoneFor(l); if (d.linked) { a.done += d.done; a.of += bidTotal(l); a.locs++; if (d.done >= bidTotal(l) && bidTotal(l) > 0) a.full++; } return a; }, { done: 0, of: 0, locs: 0, full: 0 });
        return sum.locs ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 10px', padding: '8px 12px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13, color: '#166534' }}>
            <b>{sum.done > 0 ? '✅ ' : ''}{sum.done} of {sum.of} loops done in Fieldwire</b>
            <span style={{ color: '#4b5563' }}>· {sum.full} of {sum.locs} location{sum.locs === 1 ? '' : 's'} finished</span>
          </div>
        ) : null;
      })()}
      {locations.map(loc => {
        // Several locations open folded, so the card reads as a list (Pat, Oct 9 2026); a single one opens
        // straight away, and one just added (or tapped) opens as before.
        const isOpen      = expanded[loc.id] != null ? !!expanded[loc.id] : locations.length === 1;
        const loopType    = fv(loc, 'loop_type') || loc.loop_type || 'traffic';
        const loopMeta    = LOOP_TYPE_META[loopType] || LOOP_TYPE_META.traffic;
        const isComplete  = (fv(loc, 'status') || loc.status) === 'complete';
        const photos      = locationPhotos[loc.id] || [];
        const gRows       = gateSizes[loc.id] || [];
        const tRows       = trafficDirs[loc.id] || [];
        const bid         = bidTotal(loc);
        const confirmed   = Number(fv(loc, 'confirmed_total') !== '' ? fv(loc, 'confirmed_total') : loc.confirmed_total);
        const hasConfirmed = loc.confirmed_total != null && loc.confirmed_total !== '';
        const discrepancy  = hasConfirmed && confirmed !== bid;
        const install      = (fv(loc, 'installation') || loc.installation || 'hot_sealed') === 'hot_sealed' ? 'Hot Sealed' : 'In Sand';
        const shortLoc     = (() => { const f = locLabel(loc); if (f === 'New Location') return f; const s = f.split(',')[0].trim(); return s.length > 35 ? s.slice(0, 33) + '…' : s; })();
        const dirInfo      = loopType !== 'gate' ? (fv(loc, 'directions') || loc.directions || '') : '';
        const dateStr      = loc.status_date ? new Date(loc.status_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
        const fwDone       = fwDoneFor(loc);

        return (
          <div key={loc.id} style={{ border: `2px solid ${discrepancy ? '#f59e0b' : isComplete ? '#16a34a' : '#e5e7eb'}`, borderRadius: 10, marginBottom: 14, overflow: 'hidden', background: isComplete ? '#f0fdf4' : '#fff' }}>
            {/* ── Header ── */}
            <div onClick={() => setExpanded(e => ({ ...e, [loc.id]: !isOpen }))}
              style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: isComplete ? '#dcfce7' : '#f9fafb', borderBottom: isOpen ? '1px solid #e5e7eb' : 'none' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {isComplete ? '✅' : discrepancy ? '⚠️' : '📍'}
                  <span style={{ fontSize: 11, fontWeight: 800, color: loopMeta.color, background: loopMeta.bg, borderRadius: 4, padding: '1px 7px', textTransform: 'uppercase' }}>{loopMeta.label}</span>
                  {dirInfo && <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', borderRadius: 4, padding: '1px 6px' }}>{dirInfo}</span>}
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortLoc}</span>
                </div>
                {!isOpen && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                    {bid > 0 && <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>{bid} loop{bid !== 1 ? 's' : ''}</span>}
                    {fwDone.linked > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: fwDone.done >= bid && bid > 0 ? '#15803d' : fwDone.done > 0 ? '#b45309' : '#6b7280' }}>{fwDone.done > 0 ? '✅ ' : ''}{fwDone.done} of {bid} done in Fieldwire</span>}
                    {discrepancy && <span style={{ fontSize: 11, color: '#d97706', fontWeight: 700 }}>⚠️ Confirmed: {confirmed} vs Bid: {bid}</span>}
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{install}</span>
                    {dateStr && <span style={{ fontSize: 11, color: '#6b7280' }}>📅 {dateStr}</span>}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ padding: 16 }}>

                {/* Loop Type */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Loop Type</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Object.entries(LOOP_TYPE_META).map(([key, meta]) => (
                      <button key={key} onClick={() => setField(loc.id, 'loop_type', key)}
                        style={{ padding: '6px 18px', borderRadius: 6, border: `2px solid ${loopType === key ? meta.color : '#e5e7eb'}`, background: loopType === key ? meta.bg : '#f9fafb', color: loopType === key ? meta.color : '#6b7280', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                        {meta.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Location + Map */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Location</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={fv(loc, 'street1')} onChange={e => setField(loc.id, 'street1', e.target.value)}
                      placeholder="e.g. Main St & 1st Ave"
                      style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                    <button onClick={() => setMapModal(loc.id)}
                      style={{ padding: '6px 14px', background: (loc.lat || drafts[loc.id]?.lat) ? '#eff6ff' : '#f3f4f6', border: `1px solid ${(loc.lat || drafts[loc.id]?.lat) ? '#2563eb' : '#e5e7eb'}`, borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', color: (loc.lat || drafts[loc.id]?.lat) ? '#1d4ed8' : '#374151', fontWeight: 600 }}>
                      📍 {(loc.lat || drafts[loc.id]?.lat) ? 'Pin Set ✓' : 'Set Pin'}
                    </button>
                  </div>
                </div>

                {/* ── GATE LOOPS ── */}
                {loopType === 'gate' && (<>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Gate Loop Sizes</div>
                    {gRows.map(row => (
                      <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>Qty</div>
                          <input type="text" inputMode="numeric" value={row.qty ?? 1}
                            onChange={e => updateGateSize(loc.id, row.id, 'qty', e.target.value)}
                            style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>Size</div>
                          <input value={row.size ?? ''} placeholder="e.g. 4'x12'"
                            onChange={e => updateGateSize(loc.id, row.id, 'size', e.target.value)}
                            style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <button onClick={() => deleteGateSize(loc.id, row.id)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 18, cursor: 'pointer', paddingTop: 14 }}>×</button>
                      </div>
                    ))}
                    <button onClick={() => addGateSize(loc.id)}
                      style={{ fontSize: 12, color: '#2563eb', background: 'none', border: '1px dashed #bfdbfe', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', marginTop: 4 }}>+ Add Size</button>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Linear Feet of Home Runs</div>
                    <input type="text" inputMode="decimal" value={fv(loc, 'linear_feet_home_runs')} onChange={e => setField(loc.id, 'linear_feet_home_runs', e.target.value)}
                      placeholder="0"
                      style={{ width: 160, border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                  </div>
                </>)}

                {/* ── TRAFFIC LOOPS ── */}
                {loopType === 'traffic' && (<>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Directions &amp; Quantities</div>
                    {tRows.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '22px 58px repeat(3, minmax(38px, 1fr)) auto auto' : '26px 70px 1fr 1fr 1fr auto auto', gap: narrow ? 4 : 6, marginBottom: 6, padding: '0 7px' }}>
                        {['', 'Dir', narrow ? 'Left' : 'Left Turn', 'Thru', narrow ? 'Adv' : 'Advance', fwLink && fwLink.linked ? (narrow ? 'FW' : 'Fieldwire') : '', ''].map((h, i) => (
                          <div key={i} style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>{h}</div>
                        ))}
                      </div>
                    )}
                    {tRows.map((row, ri) => (
                      <div key={row.id}
                        onDragOver={e => { if (dragDir && dragDir.locId === loc.id) e.preventDefault(); }}
                        onDrop={e => { e.preventDefault(); if (dragDir && dragDir.locId === loc.id) moveDir(loc.id, dragDir.id, ri); setDragDir(null); }}
                        style={{ display: 'grid', gridTemplateColumns: narrow ? '22px 58px repeat(3, minmax(38px, 1fr)) auto auto' : '26px 70px 1fr 1fr 1fr auto auto', gap: narrow ? 4 : 6, marginBottom: 6, alignItems: 'center',
                          padding: '5px 7px', borderRadius: 12, background: dragDir && dragDir.id === row.id ? '#e0f2fe' : '#f8fafc', border: '1px solid #e2e8f0', opacity: dragDir && dragDir.id === row.id ? 0.6 : 1 }}>
                        <div draggable onDragStart={e => { setDragDir({ locId: loc.id, id: row.id }); try { e.dataTransfer.effectAllowed = 'move'; } catch {} }} onDragEnd={() => setDragDir(null)}
                          title="Drag to move — or use the arrows" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'grab', userSelect: 'none', lineHeight: 1 }}>
                          <button type="button" onClick={() => moveDir(loc.id, row.id, ri - 1)} disabled={ri === 0} title="Move up" style={{ background: 'none', border: 'none', padding: 0, fontSize: 10, color: ri === 0 ? '#cbd5e1' : '#64748b', cursor: ri === 0 ? 'default' : 'pointer' }}>▲</button>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>⠿</span>
                          <button type="button" onClick={() => moveDir(loc.id, row.id, ri + 1)} disabled={ri === tRows.length - 1} title="Move down" style={{ background: 'none', border: 'none', padding: 0, fontSize: 10, color: ri === tRows.length - 1 ? '#cbd5e1' : '#64748b', cursor: ri === tRows.length - 1 ? 'default' : 'pointer' }}>▼</button>
                        </div>
                        <select value={row.direction || ''} onChange={e => updateTrafficDir(loc.id, row.id, 'direction', e.target.value)}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 4px', fontSize: 13, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff' }}>
                          {/* Blank is fine — shown as a dash (Pat, Oct 7 2026). */}
                          <option value="">—</option>
                          {LOOP_DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                        {['qty_left_turn', 'qty_thru', 'qty_advance'].map(field => (
                          <input key={field} type="text" inputMode="numeric" value={row[field] ?? 0}
                            onChange={e => updateTrafficDir(loc.id, row.id, field, e.target.value)}
                            style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: narrow ? '6px 2px' : '6px 8px', fontSize: narrow ? 15 : 13, fontWeight: narrow ? 700 : 400, textAlign: narrow ? 'center' : 'left', width: '100%', boxSizing: 'border-box' }} />
                        ))}
                        {/* This direction's Fieldwire task: its number + status (opens in the drawer), or link / make one. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {row.fw_task_id ? (<>
                            <button onClick={() => setFwOpen(row.fw_task_id)} title="Open the Fieldwire task"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: narrow ? 2 : 4, background: '#fff', border: `1px solid ${(row.fw_task && row.fw_task.color) || '#bfdbfe'}`, borderRadius: 999, padding: narrow ? '2px 5px' : '3px 9px', fontSize: narrow ? 10.5 : 11.5, fontWeight: 700, color: '#1e40af', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {narrow ? '📍' : '📍 '}{row.fw_task && row.fw_task.number ? '#' + row.fw_task.number : (narrow ? 'FW' : 'Fieldwire')}
                              {row.fw_task && row.fw_task.status ? (narrow
                                ? <span title={row.fw_task.status} style={{ width: 7, height: 7, borderRadius: 4, background: row.fw_task.color || '#6b7280', display: 'inline-block', marginLeft: 2 }} />
                                : <span style={{ color: row.fw_task.color || '#6b7280' }}>· {row.fw_task.status}</span>) : null}
                            </button>
                            {isAdminZone && <button onClick={() => unlinkDirFromFieldwire(loc, row)} title="Take this direction off that task (the task stays in Fieldwire)"
                              style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0, cursor: 'pointer' }}>⨯</button>}
                          </>) : (isAdminZone && fwLink && fwLink.linked && (
                            <button onClick={() => setFwPick({ loc, row })} disabled={!!fwBusy[row.id]} title="Put this direction on a Fieldwire task — one the crew already made, or a new one"
                              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 5, color: '#1e40af', fontSize: 11, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {fwBusy[row.id] ? '…' : (narrow ? '📍+' : '📍 Fieldwire task')}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => deleteTrafficDir(loc.id, row.id)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 18, cursor: 'pointer' }}>×</button>
                      </div>
                    ))}
                    <button onClick={() => addTrafficDir(loc.id)}
                      style={{ fontSize: 12, color: '#059669', background: 'none', border: '1px dashed #6ee7b7', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', marginTop: 4 }}>+ Add Direction</button>
                    {tRows.length > 2 && (
                      <button onClick={() => groupDirs(loc.id)} title="Put the same directions together: EB, then WB, NB, SB"
                        style={{ fontSize: 12, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', marginTop: 4, marginLeft: 8 }}>↕ Group by direction</button>
                    )}
                    {tRows.length > 0 && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#374151' }}>
                        Total bid loops: <strong>{bid}</strong>
                      </div>
                    )}
                  </div>
                </>)}

                {/* ── COUNTER LOOPS ── several spots, each a row like the traffic directions (Pat, Oct 7 2026) */}
                {loopType === 'speed' && (<>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Counter Locations &amp; Quantities</div>
                    {tRows.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 70px 80px auto auto', gap: 6, marginBottom: 6 }}>
                        {['Location', 'Dir', 'Thru', fwLink && fwLink.linked ? 'Fieldwire' : '', ''].map((h, i) => (
                          <div key={i} style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>{h}</div>
                        ))}
                      </div>
                    )}
                    {tRows.map(row => (
                      <div key={row.id} style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(90px, 1fr) 54px 44px auto auto' : 'minmax(160px, 1fr) 70px 80px auto auto', gap: narrow ? 4 : 6, marginBottom: 6, alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
                          <input value={row.spot_label || ''} onChange={e => updateTrafficDir(loc.id, row.id, 'spot_label', e.target.value)} placeholder="e.g. Vista Blvd n/o Baring"
                            style={{ flex: 1, minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                          <button onClick={() => setRowMap({ loc, row })} title={row.lat ? 'Pin set — tap to move it' : 'Set a map pin for this spot'}
                            style={{ padding: '4px 8px', background: row.lat ? '#eff6ff' : '#f3f4f6', border: `1px solid ${row.lat ? '#2563eb' : '#e5e7eb'}`, borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
                            📍{row.lat ? ' ✓' : ''}
                          </button>
                        </div>
                        <select value={row.direction || ''} onChange={e => updateTrafficDir(loc.id, row.id, 'direction', e.target.value)}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 4px', fontSize: 13, fontWeight: 700, color: '#d97706', background: '#fffbeb' }}>
                          <option value="">—</option>
                          {LOOP_DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <input type="text" inputMode="numeric" value={row.qty_thru ?? 0} onChange={e => updateTrafficDir(loc.id, row.id, 'qty_thru', e.target.value)}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: narrow ? '6px 2px' : '6px 8px', fontSize: narrow ? 15 : 13, fontWeight: narrow ? 700 : 400, textAlign: narrow ? 'center' : 'left', width: '100%', boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {row.fw_task_id ? (<>
                            <button onClick={() => setFwOpen(row.fw_task_id)} title="Open the Fieldwire task"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: narrow ? 2 : 4, background: '#fff', border: `1px solid ${(row.fw_task && row.fw_task.color) || '#bfdbfe'}`, borderRadius: 999, padding: narrow ? '2px 5px' : '3px 9px', fontSize: narrow ? 10.5 : 11.5, fontWeight: 700, color: '#1e40af', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {narrow ? '📍' : '📍 '}{row.fw_task && row.fw_task.number ? '#' + row.fw_task.number : (narrow ? 'FW' : 'Fieldwire')}
                              {row.fw_task && row.fw_task.status ? (narrow
                                ? <span title={row.fw_task.status} style={{ width: 7, height: 7, borderRadius: 4, background: row.fw_task.color || '#6b7280', display: 'inline-block', marginLeft: 2 }} />
                                : <span style={{ color: row.fw_task.color || '#6b7280' }}>· {row.fw_task.status}</span>) : null}
                            </button>
                            {isAdminZone && <button onClick={() => unlinkDirFromFieldwire(loc, row)} title="Take this spot off that task (the task stays in Fieldwire)"
                              style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0, cursor: 'pointer' }}>⨯</button>}
                          </>) : (isAdminZone && fwLink && fwLink.linked && (
                            <button onClick={() => setFwPick({ loc, row })} disabled={!!fwBusy[row.id]} title="Put this spot on a Fieldwire task — one the crew already made, or a new one"
                              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 5, color: '#1e40af', fontSize: 11, padding: '3px 8px', cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {fwBusy[row.id] ? '…' : (narrow ? '📍+' : '📍 Fieldwire task')}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => deleteTrafficDir(loc.id, row.id)} title="Remove this spot"
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 18, cursor: 'pointer' }}>×</button>
                      </div>
                    ))}
                    <button onClick={() => addCounterSpot(loc.id)}
                      style={{ fontSize: 12, color: '#d97706', background: 'none', border: '1px dashed #fcd34d', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', marginTop: 4 }}>+ Add Location</button>
                    {tRows.length > 0 && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#374151' }}>
                        Total counter loops: <strong>{bid}</strong>
                      </div>
                    )}
                  </div>
                  {/* A counter linked the old way (the task on the whole location) still shows, so it can be taken off. */}
                  {loc.fw_task_id && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Fieldwire (whole counter)</span>
                      <button onClick={() => setFwOpen(loc.fw_task_id)} title="Open the Fieldwire task"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: `1px solid ${(loc.fw_task && loc.fw_task.color) || '#bfdbfe'}`, borderRadius: 999, padding: '3px 9px', fontSize: 11.5, fontWeight: 700, color: '#1e40af', cursor: 'pointer' }}>
                        📍 {loc.fw_task && loc.fw_task.number ? '#' + loc.fw_task.number : 'Fieldwire'}
                        {loc.fw_task && loc.fw_task.status ? <span style={{ color: loc.fw_task.color || '#6b7280' }}>· {loc.fw_task.status}</span> : null}
                      </button>
                      {isAdminZone && <button onClick={() => unlinkCounterFromFieldwire(loc)} title="Take this counter off that task (the task stays in Fieldwire)"
                        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0, cursor: 'pointer' }}>⨯</button>}
                    </div>
                  )}
                </>)}

                {/* ── SHARED FIELDS ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Installation</div>
                    <select value={fv(loc, 'installation') || 'hot_sealed'} onChange={e => setField(loc.id, 'installation', e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}>
                      <option value="hot_sealed">Hot Sealed</option>
                      <option value="in_sand">In Sand</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Status</div>
                    <select value={fv(loc, 'status') || 'upcoming'} onChange={e => setField(loc.id, 'status', e.target.value)}
                      style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13, color: isComplete ? '#16a34a' : '#d97706', fontWeight: 600 }}>
                      <option value="upcoming">Upcoming</option>
                      <option value="complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Date</div>
                    <input type="date" value={fv(loc, 'status_date') || ''} onChange={e => setField(loc.id, 'status_date', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13 }} />
                  </div>
                </div>

                {/* Confirmed total */}
                <div style={{ marginBottom: 14, padding: 12, background: discrepancy ? '#fffbeb' : '#f9fafb', border: `1px solid ${discrepancy ? '#f59e0b' : '#e5e7eb'}`, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Field Supervisor — Confirmed Total Cut</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input type="text" inputMode="numeric" value={fv(loc, 'confirmed_total') ?? (loc.confirmed_total ?? '')}
                      onChange={e => setField(loc.id, 'confirmed_total', e.target.value)}
                      placeholder="Enter after install"
                      style={{ width: 120, border: `1px solid ${discrepancy ? '#f59e0b' : '#e5e7eb'}`, borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                    {bid > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>Bid total: <strong>{bid}</strong></span>}
                    {discrepancy && <span style={{ fontSize: 12, fontWeight: 700, color: '#d97706' }}>⚠️ Discrepancy: {Math.abs(confirmed - bid)} loop{Math.abs(confirmed - bid) !== 1 ? 's' : ''} {confirmed > bid ? 'over' : 'under'}</span>}
                    {hasConfirmed && !discrepancy && <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>✓ Matches bid</span>}
                  </div>
                </div>

                {/* Field Notes */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Field Notes</div>
                  <textarea value={fv(loc, 'field_notes') || ''} onChange={e => setField(loc.id, 'field_notes', e.target.value)}
                    placeholder="Notes about this location…" rows={3}
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
                </div>

                {hasDraft(loc.id) && (
                  <div style={{ marginBottom: 14 }}>
                    <button onClick={() => saveLoc(loc)} disabled={saving[loc.id]}
                      style={{ padding: '7px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: saving[loc.id] ? 0.6 : 1 }}>
                      {saving[loc.id] ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                )}

                {/* Photos */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Photos</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {photos.map(p => (
                      <AttachThumb key={p.id} size={72} src={photoUrl(p.filename)} name={p.original_name || p.filename}
                        href={photoUrl(p.filename)} onRemove={() => deletePhoto(loc.id, p.id)} />
                    ))}
                    <AttachButton multiple icon="📷" label="Photo / File" style={{ padding: '8px 10px', fontSize: 12 }}
                      onFiles={fs => fs.forEach(f => uploadPhoto(loc.id, f))} />
                  </div>
                </div>

                {/* Remove lives at the bottom of the opened location (Pat, Oct 9 2026) — not on the row, where it was a misclick away. */}
                {isAdminZone && (
                  <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={e => { e.stopPropagation(); deleteLoc(loc.id); }}
                      style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', background: '#fff', border: '1px solid #fecaca', borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}>✕ Remove this location</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {isAdminZone && (
        <button onClick={addLocation} disabled={addingLoc}
          style={{ width: '100%', padding: '10px', border: '2px dashed #d1d5db', borderRadius: 8, background: 'none', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
          {addingLoc ? 'Adding…' : '+ Add Location'}
        </button>
      )}
    </div>
  );
}

export default function AdminBidDetail() { return <AdminBidDetailInner />; }

// ── Extra Work / Change Orders ────────────────────────────────────────────────
const EXTRA_WORK_CATEGORIES = ['Labor', 'Material', 'Equipment', 'Other'];
// A change order tracked BY THE DAY (Pat, Sep 2026 — step 5 of the Price Book work) has
// two more groups than the old four: Common Items (pole hold, loops, a drill day…) and
// Purchase Orders. The print walks this list; a group with nothing in it is skipped, so
// an old change order prints exactly as it did.
const CO_PRINT_CATEGORIES = ['Common Items', 'Labor', 'Material', 'Equipment', 'Purchase Orders', 'Other'];
const DAY_CAT_TO_CO = { quoted: 'Common Items', labor: 'Labor', material: 'Material', equipment: 'Equipment', po: 'Purchase Orders', other: 'Other' };
// The change order's priced lines, worked out from its days: the same item on several
// days rolls up into one line (quantity summed) at the price the tracker already gives
// it, so the total here can never disagree with the tracker's. Everything downstream —
// subtotal, OH&P, the PDF, the email, print-and-lock — reads these like the old lines.
function coLinesFromDays(data) {
  const out = new Map();
  (data?.days || []).forEach(d => (d.lines || []).forEach(l => {
    const category = DAY_CAT_TO_CO[l.category] || 'Other';
    const qty = Number(l.quantity) > 0 ? Number(l.quantity) : 1;
    const amount = Number(l.price) || 0;
    const unitCost = Math.round((amount / qty) * 10000) / 10000;
    const key = [category, String(l.description || '').trim().toLowerCase(), l.unit || '', unitCost].join('|');
    const cur = out.get(key) || { id: `day:${key}`, category, description: l.description || '', unit: l.unit || '', unit_cost: unitCost, quantity: 0, _fromDays: true,
      ...(l.category === 'labor' ? { trade: l.trade_label || null, ref_id: l.ref_type === 'crew' ? l.ref_id : null, rate_typed: !!l.rate_typed } : {}) };
    cur.quantity = Math.round((cur.quantity + qty) * 10000) / 10000;
    out.set(key, cur);
  }));
  return [...out.values()];
}
// Labor rates on this change order (Ryan, Oct 26 2026): each person's hours bill at their trade's
// Rate Sheet rate; the office can type another rate here (empty = back to the trade rate). It is the
// same rate on every day of theirs on this card. The printout shows trades, not names.
function CoLaborRates({ ticket, sjId, lines, onError }) {
  const people = [];
  for (const l of lines) {
    const p = people.find(x => x.ref_id === l.ref_id);
    if (p) { p.hours += Number(l.quantity) || 0; if (Number(l.unit_cost) !== p.rate) p.mixed = true; }
    else people.push({ ref_id: l.ref_id, name: l.description, trade: l.trade, hours: Number(l.quantity) || 0, rate: Number(l.unit_cost) || 0, typed: l.rate_typed });
  }
  const [draft, setDraft] = React.useState({});
  if (!people.length) return null;
  const base = `/sub-jobs/${sjId}/extra-work/${ticket.id}`;
  async function save(p, value) {
    const v = String(value ?? '').trim();
    if (v !== '' && Number(v) === p.rate && !p.mixed) return;
    try {
      await apiFetch(`${base}/labor-rate`, { method: 'POST', body: JSON.stringify({ ref_id: p.ref_id, description: p.name, unit_price: v === '' ? null : Number(v) }) });
      setDraft(d => { const n = { ...d }; delete n[p.ref_id]; return n; });
      window.dispatchEvent(new CustomEvent('titan:ew-days-changed', { detail: { base, from: 'co-labor-rates' } }));
    } catch (e) { onError && onError(e?.error || e?.message || 'That rate did not save.'); }
  }
  const locked = !!ticket.locked_at;
  return (
    <div style={{ marginTop: 10, border: '1px solid #e0e7ff', background: '#f8faff', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#3730a3', textTransform: 'uppercase', letterSpacing: 0.4 }}>Labor rates on this change order</div>
      <div style={{ fontSize: 11.5, color: '#6b7280', margin: '2px 0 6px' }}>Each person bills at their trade's Rate Sheet rate — type another rate to change it here (empty = back to the trade rate). The printed change order shows the trade, not the name.</div>
      {people.map(p => (
        <div key={p.ref_id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 70px 110px', gap: 8, alignItems: 'center', padding: '3px 0', fontSize: 13 }}>
          <span style={{ fontWeight: 700, color: '#111' }}>{p.name}</span>
          <span style={{ color: p.trade ? '#374151' : '#b45309' }}>{p.trade || 'no trade set'}</span>
          <span style={{ color: '#6b7280', textAlign: 'right' }}>{Math.round(p.hours * 100) / 100} hr</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#6b7280' }}>$</span>
            <input type="number" step="any" disabled={locked} value={draft[p.ref_id] ?? (p.mixed ? '' : p.rate)} placeholder={p.mixed ? 'mixed' : ''}
              onChange={e => setDraft(d => ({ ...d, [p.ref_id]: e.target.value }))} onBlur={e => save(p, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={{ width: 70, border: `1px solid ${p.typed ? '#a5b4fc' : '#d1d5db'}`, borderRadius: 6, padding: '3px 6px', fontSize: 13, textAlign: 'right', background: p.typed ? '#eef2ff' : '#fff' }} />
            <span style={{ color: '#6b7280', fontSize: 12 }}>/hr</span>
          </span>
        </div>
      ))}
    </div>
  );
}
// What the customer reads (Ryan, Oct 26 2026): labor by TRADE, not by name — "Electrician 36 hr",
// "Operator 20 hr" — one line per trade and rate. The office still sees the names on the card.
function coPrintLines(lines) {
  const out = [], byTrade = new Map();
  for (const l of lines || []) {
    if (l.category !== 'Labor' || !l.trade) { out.push(l); continue; }
    const key = `${l.trade}|${Number(l.unit_cost) || 0}`;
    let g = byTrade.get(key);
    if (!g) { g = { ...l, id: 'trade:' + key, description: l.trade, quantity: 0 }; byTrade.set(key, g); out.push(g); }
    g.quantity = Math.round((g.quantity + (Number(l.quantity) || 0)) * 10000) / 10000;
  }
  return out;
}
const EW_STATUS = {
  draft:     { label: 'Draft',     color: '#6b7280', bg: '#f3f4f6' },
  submitted: { label: 'Submitted', color: '#1d4ed8', bg: '#eff6ff' },
  approved:  { label: 'Approved',  color: '#15803d', bg: '#f0fdf4' },
  rejected:  { label: 'Rejected',  color: '#b91c1c', bg: '#fef2f2' },
};
const ewMoney = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ewCO = t => `CO-${String(t.co_number).padStart(2, '0')}`;

// Client-side Change Order PDF (same page-fit approach as the bid proposal).
// The print window is an about:blank document, so a linked logo depends on the
// origin resolving and the image loading there — which silently failed and printed
// a bare header. Read the logo once and inline it as a data URI so the letterhead
// is part of the document itself and always prints.
let _logoDataUrl = null;
async function titanLogoDataUrl() {
  if (_logoDataUrl) return _logoDataUrl;
  try {
    const res = await fetch(`${window.location.origin}/titan-logo.jpg`, { cache: 'force-cache' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    _logoDataUrl = await new Promise((ok, fail) => {
      const r = new FileReader();
      r.onload = () => ok(r.result);
      r.onerror = fail;
      r.readAsDataURL(blob);
    });
    return _logoDataUrl;
  } catch { return null; }
}

// The change order as data — every number the page shows, computed once here so the
// server-drawn PDF (POST /extra-work/:id/pdf and /email) and the browser print fallback
// agree. Rows: 'cat' headings, 'line's, and per-category 'subtotal's for a T&M ticket;
// plain lines for a Unit Price ticket (its markups are already inside each unit price).
// The markups a change order is priced with (Pat / Chad, Sep 28 2026): the CO's own
// saved set, else the job's bid where the bid has real numbers, else the company
// defaults. A bid that came in with 0% tax / 0% profit (an old-quote import — its
// prices already carried the markups) must not price NEW work at 0%: 2631's unit-price
// CO-02 was quietly charging no sales tax on material for exactly that reason.
export const CO_PRICING_DEFAULTS = { sales_tax_pct: 8.265, overhead_material_pct: 0, overhead_labor_pct: 0, profit_pct: 20, supervision_pct: 6, labor_rate: 95 };
export function coPricing(ticket, bid) {
  let saved = null;
  try { saved = ticket?.pricing ? JSON.parse(ticket.pricing) : null; } catch { saved = null; }
  if (saved && typeof saved === 'object') return { ...CO_PRICING_DEFAULTS, ...saved };
  const b = bid || {};
  const pos = v => (Number(v) > 0 ? Number(v) : null);
  return {
    sales_tax_pct: pos(b.sales_tax_pct) ?? CO_PRICING_DEFAULTS.sales_tax_pct,
    overhead_material_pct: Number(b.overhead_material_pct) || 0,
    overhead_labor_pct: Number(b.overhead_labor_pct) || 0,
    profit_pct: pos(b.profit_pct) ?? CO_PRICING_DEFAULTS.profit_pct,
    supervision_pct: b.supervision_pct != null && b.supervision_pct !== '' ? Number(b.supervision_pct) || 0 : CO_PRICING_DEFAULTS.supervision_pct,
    labor_rate: pos(b.labor_rate) ?? CO_PRICING_DEFAULTS.labor_rate,
  };
}
// A T&M change order's money: lines at cost, sales tax on the Material lines (parts
// at cost, before tax — a PO line is already what was paid), then OH&P on the lot.
export function coTmTotals(ticketLines, ticket, pricing) {
  const ext = l => (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
  const ls = ticketLines || [];
  const subtotal = ls.reduce((s, l) => s + ext(l), 0);
  const materialSub = ls.filter(l => l.category === 'Material').reduce((s, l) => s + ext(l), 0);
  const taxPct = Number(pricing?.sales_tax_pct) || 0;
  const tax = materialSub * taxPct / 100;
  const ohpPct = Number(ticket?.oh_p_pct) || 0;
  const ohp = (subtotal + tax) * ohpPct / 100;
  return { subtotal, materialSub, taxPct, tax, ohpPct, ohp, total: subtotal + tax + ohp };
}
function buildChangeOrderData(ticket, ticketLines, project, opts = {}) {
  const isUnitPrice = ticket.type === 'unit_price';
  if (!isUnitPrice) ticketLines = coPrintLines(ticketLines);
  const upItems = isUnitPrice ? (opts.upItems || (() => { try { return ticket.up_items ? JSON.parse(ticket.up_items) : []; } catch { return []; } })()) : [];
  const upBid = opts.bid || {};
  const ext = l => (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
  let subtotal = 0; const rows = [];
  if (isUnitPrice) {
    for (const it of upItems) {
      const unitPrice = itemEffectiveUnitPrice(it, upBid); const lineTotal = itemEffectiveTotal(it, upBid); subtotal += lineTotal;
      rows.push({ kind: 'line', description: it.description || '', quantity: it.quantity, unit: it.unit || '', unitCost: unitPrice, amount: lineTotal });
    }
  } else {
    for (const cat of CO_PRINT_CATEGORIES) {
      const ls = (ticketLines || []).filter(l => l.category === cat && ((l.description || '').trim() || l.quantity || l.unit_cost));
      if (!ls.length) continue;
      rows.push({ kind: 'cat', description: cat });
      let sub = 0;
      for (const l of ls) { const a = ext(l); sub += a; rows.push({ kind: 'line', description: l.description || '', quantity: l.quantity, unit: l.unit || '', unitCost: Number(l.unit_cost) || 0, amount: a }); }
      subtotal += sub; rows.push({ kind: 'subtotal', description: cat, amount: sub });
    }
  }
  const tm = isUnitPrice ? null : coTmTotals(ticketLines, ticket, upBid);
  const taxPct = tm ? tm.taxPct : 0, tax = tm ? tm.tax : 0;
  const ohpPct = tm ? tm.ohpPct : 0;
  const ohp = tm ? tm.ohp : 0;
  const company = project?.awarded_by_company || project?.awarded_by_name || project?.client_name || 'Owner / General Contractor';
  const attn = project?.awarded_by_company && project?.awarded_by_name && project.awarded_by_name !== project.awarded_by_company ? project.awarded_by_name : '';
  return {
    number: ewCO(ticket), title: ticket.title || 'Extra Work', jobNo: project?.project_number || '', jobName: project?.name || '',
    dateStr: ticket.work_date ? new Date(ticket.work_date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    to: { company, attn, addr: [project?.awarded_by_address1, project?.awarded_by_address2].filter(Boolean), owner: project?.awarded_by_company && project?.client_name ? project.client_name : '' },
    reason: ticket.reason || '', description: ticket.description || '', isUnitPrice, rows, subtotal, taxPct, tax, ohpPct, ohp, total: subtotal + tax + ohp,
    acceptedBy: company, rev: opts.rev || null,
  };
}

async function generateChangeOrderPDF(ticket, ticketLines, project, opts = {}) {
  const win = window.open('', '_blank');            // opened on the click gesture
  // Everything typed by a person is text on this page, never markup.
  ticket = escDeep(ticket); ticketLines = escDeep(ticket.type === 'unit_price' ? ticketLines : coPrintLines(ticketLines)); project = escDeep(project);
  opts = { ...opts, upItems: opts.upItems ? escDeep(opts.upItems) : opts.upItems };
  const logoUrl = (await titanLogoDataUrl()) || `${window.location.origin}/titan-logo.jpg`;
  const dateStr = ticket.work_date
    ? new Date(ticket.work_date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // Unit Price change orders price through the bid engine (markups already baked
  // into each unit price), so they print as plain bid-style rows with no OH&P line.
  const isUnitPrice = ticket.type === 'unit_price';
  const upItems = isUnitPrice
    ? (opts.upItems || (() => { try { return ticket.up_items ? JSON.parse(ticket.up_items) : []; } catch { return []; } })())
    : [];
  const upBid = opts.bid || {};
  const ext = l => (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
  let subtotal = 0;
  const upRows = isUnitPrice ? upItems.map(it => {
    const unitPrice = itemEffectiveUnitPrice(it, upBid);
    const lineTotal = itemEffectiveTotal(it, upBid);
    subtotal += lineTotal;
    return `<tr>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;">${(it.description || '').replace(/</g, '&lt;')}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:center;">${it.quantity != null ? it.quantity : ''}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:center;color:#6b7280;">${it.unit || ''}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right;">${ewMoney(unitPrice)}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:600;">${ewMoney(lineTotal)}</td></tr>`;
  }).join('') : '';
  const catBlocks = isUnitPrice ? '' : CO_PRINT_CATEGORIES.map(cat => {
    const ls = (ticketLines || []).filter(l => l.category === cat && ((l.description || '').trim() || l.quantity || l.unit_cost));
    if (!ls.length) return '';
    const sub = ls.reduce((s, l) => s + ext(l), 0); subtotal += sub;
    const rows = ls.map(l => `<tr>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;">${l.description || ''}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:center;">${l.quantity != null ? l.quantity : ''}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:center;color:#6b7280;">${l.unit || ''}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right;">${ewMoney(l.unit_cost)}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:600;">${ewMoney(ext(l))}</td></tr>`).join('');
    return `<tr><td colspan="5" style="padding:9px 8px 4px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#1e3a5f;">${cat}</td></tr>${rows}
      <tr><td colspan="4" style="padding:4px 8px;text-align:right;font-size:11px;color:#6b7280;">${cat} subtotal</td><td style="padding:4px 8px;text-align:right;font-weight:700;">${ewMoney(sub)}</td></tr>`;
  }).join('');
  const tmT = isUnitPrice ? null : coTmTotals(ticketLines, ticket, opts.bid || {});
  const tax = tmT ? tmT.tax : 0, taxPct = tmT ? tmT.taxPct : 0;
  const ohp = tmT ? tmT.ohp : 0;
  const total = subtotal + tax + ohp;
  const jobNo = project?.project_number || '';
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${jobDocFileName({ job: project, type: 'Change Order', number: ticket.co_number, description: ticket.title, rev: opts.rev })}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { font-family: Arial, sans-serif; font-size: 12.5px; color: #111827; background: #e9edf2; }
    .toolbar { width: 8.5in; max-width: 96%; margin: 14px auto 0; text-align: right; }
    /* Flex column so the signature + footer can be pushed to the bottom of the
       page (margin-top:auto on .tail) instead of stacking under the totals. */
    .sheet { width: 8.5in; min-height: 11in; padding: 0.5in 0.55in; margin: 12px auto 28px; background: #fff; box-shadow: 0 2px 14px rgba(0,0,0,0.18); display: flex; flex-direction: column; }
    #fit { display: flex; flex-direction: column; flex: 1 1 auto; }
    .tail { margin-top: auto; }
    .avoid { break-inside: avoid; }
    @page { size: letter; margin: 0; }
    /* Keep a page-height sheet when printing, or the bottom block rides back up. */
    @media print { html, body { background:#fff; } .no-print{display:none!important;} .sheet{width:auto;min-height:10.4in;margin:0;box-shadow:none;} body{-webkit-print-color-adjust:exact;print-color-adjust:exact;} }
  </style></head><body>
  <div class="toolbar no-print"><button onclick="window.print()" style="padding:10px 24px;background:#1e3a5f;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;">🖨 Print / Save as PDF</button></div>
  <div class="sheet"><div id="fit">
    <div style="padding-bottom:12px;border-bottom:3px solid #1e3a5f;margin-bottom:16px;display:flex;align-items:center;">
      <img src="${logoUrl}" alt="Titan Electrical Contracting" style="height:78px;width:auto;object-fit:contain;display:block;"
        onerror="this.style.display='none';var f=document.getElementById('logo-fallback');if(f)f.style.display='block';"/>
      <div id="logo-fallback" style="display:none;font-size:19px;font-weight:800;color:#1e3a5f;letter-spacing:.5px;">TITAN ELECTRICAL CONTRACTING, INC.</div>
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px;">
      <div><div style="font-size:22px;font-weight:700;color:#1e3a5f;">CHANGE ORDER</div>
        <div style="font-size:15px;font-weight:700;margin-top:3px;">${ticket.title || 'Extra Work'}</div>
        <div style="font-size:12px;color:#6b7280;">${project?.name || ''}</div></div>
      <div style="text-align:right;font-size:12px;color:#374151;">
        <div style="font-size:16px;font-weight:800;color:#1e3a5f;">${ewCO(ticket)}</div>
        ${jobNo ? `<div>Job #: ${jobNo}</div>` : ''}<div>Date: ${dateStr}</div></div>
    </div>
    <div style="background:#f9fafb;border-radius:8px;border-left:4px solid #f59e0b;padding:12px 16px;margin-bottom:14px;font-size:13px;">
      ${(() => {
        // Mail-style address block for whoever this change order goes to: the
        // contractor that awarded us the work, or the owner when we're the prime.
        const company = project?.awarded_by_company || project?.awarded_by_name || project?.client_name || 'Owner / General Contractor';
        const attn = project?.awarded_by_company && project?.awarded_by_name && project.awarded_by_name !== project.awarded_by_company ? project.awarded_by_name : '';
        const addr = [project?.awarded_by_address1, project?.awarded_by_address2].filter(Boolean);
        return `<div style="font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;margin-bottom:5px;">To</div>
        <div style="font-size:13.5px;font-weight:700;color:#111827;">${company}</div>
        ${attn ? `<div style="font-size:12.5px;">Attn: ${attn}</div>` : ''}
        ${addr.map(a => `<div style="font-size:12.5px;color:#374151;">${a}</div>`).join('')}
        ${project?.awarded_by_company && project?.client_name ? `<div style="font-size:11.5px;color:#9ca3af;margin-top:4px;">Owner: ${project.client_name}</div>` : ''}`;
      })()}
      ${ticket.reason ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;"><span style="color:#6b7280;">Reason for change:</span> ${ticket.reason}</div>` : ''}
      ${ticket.description ? `<div style="margin-top:4px;">${ticket.description}</div>` : ''}
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:12.5px;">
      <thead><tr style="background:#1e3a5f;color:#fff;"><th style="padding:7px 8px;text-align:left;">Description</th><th style="padding:7px 8px;width:60px;">Qty</th><th style="padding:7px 8px;width:60px;">Unit</th><th style="padding:7px 8px;text-align:right;width:100px;">Unit Cost</th><th style="padding:7px 8px;text-align:right;width:110px;">Amount</th></tr></thead>
      <tbody>${(isUnitPrice ? upRows : catBlocks) || '<tr><td colspan="5" style="padding:14px;color:#9ca3af;text-align:center;">No line items.</td></tr>'}</tbody>
    </table>
    <div class="avoid" style="display:flex;justify-content:flex-end;margin-top:14px;">
      <div style="min-width:300px;font-size:13px;">
        <div style="display:flex;justify-content:space-between;padding:5px 8px;"><span style="color:#6b7280;">Subtotal</span><span style="font-weight:600;">${ewMoney(subtotal)}</span></div>
        ${isUnitPrice || !tax ? '' : `<div style="display:flex;justify-content:space-between;padding:5px 8px;"><span style="color:#6b7280;">Sales tax on material (${taxPct}%)</span><span style="font-weight:600;">${ewMoney(tax)}</span></div>`}
        ${isUnitPrice ? '' : `<div style="display:flex;justify-content:space-between;padding:5px 8px;"><span style="color:#6b7280;">Overhead &amp; Profit (${Number(ticket.oh_p_pct) || 0}%)</span><span style="font-weight:600;">${ewMoney(ohp)}</span></div>`}
        <div style="display:flex;justify-content:space-between;padding:10px 12px;margin-top:4px;background:#1e3a5f;border-radius:8px;color:#fff;"><span style="font-size:15px;font-weight:700;">CHANGE ORDER TOTAL</span><span style="font-size:16px;font-weight:700;">${ewMoney(total)}</span></div>
      </div>
    </div>
    <div class="tail avoid" style="padding-top:34px;display:grid;grid-template-columns:1fr 1fr;gap:40px;">
      <div><div style="border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280;">Authorized – Titan Electrical Contracting, Inc.</div></div>
      <div><div style="border-top:1px solid #374151;padding-top:6px;font-size:11px;color:#6b7280;">Accepted by – ${project?.awarded_by_company || project?.awarded_by_name || project?.client_name || 'Owner / GC'}</div></div>
    </div>
    <div class="avoid" style="margin-top:22px;padding-top:12px;border-top:3px solid #1e3a5f;font-size:12px;color:#6b7280;line-height:1.5;">
      <div style="font-size:14px;font-weight:700;color:#1e3a5f;">TITAN ELECTRICAL CONTRACTING, INC.</div>
      <div>Suite 100, 5450 Mill Street, Reno, NV 89502 &nbsp;·&nbsp; (775) 857-4500</div>
      <div>NV Licenses · Class A #90896 · Class B #69843 · Class C #69814</div>
    </div>
  </div></div>
  <script>window.addEventListener('load',function(){try{var f=document.getElementById('fit');if(!f)return;var p=10*96,h=f.scrollHeight;if(h>p+4){var s=p/h;if(s>=0.78)f.style.zoom=s;}}catch(e){}});</script>
  </body></html>`;
  win.document.write(html); win.document.close();
}

// Searchable combobox for a change-order line: Material picks from the parts
// database, Equipment from trucks/trailers/equipment. Filters as you type; a
// typed value that isn't in the list can still be used.
function LinePicker({ value, options, placeholder, onPick }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [rect, setRect] = React.useState(null);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const measure = React.useCallback(() => {
    if (inputRef.current) { const r = inputRef.current.getBoundingClientRect(); setRect({ left: r.left, top: r.bottom, width: r.width }); }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    measure();
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-linepicker-menu]'))) setOpen(false); }
    // Reposition while scrolling/resizing so the menu stays glued to the input.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    document.addEventListener('mousedown', onDoc);
    return () => { window.removeEventListener('scroll', measure, true); window.removeEventListener('resize', measure); document.removeEventListener('mousedown', onDoc); };
  }, [open, measure]);

  const ql = q.trim().toLowerCase();
  const list = options.filter(o => !ql || o.label.toLowerCase().includes(ql) || (o.sub || '').toLowerCase().includes(ql)).slice(0, 60);
  const exact = ql && options.some(o => o.label.toLowerCase() === ql);
  const inp = { width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 5, padding: '5px 7px', fontSize: 12 };
  const row = { padding: '7px 9px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f6f7f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const pick = o => { onPick(o); setOpen(false); setQ(''); };

  // Menu is portaled to <body> with fixed positioning so overflow:hidden on the
  // ticket / category cards can't clip it.
  const menu = (open && rect) ? createPortal(
    <div data-linepicker-menu style={{ position: 'fixed', top: rect.top + 2, left: rect.left, width: rect.width, zIndex: 10000, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 240, overflowY: 'auto', boxShadow: '0 10px 28px rgba(0,0,0,0.22)' }}>
      {q.trim() && !exact && (
        <div onMouseDown={() => pick({ custom: true, label: q.trim() })} style={{ ...row, color: '#1d4ed8', fontWeight: 600 }}>Use “{q.trim()}”</div>
      )}
      {list.map((o, i) => (
        <div key={i} onMouseDown={() => pick(o)} style={row}
          onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
          {o.label}{o.sub ? <span style={{ color: '#9ca3af' }}> · {o.sub}</span> : ''}
        </div>
      ))}
      {list.length === 0 && <div style={{ padding: '7px 9px', fontSize: 12, color: '#9ca3af' }}>{q.trim() ? 'No matches' : 'Start typing…'}</div>}
    </div>, document.body) : null;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input ref={inputRef} value={open ? q : (value || '')} onChange={e => { setQ(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder={value || placeholder || 'Search…'} style={inp} />
      {menu}
    </div>
  );
}

// Extra-work labor is billed by trade/role (not by named employee) at these
// default hourly rates. The rate auto-fills when a role is picked, and stays
// editable per line.
export const EW_LABOR_ROLES = [
  { role: 'Operator', rate: 95 },
  { role: 'Electrician', rate: 95 },
  { role: 'Electrical Apprentice', rate: 85 },
  { role: 'Laborer', rate: 65 },
  { role: 'PM', rate: 115 },
];

// Unit-Price change-order line editor — mirrors the trimmed bid sheet (Description ·
// Qty · Unit · Material $ · Labor Hrs · Sub $ · OH Mat % · Profit % · Round →
// (dje_standard / dje_takeoff are the stored column names — Ryan calls this money
//  subcontractors, so only the labels changed; renaming the columns would be a
//  breaking schema change for no gain.)
// Unit Price → Total) and reuses the same pricing engine (calcItem). Prices from
// the project's bid markup defaults. `items` is a plain array; onChange bubbles saves.
function UnitPriceCoTable({ items, onChange, bid, readOnly, parts = [] }) {
  const [expanded, setExpanded] = React.useState(() => new Set());   // parent _keys with their breakdown open
  const [pick, setPick] = React.useState(null);                      // which line/sub is showing the parts popover
  const inp = disabled => ({ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 5, padding: '5px 7px', fontSize: 12, background: disabled ? '#f9fafb' : '#fff', color: disabled ? '#9ca3af' : '#111' });
  const th = (extra = {}) => ({ padding: '7px 8px', fontSize: 11, fontWeight: 700, color: '#6b7280', whiteSpace: 'nowrap', ...extra });
  const update = (key, field, val) => onChange(items.map(it => it._key === key ? { ...it, [field]: val } : it));
  const numVal = v => (v === '' ? 0 : parseFloat(v) || 0);
  const colCount = readOnly ? 12 : 13;

  // ── Sub-items (a breakdown under a line — priced like the bid's sub-items) ──
  const newSub = () => ({ ...newItem(0), id: 's_' + Math.random().toString(36).slice(2) });
  const setSubs = (key, subs) => onChange(items.map(it => it._key === key ? { ...it, sub_items: subs } : it));
  const addSub = key => { const it = items.find(x => x._key === key); setSubs(key, [...(Array.isArray(it?.sub_items) ? it.sub_items : []), newSub()]); setExpanded(s => new Set(s).add(key)); };
  const updateSub = (key, subId, field, val) => { const it = items.find(x => x._key === key); setSubs(key, (it?.sub_items || []).map(s => s.id === subId ? { ...s, [field]: val } : s)); };
  const removeSub = (key, subId) => { const it = items.find(x => x._key === key); setSubs(key, (it?.sub_items || []).filter(s => s.id !== subId)); };
  const toggle = key => setExpanded(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const addRow = () => onChange([...items, newItem(items.length)]);
  const removeRow = key => onChange(items.filter(it => it._key !== key));
  const pct = (item, field, ph, onCh) => (
    <input type="number" onFocus={selectOnFocus} step="1" value={item[field] ?? ''} placeholder={ph} disabled={readOnly}
      onChange={e => onCh(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ ...inp(readOnly), width: 60, textAlign: 'right' }} />
  );
  // Fill a line/sub from a Parts Directory entry (description, material $, labor hrs, unit).
  const partFields = p => ({ description: p.description, material_cost: Number(p.material_cost) || 0, labor_hours: Number(p.labor_hours) || 0, ...(p.unit ? { unit: p.unit } : {}) });
  const applyPart = (p, key) => { onChange(items.map(it => it._key === key ? { ...it, ...partFields(p) } : it)); setPick(null); };
  const applySubPart = (p, key, subId) => { updateSub(key, subId, 'description', p.description); const it = items.find(x => x._key === key); setSubs(key, (it?.sub_items || []).map(s => s.id === subId ? { ...s, ...partFields(p) } : s)); setPick(null); };

  const plusBtn = (isOpen, onToggle) => (
    <button title="Look up from Parts Directory" onClick={e => { e.stopPropagation(); onToggle(); }}
      style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 10, border: 'none', background: isOpen ? 'var(--orange)' : '#e0e7ff', color: isOpen ? '#fff' : '#4f46e5', fontSize: 14, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: 0 }}>+</button>
  );
  const openPick = key => setPick(key);
  // One window for the whole table. The key it was opened with says where the part
  // lands — a line, or a sub-item under one.
  const applyPicked = p => {
    if (!pick) return;
    if (String(pick).startsWith('sub:')) {
      const subId = String(pick).slice(4);
      const owner = items.find(it => (it.sub_items || []).some(s => s.id === subId));
      if (owner) applySubPart(p, owner._key, subId); else setPick(null);
    } else applyPart(p, pick);
  };
  const partOptions = parts.map(p => ({
    key: p.id, label: p.description, category: p.category || '',
    sub: [p.category, p.unit, p.labor_hours ? p.labor_hours + 'h labor' : ''].filter(Boolean).join(' · '),
    price: p.material_cost, original: p,
  }));

  return (
    <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      {pick && (
        <PartsPickerModal options={partOptions} onPick={applyPicked} onClose={() => setPick(null)}
          subtitle="Pick a part to fill this line — description, material cost, labor hours and unit."
          emptyText="No parts in the directory yet." />
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 880 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              <th style={th({ textAlign: 'left', minWidth: 200 })}>Description</th>
              <th style={th({ textAlign: 'right' })}>Qty</th>
              <th style={th({ textAlign: 'left' })}>Unit</th>
              <th style={th({ textAlign: 'right' })}>Material $</th>
              <th style={th({ textAlign: 'right' })}>Labor Hrs</th>
              <th style={th({ textAlign: 'right' })}>Labor $/hr</th>
              <th style={th({ textAlign: 'right' })}>Sub $</th>
              <th style={th({ textAlign: 'right' })}>OH Mat %</th>
              <th style={th({ textAlign: 'right' })}>Profit %</th>
              <th style={th({ textAlign: 'center' })}>Round</th>
              <th style={th({ textAlign: 'right' })}>Unit Price</th>
              <th style={th({ textAlign: 'right' })}>Total</th>
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const c = calcItem(item, bid);
              const subs = Array.isArray(item.sub_items) ? item.sub_items : [];
              const hasSubs = subs.length > 0;
              const isOpen = expanded.has(item._key);
              // Subs: the main line's cost columns stay blank; the total row under the subs extends them.
              const extSub = f => subs.reduce((s, x) => s + (parseFloat(x[f]) || 0) * pricedQty(x), 0);
              const tot = v => <span style={{ fontSize: 12, fontWeight: 800, color: '#4c1d95' }}>{v ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'}</span>;
              return (
                <React.Fragment key={item._key}>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 6px', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button onClick={e => { e.stopPropagation(); toggle(item._key); }}
                        title={isOpen ? 'Collapse breakdown' : hasSubs ? `${subs.length} sub-item${subs.length !== 1 ? 's' : ''}` : 'Add cost breakdown'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 2px', color: hasSubs ? '#6366f1' : '#d1d5db', fontSize: 13, lineHeight: 1, flexShrink: 0, transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</button>
                      <input value={item.description || ''} disabled={readOnly} onChange={e => update(item._key, 'description', e.target.value)} placeholder="Description" style={{ ...inp(readOnly), minWidth: 150, flex: 1 }} />
                      {!readOnly && plusBtn(pick === item._key, () => pick === item._key ? setPick(null) : openPick(item._key))}
                    </div>
                    {!isOpen && hasSubs && <div style={{ fontSize: 9, color: '#6366f1', marginTop: 2, marginLeft: 18, whiteSpace: 'nowrap', fontWeight: 600 }}>{subs.length} sub · {fmt$(subs.reduce((s, x) => s + calcItem(x, bid).selling * pricedQty(x), 0))}</div>}
                  </td>
                  <td style={{ padding: '4px 6px' }}><input type="number" onFocus={selectOnFocus} value={item.quantity || ''} disabled={readOnly} onChange={e => update(item._key, 'quantity', numVal(e.target.value))} style={{ ...inp(readOnly), width: 58, textAlign: 'right' }} /></td>
                  <td style={{ padding: '4px 6px' }}><input value={item.unit || ''} disabled={readOnly} onChange={e => update(item._key, 'unit', e.target.value)} style={{ ...inp(readOnly), width: 60 }} /></td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{hasSubs ? null : <input type="number" onFocus={selectOnFocus} value={item.material_cost || ''} disabled={readOnly} onChange={e => update(item._key, 'material_cost', numVal(e.target.value))} style={{ ...inp(readOnly), width: 90, textAlign: 'right' }} />}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{hasSubs ? null : <input type="number" onFocus={selectOnFocus} step="0.5" value={item.labor_hours || ''} disabled={readOnly} onChange={e => update(item._key, 'labor_hours', numVal(e.target.value))} style={{ ...inp(readOnly), width: 70, textAlign: 'right' }} />}</td>
                  <td style={{ padding: '4px 6px', ...(hasSubs ? { textAlign: 'center', color: '#c4b5fd' } : {}) }}>{hasSubs ? '—' : <input type="number" onFocus={selectOnFocus} value={item.labor_rate || ''} placeholder={bid.labor_rate != null ? `${bid.labor_rate}` : ''} disabled={readOnly} onChange={e => update(item._key, 'labor_rate', e.target.value === '' ? null : parseFloat(e.target.value))} style={{ ...inp(readOnly), width: 74, textAlign: 'right' }} />}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{hasSubs ? null : <input type="number" onFocus={selectOnFocus} value={item.dje_standard || ''} disabled={readOnly} onChange={e => update(item._key, 'dje_standard', numVal(e.target.value))} style={{ ...inp(readOnly), width: 80, textAlign: 'right' }} />}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{hasSubs ? null : pct(item, 'overhead_material_pct', bid.overhead_material_pct + '%', v => update(item._key, 'overhead_material_pct', v))}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{hasSubs ? null : pct(item, 'profit_pct', bid.profit_pct + '%', v => update(item._key, 'profit_pct', v))}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                    <select value={isRoundNone(item.rounding) ? ROUND_NONE : item.rounding} disabled={readOnly} onChange={e => update(item._key, 'rounding', parseInt(e.target.value))} style={{ ...inp(readOnly), width: 68, fontSize: 11 }}>
                      <option value={ROUND_NONE}>None</option><option value={-2}>0.01</option><option value={-1}>0.1</option><option value={0}>1</option><option value={1}>10</option><option value={2}>100</option><option value={3}>1,000</option>
                    </select>
                  </td>
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: 'var(--orange)', whiteSpace: 'nowrap' }}>{fmt$(hasSubs ? itemEffectiveUnitPrice(item, bid) : c.unitPrice)}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt$(hasSubs ? itemEffectiveTotal(item, bid) : c.itemTotal)}</td>
                  {!readOnly && <td style={{ padding: '4px 4px', textAlign: 'center' }}><button onClick={() => removeRow(item._key)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 15, cursor: 'pointer', lineHeight: 1 }}>×</button></td>}
                </tr>

                {/* Sub-item rows — a cost breakdown under the line (subs price unrounded; the parent line's Round shapes the final unit price). */}
                {isOpen && subs.map(sub => {
                  const sc = calcItem(sub, bid);
                  return (
                    <tr key={sub.id} style={{ borderBottom: '1px solid #ede9fe', background: '#faf5ff' }}>
                      <td style={{ padding: '4px 6px 4px 20px', position: 'relative', borderLeft: '3px solid #a78bfa' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: '#c4b5fd', fontSize: 13, userSelect: 'none' }}>└</span>
                          <input value={sub.description || ''} disabled={readOnly} placeholder="Sub-item…" onChange={e => updateSub(item._key, sub.id, 'description', e.target.value)} style={{ ...inp(readOnly), minWidth: 130, flex: 1 }} />
                          {!readOnly && plusBtn(pick === 'sub:' + sub.id, () => pick === 'sub:' + sub.id ? setPick(null) : openPick('sub:' + sub.id))}
                        </div>
                      </td>
                      <td style={{ padding: '4px 6px' }}><input type="number" onFocus={selectOnFocus} value={sub.quantity || ''} disabled={readOnly} onChange={e => updateSub(item._key, sub.id, 'quantity', numVal(e.target.value))} style={{ ...inp(readOnly), width: 58, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px' }}><input value={sub.unit || ''} disabled={readOnly} onChange={e => updateSub(item._key, sub.id, 'unit', e.target.value)} style={{ ...inp(readOnly), width: 60 }} /></td>
                      <td style={{ padding: '4px 6px' }}><input type="number" onFocus={selectOnFocus} value={sub.material_cost || ''} disabled={readOnly} onChange={e => updateSub(item._key, sub.id, 'material_cost', numVal(e.target.value))} style={{ ...inp(readOnly), width: 90, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px' }}><input type="number" onFocus={selectOnFocus} step="0.5" value={sub.labor_hours || ''} disabled={readOnly} onChange={e => updateSub(item._key, sub.id, 'labor_hours', numVal(e.target.value))} style={{ ...inp(readOnly), width: 70, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px' }}><input type="number" onFocus={selectOnFocus} value={sub.labor_rate || ''} placeholder={bid.labor_rate != null ? `${bid.labor_rate}` : ''} disabled={readOnly} onChange={e => updateSub(item._key, sub.id, 'labor_rate', e.target.value === '' ? null : parseFloat(e.target.value))} style={{ ...inp(readOnly), width: 74, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px' }}><input type="number" onFocus={selectOnFocus} value={sub.dje_standard || ''} disabled={readOnly} onChange={e => updateSub(item._key, sub.id, 'dje_standard', numVal(e.target.value))} style={{ ...inp(readOnly), width: 80, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{pct(sub, 'overhead_material_pct', bid.overhead_material_pct + '%', v => updateSub(item._key, sub.id, 'overhead_material_pct', v))}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{pct(sub, 'profit_pct', bid.profit_pct + '%', v => updateSub(item._key, sub.id, 'profit_pct', v))}</td>
                      <td title="Rounding is set on the main line" style={{ padding: '4px 6px', textAlign: 'center', color: '#c4b5fd' }}>—</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', color: '#7c3aed', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 11 }}>{fmt$(sc.selling)}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', fontSize: 11 }}>{fmt$(sc.selling * pricedQty(sub))}</td>
                      {!readOnly && <td style={{ padding: '4px 4px', textAlign: 'center' }}><button onClick={() => removeSub(item._key, sub.id)} style={{ background: 'none', border: 'none', color: '#a78bfa', fontSize: 15, cursor: 'pointer', lineHeight: 1 }}>×</button></td>}
                    </tr>
                  );
                })}
                {isOpen && hasSubs && (
                  <tr style={{ background: '#ede9fe', borderBottom: '1px solid #ddd6fe' }}>
                    <td style={{ padding: '6px 6px 6px 20px', borderLeft: '3px solid #a78bfa', fontSize: 11.5, fontWeight: 800, color: '#4c1d95', whiteSpace: 'nowrap' }}>Assembly total · {subs.length} sub-item{subs.length === 1 ? '' : 's'}</td>
                    <td /><td />
                    <td style={{ padding: '6px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{tot(extSub('material_cost'))}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{tot(extSub('labor_hours'))}{extSub('labor_hours') ? <span style={{ fontSize: 10, fontWeight: 600, color: '#7c3aed' }}> hrs</span> : null}</td>
                    <td />
                    <td style={{ padding: '6px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{tot(extSub('dje_standard'))}</td>
                    <td /><td /><td /><td />
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap', fontSize: 11.5, color: '#4c1d95' }}>{fmt$(subs.reduce((s, x) => s + calcItem(x, bid).selling * pricedQty(x), 0))}</td>
                    {!readOnly && <td />}
                  </tr>
                )}
                {isOpen && !readOnly && (
                  <tr style={{ background: '#faf5ff', borderBottom: '1px solid #ede9fe' }}>
                    <td colSpan={colCount} style={{ padding: '5px 6px 7px 20px', borderLeft: '3px solid #a78bfa' }}>
                      <button onClick={() => addSub(item._key)} style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: 'none', border: '1px dashed #c4b5fd', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>+ Add sub-item</button>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {items.length === 0 && <tr><td colSpan={colCount} style={{ padding: 14, textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>No line items yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div style={{ padding: '8px 10px', background: '#f9fafb', borderTop: '1px solid #eef2f7' }}>
          <button onClick={addRow} style={{ fontSize: 12, fontWeight: 700, color: '#15803d', background: 'none', border: '1px dashed #86efac', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>+ Add line item</button>
        </div>
      )}
    </div>
  );
}

// Which field board a work order can be tracked on. Mirrors WO_BOARDS in server/api.js.
export const WO_BOARD_OPTS = [
  ['small_jobs', 'Small Jobs'], ['loops', 'Loops'], ['vertical_drilling', 'Vertical Drilling'],
  ['horizontal_drilling', 'Directional Drilling'], ['nve', 'NVE'], ['usa_digs', 'USA Digs'],
];

// Pictures on a change order / extra-work ticket (Ryan, Nov 4 2026). Kept on the ticket itself
// (POST …/extra-work/:id/photos), so office-written and Unit Price change orders carry pictures
// too. Picked or dropped onto the box, up at once; a caption per picture (saved on Enter / leaving
// the box); they print at the end of the change order PDF and show on the job's Photos tile.
function CoPictures({ ticket, sjId, apiFetch, API_BASE, getToken, canEdit, onError, onChanged }) {
  const [photos, setPhotos] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [over, setOver] = React.useState(false);
  const [caps, setCaps] = React.useState({});
  React.useEffect(() => { let on = true; apiFetch(`/sub-jobs/${sjId}/extra-work/${ticket.id}/photos`).then(p => { if (on) setPhotos(Array.isArray(p) ? p : []); }).catch(() => { if (on) setPhotos([]); }); return () => { on = false; }; }, [ticket.id, sjId]); // eslint-disable-line react-hooks/exhaustive-deps
  const fileUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
  async function add(list) {
    const files = Array.from(list || []).filter(f => /^image\//.test(f.type) || /\.(jpe?g|png|gif|webp|heic)$/i.test(f.name)).slice(0, 12);
    if (!files.length || busy) { if (!files.length && list && list.length) onError('Pick pictures (JPG, PNG, HEIC…).'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); files.forEach(f => fd.append('photos', f));
      const r = await fetch(`${API_BASE}/sub-jobs/${sjId}/extra-work/${ticket.id}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Upload failed (${r.status})`);
      setPhotos(Array.isArray(j) ? j : []);
      try { window.dispatchEvent(new Event('titan:documents-changed')); } catch { /* fine */ }
      if (onChanged) onChanged();
    } catch (e) { onError(e.message || 'Could not add those pictures.'); }
    setBusy(false);
  }
  async function remove(ph) {
    if (!window.confirm('Take this picture off the change order?')) return;
    try { const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticket.id}/photos/${ph.id}`, { method: 'DELETE' }); setPhotos(Array.isArray(r) ? r : []); try { window.dispatchEvent(new Event('titan:documents-changed')); } catch { /* fine */ } }
    catch (e) { onError(e.message || 'Could not remove that picture.'); }
  }
  async function saveCap(ph) {
    const v = caps[ph.id]; if (v === undefined || v === (ph.caption || '')) return;
    try { const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticket.id}/photos/${ph.id}`, { method: 'PATCH', body: JSON.stringify({ caption: v }) }); setPhotos(Array.isArray(r) ? r : []); setCaps(c => { const n = { ...c }; delete n[ph.id]; return n; }); }
    catch (e) { onError(e.message || 'Could not save that caption.'); }
  }
  const list = photos || [];
  return (
    <div style={{ marginBottom: 12, border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.4, flex: 1 }}>📷 Pictures{list.length ? ` · ${list.length}` : ''}</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>They print at the end of the change order</span>
      </div>
      <div style={{ padding: 10 }}>
        {list.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: canEdit ? 10 : 0 }}>
            {list.map(ph => (
              <div key={ph.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <a href={fileUrl(ph.filename)} target="_blank" rel="noreferrer" title={ph.original_name || 'Open the picture'} style={{ display: 'block', position: 'relative' }}>
                  <img src={fileUrl(ph.filename)} alt="" style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                  {canEdit && <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); remove(ph); }} title="Take this picture off" style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: 11, border: 'none', background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer', lineHeight: '22px', padding: 0 }}>✕</button>}
                </a>
                {canEdit
                  ? <input value={caps[ph.id] !== undefined ? caps[ph.id] : (ph.caption || '')} onChange={e => setCaps(c => ({ ...c, [ph.id]: e.target.value }))} onBlur={() => saveCap(ph)} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} placeholder="What is this? (prints under it)"
                      style={{ width: '100%', boxSizing: 'border-box', border: 'none', borderTop: '1px solid #f1f5f9', padding: '5px 7px', fontSize: 11.5 }} />
                  : <div style={{ padding: '5px 7px', fontSize: 11.5, color: '#374151', minHeight: 16 }}>{ph.caption || ''}</div>}
                <div style={{ padding: '0 7px 5px', fontSize: 10.5, color: '#9ca3af' }}>{ph.by_name || ''}{ph.created_at ? ` · ${new Date(ph.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</div>
              </div>
            ))}
          </div>
        )}
        {photos === null && <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading pictures…</div>}
        {canEdit && (
          <label onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={e => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
            style={{ display: 'block', textAlign: 'center', padding: '14px 10px', border: `2px dashed ${over ? '#1e3a5f' : '#bfdbfe'}`, borderRadius: 8, background: over ? '#eff6ff' : '#f8fbff', color: '#1e3a5f', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Putting them up…' : '📷 Add pictures — tap to pick them, or drop them here'}
            <input type="file" accept="image/*" multiple disabled={busy} style={{ display: 'none' }} onChange={e => { add(e.target.files); e.target.value = ''; }} />
          </label>
        )}
        {!canEdit && !list.length && photos !== null && <div style={{ fontSize: 12, color: '#9ca3af' }}>No pictures on this one.</div>}
      </div>
    </div>
  );
}

export function ExtraWorkTab({ sjId, projectId, project, isAdminZone, bid, isChangeOrders, onWorkflowsChanged, API_BASE, getToken, showToast, openItemId }) {
  const [tickets, setTickets] = React.useState([]);
  // The number box while it's being typed (saved when you leave it — a half-typed "1" of "12"
  // shouldn't bounce off another ticket's number).
  const [coNum, setCoNum] = React.useState({});
  const [lines, setLines] = React.useState({});     // ticketId -> [lines]  (T&M)
  // ticketId -> true when the ticket is tracked BY THE DAY: it has days, or it has nothing
  // yet (a new one starts that way). Its lines above are then worked out from the days.
  // A ticket that already carries the old priced line list keeps it, untouched.
  const [dayMode, setDayMode] = React.useState({});
  const [upItems, setUpItems] = React.useState({}); // ticketId -> [line items] (Unit Price)
  const [expanded, setExpanded] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  // Sent straight to one change order — open that one and take them to it.
  React.useEffect(() => {
    if (!openItemId) return;
    setExpanded(e => ({ ...e, [openItemId]: true }));
    const el = document.getElementById(`ew-${openItemId}`);
    if (el) el.scrollIntoView({ block: 'center' });
  }, [openItemId, tickets.length]);
  const [addChoice, setAddChoice] = React.useState(false); // show the T&M / Unit Price picker
  const [parts, setParts] = React.useState([]);
  const [equipment, setEquipment] = React.useState([]);
  const upSaveTimers = React.useRef({});
  // Each change order prices with its own markups (saved on the ticket), seeded from
  // the job's bid or the company defaults — see coPricing.
  const pricingFor = t => coPricing(t, bid);
  function savePricing(t, patch) {
    const next = { ...coPricing(t, bid), ...patch };
    setTickets(p => p.map(x => (x.id === t.id ? { ...x, pricing: JSON.stringify(next) } : x)));
    apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}`, { method: 'PATCH', body: JSON.stringify({ pricing: JSON.stringify(next) }) }).catch(e => toast(e.message, 'error'));
  }

  function apiFetch(path, opts = {}) {
    return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json', ...(opts.headers || {}) } })
      .then(async r => { if (r.ok) return r.json(); const tx = await r.text(); let m; try { m = JSON.parse(tx)?.error || tx; } catch { m = `Server error ${r.status}`; } return Promise.reject(new Error(m)); });
  }
  const toast = (m, k) => showToast && showToast(m, k);

  // Parse a ticket's stored unit-price items (JSON) into editable rows with keys.
  function parseUpItems(json) {
    let arr = [];
    try { arr = json ? JSON.parse(json) : []; } catch { arr = []; }
    return (Array.isArray(arr) ? arr : []).map(it => ({ ...newItem(0), ...it, _key: it._key || Math.random().toString(36).slice(2) }));
  }
  // Save a unit-price CO's line items (debounced) as a JSON blob on the ticket.
  function updateUpItems(ticketId, newItems) {
    setUpItems(u => ({ ...u, [ticketId]: newItems }));
    clearTimeout(upSaveTimers.current[ticketId]);
    upSaveTimers.current[ticketId] = setTimeout(() => {
      const clean = newItems.map(({ _key, ...rest }) => rest);
      apiFetch(`/sub-jobs/${sjId}/extra-work/${ticketId}`, { method: 'PATCH', body: JSON.stringify({ up_items: JSON.stringify(clean) }) }).catch(e => toast(e.message, 'error'));
    }, 1000);
  }

  React.useEffect(() => {
    apiFetch('/parts').then(p => setParts(Array.isArray(p) ? p : [])).catch(() => {});
    apiFetch('/equipment').then(e => setEquipment(Array.isArray(e) ? e : [])).catch(() => {});
  }, []);

  // Dropdown options: Material from the parts database, Equipment from the fleet
  // (trucks, CDL trucks, trailers, construction equipment, tools).
  const partOptions = React.useMemo(() => parts.map(p => ({ label: p.description, sub: p.category || p.unit, part: p })), [parts]);
  const equipOptions = React.useMemo(() => equipment.map(e => ({ label: e.asset_number ? `${e.asset_number} — ${e.name}` : e.name, sub: e.category, eq: e })), [equipment]);

  // Set several fields on a line at once (local + server), e.g. when a part or
  // piece of equipment is picked and fills description/unit/cost together.
  function pickLine(ticketId, line, fields) {
    setLines(l => ({ ...l, [ticketId]: (l[ticketId] || []).map(x => x.id === line.id ? { ...x, ...fields } : x) }));
    Object.entries(fields).forEach(([f, v]) => saveLineVal(ticketId, line.id, f, v == null ? '' : v));
  }

  React.useEffect(() => {
    apiFetch(`/sub-jobs/${sjId}/extra-work`).then(async ts => {
      setTickets(ts); setLoading(false);
      const up = {}; ts.forEach(t => { if (t.type === 'unit_price') up[t.id] = parseUpItems(t.up_items); }); setUpItems(up);
      const res = await Promise.all(ts.map(t => Promise.all([
        apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/lines`).catch(() => []),
        t.type === 'unit_price' ? Promise.resolve(null) : apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/days`).catch(() => null),
      ]).then(([l, d]) => ({ id: t.id, l: Array.isArray(l) ? l : [], d, unit: t.type === 'unit_price' }))));
      const m = {}, dm = {};
      res.forEach(x => {
        const byDay = !x.unit && ((x.d?.days || []).length > 0 || x.l.length === 0);
        dm[x.id] = byDay;
        m[x.id] = byDay ? coLinesFromDays(x.d) : x.l;
      });
      setLines(m); setDayMode(dm);
    }).catch(e => { setLoading(false); toast(e.message, 'error'); });
  }, [sjId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ext = l => (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
  function totals(t) { return coTmTotals(lines[t.id] || [], t, pricingFor(t)); }

  async function addTicket(type = 'tm') {
    setAdding(true); setAddChoice(false);
    try {
      const t = await apiFetch(`/sub-jobs/${sjId}/extra-work`, { method: 'POST', body: JSON.stringify({ work_date: new Date().toISOString().slice(0, 10), type }) });
      setTickets(p => [...p, t]); setLines(l => ({ ...l, [t.id]: [] }));
      if (type === 'unit_price') setUpItems(u => ({ ...u, [t.id]: [] }));
      else setDayMode(d => ({ ...d, [t.id]: true }));   // a new T&M one is tracked by the day
      setExpanded(e => ({ ...e, [t.id]: true }));
    } catch (e) { toast(e.message, 'error'); }
    setAdding(false);
  }
  // Change the number (Ryan, Nov 4 2026). The server refuses a number another ticket on the
  // job already has, so this one asks first and only then shows the new number.
  async function renumber(t, raw) {
    setCoNum(c => { const n = { ...c }; delete n[t.id]; return n; });
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n) || n < 1 || n === Number(t.co_number)) return;
    try {
      const u = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}`, { method: 'PATCH', body: JSON.stringify({ co_number: n }) });
      setTickets(p => p.map(x => x.id === t.id ? { ...x, co_number: u.co_number } : x).sort((a, b) => (a.co_number || 0) - (b.co_number || 0)));
      toast(`Now ${ewCO(u)}.`, 'success');
    } catch (e) { toast(e.message, 'error'); }
  }
  // The Title box saves once you pause (or leave it) — a save per keystroke wrote a 'renamed' line on
  // the job for every letter (Nov 4 2026).
  const titleTimers = React.useRef({});
  const titleDrafts = React.useRef({});
  function typeTitle(t, v) {
    setTickets(p => p.map(x => x.id === t.id ? { ...x, title: v } : x));
    titleDrafts.current[t.id] = v;
    clearTimeout(titleTimers.current[t.id]);
    titleTimers.current[t.id] = setTimeout(() => flushTitle(t), 800);
  }
  function flushTitle(t) {
    clearTimeout(titleTimers.current[t.id]);
    const v = titleDrafts.current[t.id];
    if (v === undefined) return;
    delete titleDrafts.current[t.id];
    apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}`, { method: 'PATCH', body: JSON.stringify({ title: v }) }).catch(e => toast(e.message, 'error'));
  }
  async function patchTicket(t, fields) {
    setTickets(p => p.map(x => x.id === t.id ? { ...x, ...fields } : x));
    try { await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}`, { method: 'PATCH', body: JSON.stringify(fields) }); } catch (e) { toast(e.message, 'error'); }
  }
  // Field extra work → office Change Order. Keeps the crew's original ticket and
  // marks it converted; the new CO lands on the job's Change Orders card, priced.
  const [converting, setConverting] = React.useState('');
  async function convertToChangeOrder(t) {
    if (converting) return;
    if (!window.confirm(`Make this extra work a Change Order?\n\nIt stays the same record — the crew keep seeing it on Extra Work with no prices, and it appears priced on the Change Orders tab. Anything they add still comes through, until you print the billing summary.`)) return;
    setConverting(t.id);
    try {
      const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/promote`, { method: 'POST' });
      setTickets(p => p.map(x => x.id === t.id ? { ...x, promoted_at: r.promoted_at, promoted_by: r.promoted_by } : x));
      toast('Now a Change Order — see the Change Orders tab in Zone 1.', 'success');
      // The Change Orders card may have just been created — pull the tab list again
      // so it shows up in Zone 1 without needing a page reload.
      if (onWorkflowsChanged) onWorkflowsChanged();
    } catch (e) { toast(e.message || 'Could not convert this ticket.', 'error'); }
    setConverting('');
  }

  // Field "done" button — flips the ticket to submitted and posts a daily update on
  // the job so it lands on the feed and in the next morning brief, instead of sitting
  // on a card until someone happens to open it.
  const [completing, setCompleting] = React.useState('');
  // Printing the billing summary is the moment a change order becomes a document that
  // has gone out — from then on the field can't move the numbers under it. The PDF is
  // drawn on the server and filed on the Documents tile; the browser print window is
  // only the fallback if the server can't.
  async function printAndLock(t, tl) {
    const win = window.open('', '_blank');
    const co = buildChangeOrderData(t, tl, project, { bid: pricingFor(t), upItems: upItems[t.id] || [], rev: coRev[t.id] });
    try {
      const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ co }) });
      const url = `${API_BASE}/documents/${r.document.id}/file`;
      if (win) win.location.href = url; else window.open(url, '_blank');
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId } }));
    } catch {
      if (win) win.close();
      generateChangeOrderPDF(t, tl, project, { bid: pricingFor(t), upItems: upItems[t.id] || [], rev: coRev[t.id] });
    }
    if (t.locked_at) return;
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/lock`, { method: 'POST' });
      setTickets(p => p.map(x => x.id === t.id ? { ...x, locked_at: updated.locked_at, locked_by: updated.locked_by } : x));
    } catch { /* the PDF is already open; a failed lock shouldn't look like a failed print */ }
  }
  // Email the change order to the contractor: same data, drawn and filed on the server,
  // attached, sent from the sender's own mailbox; the ticket locks and reads submitted.
  const [coEmail, setCoEmail] = React.useState(null);   // { ticketId, to, cc, subject, message, busy, err }
  const [mailStatus, setMailStatus] = React.useState(null);
  React.useEffect(() => { apiFetch('/mail/status').then(setMailStatus).catch(() => setMailStatus({ configured: false })); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  function openCoEmail(t) {
    const who = project?.awarded_by_company && project?.awarded_by_name && project.awarded_by_name !== project.awarded_by_company ? project.awarded_by_name : '';
    const first = who ? who.trim().split(/\s+/)[0] : '';
    setCoEmail({ ticketId: t.id, to: project?.awarded_by_email || '', cc: '',
      subject: `${ewCO(t)}${t.title ? ' — ' + t.title : ''} · ${String(project?.name || '').startsWith(String(project?.project_number || ' ')) ? '' : (project?.project_number || '') + ' '}${project?.name || ''}`.replace(/\s+/g, ' ').trim(),
      message: `Hello${first ? ' ' + first : ''},\n\nPlease find ${ewCO(t)} for ${project?.project_number || 'the job'} attached for your review and approval. Let us know if you have any questions.\n\nThank you,`,
      busy: false, err: '' });
  }
  async function sendCoEmail(t, tl) {
    if (!coEmail || coEmail.busy) return;
    setCoEmail(c => ({ ...c, busy: true, err: '' }));
    try {
      const co = buildChangeOrderData(t, tl, project, { bid: pricingFor(t), upItems: upItems[t.id] || [], rev: coRev[t.id] });
      const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/email`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ co, to: coEmail.to, cc: coEmail.cc, subject: coEmail.subject, message: coEmail.message }) });
      if (r.ticket) setTickets(p => p.map(x => x.id === t.id ? { ...x, ...r.ticket } : x));
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId } }));
      if (r.warning) toast(`${ewCO(t)} emailed to ${coEmail.to}. ${r.warning}`, 'error');
      else toast(`${ewCO(t)} emailed to ${coEmail.to}${r.from ? ' from ' + r.from : ''}`, 'success');
      setCoEmail(null);
    } catch (e) { setCoEmail(c => c ? { ...c, busy: false, err: e.message || 'The email didn’t go.' } : c); }
  }
  async function unlockTicket(t) {
    if (!window.confirm(`Unlock CO-${t.co_number}?

Its billing summary has already printed. Anything changed now won't match the copy that went out — reprint it afterwards.`)) return;
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/unlock`, { method: 'POST' });
      setTickets(p => p.map(x => x.id === t.id ? { ...x, locked_at: null, locked_by: null } : x));
      toast('Unlocked — remember to reprint the billing summary.', 'success');
    } catch (e) { toast(e.error || e.message || 'Could not unlock that.', 'error'); }
  }
  // Revision on a printed change order — set before exporting when this copy
  // supersedes one already sent, otherwise left alone.
  const [coRev, setCoRev] = React.useState({});   // ticketId -> 2,3…
  async function markComplete(t) {
    if (completing) return;
    setCompleting(t.id);
    try {
      const updated = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/complete`, { method: 'POST' });
      setTickets(p => p.map(x => x.id === t.id ? { ...x, status: updated.status } : x));
      toast('Sent to your PM and added to today’s job update.', 'success');
    } catch (e) { toast(e.error || e.message || 'Could not send that — try again.', 'error'); }
    setCompleting('');
  }

  // Field extra work → its own work order. The on-call pattern: the umbrella job is
  // the contract, each call-out is really its own small job that wants its own
  // number, POs and billing. The ticket rides along onto the new work order as a
  // priced draft Change Order so the money follows the work.
  const [woFor, setWoFor] = React.useState('');            // ticket id the picker is open on
  const [woBoardPick, setWoBoardPick] = React.useState('small_jobs');
  const [makingWo, setMakingWo] = React.useState('');
  async function makeWorkOrder(t) {
    if (makingWo) return;
    setMakingWo(t.id);
    try {
      const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}/make-work-order`, { method: 'POST', body: JSON.stringify({ board: woBoardPick }) });
      setTickets(p => p.map(x => x.id === t.id ? { ...x, work_order_id: r.work_order_id, converted_to_id: r.ticket_id } : x));
      setWoFor('');
      toast(`${r.number} created — it's on the job's Work Orders list and the ${WO_BOARD_OPTS.find(b => b[0] === woBoardPick)?.[1] || 'board'} board.`, 'success');
    } catch (e) { toast(e.error || e.message || 'Could not make a work order from this ticket.', 'error'); }
    setMakingWo('');
  }

  async function deleteTicket(id) {
    if (!window.confirm('Delete this change order and its line items?')) return;
    try { await apiFetch(`/sub-jobs/${sjId}/extra-work/${id}`, { method: 'DELETE' }); setTickets(p => p.filter(x => x.id !== id)); } catch (e) { toast(e.message, 'error'); }
  }
  async function addLine(ticketId, category) {
    const unit = (category === 'Labor' || category === 'Equipment') ? 'hr' : '';
    try {
      const line = await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticketId}/lines`, { method: 'POST', body: JSON.stringify({ category, unit }) });
      setLines(l => ({ ...l, [ticketId]: [...(l[ticketId] || []), line] }));
    } catch (e) { toast(e.message, 'error'); }
  }
  const setLineLocal = (ticketId, lineId, field, val) => setLines(l => ({ ...l, [ticketId]: (l[ticketId] || []).map(x => x.id === lineId ? { ...x, [field]: val } : x) }));
  async function saveLine(ticketId, line, field) {
    try { await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticketId}/lines/${line.id}`, { method: 'PATCH', body: JSON.stringify({ [field]: line[field] === '' ? null : line[field] }) }); }
    catch (e) { toast(e.message || 'Could not save that line — try again.', 'error'); }
  }
  async function saveLineVal(ticketId, lineId, field, value) {
    try { await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticketId}/lines/${lineId}`, { method: 'PATCH', body: JSON.stringify({ [field]: value === '' ? null : value }) }); }
    catch (e) { toast(e.message || 'Could not save that line — try again.', 'error'); }
  }
  async function deleteLine(ticketId, lineId) {
    try { await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticketId}/lines/${lineId}`, { method: 'DELETE' }); setLines(l => ({ ...l, [ticketId]: (l[ticketId] || []).filter(x => x.id !== lineId) })); } catch (e) { toast(e.message, 'error'); }
  }

  const numInput = { width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 5, padding: '5px 7px', fontSize: 12 };

  if (loading) return <div style={{ padding: 16, color: '#9ca3af', fontSize: 13 }}>Loading extra work…</div>;

  return (
    <div>
      {tickets.map(t => {
        const isOpen = expanded[t.id] !== false;
        const sm = EW_STATUS[t.status] || EW_STATUS.draft;
        const isUnit = t.type === 'unit_price';
        const upList = upItems[t.id] || [];
        const pr = pricingFor(t);
        const tot = isUnit
          ? { subtotal: 0, tax: 0, ohp: 0, total: upList.reduce((s, it) => s + itemEffectiveTotal(it, pr), 0) }
          : totals(t);
        const tl = lines[t.id] || [];
        return (
          <div key={t.id} id={`ew-${t.id}`} style={{ border: `2px solid ${sm.color}33`, borderRadius: 10, marginBottom: 14, overflow: 'hidden', background: '#fff' }}>
            <div onClick={() => setExpanded(e => ({ ...e, [t.id]: !isOpen }))} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: sm.bg }}>
              <span onClick={isAdminZone ? e => { e.stopPropagation(); const v = window.prompt(`Change order number (now ${ewCO(t)}):`, String(t.co_number || '')); if (v != null) renumber(t, v); } : undefined}
                title={isAdminZone ? 'Change the number' : undefined}
                style={{ fontSize: 12, fontWeight: 800, color: '#1e3a5f', fontFamily: 'monospace', cursor: isAdminZone ? 'pointer' : 'default', borderBottom: isAdminZone ? '1px dashed #94a3b8' : 'none' }}>{ewCO(t)}</span>
              {/* This one change order, sent on its own. */}
              <span onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                <SendPointer path={`${itemJobPath(projectId)}?pillow=${isChangeOrders ? 'change_orders' : 'extra_work'}&item=${t.id}`}
                  label={`${project?.project_number || ''} ${ewCO(t)}${t.title ? ' · ' + t.title : ''}`.trim()} />
              </span>
              <span style={{ fontSize: 9, fontWeight: 800, color: isUnit ? '#7c3aed' : '#0369a1', background: isUnit ? '#f5f3ff' : '#eff6ff', border: `1px solid ${isUnit ? '#ddd6fe' : '#bae6fd'}`, borderRadius: 4, padding: '1px 5px', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{isUnit ? 'UNIT PRICE' : 'T&M'}</span>
              <span style={{ flex: 1, fontWeight: 700, fontSize: 14, color: '#111', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title || 'Extra Work'}</span>
                {/* Rename right from the header — the PM and Executive shouldn't have to open
                    the ticket and hunt for the Title box to fix a name (2624, Sep 2026). */}
                {isAdminZone && (
                  <button title={t.locked_at ? 'Rename (locked — the printed copy keeps the old name until you reprint)' : 'Rename'} onClick={e => { e.stopPropagation(); const v = window.prompt('Rename this change order / extra work:', t.title || ''); if (v != null && v.trim() !== (t.title || '')) patchTicket(t, { title: v.trim() }); }}
                    style={{ border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>✎</button>
                )}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: sm.color, background: '#fff', border: `1px solid ${sm.color}44`, borderRadius: 10, padding: '1px 8px' }}>{sm.label}</span>
              {/* Money is office-side only. The line items below are already gated, but
                  this header total wasn't — so the field saw a running dollar figure
                  priced off rates they can't (and shouldn't) see. They log what was
                  used; the PM prices it. Quantity count keeps the card useful to them. */}
              {isAdminZone
                ? <span style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>{ewMoney(tot.total)}</span>
                : <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>{tl.length + upList.length} item{tl.length + upList.length === 1 ? '' : 's'}</span>}
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
              <div style={{ padding: 14 }}>
                {/* Ticket fields */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
                    {isAdminZone && (
                      <div style={{ width: 96, flexShrink: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>Number</div>
                        <input type="number" min={1} step={1} value={coNum[t.id] !== undefined ? coNum[t.id] : (t.co_number ?? '')}
                          onChange={e => setCoNum(c => ({ ...c, [t.id]: e.target.value }))} onBlur={e => renumber(t, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                          title="The change order's number on this job" style={{ ...numInput, fontSize: 13, fontFamily: 'monospace', fontWeight: 700 }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>Title</div>
                      <input value={t.title || ''} onChange={e => typeTitle(t, e.target.value)} onBlur={() => flushTitle(t)} placeholder="e.g. Added conduit for relocated pole" style={{ ...numInput, fontSize: 13 }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>Work Date</div>
                    <input type="date" value={t.work_date || ''} onChange={e => patchTicket(t, { work_date: e.target.value })} style={{ ...numInput, fontSize: 13 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>Status</div>
                    {/* Approving extra work is the office's call — the field gets a
                        read-only pill and the Mark Complete button below, not a menu
                        where they could set their own ticket to "Approved". */}
                    {isAdminZone ? (
                      <select value={t.status} onChange={e => patchTicket(t, { status: e.target.value })} style={{ ...numInput, fontSize: 13 }}>
                        {Object.keys(EW_STATUS).map(k => <option key={k} value={k}>{EW_STATUS[k].label}</option>)}
                      </select>
                    ) : (
                      <div style={{ ...numInput, fontSize: 13, display: 'flex', alignItems: 'center', fontWeight: 700, color: sm.color, background: sm.bg, border: `1px solid ${sm.color}44` }}>
                        {t.status === 'draft' ? 'Not sent yet' : sm.label}
                      </div>
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>Reason / Description</div>
                    <textarea value={t.reason || ''} onChange={e => patchTicket(t, { reason: e.target.value })} rows={2} placeholder="Why was this extra work needed?" style={{ ...numInput, fontSize: 13, resize: 'vertical' }} />
                  </div>
                </div>

                <CoPictures ticket={t} sjId={sjId} apiFetch={apiFetch} API_BASE={API_BASE} getToken={getToken} canEdit onError={m => toast(m, 'error')} />

                {/* What this change order is priced with — visible, and the office can
                    change it here without touching the job's bid. */}
                {isAdminZone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5 }}>Priced with</span>
                    {(isUnit
                      ? [['sales_tax_pct', 'Sales tax %'], ['overhead_material_pct', 'OH mat %'], ['overhead_labor_pct', 'OH labor %'], ['profit_pct', 'Profit %'], ['supervision_pct', 'Supervision %'], ['labor_rate', 'Labor $/hr']]
                      : [['sales_tax_pct', 'Sales tax on material %']]
                    ).map(([k, l]) => (
                      <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}>
                        {l}
                        <input type="number" step="0.001" value={pr[k] ?? ''} onFocus={selectOnFocus}
                          onChange={e => savePricing(t, { [k]: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 })}
                          style={{ width: 70, border: '1px solid #fcd34d', borderRadius: 5, padding: '3px 6px', fontSize: 12, textAlign: 'right', background: '#fff' }} />
                      </label>
                    ))}
                    <span style={{ fontSize: 11, color: '#92400e' }}>{t.pricing ? 'set on this change order' : bid && Number(bid.sales_tax_pct) > 0 ? 'from the job\'s bid' : 'company defaults — the bid has no markups of its own'}</span>
                  </div>
                )}
                {isUnit ? (
                  <UnitPriceCoTable items={upList} onChange={its => updateUpItems(t.id, its)} bid={pr} readOnly={!isAdminZone} parts={parts} />
                ) : dayMode[t.id] ? (
                  // Tracked by the day — the same log as Extra Work and the small-job tile
                  // (Common Items on top, Standard T&M below). Locked once printed; the
                  // office unlocks it to correct a day.
                  <div style={{ marginBottom: 12 }}>
                    <TicketDayLog ticket={t} sjId={sjId} projectId={projectId} projectNumber={project?.project_number || ''}
                      canEdit={!t.locked_at} canSeeMoney={!!isAdminZone}
                      onData={d => setLines(prev => ({ ...prev, [t.id]: coLinesFromDays(d) }))} />
                    {t.locked_at && <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>Printed and locked — unlock it to change a day.</div>}
                    {isAdminZone && <CoLaborRates ticket={t} sjId={sjId} lines={(lines[t.id] || []).filter(l => l.category === 'Labor' && l._fromDays && l.ref_id)} onError={m => toast(m, 'error')} />}
                  </div>
                ) : (<>
                {/* Line items by category. Field supervisors log who/what + hours/qty;
                    pricing (cost, totals, export) is PM-only. */}
                {EXTRA_WORK_CATEGORIES.map(cat => {
                  const catLines = tl.filter(l => l.category === cat);
                  const catSub = catLines.reduce((s, l) => s + ext(l), 0);
                  const isHours = cat === 'Labor' || cat === 'Equipment';
                  const qtyPh = isHours ? 'Hours' : 'Qty';
                  return (
                    <div key={cat} style={{ marginBottom: 10, border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f8fafc' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.4, flex: 1 }}>{cat}{isHours ? ' · hours' : ''}</span>
                        {isAdminZone && catLines.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{ewMoney(catSub)}</span>}
                        <button onClick={() => addLine(t.id, cat)} style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: 'none', border: '1px dashed #86efac', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>+ Add</button>
                      </div>
                      {catLines.length > 0 && (
                        <div style={{ padding: '6px 10px' }}>
                          {catLines.map(l => {
                            const first = cat === 'Labor'
                              ? <select value={l.description || ''} onChange={e => { const role = e.target.value; const def = EW_LABOR_ROLES.find(r => r.role === role); pickLine(t.id, l, { description: role, ...(def ? { unit_cost: def.rate } : {}) }); }} style={numInput}>
                                  <option value="">— Select role —</option>
                                  {/* Rates are office-only — the field picks the role, never sees the $/hr. */}
                                  {EW_LABOR_ROLES.map(r => <option key={r.role} value={r.role}>{isAdminZone ? `${r.role} ($${r.rate}/hr)` : r.role}</option>)}
                                </select>
                              : cat === 'Material'
                              ? <LinePicker value={l.description || ''} options={partOptions} placeholder="Search parts…"
                                  onPick={o => { const f = { description: o.label }; if (o.part) { if (o.part.unit) f.unit = o.part.unit; if (o.part.material_cost != null) f.unit_cost = o.part.material_cost; } pickLine(t.id, l, f); }} />
                              : cat === 'Equipment'
                              ? <LinePicker value={l.description || ''} options={equipOptions} placeholder="Search trucks, equipment, trailers…"
                                  onPick={o => { const f = { description: o.label }; if (o.eq && o.eq.billing_rate != null) f.unit_cost = o.eq.billing_rate; pickLine(t.id, l, f); }} />
                              : <input value={l.description || ''} placeholder="Description" onChange={e => setLineLocal(t.id, l.id, 'description', e.target.value)} onBlur={() => saveLine(t.id, l, 'description')} style={numInput} />;
                            const qty = <input type="number" value={l.quantity ?? ''} placeholder={qtyPh} onChange={e => setLineLocal(t.id, l.id, 'quantity', e.target.value)} onBlur={() => saveLine(t.id, l, 'quantity')} style={{ ...numInput, textAlign: 'center' }} />;
                            const del = <button onClick={() => deleteLine(t.id, l.id)} style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: 15, padding: 0 }}>×</button>;
                            if (!isAdminZone) {
                              return <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1fr 84px 22px', gap: 6, alignItems: 'center', marginBottom: 5 }}>{first}{qty}{del}</div>;
                            }
                            return (
                              <div key={l.id} style={{ display: 'grid', gridTemplateColumns: `1fr 58px ${isHours ? '' : '46px '}78px 82px 22px`, gap: 6, alignItems: 'center', marginBottom: 5 }}>
                                {first}{qty}
                                {!isHours && <input value={l.unit || ''} placeholder="Unit" onChange={e => setLineLocal(t.id, l.id, 'unit', e.target.value)} onBlur={() => saveLine(t.id, l, 'unit')} style={{ ...numInput, textAlign: 'center' }} />}
                                <input type="number" value={l.unit_cost ?? ''} placeholder={isHours ? '$/hr' : '$/unit'} onChange={e => setLineLocal(t.id, l.id, 'unit_cost', e.target.value)} onBlur={() => saveLine(t.id, l, 'unit_cost')} style={{ ...numInput, textAlign: 'right' }} />
                                <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'right', color: '#111' }}>{ewMoney(ext(l))}</span>
                                {del}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                </>)}

                {isAdminZone ? (<>
                  {/* Totals — T&M shows subtotal + OH&P; Unit Price is just the sum
                      of line totals (markups are baked into each line's unit price). */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <div style={{ minWidth: 280, fontSize: 13 }}>
                      {!isUnit && <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 4px' }}><span style={{ color: '#6b7280' }}>Subtotal</span><span style={{ fontWeight: 600 }}>{ewMoney(tot.subtotal)}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 4px' }} title={tot.materialSub ? `${ewMoney(tot.materialSub)} of material × ${tot.taxPct}%` : 'No Material lines on this change order'}><span style={{ color: '#6b7280' }}>Sales tax on material ({tot.taxPct}%)</span><span style={{ fontWeight: 600 }}>{ewMoney(tot.tax)}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 4px' }}>
                          <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>Overhead &amp; Profit
                            <input type="number" value={t.oh_p_pct ?? ''} onFocus={selectOnFocus} onChange={e => patchTicket(t, { oh_p_pct: e.target.value === '' ? 0 : parseFloat(e.target.value) })} style={{ width: 54, border: '1px solid #e5e7eb', borderRadius: 5, padding: '3px 6px', fontSize: 12, textAlign: 'right' }} />%
                          </span>
                          <span style={{ fontWeight: 600 }}>{ewMoney(tot.ohp)}</span>
                        </div>
                      </>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 12px', marginTop: 4, background: '#1e3a5f', borderRadius: 8, color: '#fff' }}><span style={{ fontWeight: 700 }}>TOTAL</span><span style={{ fontWeight: 800 }}>{ewMoney(tot.total)}</span></div>
                    </div>
                  </div>
                  {coEmail?.ticketId === t.id && (
                    <div style={{ marginTop: 12, border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.4 }}>Email this change order — from {mailStatus?.from || 'the company mailbox'}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <AddressPicker value={coEmail.to} onChange={v => setCoEmail(c => ({ ...c, to: v }))} placeholder="To — the contractor; pick from the job or type" projectId={projectId}
                          inputStyle={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                        <AddressPicker value={coEmail.cc} onChange={v => setCoEmail(c => ({ ...c, cc: v }))} placeholder="Cc (optional)" projectId={projectId}
                          inputStyle={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                      </div>
                      <input value={coEmail.subject} onChange={e => setCoEmail(c => ({ ...c, subject: e.target.value }))} placeholder="Subject"
                        style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                      <textarea value={coEmail.message} rows={5} onChange={e => setCoEmail(c => ({ ...c, message: e.target.value }))}
                        style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                      <div style={{ fontSize: 11.5, color: '#6b7280' }}>Attached: {ewCO(t)} as a PDF ({ewMoney(tot.total)}) — filed on the Documents tile; the ticket locks and reads submitted once it goes.</div>
                      {coEmail.err && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{coEmail.err}</div>}
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => setCoEmail(null)} disabled={coEmail.busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                        {/* The change order as it will be attached — drawn only; nothing filed, nothing locked. */}
                        <button onClick={() => openPdfFrom(`/sub-jobs/${sjId}/extra-work/${t.id}/pdf?preview=1`, { method: 'POST', body: { co: buildChangeOrderData(t, tl, project, { bid: pricingFor(t), upItems: upItems[t.id] || [], rev: coRev[t.id] }) } }).catch(e => setCoEmail(c => c ? { ...c, err: e?.message || 'Could not open the preview.' } : c))}
                          disabled={coEmail.busy} title="Open the change order PDF that will be attached" style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>👁 Preview PDF</button>
                        <button onClick={() => sendCoEmail(t, tl)} disabled={coEmail.busy || !coEmail.to.trim()}
                          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: coEmail.to.trim() ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: coEmail.to.trim() ? 'pointer' : 'default' }}>
                          {coEmail.busy ? 'Sending…' : '✉️ Send change order'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {/* Both formats print — Unit Price uses the bid-priced line items. */}
                    <button onClick={() => printAndLock(t, tl)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>⬇ Export Change Order PDF</button>
                    <button onClick={() => mailStatus?.configured ? (coEmail?.ticketId === t.id ? setCoEmail(null) : openCoEmail(t)) : toast('Email isn’t set up on the server yet.', 'error')}
                      title={mailStatus?.configured ? `Sends from ${mailStatus.from}` : 'Email isn’t set up on the server yet'}
                      style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: mailStatus?.configured ? '#15803d' : '#e5e7eb', color: mailStatus?.configured ? '#fff' : '#9ca3af', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>✉️ Email Change Order</button>
                    {t.locked_at && (
                      <button onClick={() => unlockTicket(t)} title="Locked when the billing summary printed — the field can't change it until you unlock"
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>🔒 Locked — unlock</button>
                    )}
                    <select value={coRev[t.id] || ''} onChange={e => setCoRev(r => ({ ...r, [t.id]: e.target.value }))}
                      title="Mark this as a revision of a change order already sent"
                      style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px', fontSize: 12, background: '#fff' }}>
                      <option value="">First issue</option>
                      {[2, 3, 4, 5, 6].map(v => <option key={v} value={v}>rev{v}</option>)}
                    </select>
                    {/* The task this became in Fieldwire — on its sheet when it's pinned. */}
                    {t.fw_task_url && (
                      <a href={t.fw_task_url} target="_blank" rel="noreferrer" title="Open this change order's task in Fieldwire"
                        style={{ alignSelf: 'center', fontSize: 12, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '6px 12px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                        Fieldwire task #{t.fw_task_number || ''} ↗
                      </a>
                    )}
                    {/* Field ticket → priced Change Order, or → its own work order.
                        Both only on the field Extra Work tab, and only once. */}
                    {!isChangeOrders && (t.work_order_id
                      ? <span style={{ alignSelf: 'center', fontSize: 12, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '6px 12px' }}>✓ Made into its own work order</span>
                      : (t.converted_to_id || t.promoted_at)
                      ? <span style={{ alignSelf: 'center', fontSize: 12, fontWeight: 700, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '6px 12px' }}>✓ Also a Change Order</span>
                      : <>
                          <button onClick={() => convertToChangeOrder(t)} disabled={converting === t.id}
                            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0f4c35', color: '#fff', fontSize: 13, fontWeight: 700, cursor: converting === t.id ? 'default' : 'pointer', opacity: converting === t.id ? 0.6 : 1 }}>
                            {converting === t.id ? 'Working…' : '→ Make it a Change Order'}
                          </button>
                          <button onClick={() => setWoFor(woFor === t.id ? '' : t.id)}
                            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: woFor === t.id ? '#1d4ed8' : '#eff6ff', color: woFor === t.id ? '#fff' : '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            → Make separate work order
                          </button>
                        </>)}
                    <button onClick={() => deleteTicket(t.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>Delete</button>
                  </div>
                  {/* Which board should the new work order be tracked on? */}
                  {woFor === t.id && !t.work_order_id && (
                    <div style={{ border: '1px dashed #bfdbfe', background: '#eff6ff', borderRadius: 8, padding: 12, marginTop: 8 }}>
                      <div style={{ fontSize: 12, color: '#1e3a5f', marginBottom: 8 }}>
                        Opens <strong>{project?.project_number || 'this job'}-WO###</strong> as its own job under {project?.project_number || 'this job'}, so its POs, time and billing land on it. This ticket comes along as a priced draft Change Order.
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Track on</span>
                        <select value={woBoardPick} onChange={e => setWoBoardPick(e.target.value)}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, background: '#fff' }}>
                          {WO_BOARD_OPTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                        </select>
                        <span style={{ flex: 1 }} />
                        <button onClick={() => makeWorkOrder(t)} disabled={makingWo === t.id}
                          style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 800, cursor: makingWo === t.id ? 'default' : 'pointer', opacity: makingWo === t.id ? 0.6 : 1 }}>
                          {makingWo === t.id ? 'Creating…' : 'Create Work Order'}
                        </button>
                        <button onClick={() => setWoFor('')} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </>) : (
                  /* Field side: one obvious "I'm done" button. A status dropdown told
                     nobody anything — this also posts a daily update, so the job feed
                     and the morning brief pick the work up. */
                  <div style={{ marginTop: 12 }}>
                    {t.locked_at ? (
                      <div style={{ padding: '12px 14px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13, fontWeight: 700, textAlign: 'center', lineHeight: 1.45 }}>
                        🔒 The office has billed this one — it can't be changed now.<br />
                        <span style={{ fontWeight: 500, fontSize: 12 }}>More work on this job? Start a new extra work ticket.</span>
                      </div>
                    ) : t.status === 'draft' ? (
                      <button onClick={() => markComplete(t)} disabled={completing === t.id}
                        style={{ width: '100%', padding: '14px 16px', borderRadius: 10, border: 'none', background: completing === t.id ? '#86efac' : '#15803d', color: '#fff', fontSize: 16, fontWeight: 800, cursor: completing === t.id ? 'default' : 'pointer' }}>
                        {completing === t.id ? 'Sending…' : '✓ Mark Complete — send to PM'}
                      </button>
                    ) : (
                      <div style={{ padding: '12px 14px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: 14, fontWeight: 700, textAlign: 'center' }}>
                        ✓ Sent to your PM — they'll price it and send the Change Order.
                      </div>
                    )}
                    <div style={{ display: 'flex', marginTop: 10 }}>
                      <button onClick={() => deleteTicket(t.id)} style={{ marginLeft: 'auto', padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {tickets.length === 0 && <div style={{ padding: '16px 0', color: '#9ca3af', fontSize: 13 }}>No extra work yet. Add a change order to log Labor, Material, and Equipment for extra work on this job.</div>}
      {addChoice ? (
        <div style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: 12, marginTop: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>What kind of change order?</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => addTicket('tm')} disabled={adding} style={{ flex: 1, minWidth: 200, textAlign: 'left', padding: '10px 14px', borderRadius: 8, border: '1px solid #bae6fd', background: '#eff6ff', cursor: 'pointer' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0369a1' }}>⏱ Time &amp; Material</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Log Labor, Material &amp; Equipment with OH&amp;P — as it works now.</div>
            </button>
            <button onClick={() => addTicket('unit_price')} disabled={adding} style={{ flex: 1, minWidth: 200, textAlign: 'left', padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd6fe', background: '#f5f3ff', cursor: 'pointer' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#7c3aed' }}>📋 Unit Price</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Priced like the bid sheet — line items with unit prices.</div>
            </button>
          </div>
          <button onClick={() => setAddChoice(false)} style={{ marginTop: 8, background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
        </div>
      ) : (
        <button onClick={() => setAddChoice(true)} disabled={adding} style={{ width: '100%', padding: 10, border: '2px dashed #cbd5e1', borderRadius: 8, background: 'none', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}>
          {adding ? 'Adding…' : '+ New Change Order'}
        </button>
      )}
    </div>
  );
}

// Type-ahead picker over the combined companies list (customers + vendors).
// `value` is the currently-selected name; onSelect gets the full company object.
// Company as it's shaped for the pickers, built from a raw client/vendor record.
function clientToCompany(rec) {
  return { name: rec.name, clientId: rec.id, source: 'Customer', contact: rec.contact || '', phone: rec.phone || '', fax: rec.fax || '', email: '', address1: rec.address1 || '', address2: rec.address2 || '' };
}
function vendorToCompany(rec) {
  return { name: rec.name, vendorId: rec.id, source: 'Vendor', contact: rec.contact || '', phone: rec.phone || '', fax: rec.fax || '', email: rec.email || '', address1: rec.address || '', address2: [rec.city, rec.state, rec.zip].filter(Boolean).join(' ') };
}

// One combined, de-duplicated company list (customers + vendors) shared by every
// company picker so the lists always match. Companies that are both keep their
// clientId (for saved contacts) and are tagged "Customer + Vendor". Sorted A→Z.
export function buildCompanies(clients, vendors) {
  const byName = new Map();
  const norm = s => (s || '').trim().toLowerCase();
  (clients || []).forEach(c => {
    const k = norm(c.name); if (!k) return;
    byName.set(k, { name: c.name, contact: c.contact || '', phone: c.phone || '', fax: c.fax || '', email: '', address1: c.address1 || '', address2: c.address2 || '', clientId: c.id, source: 'Customer' });
  });
  (vendors || []).forEach(v => {
    const k = norm(v.name); if (!k) return;
    const addr2 = [v.city, v.state, v.zip].filter(Boolean).join(' ');
    const ex = byName.get(k);
    if (ex) {
      ex.contact = ex.contact || v.contact || '';
      ex.phone = ex.phone || v.phone || '';
      ex.fax = ex.fax || v.fax || '';
      ex.email = ex.email || v.email || '';
      ex.address1 = ex.address1 || v.address || '';
      ex.address2 = ex.address2 || addr2;
      ex.vendorId = v.id;
      ex.source = 'Customer + Vendor';
    } else {
      byName.set(k, { name: v.name, contact: v.contact || '', phone: v.phone || '', fax: v.fax || '', email: v.email || '', address1: v.address || '', address2: addr2, vendorId: v.id, source: 'Vendor' });
    }
  });
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// Type-ahead company picker (customers + vendors). Typing jumps alphabetically
// to names that START with the letters (then substring matches). When
// `allowCreate` and the typed name isn't in the list, offers to add it as a new
// Customer or Vendor — which also saves it to the master file.
// customersOnly hides the "add as Vendor" choice (a bid's owner is never a vendor); allowOneOff
// offers "just this once" — the name is used on this bid and saved nowhere (Ryan, Oct 1 2026).
export function CompanyCombo({ companies, value, onSelect, placeholder, style, disabled, allowCreate, onCreated, customersOnly = false, allowOneOff = false }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  // Anchor the dropdown with fixed positioning measured from the input, so it is
  // never clipped by an ancestor's `overflow: hidden` (which made the list
  // unreadable/unclickable inside the Project Owner card).
  const wrapRef = React.useRef(null);
  const [pos, setPos] = React.useState(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const el = wrapRef.current;
      if (el) { const r = el.getBoundingClientRect(); setPos({ left: r.left, top: r.bottom, width: r.width }); }
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('scroll', measure, true); window.removeEventListener('resize', measure); };
  }, [open]);
  const ql = q.trim().toLowerCase();
  // Prefix matches first (A→Z), then remaining substring matches.
  const list = React.useMemo(() => {
    if (!ql) return companies;
    const starts = [], contains = [];
    companies.forEach(c => { const n = c.name.toLowerCase(); if (n.startsWith(ql)) starts.push(c); else if (n.includes(ql)) contains.push(c); });
    return [...starts, ...contains];
  }, [companies, ql]);
  const exact = companies.some(c => c.name.trim().toLowerCase() === ql);
  const canCreate = allowCreate && !disabled && ql.length > 0 && !exact;
  const srcColor = s => s === 'Vendor' ? '#7c3aed' : s === 'Customer' ? '#0f766e' : '#b45309';   // Customer + Vendor and One-time read amber

  async function create(kind) {
    const name = q.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const rec = await apiFetch(kind === 'client' ? '/clients' : '/vendors', { method: 'POST', body: JSON.stringify({ name }) });
      if (onCreated) onCreated(kind, rec);
      onSelect(kind === 'client' ? clientToCompany(rec) : vendorToCompany(rec));
      setQ(''); setOpen(false);
    } catch (e) { window.alert(e?.error || e?.message || 'Could not add. Try again.'); }
    finally { setCreating(false); }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input value={open ? q : (value || '')} disabled={disabled}
        onChange={e => { setQ(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setQ(''); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder || 'Type or select…'} style={style} />
      {open && !disabled && pos && (
        <div style={{ position: 'fixed', top: pos.top + 4, left: pos.left, width: pos.width, zIndex: 4000, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, maxHeight: 300, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.18)' }}>
          {list.map((c, i) => (
            <div key={i} onMouseDown={e => { e.preventDefault(); onSelect(c); setQ(''); setOpen(false); }}
              style={{ padding: '11px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, background: c.name === value ? '#f0fdf4' : '#fff', borderBottom: i < list.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: srcColor(c.source), flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.3 }}>{c.source}</span>
            </div>
          ))}
          {list.length === 0 && !canCreate && <div style={{ padding: '12px 14px', color: '#9ca3af', fontSize: 13 }}>No matches.</div>}
          {canCreate && (
            <div style={{ borderTop: list.length ? '1px solid #e5e7eb' : 'none' }}>
              <div style={{ padding: '8px 14px 3px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Not in the list?</div>
              <div onMouseDown={e => { e.preventDefault(); create('client'); }}
                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#0f766e', fontWeight: 600 }}>
                + Add “{q.trim()}” as new Customer
              </div>
              {allowOneOff && (
                <div onMouseDown={e => { e.preventDefault(); onSelect({ name: q.trim(), oneOff: true, source: 'One-time', contact: '', phone: '', fax: '', email: '', address1: '', address2: '', clientId: null }); setQ(''); setOpen(false); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#b45309', fontWeight: 600 }}>
                  Use “{q.trim()}” just this once <span style={{ fontWeight: 500, color: '#9ca3af' }}>— a person or a one-off, not saved to the directory</span>
                </div>
              )}
              {!customersOnly && (
                <div onMouseDown={e => { e.preventDefault(); create('vendor'); }}
                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#7c3aed', fontWeight: 600 }}>
                + Add “{q.trim()}” as new Vendor
              </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Pick / add / remove the contact person for a company (GC/client). Contacts
// are saved on the client record so they persist and can be reused, since the
// point of contact changes over time. Requires a saved client (clientId).
// Who at a GC a quote is addressed to. Shown as plain text until you click it, so
// the recipients list stays readable, then becomes a picker of that company's saved
// contacts — plus "add a new one", since the right person often isn't on file yet.
function RecipientContact({ recipient, disabled, onChange }) {
  const [editing, setEditing] = React.useState(false);
  const [contacts, setContacts] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!editing || loaded || !recipient.client_id) return;
    apiFetch(`/clients/${recipient.client_id}/contacts`)
      .then(cs => { setContacts(Array.isArray(cs) ? cs : []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [editing, loaded, recipient.client_id]);

  const line = [recipient.contact_name, recipient.contact_email, recipient.contact_phone].filter(Boolean).join(' · ');

  if (!editing) {
    return (
      <>
        <div onClick={() => !disabled && setEditing(true)}
          title={disabled ? undefined : 'Click to change who this is addressed to'}
          style={{ fontSize: 12, color: line ? '#6b7280' : '#c2410c', marginTop: 1, cursor: disabled ? 'default' : 'pointer' }}>
          {line || 'No contact — click to add one'}
          {!disabled && <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 11 }}>✎</span>}
        </div>
        {/* A typed-in company: is the person (or the company) in the Customer List? (Oct 6 2026) */}
        {!recipient.client_id && !disabled && (recipient.contact_name || recipient.contact_email) && (
          <ContactCheck name={recipient.contact_name} email={recipient.contact_email} phone={recipient.contact_phone} clientName={recipient.client_name}
            onUse={c => onChange({ client_id: c.client_id, contact_id: c.id, contact_name: c.name, contact_email: c.email || recipient.contact_email || '', contact_phone: c.phone || recipient.contact_phone || '' })}
            onAdded={c => onChange({ client_id: c.client_id, contact_id: c.id })} />
        )}
      </>
    );
  }
  return (
    <div style={{ marginTop: 6, padding: 8, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
      {recipient.client_id ? (
        <ContactManager clientId={recipient.client_id} contacts={contacts} setContacts={setContacts}
          selectedId={recipient.contact_id}
          onSelect={c => {
            onChange(c
              ? { contact_id: c.id, contact_name: c.name, contact_email: c.email || '', contact_phone: c.phone || '' }
              : { contact_id: null, contact_name: '', contact_email: '', contact_phone: '' });
            setEditing(false);
          }} />
      ) : (
        // Free-typed company — no client record to hang contacts off, so just let
        // them write the name in rather than blocking the edit entirely.
        <input autoFocus defaultValue={recipient.contact_name || ''} placeholder="Contact name"
          onBlur={e => { onChange({ contact_name: e.target.value.trim() }); setEditing(false); }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
      )}
      <button onClick={() => setEditing(false)}
        style={{ marginTop: 6, background: 'none', border: 'none', color: '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Done</button>
    </div>
  );
}

export function ContactManager({ clientId, contacts, setContacts, selectedId, onSelect, disabled }) {
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', email: '', phone: '' });
  const [busy, setBusy] = React.useState(false);
  const inp = { border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  if (!clientId) {
    return <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Select this company from the list (not free-typed) to save contact people for it.</div>;
  }

  async function add() {
    if (!form.name.trim() || busy) return;
    setBusy(true);
    try {
      const c = await apiFetch(`/clients/${clientId}/contacts`, { method: 'POST', body: JSON.stringify({ name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null }) });
      setContacts([...(contacts || []), c]);
      onSelect(c);
      setForm({ name: '', email: '', phone: '' });
      setAdding(false);
    } catch { /* ignore */ } finally { setBusy(false); }
  }
  async function remove(cid) {
    if (!window.confirm('Remove this contact person?')) return;
    try {
      await apiFetch(`/clients/${clientId}/contacts/${cid}`, { method: 'DELETE' });
      setContacts((contacts || []).filter(c => c.id !== cid));
      if (selectedId === cid) onSelect(null);
    } catch { /* ignore */ }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={selectedId || ''} disabled={disabled}
          onChange={e => onSelect((contacts || []).find(c => c.id === e.target.value) || null)}
          style={{ ...inp, flex: '1 1 180px', width: 'auto' }}>
          <option value="">— Select contact —</option>
          {(contacts || []).map(c => <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ''}</option>)}
        </select>
        {selectedId && !disabled && (
          <button type="button" onClick={() => remove(selectedId)} title="Remove this contact"
            style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 7, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
        )}
        {!disabled && (
          <button type="button" onClick={() => { setAdding(v => !v); setForm({ name: '', email: '', phone: '' }); }}
            style={{ border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', borderRadius: 7, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{adding ? 'Cancel' : '+ Add contact'}</button>
        )}
      </div>
      {adding && !disabled && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: 10, border: '1px dashed #cbd5e1', borderRadius: 8, background: '#f8fafc' }}>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name *" style={{ ...inp, gridColumn: '1 / -1' }} autoFocus />
          <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" style={inp} />
          <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" style={inp} />
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={add} disabled={!form.name.trim() || busy}
              style={{ border: 'none', background: form.name.trim() ? '#0f4c35' : '#d1d5db', color: '#fff', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: form.name.trim() ? 'pointer' : 'default' }}>{busy ? 'Saving…' : 'Save contact'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile shell for the bid detail when a PM opens it from their phone — a simple
// back bar + padded body, NO desktop AdminLayout sidebar. Module-level so its
// identity stays stable (the heavy detail subtree isn't remounted each render).
// The title is a prop now: this shell carries the job page on a phone as well as a
// bid, and a job that says "Bid Details" at the top reads as the wrong screen.
export function MobileBidShell({ children, onBack, title = 'Bid Details', bottomNav = false }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #f9fafb)' }}>
      <div style={{ background: '#111', color: '#fff', paddingTop: 'max(env(safe-area-inset-top), 14px)', paddingBottom: 12, paddingLeft: 14, paddingRight: 14, display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, zIndex: 50 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0, color: '#e87722', lineHeight: 1 }}>‹</button>
        <div style={{ fontWeight: 800, fontSize: 16 }}>{title}</div>
      </div>
      <div style={{ padding: bottomNav ? '10px 10px 110px' : '10px 10px 100px' }}>{children}</div>
      {bottomNav && <MobileBottomNav />}
    </div>
  );
}

export function AdminBidDetailInner({ bidId: bidIdProp, projectId: projectIdProp, embedded = false, headerSlot = null }) {
  const params = useParams();
  const id = bidIdProp || params.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = projectIdProp || searchParams.get('project');
  const Wrap = embedded ? MobileBidShell : AdminLayout;

  const [bid, setBid]       = useState(null);
  const [items, setItems]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(null); // attId being extracted
  const [showRateSheet, setShowRateSheet] = useState(false);
  const [showRateSheetPdf, setShowRateSheetPdf] = useState(false);
  const [recipients, setRecipients] = useState([]);
  // The quote a recipient was actually sent, if we recorded it. On an awarded bid
  // this is what "View Used Quote PDF" renders — the whole point being that it stops
  // following later edits to the bid. Null when there's no snapshot, and the caller
  // falls back to live data.
  //
  // ONLY once the bid is awarded. Before that a quote is still being revised, and the
  // newest revision is what must go out — this used to hand back the FIRST version sent
  // to a recipient on every later export or email, so Bart's revised quote went out as
  // the original while the record said the revision had been sent (Sep 2026).
  const lockedQuoteFor = React.useCallback(rid => {
    // bid is null until it loads, and this hook runs on that first render too — reading
    // bid.status bare crashed the whole bid page for everyone (my regression, Sep 17 2026).
    if (bid?.status !== 'awarded') return null;
    const r = recipients.find(x => x.id === rid);
    if (!r || !r.quote_snapshot) return null;
    try {
      const snap = typeof r.quote_snapshot === 'string' ? JSON.parse(r.quote_snapshot) : r.quote_snapshot;
      if (!snap || !Array.isArray(snap.items)) return null;
      return {
        ...snap,
        items: snap.items.map((it, i) => ({
          ...it, _key: `snap-${i}`,
          sub_items: it.sub_items ? (typeof it.sub_items === 'string' ? JSON.parse(it.sub_items) : it.sub_items) : [],
        })),
      };
    } catch { return null; }
  }, [recipients, bid?.status]);
  // Bid alternates — the groups this bid is cut into. Empty on every bid that
  // doesn't use them, which is every bid that existed before the feature.
  const [alternates, setAlternates] = useState([]);
  const [altModal, setAltModal] = useState(null);
  const [altPickModal, setAltPickModal] = useState(null);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [showJobDesc, setShowJobDesc] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [showScope, setShowScope] = useState(false);
  const [showAdditionalComments, setShowAdditionalComments] = useState(() => !!(bid?.exclusions));
  const [showLineItems, setShowLineItems] = useState(false);
  const [showVendorQuotes, setShowVendorQuotes] = useState(false);
  // Bid mode only: which section tile is open (null = the tile grid / hub). The old job
  // page (project mode) keeps the stacked accordion, so this stays null there.
  const [openSection, setOpenSection] = useState(null);
  // Bid mode: tiles the office has taken off this bid's board. Saved on the bid so the
  // whole office sees the same board. NULL on the bid = the default (Scope & Bid Details
  // off); the mark-ups and Bid Summary it held still live on the Line Items tile.
  const BID_TILES_DEFAULT_HIDDEN = ['scope'];
  const hiddenTiles = (() => {
    if (!bid || bid.hidden_tiles == null) return BID_TILES_DEFAULT_HIDDEN;
    try { const l = JSON.parse(bid.hidden_tiles); return Array.isArray(l) ? l : BID_TILES_DEFAULT_HIDDEN; } catch { return BID_TILES_DEFAULT_HIDDEN; }
  })();
  async function setHiddenTiles(list) {
    setBid(b => b ? { ...b, hidden_tiles: JSON.stringify(list) } : b);
    try { await apiFetch(`/bids/${bid.id}/tiles`, { method: 'PATCH', body: JSON.stringify({ hidden_tiles: list }) }); }
    catch (e) { showToast && showToast(e?.error || e?.message || 'Could not save the board layout.', 'error'); }
  }
  // Bid mode: Scope & Bid Details reads first (a card of facts) and opens into its
  // form on ✎ Edit, the same way the Job Information card does.
  const [scopeEditing, setScopeEditing] = useState(false);
  // Line Items opens on the estimating sheet — Material $, Labor Hrs, Sub $, OH %, Profit %,
  // Round, with each column hideable from its header — because that is where the
  // estimators work (Ryan, Sep 16: the read-first quote table had "lost" those columns).
  // 👁 Quote view swaps in the priced table as it prints. Project mode is always the sheet.
  const [lineItemsEditing, setLineItemsEditing] = useState(true);
  const lineItemsForm = !!projectId || lineItemsEditing;
  // The "✦ Extract with AI" picker on the Line Items header (which PDF to read).
  const [extractPick, setExtractPick] = useState(false);
  // Where the picker's button is on screen. The list is floated over the page from
  // there (a portal), because the Line Items card clips anything hanging out of it —
  // on a short card only the first two files showed and the rest were cut off.
  const [extractPickRect, setExtractPickRect] = useState(null);
  useEffect(() => {
    if (!extractPick) return undefined;
    const close = e => { if (e.target.closest && e.target.closest('[data-extract-pick]')) return; setExtractPick(false); };
    const closeNow = () => setExtractPick(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', closeNow);
    window.addEventListener('scroll', closeNow, true);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('resize', closeNow); window.removeEventListener('scroll', closeNow, true); };
  }, [extractPick]);
  // Line Items header tabs on a hard bid: 'summary' (the cost stack) | 'markups' | null.
  const [lineItemsPanel, setLineItemsPanel] = useState(null);
  // "How long is this job?" scratch pad on the Bid Summary — crew size × hours a day
  // against the bid's total hours. Informational only; nothing is saved.
  const [crewCalc, setCrewCalc] = useState({ crew: 3, hours: 10 });
  // Bid Updates — for the tile preview (latest posts) and the header count.
  const [bidUpdates, setBidUpdates] = useState([]);
  // The header's 💬 Update button — a quick composer that posts to the bid's thread.
  const [bidUpdateOpen, setBidUpdateOpen] = useState(false);
  const [bidUpdateText, setBidUpdateText] = useState('');
  const [bidUpdateBanner, setBidUpdateBanner] = useState(false);
  const [postingBidUpdate, setPostingBidUpdate] = useState(false);
  async function postBidUpdateFromHeader() {
    const notes = bidUpdateText.trim();
    if (!notes || postingBidUpdate) return;
    setPostingBidUpdate(true);
    try {
      await apiFetch(`/bids/${id}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes, banner: bidUpdateBanner }) });
      setBidUpdateText(''); setBidUpdateOpen(false); setBidUpdateBanner(false);
      window.dispatchEvent(new CustomEvent('titan:bid-updates-changed'));
      showToast(bid.bid_pm_id ? 'Posted — the PM has been notified.' : 'Posted to the bid’s updates.');
    } catch (e) { showToast(e?.message || 'Could not post that update', 'error'); }
    setPostingBidUpdate(false);
  }
  useEffect(() => {
    if (projectId || !id) return;
    const load = () => apiFetch(`/bids/${id}/daily-updates`).then(r => setBidUpdates(Array.isArray(r) ? r : [])).catch(() => {});
    load();
    window.addEventListener('titan:bid-updates-changed', load);
    return () => window.removeEventListener('titan:bid-updates-changed', load);
  }, [id, projectId]);
  // Documents: the list reads first; Edit adds the upload drop zone and remove buttons.
  const [docsEditing, setDocsEditing] = useState(false);
  // A T&M bid's quote is its list of rate lines (TmTile) rather than the hard-bid
  // estimate; kept here for the tile preview and the section header count.
  const [tmQuoteItems, setTmQuoteItems] = useState([]);
  useEffect(() => {
    if (projectId || !id || bid?.pricing_type !== 'tm') return;
    const load = () => apiFetch(`/bids/${id}/tm-items`).then(r => setTmQuoteItems(Array.isArray(r) ? r : [])).catch(() => {});
    load();
    window.addEventListener('titan:tm-items-changed', load);
    return () => window.removeEventListener('titan:tm-items-changed', load);
  }, [id, projectId, bid?.pricing_type]);
  // A tile that saves a bid field (the T&M not-to-exceed cap) hands the updated bid
  // back here, so the tile previews and the quote read what was just saved instead of
  // the copy this page loaded.
  useEffect(() => {
    const onBid = e => { if (e.detail && e.detail.id === id) setBid(b => ({ ...b, ...e.detail })); };
    window.addEventListener('titan:bid-changed', onBid);
    return () => window.removeEventListener('titan:bid-changed', onBid);
  }, [id]);
  // The Documents tile's files — the count shows on the tile before it's opened, and the
  // PDFs among them are offered to ✦ Extract with AI alongside the classic attachments
  // (a bid document dropped on the Documents tile is the normal case now).
  const [docCount, setDocCount] = useState(null);
  // The bid's Fieldwire project, once its plans are up (Ryan, Sep 29 2026): two tiles of
  // its own — Fieldwire Sheets and Fieldwire Tasks — the same pillows the job page has.
  const [fwOv, setFwOv] = useState(null);
  useEffect(() => {
    if (projectId) return;
    apiFetch(`/projects/${id}/fieldwire`).then(d => setFwOv(d && d.linked ? d : null)).catch(() => setFwOv(null));
  }, [id, projectId, docCount]); // eslint-disable-line react-hooks/exhaustive-deps
  const [bidDocs, setBidDocs] = useState([]);
  useEffect(() => {
    if (projectId || !id) return;
    const load = () => apiFetch(`/bids/${id}/documents`).then(d => { const list = Array.isArray(d) ? d : []; setDocCount(list.length); setBidDocs(list); }).catch(() => {});
    load();
    window.addEventListener('titan:documents-changed', load);
    return () => window.removeEventListener('titan:documents-changed', load);
  }, [id, projectId]);
  const [hiddenCols, setHiddenCols] = useState(new Set());
  function toggleCol(key) { setHiddenCols(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; }); }
  const [expandedSubs, setExpandedSubs] = useState(new Set());
  function toggleSubs(key) { setExpandedSubs(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; }); }
  const [showLocationMap, setShowLocationMap] = useState(false);
  const [showLocationView, setShowLocationView] = useState(false); // read-only map from the top pill
  const [projNumDraft, setProjNumDraft] = useState(''); // editable Job/Project number (project mode)
  const [addItemModal, setAddItemModal] = useState(false);
  const [addAfterKey, setAddAfterKey] = useState('');
  const [showPartsDir, setShowPartsDir] = useState(false);
  const [fwLinesOpen, setFwLinesOpen] = useState(false);   // 📍 Lines from Fieldwire (Ryan, Oct 22 2026)
  const [partsSuggestKey, setPartsSuggestKey] = useState(null); // _key of item showing suggest popover
  const [partsSearch, setPartsSearch] = useState('');
  const [partsAll, setPartsAll] = useState([]);
  const [partsLoaded, setPartsLoaded] = useState(false);
  function updateSubItems(itemKey, newSubs) {
    setItems(its => its.map(i => i._key === itemKey ? { ...i, sub_items: newSubs } : i));
    setDirty(true);
    scheduleItemsSave();   // sub-item edits must auto-save too (they used to only mark dirty → lost on reopen)
  }
  function updateSubItem(itemKey, subId, field, value) {
    setItems(its => its.map(i => i._key === itemKey
      ? { ...i, sub_items: i.sub_items.map(s => {
          if (s.id !== subId) return s;
          const next = { ...s, [field]: value };
          // A typed quantity on an assembly sub-line becomes its new "per one".
          if (field === 'quantity' && s.per_unit != null && Number(i.quantity) > 0) next.per_unit = (Number(value) || 0) / Number(i.quantity);
          return next;
        }) }
      : i));
    setDirty(true);
    scheduleItemsSave();
  }
  function newSubItem() {
    return { id: Math.random().toString(36).slice(2), description: '', quantity: 1, unit: 'EACH', material_cost: 0, material_escalation: 0, labor_hours: 0, dje_standard: 0, dje_takeoff: 0, overhead_material_pct: null, overhead_labor_pct: null, profit_pct: null, rounding: 0 };
  }
  const [recipientContactId, setRecipientContactId] = useState('');
  const [recipientContacts, setRecipientContacts] = useState([]);
  const [recipientCompany, setRecipientCompany] = useState(null); // selected combined company
  const [exportRecipientModal, setExportRecipientModal] = useState(false);
  // Emailing the quote: who it's going to (a recipient, or the owner) and the note that
  // rides with the PDF. `mailStatus` says whether the server can send, and as whom.
  const [quoteEmail, setQuoteEmail] = useState(null);
  const [mailStatus, setMailStatus] = useState(null);
  useEffect(() => { apiFetch('/mail/status').then(setMailStatus).catch(() => setMailStatus({ configured: false })); }, []);
  const [extractModal, setExtractModal] = useState(null); // { items }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty]   = useState(false);
  const [toast, setToast]   = useState(null);
  const [showAward, setShowAward] = useState(false);
  const [oldWizard, setOldWizard] = useState(false);   // finish a job brought in from the old system (Oct 7 2026)
  const [activeItemKey, setActiveItemKey] = useState(null);
  const fileInputRef = useRef(null);

  // Project mode (when accessed from New Projects page)
  const [project, setProject] = useState(null);
  const [subJobs, setSubJobs] = useState([]);
  const [subJobNotes, setSubJobNotes] = useState({}); // id -> notes
  const [subJobSaving, setSubJobSaving] = useState({}); // id -> bool
  const [drillCardSj, setDrillCardSj] = useState(null); // drilling sub-job opened as the Trello-style card
  const [showSubJobs, setShowSubJobs] = useState({});
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);

  // Zone 1 — Admin sections
  const [showBidZone, setShowBidZone] = useState(false); // collapsed by default in project mode
  const [showContractZone, setShowContractZone] = useState(true);
  const contractEnsuredRef = React.useRef(null); // guard so Contract auto-adds only once per job
  const [contract, setContract] = useState({});
  const [contractSaving, setContractSaving] = useState(false);

  // Zone 2 — Field sections
  const [dailyUpdates, setDailyUpdates] = useState([]);
  // Field notes (daily_notes) — the mobile "Photos from the field" path posts here
  // with mediaUrls. Separate table from project_daily_updates, so the Daily Updates
  // section merges both; otherwise photo posts are invisible on the job.
  const [fieldNotes, setFieldNotes] = useState([]);
  // Work orders under this job (on-call call-outs). Each is its own small job.
  const [workOrders, setWorkOrders] = useState([]);
  const [showWOs, setShowWOs] = useState(true);
  const [showWoAdd, setShowWoAdd] = useState(false);
  const [woName, setWoName] = useState('');
  const [woBoard, setWoBoard] = useState('small_jobs');
  const [creatingWo, setCreatingWo] = useState(false);
  const [newUpdate, setNewUpdate] = useState('');
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [showDailyUpdates, setShowDailyUpdates] = useState(true);
  const [punchList, setPunchList] = useState([]);
  const [newPunchItem, setNewPunchItem] = useState('');
  const [newPunchAssignee, setNewPunchAssignee] = useState('');
  const [showPunchList, setShowPunchList] = useState(true);
  const [closeout, setCloseout] = useState([]);
  const [newCloseoutItem, setNewCloseoutItem] = useState('');
  const [showCloseout, setShowCloseout] = useState(false);
  const [projectPOs, setProjectPOs] = useState([]);
  const [showPOs, setShowPOs] = useState(true);
  const [poMajorOnly, setPoMajorOnly] = useState(false);
  const [vendors, setVendors] = useState([]);
  // One combined company list (customers + vendors) for the Project Owner and Bid
  // Recipient pickers — the SAME deduped list is used by both (and by the New Bid
  // modal), so the two lists always match.
  const companies = React.useMemo(() => buildCompanies(clients, vendors), [clients, vendors]);
  // A newly-created company (from a picker) is folded into the master list state
  // so it stays in the dropdown for the rest of the session.
  const foldCompany = React.useCallback((kind, rec) => {
    if (kind === 'client') setClients(cs => [...(cs || []), rec]);
    else setVendors(vs => [...(vs || []), rec]);
  }, []);
  // Who submittals should be addressed to. When we're a SUB, address them to the
  // prime that awarded us the job (the recipient marked Awarded, else awarded_by_*).
  // When we're the PRIME, address them straight to the project owner (client_*).
  const submittalAddressee = React.useMemo(() => {
    if (!bid) return null;
    const isSub = (bid.job_type || '') === 'Sub-Contractor';
    if (isSub) {
      const awarded = recipients.find(r => r.low_bidder) || null;
      if (awarded && (awarded.contact_name || awarded.client_name)) {
        const to = awarded.contact_name || awarded.client_name;
        const company = awarded.client_name || '';
        return { to, email: awarded.contact_email || '', role: 'prime',
          display: company && to !== company ? `${to} (${company})` : to };
      }
      // The awarded-by contact lives on the PROJECT — the bids table has no
      // awarded_by_* columns at all, so reading it off `bid` meant this fallback
      // never once fired and those jobs looked like they had nobody to address.
      if (project?.awarded_by_name) {
        const to = project.awarded_by_name;
        const company = project.awarded_by_company || '';
        return { to, email: project.awarded_by_email || '', role: 'prime',
          display: company && to !== company ? `${to} (${company})` : to };
      }
      return null;
    }
    // Prime → owner
    const to = bid.client_contact || bid.client_name || '';
    if (!to) return null;
    return { to, email: bid.client_email || '', role: 'owner',
      display: bid.client_name && to !== bid.client_name ? `${to} (${bid.client_name})` : to };
  }, [bid, recipients, project]);

  // Every party a submittal could be addressed to — the owner plus each bid
  // recipient (GC/prime) — so a PM can switch a submittal's recipient by picking
  // from a dropdown instead of re-typing the name/email.
  const submittalAddresseeOptions = React.useMemo(() => {
    if (!bid) return [];
    const opts = [];
    const ownerTo = bid.client_contact || bid.client_name || '';
    if (ownerTo) opts.push({ key: 'owner', label: `Owner — ${ownerTo}${bid.client_name && ownerTo !== bid.client_name ? ` (${bid.client_name})` : ''}`, to: ownerTo, email: bid.client_email || '' });
    recipients.forEach(r => {
      const to = r.contact_name || r.client_name || '';
      if (!to) return;
      const tag = r.low_bidder ? 'Prime (awarded)' : 'GC / Client';
      opts.push({ key: `rec-${r.id}`, label: `${tag} — ${to}${r.client_name && to !== r.client_name ? ` (${r.client_name})` : ''}`, to, email: r.contact_email || '' });
    });
    return opts;
  }, [bid, recipients]);

  const [showPoModal, setShowPoModal] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [showAddWorkflow, setShowAddWorkflow] = useState(false);
  const [showAddZone1, setShowAddZone1] = useState(false);
  const [addingWorkflow, setAddingWorkflow] = useState(false);
  const [showWorkflowSetup, setShowWorkflowSetup] = useState(false); // multi-select category picker
  const [wfSel, setWfSel] = useState({}); // category key -> selected

  // Current user role (for zone gating)
  const currentUserRole = (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role; } catch { return null; } })();
  const isAdminZone = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'].includes(currentUserRole);

  const saveTimer = useRef(null);

  useEffect(() => {
    Promise.all([
      apiFetch(`/bids/${id}`),
      apiFetch('/users'),
      apiFetch('/clients'),
      apiFetch('/vendors').catch(() => []),
    ]).then(([bidData, empData, clientData, vendorData]) => {
      const { items: rawItems, ...bidFields } = bidData;
      setBid(bidFields);
      setVendors(Array.isArray(vendorData) ? vendorData : []);
      const manualItems = (rawItems || []).map(i => ({ ...i, _key: i.id || Math.random().toString(36).slice(2), sub_items: i.sub_items ? JSON.parse(i.sub_items) : [] }));
      const holeSchedule = bidFields.drill_hole_schedule ? JSON.parse(bidFields.drill_hole_schedule) : [];
      const holeItems = computeHoleItems(holeSchedule, bidFields);
      setItems([...holeItems, ...manualItems]);
      setEmployees(empData);
      setClients(clientData);
      // Load contacts for the already-selected client, if any
      if (bidFields.client_id) {
        apiFetch(`/clients/${bidFields.client_id}/contacts`).then(setClientContacts).catch(() => {});
      }
      apiFetch(`/bids/${id}/attachments`).then(setAttachments).catch(() => {});
      apiFetch(`/bids/${id}/recipients`).then(setRecipients).catch(() => {});
      apiFetch(`/bids/${id}/alternates`).then(a => setAlternates(Array.isArray(a) ? a : [])).catch(() => {});
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!projectId) return;
    apiFetch(`/projects/${projectId}`).then(p => {
      setProject(p);
      // Contract fields
      const contractFields = ['contract_number','contract_value','contract_signed_date','contract_contact','contract_notes','certified_payroll','prevailing_wage_notes','payroll_schedule','compliance_notes'];
      const c = {};
      contractFields.forEach(f => { c[f] = p[f] ?? ''; });
      setContract(c);
      // Workflow sub-jobs
      const sjs = Array.isArray(p.subJobs) ? p.subJobs : [];
      setSubJobs(sjs);
      const notes = {}; const open = {};
      sjs.forEach(sj => { notes[sj.id] = sj.notes || ''; open[sj.id] = false; }); // all collapsed on load
      setSubJobNotes(notes);
      setShowSubJobs(open);
      // Contract is always-on for large Jobs (not Work Orders). Auto-create it
      // once if a large job doesn't already have a Contract workflow.
      if (isAdminZone && p.division === 'large' && !sjs.some(sj => sj.type === 'contract') && contractEnsuredRef.current !== projectId) {
        contractEnsuredRef.current = projectId;
        addWorkflowTab('contract', 'Contract');
      }
    }).catch(console.error);
    // Load Zone 2 data
    Promise.all([
      apiFetch(`/projects/${projectId}/daily-updates`).catch(() => []),
      apiFetch(`/projects/${projectId}/punch-list`).catch(() => []),
      apiFetch(`/projects/${projectId}/closeout`).catch(() => []),
      apiFetch(`/projects/${projectId}/purchase-orders`).catch(() => []),
      apiFetch('/vendors').catch(() => []),
      apiFetch(`/projects/${projectId}/notes`).catch(() => []),
      apiFetch(`/projects/${projectId}/work-orders`).catch(() => []),
    ]).then(([updates, punch, co, pos, vnd, notes, wos]) => {
      setDailyUpdates(updates);
      setFieldNotes(Array.isArray(notes) ? notes : []);
      setWorkOrders(Array.isArray(wos) ? wos : []);
      setPunchList(punch);
      setCloseout(co);
      setProjectPOs(pos);
      setVendors(Array.isArray(vnd) ? vnd : []);
    });
  }, [projectId]);

  // Confirmation modal state for project-mode Job Description edits
  const [pendingJobDescSave, setPendingJobDescSave] = useState(null); // { bidData }

  // Auto-save bid header when it changes
  const saveBid = useCallback(async (bidData) => {
    try {
      const updated = await apiFetch(`/bids/${id}`, { method: 'PATCH', body: JSON.stringify(bidData) });
      const { items: _, ...bidFields } = updated;
      setBid(prev => ({ ...prev, ...bidFields }));
      setDirty(false);
    } catch (ex) { showToast(ex.message, 'error'); }
  }, [id]);

  // The Additional Comments box is gated by a checkbox whose initial state is
  // computed at mount — before the bid finishes loading — so a bid that already
  // has comments would open with the box hidden until re-checked. Re-sync it
  // once the loaded bid actually has comments. (Only ever reveals; unchecking
  // "Include" clears exclusions, so this never fights the user hiding it.)
  useEffect(() => {
    if (bid?.exclusions) setShowAdditionalComments(true);
  }, [bid?.exclusions]);

  // After a PO is written from the project header, refresh the PO list and offer
  // to spin up a Deliver Materials shop order to pick it up and bring it to the job.
  function afterPoCreated(po) {
    // Whether to also spin up a pickup/delivery shop order is now a checkbox in the
    // PO form (CreatePoModal creates it and opens its card itself) — no more popup.
    apiFetch(`/projects/${projectId}/purchase-orders`).then(setProjectPOs).catch(() => {});
    showToast(`PO #${po.po_number} created`);
  }

  async function addWorkflowTab(type, customName) {
    // One card per office workflow type — a second "Change Orders" (or Contract,
    // Submittals…) card just splits the same work across two tabs.
    const dup = OFFICE_WORKFLOW_TYPES.includes(type) && subJobs.find(s => s.type === type);
    if (dup) {
      setShowAddWorkflow(false);
      setShowSubJobs(s => ({ ...s, [dup.id]: true }));
      showToast(`This job already has a ${WORKFLOW_COLORS[type]?.label || type} tab.`);
      return;
    }
    setAddingWorkflow(true);
    try {
      const name = customName || type;
      const sj = await apiFetch(`/projects/${projectId}/sub-jobs`, {
        method: 'POST',
        body: JSON.stringify({ type, name, sortOrder: subJobs.length }),
      });
      setSubJobs(s => [...s, sj]);
      setSubJobNotes(n => ({ ...n, [sj.id]: '' }));
      setShowSubJobs(s => ({ ...s, [sj.id]: true }));
      setShowAddWorkflow(false);
    } catch (e) { showToast(e.message, 'error'); }
    setAddingWorkflow(false);
  }

  // Create several workflow tabs at once (imported-project setup picker).
  async function addWorkflowTabs(keys) {
    if (!keys.length) return;
    setAddingWorkflow(true);
    try {
      const created = [];
      let order = subJobs.length;
      for (const key of keys) {
        const opt = ALL_WORKFLOW_OPTIONS.find(o => o.key === key);
        const sj = await apiFetch(`/projects/${projectId}/sub-jobs`, {
          method: 'POST', body: JSON.stringify({ type: key, name: opt?.label || key, sortOrder: order++ }),
        });
        created.push(sj);
      }
      setSubJobs(s => [...s, ...created]);
      setSubJobNotes(n => { const nn = { ...n }; created.forEach(sj => nn[sj.id] = ''); return nn; });
      setShowSubJobs(s => { const ns = { ...s }; created.forEach(sj => ns[sj.id] = true); return ns; });
      setWfSel({});
      setShowWorkflowSetup(false);
      showToast(`Added ${created.length} workflow${created.length !== 1 ? 's' : ''}`, 'success');
    } catch (e) { showToast(e.message, 'error'); }
    setAddingWorkflow(false);
  }

  const dragSjRef = React.useRef(null);
  // Drag-to-reorder collapsed tabs. Moves the dragged sub-job to the target's
  // spot (within its zone) and persists the new order.
  async function moveSubJob(draggedId, targetId) {
    if (!draggedId || draggedId === targetId) return;
    const arr = [...subJobs];
    const from = arr.findIndex(s => s.id === draggedId);
    const origTo = arr.findIndex(s => s.id === targetId);
    if (from < 0 || origTo < 0) return;
    const [moved] = arr.splice(from, 1);
    const newTo = arr.findIndex(s => s.id === targetId);
    arr.splice(from < origTo ? newTo + 1 : newTo, 0, moved); // dropping onto an item below → land after it
    setSubJobs(arr);
    try { await apiFetch(`/projects/${projectId}/sub-jobs/reorder`, { method: 'POST', body: JSON.stringify({ order: arr.map(s => s.id) }) }); } catch {}
  }

  async function deleteWorkflowTab(sjId) {
    if (!window.confirm('Remove this workflow tab? Notes will be lost.')) return;
    try {
      await apiFetch(`/projects/${projectId}/sub-jobs/${sjId}`, { method: 'DELETE' });
      setSubJobs(s => s.filter(x => x.id !== sjId));
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function saveContract() {
    setContractSaving(true);
    try {
      await apiFetch(`/projects/${projectId}/contract`, { method: 'PATCH', body: JSON.stringify(contract) });
      showToast('Contract info saved');
    } catch (e) { showToast(e.message, 'error'); }
    setContractSaving(false);
  }

  async function postDailyUpdate() {
    if (!newUpdate.trim()) return;
    setPostingUpdate(true);
    try {
      const u = await apiFetch(`/projects/${projectId}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: newUpdate }) });
      setDailyUpdates(d => [u, ...d]);
      setNewUpdate('');
    } catch (e) { showToast(e.message, 'error'); }
    setPostingUpdate(false);
  }

  async function addPunchItem() {
    if (!newPunchItem.trim()) return;
    try {
      const item = await apiFetch(`/projects/${projectId}/punch-list`, { method: 'POST', body: JSON.stringify({ description: newPunchItem, assigned_to: newPunchAssignee }) });
      setPunchList(p => [...p, item]);
      setNewPunchItem(''); setNewPunchAssignee('');
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function updatePunchStatus(itemId, status) {
    try {
      const updated = await apiFetch(`/projects/${projectId}/punch-list/${itemId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setPunchList(p => p.map(x => x.id === itemId ? updated : x));
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function deletePunchItem(itemId) {
    try {
      await apiFetch(`/projects/${projectId}/punch-list/${itemId}`, { method: 'DELETE' });
      setPunchList(p => p.filter(x => x.id !== itemId));
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function addCloseoutItem() {
    if (!newCloseoutItem.trim()) return;
    try {
      const item = await apiFetch(`/projects/${projectId}/closeout`, { method: 'POST', body: JSON.stringify({ description: newCloseoutItem }) });
      setCloseout(c => [...c, item]);
      setNewCloseoutItem('');
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function toggleCloseoutItem(itemId, checked) {
    try {
      const updated = await apiFetch(`/projects/${projectId}/closeout/${itemId}`, { method: 'PATCH', body: JSON.stringify({ checked }) });
      setCloseout(c => c.map(x => x.id === itemId ? updated : x));
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function deleteCloseoutItem(itemId) {
    try {
      await apiFetch(`/projects/${projectId}/closeout/${itemId}`, { method: 'DELETE' });
      setCloseout(c => c.filter(x => x.id !== itemId));
    } catch (e) { showToast(e.message, 'error'); }
  }

  async function deleteProject() {
    setDeletingProject(true);
    try {
      await apiFetch(`/projects/${projectId}`, { method: 'DELETE' });
      // The delete already unlinks the bid; best-effort return it to the Bids list.
      // Don't let this follow-up failing report the whole delete as failed.
      try { await apiFetch(`/bids/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'draft' }) }); } catch {}
      navigate('/admin/new-projects');
    } catch (e) { alert('Delete failed: ' + (e.message || e.error || 'Please try again.')); }
    setDeletingProject(false);
  }

  async function saveSubJob(sj) {
    setSubJobSaving(s => ({ ...s, [sj.id]: true }));
    try {
      await apiFetch(`/projects/${projectId}/sub-jobs/${sj.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: subJobNotes[sj.id], status: sj.status }),
      });
    } catch (e) { alert('Save failed: ' + e.message); }
    setSubJobSaving(s => ({ ...s, [sj.id]: false }));
  }

  // Keep the editable Job/Project number field in sync with the loaded project.
  React.useEffect(() => { setProjNumDraft(project?.project_number || ''); }, [project?.project_number]);
  async function saveProjectNumber() {
    const v = projNumDraft.trim();
    if (!projectId || !project || v === (project.project_number || '')) return;
    try {
      await apiFetch(`/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify({ projectNumber: v }) });
      setProject(p => ({ ...p, project_number: v }));
      if (showToast) showToast('Job number updated', 'success');
    } catch { if (showToast) showToast('Could not update the job number', 'error'); }
  }

  // Awarded By — the contractor who gave US the work (when we're a sub). Captured in
  // the award modal, but plenty of jobs were awarded before that or need correcting,
  // and it drives who submittals and change orders are addressed to (vs the Owner).
  const [awardedByDraft, setAwardedByDraft] = useState('');
  React.useEffect(() => { setAwardedByDraft(project?.awarded_by_name || ''); }, [project?.awarded_by_name]);
  // rec (optional) = the bid recipient picked from a chip — sending its client_id lets
  // the server attach that company's mailing address for the letter block.
  async function saveAwardedBy(nameOverride, rec) {
    const v = (nameOverride !== undefined ? nameOverride : awardedByDraft).trim();
    if (!projectId || !project) return;
    if (!rec && v === (project.awarded_by_name || '')) return;
    const body = rec
      ? { awardedByName: rec.contact_name || rec.client_name, awardedByClientId: rec.client_id, awardedByContact: rec.contact_name || null, awardedByEmail: rec.contact_email || null, awardedByPhone: rec.contact_phone || null }
      : { awardedByName: v || null };
    try {
      await apiFetch(`/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(body) });
      // Re-read so the address the server resolved shows up (and prints).
      const fresh = await apiFetch(`/projects/${projectId}`).catch(() => null);
      if (fresh && fresh.id) setProject(fresh);
      else setProject(p => ({ ...p, awarded_by_name: body.awardedByName || null }));
      if (showToast) showToast(body.awardedByName ? `Change orders will be addressed to ${rec ? rec.client_name : v}` : 'Cleared — documents will address the Owner', 'success');
    } catch { if (showToast) showToast('Could not update who awarded this job', 'error'); }
  }

  // Target completion — set during the award flow, but jobs move, so it has to be
  // editable here too. Drives the due-date pills and the overdue flags on the PM
  // dashboards and boards. Note: PATCH /projects/:id expects camelCase.
  // Open a call-out under this job. It becomes its own small job (numbered
  // <this job>-WO###) so its POs and billing land on it, not on the contract.
  async function createWorkOrder() {
    const name = woName.trim();
    if (!name || creatingWo) return;
    setCreatingWo(true);
    try {
      const wo = await apiFetch(`/projects/${projectId}/work-orders`, { method: 'POST', body: JSON.stringify({ name, board: woBoard }) });
      const fresh = await apiFetch(`/projects/${projectId}/work-orders`).catch(() => null);
      if (Array.isArray(fresh)) setWorkOrders(fresh);
      setWoName(''); setShowWoAdd(false);
      if (showToast) showToast(`${wo.project_number} created`, 'success');
    } catch (e) { if (showToast) showToast(e?.error || e?.message || 'Could not create the work order', 'error'); }
    setCreatingWo(false);
  }

  // Work orders: which scheduled field board tracks this job (Small Jobs board, or
  // hand it to a drilling/loops board). Set here on the job; the boards read it.
  async function setScheduleWorkflow(v) {
    setProject(p => ({ ...p, schedule_workflow: v }));
    try { await apiFetch(`/work-orders/${projectId}`, { method: 'PATCH', body: JSON.stringify({ schedule_workflow: v }) }); }
    catch (e) { if (showToast) showToast(e.message || 'Could not update the schedule board', 'error'); }
  }

  // Refresh just the sub-job rows (drill/status/counts) without touching the
  // notes being edited — used after the drilling card makes a change.
  const reloadSubJobs = React.useCallback(() => {
    if (!projectId) return;
    apiFetch(`/projects/${projectId}`).then(p => { if (Array.isArray(p.subJobs)) setSubJobs(p.subJobs); }).catch(() => {});
  }, [projectId]);

  // Name for a PM id, tolerant of either employee-record shape.
  function pmNameFor(pid) {
    if (!pid) return 'Unassigned';
    const e = (employees || []).find(x => String(x.id) === String(pid));
    if (!e) return 'Unassigned';
    return `${e.first_name || e.firstName || ''} ${e.last_name || e.lastName || ''}`.trim() || 'Unassigned';
  }

  // The card object the shared DrillCardModal expects (built from sub-job + project).
  function drillCardFor(sj) {
    return {
      id: sj.id, project_id: projectId, project_number: project?.project_number,
      project_name: project?.name, client_name: project?.client_name, pm_id: project?.pm_id,
      status: sj.status, drill_rig: sj.drill_rig, name: sj.name,
      drill_date: sj.drill_date, job_update: sj.job_update, drill_comment: sj.drill_comment,
    };
  }

  // Job Description fields — in project mode these need confirmation before saving
  const JOB_DESC_FIELDS = new Set(['job_name','job_location','bid_date','bid_time','bid_type','job_type','public_private','project_manager_id','pre_bid','certified_payroll']);

  function updateBid(key, value) {
    setBid(b => {
      const next = { ...b, [key]: value };
      if (projectId && JOB_DESC_FIELDS.has(key)) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => setPendingJobDescSave({ bidData: next }), 1200);
      } else {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveBid(next), 1200);
      }
      return next;
    });
    setDirty(true);
  }

  // Hard Bid ⇄ Time & Material. This choice used to be available only while
  // creating the bid, so an existing job could never be switched to T&M (and the
  // T&M price + billing sections could never appear on it). Uses the dedicated
  // pricing endpoint; office roles only, enforced server-side.
  async function setPricingType(v) {
    const prev = bid.pricing_type;
    if (v === prev) return;
    setBid(b => ({ ...b, pricing_type: v }));
    try {
      const updated = await apiFetch(`/bids/${id}/pricing`, { method: 'PATCH', body: JSON.stringify({ pricing_type: v }) });
      if (updated && updated.id) setBid(b => ({ ...b, ...updated }));
      showToast(v === 'tm' ? 'Now a Time & Material job — set the rate below.' : 'Now a Hard Bid.');
    } catch (e) {
      setBid(b => ({ ...b, pricing_type: prev }));
      showToast(e?.error || e?.message || 'Could not change the pricing type', 'error');
    }
  }

  function updateBidFields(fields) {
    setBid(b => {
      const next = { ...b, ...fields };
      const hasJobDescField = Object.keys(fields).some(k => JOB_DESC_FIELDS.has(k));
      if (projectId && hasJobDescField) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => setPendingJobDescSave({ bidData: next }), 1200);
      } else {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveBid(next), 1200);
      }
      return next;
    });
    setDirty(true);
  }

  // Workflows chosen on the bid card — defaults from the bid type until explicitly set.
  function currentBidWorkflows() {
    try { const a = bid.bid_workflows ? JSON.parse(bid.bid_workflows) : []; if (Array.isArray(a) && a.length) return a; } catch {}
    const arr = [];
    if (bid.bid_type === 'Vertical Drilling') arr.push('vertical_drilling');
    if (bid.bid_type === 'Directional Drilling') arr.push('horizontal_drilling');
    // A Loops work type has no bid_type of its own — it defaults the workflow directly.
    if (bid.work_type === 'loops') arr.push('loops');
    return arr;
  }
  function toggleBidWorkflow(key) {
    const cur = currentBidWorkflows();
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    setBid(b => ({ ...b, bid_workflows: JSON.stringify(next) }));
    setDirty(true);
    apiFetch(`/bids/${id}`, { method: 'PATCH', body: JSON.stringify({ bid_workflows: next }) }).catch(() => {});
  }

  function updateHoleSchedule(schedule) {
    const json = JSON.stringify(schedule);
    const holeItems = computeHoleItems(schedule, bid);
    setBid(b => {
      const next = { ...b, drill_hole_schedule: json };
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => saveBid(next), 1200);
      return next;
    });
    setItems(its => [...holeItems, ...its.filter(i => i.auto_source !== 'hole_schedule')]);
    setDirty(true);
  }

  function updateItem(key, field, value) {
    setItems(its => its.map(i => {
      if (i._key !== key) return i;
      const next = { ...i, [field]: value };
      if (field === 'quantity' && Array.isArray(i.sub_items) && i.sub_items.some(sub => sub.per_unit != null)) {
        const q = Number(value) || 0;
        next.sub_items = i.sub_items.map(sub => (sub.per_unit != null ? { ...sub, quantity: Math.round(Number(sub.per_unit) * q * 10000) / 10000 } : sub));
      }
      return next;
    }));
    setDirty(true);
    scheduleItemsSave();
  }

  // Drag-to-reorder the manual bid line items. Order is persisted on Save as
  // sort_order = array index (see saveItems); auto hole-schedule rows stay pinned
  // on top and are not reorderable.
  const dragItemKey = React.useRef(null);
  const [dragOverKey, setDragOverKey] = React.useState(null);
  // Advance a line's colour flag: unset → red → yellow → green → blue → black → unset.
  // Reads the CURRENT flag out of state rather than the one closed over by the click
  // handler, so tapping twice quickly actually moves two colours instead of computing
  // both from the same stale value.
  function cycleFlag(key) {
    setItems(its => its.map(i => {
      if (i._key !== key) return i;
      const at = ITEM_FLAGS.findIndex(f => f.key === i.flag);
      return { ...i, flag: at === ITEM_FLAGS.length - 1 ? null : ITEM_FLAGS[at + 1].key };
    }));
    setDirty(true);
    scheduleItemsSave();
  }

  // Comment thread on a line item. Append-only per author so a reviewer adds to what
  // the estimator wrote instead of replacing it; you can delete your own, and an
  // owner/Executive can delete any. Stored as JSON on the row (same shape as
  // sub_items), which is why the save path had to be taught about the column.
  const [commentsFor, setCommentsFor] = React.useState(null); // item._key whose thread is open
  const [commentDraft, setCommentDraft] = React.useState('');
  const me = (() => { try { return JSON.parse(localStorage.getItem('te_worker') || 'null'); } catch { return null; } })();
  const myName = me ? `${me.firstName || ''} ${me.lastName || ''}`.trim() || 'Office' : 'Office';
  const itemComments = it => {
    const c = it?.comments;
    if (!c) return [];
    try { return typeof c === 'string' ? (JSON.parse(c) || []) : c; } catch { return []; }
  };
  function addComment(key) {
    const text = commentDraft.trim();
    if (!text) return;
    setItems(its => its.map(i => i._key !== key ? i : {
      ...i,
      comments: [...itemComments(i), { id: Math.random().toString(36).slice(2), author: myName, authorId: me?.id || null, text, at: new Date().toISOString() }],
    }));
    setCommentDraft('');
    setDirty(true);
    scheduleItemsSave();
  }
  function deleteComment(key, commentId) {
    setItems(its => its.map(i => i._key !== key ? i : { ...i, comments: itemComments(i).filter(c => c.id !== commentId) }));
    setDirty(true);
    scheduleItemsSave();
  }

  function reorderItems(fromKey, toKey) {
    if (!fromKey || fromKey === toKey) return;
    setItems(its => {
      const auto = its.filter(i => i.auto_source === 'hole_schedule');
      const manual = its.filter(i => i.auto_source !== 'hole_schedule');
      const from = manual.findIndex(i => i._key === fromKey);
      const to = manual.findIndex(i => i._key === toKey);
      if (from < 0 || to < 0) return its;
      const next = [...manual];
      const [moved] = next.splice(from, 1);
      // Dropping a line onto another line puts it in that line's group. The sheet is
      // drawn base-first then group by group, so dragging up past a heading and
      // letting go is the obvious way to move something between them — and it was
      // the one thing the drag handle didn't do.
      const landedOn = next[to > from ? to - 1 : to];
      next.splice(to, 0, { ...moved, alternate_id: landedOn ? (landedOn.alternate_id || null) : (moved.alternate_id || null) });
      return [...auto, ...next];
    });
    setDirty(true);
    scheduleItemsSave();
  }

  // Dropping onto the heading itself — the only way into a group that has no lines
  // to aim at yet, and the natural target when you want a line at the top of one.
  function dropItemOnAlternate(fromKey, altId) {
    if (!fromKey) return;
    setItems(its => its.map(i => i._key === fromKey ? { ...i, alternate_id: altId } : i));
    setDirty(true);
    scheduleItemsSave();
  }

  // Sort the line items by their bid item number. This reorders the rows for real
  // (rather than sorting a view) because row order IS sort_order — a display-only
  // sort would make the drag handles reorder something you can't see. Numeric-aware
  // so 2 lands before 10, mixed forms like "7A" sort next to 7, and rows with no
  // number go last instead of jumping to the top. Auto hole-schedule rows stay
  // pinned above, as they are for drag.
  const [itemSortDir, setItemSortDir] = React.useState(null); // null | 'asc' | 'desc'
  // Which flag colour the list is filtered to. A FILTER, not a sort — the bid keeps
  // its own order and nothing is written; this only changes what you're looking at.
  const [flagFilter, setFlagFilter] = React.useState(null);
  function sortItemsByNumber() {
    const dir = itemSortDir === 'asc' ? 'desc' : 'asc';
    setItemSortDir(dir);
    const rank = it => {
      const raw = String(it.item_number ?? '').trim();
      if (!raw) return null;
      const m = raw.match(/^(\d+)/);
      return m ? [parseInt(m[1], 10), raw] : [Number.MAX_SAFE_INTEGER - 1, raw];
    };
    setItems(its => {
      const auto = its.filter(i => i.auto_source === 'hole_schedule');
      const manual = [...its.filter(i => i.auto_source !== 'hole_schedule')];
      manual.sort((a, b) => {
        const ra = rank(a), rb = rank(b);
        if (ra === null && rb === null) return 0;
        if (ra === null) return 1;   // blanks always last, both directions
        if (rb === null) return -1;
        const cmp = ra[0] !== rb[0] ? ra[0] - rb[0] : String(ra[1]).localeCompare(String(rb[1]), undefined, { numeric: true });
        return dir === 'asc' ? cmp : -cmp;
      });
      return [...auto, ...manual];
    });
    setDirty(true);
    scheduleItemsSave();
    if (showToast) showToast(`Line items sorted by item # (${dir === 'asc' ? 'low to high' : 'high to low'})`, 'success');
  }

  async function saveItems(itemList, opts = {}) {
    setSaving(true);
    try {
      const manualOnly = itemList.filter(i => i.auto_source !== 'hole_schedule');
      const result = await apiFetch(`/bids/${id}/items`, {
        method: 'PUT',
        body: JSON.stringify({ items: manualOnly.map((it, idx) => ({ ...it, sort_order: idx, sub_items: it.sub_items?.length ? JSON.stringify(it.sub_items) : null })) }),
      });
      // Reconcile server ids back into state ONLY if nothing changed while saving —
      // keeps row keys stable (no input remount / focus loss) and never clobbers
      // characters typed during the round-trip. If the user kept editing, the next
      // debounce saves again.
      if (itemsRef.current === itemList) {
        const savedManual = result.items.map((i, idx) => ({ ...i, _key: manualOnly[idx]?._key || i.id, sub_items: i.sub_items ? JSON.parse(i.sub_items) : [] }));
        const holeSchedule = bid.drill_hole_schedule ? JSON.parse(bid.drill_hole_schedule) : [];
        const holeItems = computeHoleItems(holeSchedule, bid);
        setItems([...holeItems, ...savedManual]);
        setDirty(false);
      }
      if (!opts.silent) showToast('Saved');
      return true;
    } catch (ex) { showToast(ex.message, 'error'); return false; } finally { setSaving(false); }
  }

  // Live ref to current items + a debounced auto-save so bids save as they're typed.
  const itemsRef = React.useRef(items);
  React.useEffect(() => { itemsRef.current = items; }, [items]);
  const itemsSaveTimer = React.useRef(null);
  // True from the moment something is typed until that edit is actually on the
  // server. Drives the "Saving…/Saved" pill, the unmount flush and the tab-close
  // warning below — all of which need to know about work that only exists in the
  // browser so far.
  const pendingSaveRef = React.useRef(false);
  const [saveState, setSaveState] = React.useState('idle'); // idle | pending | saving | saved | error
  function scheduleItemsSave() {
    clearTimeout(itemsSaveTimer.current);
    pendingSaveRef.current = true;
    setSaveState('pending');
    itemsSaveTimer.current = setTimeout(async () => {
      setSaveState('saving');
      const ok = await saveItems(itemsRef.current, { silent: true }).catch(() => false);
      if (ok) { pendingSaveRef.current = false; setSaveState('saved'); }
      else setSaveState('error');
    }, 1200);
  }

  // Auto-save is a 1.2s debounce, so an edit lives ONLY in the browser until you
  // pause. Leaving the page inside that window used to destroy the component and
  // the pending timer with it, and the edit was gone with nothing said — which is
  // how Pat lost line items twice. Flush on the way out instead, with keepalive so
  // the request survives the page tearing down, and don't touch state afterwards
  // because the component is already going.
  React.useEffect(() => () => {
    clearTimeout(itemsSaveTimer.current);
    if (!pendingSaveRef.current) return;
    try {
      const manualOnly = (itemsRef.current || []).filter(i => i.auto_source !== 'hole_schedule');
      fetch(`${API_BASE}/bids/${id}/items`, {
        method: 'PUT', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() },
        body: JSON.stringify({ items: manualOnly.map((it, idx) => ({ ...it, sort_order: idx, sub_items: it.sub_items?.length ? JSON.stringify(it.sub_items) : null })) }),
      }).catch(() => {});
    } catch { /* last-ditch — never block navigation */ }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closing or reloading the tab can't be flushed reliably, so ask instead.
  React.useEffect(() => {
    const warn = e => { if (pendingSaveRef.current) { e.preventDefault(); e.returnValue = ''; return ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  // Add a blank line straight into an alternate. Without this the only route into a
  // group was: add to the base, open Choose lines, tick it, save — four steps to type
  // one line, and nothing on the group itself said so.
  function addItemToAlternate(altId) {
    const isDrilling = bid.bid_type === 'Vertical Drilling';   // Directional lines carry mark-ups like any other bid
    const item = {
      ...newItem(items.length),
      ...(isDrilling ? { overhead_material_pct: 0, overhead_labor_pct: 0, profit_pct: 0 } : {}),
      alternate_id: altId,
    };
    setItems(its => [...its, item]);
    setActiveItemKey(item._key);
    setDirty(true);
    // setDirty only lights the pill — scheduleItemsSave is what actually writes.
    // Without it the new line lived in the browser and vanished on reload.
    scheduleItemsSave();
  }

  function addItem() {
    const manualItems = items.filter(i => i.auto_source !== 'hole_schedule');
    if (manualItems.length === 0) {
      doAddItem('bottom', '');
    } else {
      setAddAfterKey(manualItems[manualItems.length - 1]._key);
      setAddItemModal(true);
    }
  }
  function doAddItem(pos, afterKey) {
    const isDrilling = bid.bid_type === 'Vertical Drilling';   // Directional lines carry mark-ups like any other bid
    const item = {
      ...newItem(items.length),
      ...(isDrilling ? { overhead_material_pct: 0, overhead_labor_pct: 0, profit_pct: 0 } : {}),
    };
    setItems(its => {
      const auto = its.filter(i => i.auto_source === 'hole_schedule');
      const manual = its.filter(i => i.auto_source !== 'hole_schedule');
      let next;
      if (pos === 'top') {
        next = [item, ...manual];
      } else if (pos === 'after' && afterKey) {
        const idx = manual.findIndex(i => i._key === afterKey);
        if (idx === -1) {
          next = [...manual, item];
        } else {
          next = [...manual.slice(0, idx + 1), item, ...manual.slice(idx + 1)];
        }
      } else {
        next = [...manual, item];
      }
      return [...auto, ...next];
    });
    setActiveItemKey(item._key);
    setDirty(true);
    setAddItemModal(false);
    scheduleItemsSave();
  }

  const [pickLists, setPickLists] = useState({ equipment: [], common: [], assemblies: [], trench: [] });
  const [trenchFor, setTrenchFor] = useState(null);   // { key, setupId } — the Trenching window on a line (Ryan, Oct 23 2026)
  async function loadParts() {
    if (partsLoaded) return;
    // Equipment, the Price Book's common prices and the Bid Assemblies (Ryan, Oct 22 2026).
    Promise.all([
      apiFetch('/equipment').catch(() => []), apiFetch('/common-items').catch(() => []), apiFetch('/bid-assemblies').catch(() => []), apiFetch('/trenching').catch(() => null),
    ]).then(([equipment, common, assemblies, trench]) => setPickLists({
      equipment: Array.isArray(equipment) ? equipment : [], common: Array.isArray(common) ? common : [], assemblies: Array.isArray(assemblies) ? assemblies : [],
      trench: trench && Array.isArray(trench.setups) ? trench.setups : [],
    }));
    try {
      const res = await fetch(`${API_BASE}/parts`, { headers: { 'x-worker-token': getToken() } });
      if (res.ok) { setPartsAll(await res.json()); setPartsLoaded(true); }
    } catch {}
  }

  function openPartsSuggest(itemKey, currentDesc) {
    // A line already filled from Trenching goes straight back to its Trenching window, with what it
    // was set to (Ryan, Oct 23 2026) — "Other items…" in there opens this list.
    const line = items.find(i => i._key === itemKey);
    if (line && (line.trench_json || (Array.isArray(line.sub_items) && line.sub_items.some(x => x && x.trench)))) {
      let saved = null; try { saved = line.trench_json ? (typeof line.trench_json === 'string' ? JSON.parse(line.trench_json) : line.trench_json) : null; } catch { saved = null; }
      setTrenchFor({ key: itemKey, setupId: saved && saved.setupId, saved });
      return;
    }
    loadParts();
    setPartsSearch(currentDesc.trim());
    setPartsSuggestKey(itemKey);
  }

  function applyPart(part, itemKey) {
    setItems(its => its.map(i => {
      if (i._key !== itemKey) return i;
      return {
        ...i,
        description: part.description,
        unit: part.unit,
        material_cost: part.material_cost,
        labor_hours: part.labor_hours,
      };
    }));
    setDirty(true);
    scheduleItemsSave();
    setPartsSuggestKey(null);
  }

  // Same Parts Directory lookup, but for a sub-item (cost-breakdown line). Sub-item
  // popovers are keyed 'sub:<id>' so they don't clash with main-line keys.
  function openSubPartsSuggest(subId, currentDesc) {
    const sub = items.flatMap(i => Array.isArray(i.sub_items) ? i.sub_items : []).find(x => x && x.id === subId);
    if (sub && sub.trench_json) {
      let saved = null; try { saved = typeof sub.trench_json === 'string' ? JSON.parse(sub.trench_json) : sub.trench_json; } catch { saved = null; }
      setTrenchFor({ key: 'sub:' + subId, setupId: saved && saved.setupId, saved });
      return;
    }
    loadParts();
    setPartsSearch((currentDesc || '').trim());
    setPartsSuggestKey('sub:' + subId);
  }

  // One window for every line on the bid. The key it was opened with says where the
  // part goes — a line, or a sub-item under one.
  // What a pick from each tab puts on a line or sub-line. Equipment and Price Book rates go in
  // Sub $ (cost — the bid's overhead and profit go on top); labor is an hour at that trade's rate.
  function pickFields(tab, x) {
    if (tab === 'equipment') return { description: [x.asset_number, x.name].filter(Boolean).join(' · ') || x.name, unit: 'HR', material_cost: 0, labor_hours: 0, dje_standard: Number(x.hourly_rate) || Number(x.billing_rate) || 0 };
    if (tab === 'labor') return { description: x.role, unit: 'HR', material_cost: 0, labor_hours: 1, dje_standard: 0, labor_rate: x.rate };
    if (tab === 'common') return { description: x.name, unit: x.unit || 'EACH', material_cost: 0, labor_hours: 0, dje_standard: Number(x.rate) || 0 };
    return null;
  }
  // An assembly drops its lines in under the line, each times the line's quantity.
  function applyAssembly(asm, itemKey) {
    setItems(its => its.map(i => {
      if (i._key !== itemKey) return i;
      const q = Number(i.quantity) || 1;
      const subs = (asm.items || []).map(it => ({
        ...newSubItem(), description: it.description, unit: it.unit || 'EACH', per_unit: Number(it.quantity) || 0,
        quantity: Math.round((Number(it.quantity) || 0) * q * 10000) / 10000,
        material_cost: Number(it.material_cost) || 0, labor_hours: Number(it.labor_hours) || 0, dje_standard: Number(it.dje_standard) || 0,
        ...(it.labor_rate != null ? { labor_rate: Number(it.labor_rate) } : {}), notes: it.notes || '', assembly_id: asm.id,
      }));
      const blank = !String(i.description || '').trim();
      return { ...i, description: blank ? asm.name : i.description, unit: blank ? (asm.unit || i.unit) : i.unit, sub_items: [...(Array.isArray(i.sub_items) ? i.sub_items : []), ...subs] };
    }));
    setExpandedSubs(x => { const n = new Set(x); n.add(itemKey); return n; });
    setDirty(true);
    scheduleItemsSave();
    setPartsSuggestKey(null);
  }
  function applyTrench(itemKey, lines, lf, inputs = null, name = '', summary = null, note = '') {
    const sumFields = summary ? (({ totals, ...f }) => f)(summary) : null;
    const tj = inputs ? { trench_json: JSON.stringify(inputs) } : {};
    // A sub-line: that sub-line becomes the trench, summed (Ryan, Oct 26 2026).
    if (String(itemKey).startsWith('sub:')) {
      const subId = String(itemKey).slice(4);
      setItems(its => its.map(i => {
        if (!(i.sub_items || []).some(x => x.id === subId)) return i;
        const pq = Number(i.quantity) || 0;
        return { ...i, sub_items: i.sub_items.map(x => (x.id === subId ? { ...x, ...sumFields, ...tj, description: String(name || '').trim() || x.description || 'Trenching', unit: 'LF', quantity: lf, per_unit: pq > 0 ? lf / pq : null, notes: note || x.notes || '' } : x)) };
      }));
      setDirty(true); scheduleItemsSave(); setTrenchFor(null);
      return;
    }
    setItems(its => its.map(i => {
      if (i._key !== itemKey) return i;
      // One summed line: into the line itself — or, when the line already has other sub-lines (which is
      // what prices it then), as one sub-line beside them.
      if (inputs && inputs.mode === 'one' && sumFields) {
        const kept = (Array.isArray(i.sub_items) ? i.sub_items : []).filter(x => !(x && x.trench));
        const nm = String(name || '').trim() || String(i.description || '').trim() || 'Trenching';
        if (kept.length) {
          const qty = Number(i.quantity) || 1;
          const one = { ...newSubItem(), ...sumFields, description: nm, unit: 'LF', quantity: lf, per_unit: qty > 0 ? lf / qty : null, notes: note, trench: true };
          return { ...i, description: nm, sub_items: [...kept, one], ...tj };
        }
        return { ...i, ...sumFields, description: nm, unit: 'LF', quantity: lf, notes: note || i.notes || '', sub_items: [], ...tj };
      }
      const kept = (Array.isArray(i.sub_items) ? i.sub_items : []).filter(x => !(x && x.trench));
      const redo = kept.length !== (Array.isArray(i.sub_items) ? i.sub_items.length : 0);
      const hadSubs = !redo && kept.length > 0;
      const asLf = redo ? /^LF$/i.test(i.unit || '') : !hadSubs && (/^LF$/i.test(i.unit || '') || !(Number(i.quantity) > 1));
      const qty = asLf ? lf : (Number(i.quantity) || lf);
      const subs = lines.map(l => ({ ...newSubItem(), ...l, per_unit: qty > 0 ? Number(l.quantity) / qty : null, quantity: Number(l.quantity) || 0, trench: true }));
      const nm = String(name || '').trim() || String(i.description || '').trim() || 'Trenching';
      return { ...i, description: nm, unit: asLf ? 'LF' : i.unit, quantity: qty, sub_items: [...kept, ...subs], ...(inputs ? { trench_json: JSON.stringify(inputs) } : {}) };
    }));
    setExpandedSubs(x => { const s2 = new Set(x); s2.add(itemKey); return s2; });
    setDirty(true); scheduleItemsSave(); setTrenchFor(null);
  }
  function applyPickedPart(part, tab = 'material') {
    const key = partsSuggestKey;
    if (!key) return;
    if (tab === 'assembly') { if (!String(key).startsWith('sub:')) applyAssembly(part, key); return; }
    if (tab === 'trenching') { setTrenchFor({ key, setupId: part.id }); setPartsSuggestKey(null); return; }
    const f = pickFields(tab, part);
    if (f) {
      if (String(key).startsWith('sub:')) {
        const subId = String(key).slice(4);
        setItems(its => its.map(i => (i.sub_items || []).some(x => x.id === subId) ? { ...i, sub_items: i.sub_items.map(x => (x.id === subId ? { ...x, ...f } : x)) } : i));
      } else setItems(its => its.map(i => (i._key === key ? { ...i, ...f } : i)));
      setDirty(true); scheduleItemsSave(); setPartsSuggestKey(null);
      return;
    }
    if (String(key).startsWith('sub:')) {
      const subId = String(key).slice(4);
      const owner = items.find(it => (it.sub_items || []).some(s => s.id === subId));
      if (owner) applySubPart(part, owner._key, subId); else setPartsSuggestKey(null);
    } else applyPart(part, key);
  }
  const partsPickerOptions = partsAll.map(p => ({
    key: p.id, label: p.description, category: p.category || '',
    sub: [p.category, p.unit, p.labor_hours ? p.labor_hours + 'h labor' : ''].filter(Boolean).join(' · '),
    price: p.material_cost, original: p,
  }));

  function applySubPart(part, itemKey, subId) {
    setItems(its => its.map(i => {
      if (i._key !== itemKey) return i;
      const subs = Array.isArray(i.sub_items) ? i.sub_items : [];
      return {
        ...i,
        sub_items: subs.map(s => s.id === subId
          ? { ...s, description: part.description, unit: part.unit, material_cost: part.material_cost, labor_hours: part.labor_hours }
          : s),
      };
    }));
    setDirty(true);
    scheduleItemsSave();
    setPartsSuggestKey(null);
  }

  async function addRecipient() {
    const co = recipientCompany;
    if (!co) return;
    const contact = recipientContacts.find(c => c.id === recipientContactId);
    const body = {
      client_id: co.clientId || null,
      client_name: co.name,
      contact_id: contact?.id || null,
      contact_name: contact?.name || co.contact || null,
      contact_email: contact?.email || co.email || null,
      contact_phone: contact?.phone || co.phone || null,
    };
    try {
      const updated = await apiFetch(`/bids/${id}/recipients`, { method: 'POST', body: JSON.stringify(body) });
      setRecipients(Array.isArray(updated) ? updated : []);
      setShowAddRecipient(false);
      setRecipientCompany(null);
      setRecipientContactId('');
      setRecipientContacts([]);
    } catch (err) {
      showToast(err.message || 'Failed to add recipient', 'error');
    }
  }

  async function removeRecipient(recId) {
    await apiFetch(`/bids/${id}/recipients/${recId}`, { method: 'DELETE' });
    setRecipients(r => r.filter(x => x.id !== recId));
  }

  // Update a recipient's GC-price / low-bidder / used-us tracking. Optimistic
  // update so typing feels instant; the server returns the authoritative list
  // (which also enforces one-low-bidder-per-job).
  async function patchRecipient(recId, fields) {
    setRecipients(rs => rs.map(r => {
      if (r.id === recId) return { ...r, ...fields };
      // Reflect low-bidder exclusivity locally too
      if (fields.low_bidder) return { ...r, low_bidder: 0 };
      return r;
    }));
    try {
      const updated = await apiFetch(`/bids/${id}/recipients/${recId}`, { method: 'PATCH', body: JSON.stringify(fields) });
      if (Array.isArray(updated)) setRecipients(updated);
    } catch (e) {
      showToast(e.message || 'Failed to save recipient', 'error');
    }
  }

  async function markRecipientSent(recId) {
    const updated = await apiFetch(`/bids/${id}/recipients/${recId}/mark-sent`, { method: 'PATCH' });
    setRecipients(updated);
  }

  // Open the composer for a recipient (or the owner when `r` is null): To from their
  // contact, a subject and a short note ready to edit.
  function openQuoteEmail(r) {
    const to = r ? (r.contact_email || '') : (bid?.client_email || '');
    const first = (r ? r.contact_name : bid?.client_contact) ? String(r ? r.contact_name : bid.client_contact).trim().split(/\s+/)[0] : '';
    setQuoteEmail({
      recipient: r || null, to, cc: '',
      subject: `Bid Proposal — ${bid?.job_name || ''}`.trim(),
      message: `Hello${first ? ' ' + first : ''},\n\nPlease find our bid proposal for ${bid?.job_name || 'the project'} attached. Let us know if you have any questions.\n\nThank you,`,
      busy: false, err: '',
    });
  }
  // Draw the quote exactly as Export would (same data, the sent snapshot on an awarded
  // bid), then the server files it, attaches it and sends it from the sender's mailbox.
  // Preview: the very PDF the email would attach, drawn from the same quote payload,
  // shown in a new tab and filed nowhere.
  async function previewQuoteEmail() {
    if (!quoteEmail) return;
    try {
      const r = quoteEmail.recipient;
      const snap = r ? lockedQuoteFor(r.id) : null;
      const bidForQuote = snap ? { ...bid, ...snap.bid } : bid;
      const itemsForQuote = snap ? snap.items : items;
      let tmItems = [];
      if (bidForQuote.pricing_type === 'tm') { try { const t = await apiFetch(`/bids/${id}/tm-items`); tmItems = Array.isArray(t) ? t : []; } catch {} }
      const quote = buildQuoteData(bidForQuote, itemsForQuote, r, alternates, tmItems);
      await openPdfFrom(`/bids/${id}/quote-pdf?preview=1`, { method: 'POST', body: quote });
    } catch (e) { setQuoteEmail(q => q ? { ...q, err: e?.message || 'Could not open the preview.' } : q); }
  }
  async function sendQuoteEmail() {
    if (!quoteEmail || quoteEmail.busy) return;
    // Same check as Export: a quote with no inclusions/exclusions marked gets a "is that right?"
    if (!scopeChosen(bid) && !window.confirm(NO_EXCLUSIONS_MSG)) return;
    if (!addendaOk(bid)) return;
    setQuoteEmail(q => ({ ...q, busy: true, err: '' }));
    try {
      const r = quoteEmail.recipient;
      const snap = r ? lockedQuoteFor(r.id) : null;
      const bidForQuote = snap ? { ...bid, ...snap.bid } : bid;
      const itemsForQuote = snap ? snap.items : items;
      let tmItems = [];
      if (bidForQuote.pricing_type === 'tm') { try { const t = await apiFetch(`/bids/${id}/tm-items`); tmItems = Array.isArray(t) ? t : []; } catch {} }
      const quote = buildQuoteData(bidForQuote, itemsForQuote, r, alternates, tmItems);
      const res = await apiFetch(`/bids/${id}/quote-email`, { method: 'POST', body: JSON.stringify({
        quote, recipient_id: r ? r.id : null, to: quoteEmail.to, cc: quoteEmail.cc, subject: quoteEmail.subject, message: quoteEmail.message }) });
      if (Array.isArray(res.recipients)) setRecipients(res.recipients);
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { bidId: id } }));
      announceBidUpdates();
      if (res.warning) showToast(`Quote emailed to ${quoteEmail.to}. ${res.warning}`, 'error');
      else showToast(`Quote emailed to ${quoteEmail.to}${res.from ? ' from ' + res.from : ''}`);
      offerToSaveRecipients({ to: quoteEmail.to, cc: quoteEmail.cc, clientId: r ? r.client_id : bid.client_id, clientName: r ? r.client_name : bid.client_name });   // in the Customer List? (Oct 6 2026)
      setQuoteEmail(null);
    } catch (e) {
      setQuoteEmail(q => q ? { ...q, busy: false, err: e?.error || e?.message || 'The email didn’t go.' } : q);
    }
  }

  async function extractFromAttachment(att) {
    setExtracting(att.id);
    try {
      const result = await apiFetch(`/bids/${id}/attachments/${att.id}/extract`, { method: 'POST' });
      if (!result.items || result.items.length === 0) {
        showToast('No line items found in this document', 'error');
      } else {
        setExtractModal({ items: result.items });
      }
    } catch (ex) {
      showToast('Extraction failed: ' + ex.message, 'error');
    } finally {
      setExtracting(null);
    }
  }

  // Read one of OUR past quotes and bring its priced lines in as they were quoted.
  // Separate from Extract with AI, which reads someone else's solicitation and works
  // out what the material should cost — here the price is already decided.
  const [importingQuote, setImportingQuote] = React.useState(null);
  async function importOldQuote(att) {
    setImportingQuote(att.id);
    try {
      const result = await apiFetch(`/bids/${id}/attachments/${att.id}/import-quote`, { method: 'POST' });
      if (!result.items || result.items.length === 0) showToast('No priced lines found in that quote', 'error');
      else setExtractModal({ items: result.items, fromQuote: true });
    } catch (ex) {
      showToast('Could not read that quote: ' + (ex?.error || ex.message), 'error');
    } finally {
      setImportingQuote(null);
    }
  }

  function addExtractedItems(selected) {
    const newItems = selected.map((it, idx) => ({
      ...newItem(items.length + idx),
      item_number: it.item_number || '',
      pay_item_number: it.pay_item_number || '',
      description: it.description || '',
      quantity: it.quantity || 1,
      unit: it.unit || 'EA',
      material_cost: it.material_cost || 0,
      labor_hours: it.labor_hours || 0,
      notes: it.notes || '',
      // A line off an OLD QUOTE arrives already priced, so it comes in as a
      // pass-through cost with the markups zeroed and rounding off — otherwise the
      // bid would add sales tax, overhead and profit on top of a number that already
      // includes all three, and round a price like $2,437.50 to the dollar.
      ...(it.from_quote ? {
        dje_standard: it.dje_standard || 0,
        overhead_material_pct: 0, overhead_labor_pct: 0, profit_pct: 0,
        rounding: ROUND_NONE,
      } : {}),
    }));
    setItems(its => [...its, ...newItems]);
    setDirty(true);
    setExtractModal(null);
    scheduleItemsSave();
    const fromQuote = selected.some(s => s.from_quote);
    showToast(`Added ${newItems.length} line item${newItems.length !== 1 ? 's' : ''}${fromQuote ? ' from the old quote — prices came in as quoted' : ' from AI extraction'}`);
  }

  function removeItem(key) {
    setItems(its => its.filter(i => i._key !== key));
    setDirty(true);
    scheduleItemsSave();
  }

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function setStatus(status) {
    try {
      const updated = await apiFetch(`/bids/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setBid(b => ({ ...b, status: updated.status }));
      showToast(`Status updated to ${status}`);
    } catch (ex) { showToast(ex.message, 'error'); }
  }

  if (loading) return <Wrap onBack={embedded ? () => navigate('/sup/bids') : undefined}><div style={{ padding: 40, color: '#9ca3af' }}>Loading…</div></Wrap>;
  if (!bid) return <Wrap onBack={embedded ? () => navigate('/sup/bids') : undefined}><div style={{ padding: 40 }}>Bid not found</div></Wrap>;

  const isLocked = bid.status === 'awarded';
  // Job Description info stays editable when viewing an awarded bid AS a project
  // (e.g. a legacy job moved into New Projects) — pricing/line items stay locked.
  const jobInfoLocked = projectId ? false : isLocked;
  const statusMeta = STATUS_META[bid.status] || STATUS_META.draft;

  // Totals across all items
  const grandTotal = items.reduce((sum, item) => sum + (itemEffectiveTotal(item, bid) || 0), 0);

  // ── Bid alternates ──────────────────────────────────────────────────────────
  // A bid cut into groups: the base, then alternates the owner picks from.
  //
  // The base bid is the ungrouped lines and nothing else — the figure that goes on
  // the bid form. Every alternate sits outside it:
  //   additive  — taking it ADDS its subtotal to the price.
  //   deductive — taking it SUBTRACTS its subtotal from the price.
  // So the two are mirror images, and the only difference is the sign.
  // Plain computations, not useMemo — this sits after an early return, and a hook
  // here would run conditionally. The lists are tens of rows, not thousands.
  const altById = Object.fromEntries(alternates.map(a => [a.id, a]));
  const altTotals = (() => {
    const t = {};
    alternates.forEach(a => { t[a.id] = 0; });
    let base = 0;
    items.forEach(i => {
      const v = itemEffectiveTotal(i, bid) || 0;
      const a = i.alternate_id && altById[i.alternate_id];
      if (!a) { base += v; return; }              // no group — plain base bid line
      t[a.id] += v;
    });
    const accepted = alternates.reduce((s, a) => s + (a.accepted ? (a.kind === 'deductive' ? -t[a.id] : t[a.id]) : 0), 0);
    // The base bid is the ungrouped lines and nothing else — the number that goes on
    // the bid form. Alternates sit outside it either way; a deductive one simply
    // subtracts when taken.
    const everything = alternates.reduce((s, a) => s + (a.kind === 'deductive' ? -t[a.id] : t[a.id]), 0);
    return { perGroup: t, base, ungrouped: base, accepted, contract: base + accepted, withAll: base + everything };
  })();
  const hasAlternates = alternates.length > 0;
  // Nobody has accepted anything while the bid is still being priced — the owner
  // hasn't seen it yet. The tick appears once the bid is out (submitted or awarded)
  // or once it's a job, which is when picking alternates actually happens.
  const alternatesDecidable = !!projectId || ['submitted', 'awarded'].includes(String(bid.status || '').toLowerCase());
  // Base first, then each group in its own order — the sheet has to read top to
  // bottom the way the quote prints, or the subtotals land in the wrong places.
  const orderedItems = (() => {
    if (!hasAlternates) return items;
    const rank = id => { const i = alternates.findIndex(a => a.id === id); return i < 0 ? 0 : i + 1; };
    const sorted = [...items].sort((x, y) => rank(x.alternate_id) - rank(y.alternate_id));
    // A group with no lines yet still has to appear. Headings are drawn from the rows
    // beneath them, so an empty group used to render nothing at all — no heading, no
    // way to put lines in it, and no × to delete it. It just looked like nothing had
    // happened. A placeholder row gives every group somewhere to live.
    const empties = alternates
      .filter(a => !items.some(i => i.alternate_id === a.id))
      .map(a => ({ _key: `empty_${a.id}`, _placeholder: true, alternate_id: a.id }));
    if (!empties.length) return sorted;
    return [...sorted, ...empties].sort((x, y) => rank(x.alternate_id) - rank(y.alternate_id));
  })();

  // Move a set of lines into a group (or back to the base when altId is null).
  function assignItemsToAlternate(keys, altId) {
    const set = new Set(keys);
    saveItems(items.map(i => set.has(i._key) ? { ...i, alternate_id: altId } : i));
  }

  async function addAlternate({ title, kind, beforeKey }) {
    const created = await apiFetch(`/bids/${bid.id}/alternates`, {
      method: 'POST', body: JSON.stringify({ title, kind }),
    });
    setAlternates(prev => [...prev, created]);
    // "Insert a break between bid items": everything from that line down joins the
    // new group, which is how it reads on paper.
    if (beforeKey) {
      const idx = orderedItems.findIndex(i => i._key === beforeKey);
      if (idx >= 0) {
        const moving = new Set(orderedItems.slice(idx).filter(i => !i.alternate_id).map(i => i._key));
        if (moving.size) saveItems(items.map(i => moving.has(i._key) ? { ...i, alternate_id: created.id } : i));
      }
    }
    return created;
  }
  async function patchAlternate(id, patch) {
    const updated = await apiFetch(`/bids/${bid.id}/alternates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    setAlternates(prev => prev.map(a => a.id === id ? updated : a));
  }
  async function removeAlternate(id) {
    const r = await apiFetch(`/bids/${bid.id}/alternates/${id}`, { method: 'DELETE' });
    setAlternates(r.alternates || []);
    // Its lines fall back to the base rather than being deleted with the heading.
    setItems(prev => prev.map(i => i.alternate_id === id ? { ...i, alternate_id: null } : i));
  }

  // Bid summary — the cost stack rolled up from the line items. Every figure the
  // Excel export already dumps column-by-column, added up so the bid can be checked
  // at a glance: Material + Labor + Subs = Prime, + Overhead = Cost, + Profit = Sell.
  // A line with sub-items takes its numbers from the children, since the parent's own
  // cost fields are empty in that case and would otherwise read as zero.
  // Plain computation, not a hook: this sits after an early return, and grandTotal
  // above already recomputes per render at the same cost.
  const bidSummary = (() => {
    const z = { material: 0, matTax: 0, prodHours: 0, supHours: 0, labor: 0, subs: 0, ohMat: 0, ohLab: 0, cost: 0, profit: 0, selling: 0 };
    // Add one row's figures into the running totals, scaled by however many of it
    // there are. calcItem returns everything PER UNIT, so the multiply belongs here.
    const add = (row, times) => {
      const c = calcItem(row, bid);
      const n = times || 1;
      z.material  += (c.matBase   || 0) * n;  z.matTax   += (c.matTax   || 0) * n;
      z.prodHours += (c.prodHours || 0) * n;  z.supHours += (c.supHours || 0) * n;
      z.labor     += (c.laborTotal|| 0) * n;  z.subs     += (c.dje      || 0) * n;
      z.ohMat     += (c.ohMat     || 0) * n;  z.ohLab    += (c.ohLab    || 0) * n;
      z.cost      += (c.totalCost || 0) * n;  z.profit   += (c.profit   || 0) * n;
      z.selling   += (c.selling   || 0) * n;
    };
    items.forEach(it => {
      const subs = Array.isArray(it.sub_items) ? it.sub_items : [];
      if (subs.length) {
        // A parent with sub-items carries no cost of its own — the children are the
        // cost. Each child is counted by its OWN quantity and NOT by the parent's:
        // itemEffectiveUnitPrice divides the children's total by the parent quantity
        // before multiplying it back, so the line total is just the children's total.
        subs.forEach(sub => add(sub, pricedQty(sub)));
      } else {
        add(it, pricedQty(it));
      }
    });
    const materialTotal = z.material + z.matTax;
    const prime = materialTotal + z.labor + z.subs;
    const overhead = z.ohMat + z.ohLab;
    // The bid total is the sum of ROUNDED unit prices, so it drifts from the raw
    // selling total. Show that gap rather than presenting a stack that doesn't add up.
    const rounding = grandTotal - z.selling;
    const margin = grandTotal > 0 ? (z.profit / grandTotal) * 100 : 0;
    return { ...z, materialTotal, prime, overhead, rounding, margin, hours: z.prodHours + z.supHours, total: grandTotal };
  })();

  // The cost stack rolled up from the line items, and the mark-ups that drive it. Both
  // are drawn in two places — the Scope & Bid Details tile (as always) and, on a hard
  // bid, as tabs on the Line Items header where the estimator is actually working.
  const renderBidSummary = () => {
    const S = bidSummary;
    const pct = v => S.total > 0 ? ((v / S.total) * 100).toFixed(0) + '%' : '—';
    const fmtHrs = h => Math.round(h).toLocaleString('en-US') + ' hrs';
    const Row = ({ label, value, sub, bold, top, accent }) => (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: bold ? '9px 0' : '5px 0', borderTop: top ? '1px solid #e5e7eb' : 'none' }}>
        <span style={{ fontSize: bold ? 13 : 12.5, fontWeight: bold ? 800 : 600, color: bold ? '#111827' : '#4b5563' }}>{label}</span>
        {sub && <span style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</span>}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 700, color: accent || (bold ? '#111827' : '#374151'), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
      </div>
    );
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1.1fr))', gap: 12, alignItems: 'start' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Bid Summary</div>
          <Row label="Material" sub={`incl. tax · ${pct(S.materialTotal)}`} value={fmt$(S.materialTotal)} />
          <Row label="Labor" sub={`${fmtHrs(S.hours)} · ${pct(S.labor)}`} value={fmt$(S.labor)} />
          <Row label="Subcontractors" sub={pct(S.subs)} value={fmt$(S.subs)} />
          <Row label="Prime Cost" value={fmt$(S.prime)} bold top />
          <Row label="Overhead" sub="material + labor" value={fmt$(S.overhead)} />
          <Row label="Total Cost" value={fmt$(S.cost)} bold top />
          <Row label="Profit" sub={`${S.margin.toFixed(1)}% of bid · target ${bid.profit_pct}%`} value={fmt$(S.profit)} accent="#15803d" />
          {Math.abs(S.rounding) >= 0.5 && (
            <Row label="Rounding" sub="from the Round column" value={(S.rounding > 0 ? '+' : '') + fmt$(S.rounding)} />
          )}
          <Row label="BID TOTAL" value={fmt$(S.total)} bold top accent="var(--orange)" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
            {[
              ['Line Items', items.length.toLocaleString('en-US')],
              ['Total Hours', fmtHrs(S.hours)],
              ['Effective Margin', S.margin.toFixed(1) + '%'],
              ['Target Profit', bid.profit_pct + '%'],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: 'var(--black)' }}>{val}</div>
              </div>
            ))}
          </div>
          {/* Duration scratch pad: the bid's hours spread over a crew. Nothing saved. */}
          {(() => {
            const crew = Math.max(0, Number(crewCalc.crew) || 0), hpd = Math.max(0, Number(crewCalc.hours) || 0);
            const perDay = crew * hpd;
            const days = perDay > 0 ? S.hours / perDay : null;
            const fmtN = n => n.toLocaleString('en-US', { maximumFractionDigits: 1 });
            const inp = { width: 64, border: '1px solid #d1d5db', borderRadius: 7, padding: '6px 8px', fontSize: 14, fontWeight: 700, textAlign: 'center', fontFamily: 'inherit' };
            return (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>How long is this job? <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>· scratch pad, not saved</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: '#374151' }}>
                  <span>A crew of</span>
                  <input type="number" min="0" step="1" value={crewCalc.crew} onFocus={selectOnFocus} onChange={e => setCrewCalc(c => ({ ...c, crew: e.target.value }))} style={inp} />
                  <span>working</span>
                  <input type="number" min="0" step="0.5" value={crewCalc.hours} onFocus={selectOnFocus} onChange={e => setCrewCalc(c => ({ ...c, hours: e.target.value }))} style={inp} />
                  <span>hours a day</span>
                </div>
                <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                  {days == null ? <span style={{ color: '#9ca3af' }}>Enter a crew size and hours a day.</span> : (
                    <>
                      <span style={{ fontSize: 22, fontWeight: 800, color: '#111' }}>{fmtN(Math.ceil(days))} day{Math.ceil(days) !== 1 ? 's' : ''}</span>
                      <span style={{ color: '#6b7280' }}> · about <b>{fmtN(days / 5)} week{days / 5 >= 1.05 || days / 5 < 0.95 ? 's' : ''}</b> at 5 days a week</span>
                      <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>{fmtHrs(S.hours)} ÷ ({crew} × {hpd} hrs = {fmtN(perDay)} crew-hours a day). Bid hours include supervision.</div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };
  // Exclusions as a tab on the Line Items tile (Ryan, Sep 2026): the Included / Excluded
  // checklist and the Additional Comments that print under the quote — the SAME fields
  // the Scope & Bid Details tile and the T&M tile edit (bid.drill_scope_items,
  // bid.exclusions), so the tiles never disagree. Scope & Bid Details is off most bid
  // boards now, so this is where the estimator reaches them.
  const renderExclusions = () => {
    const sel = currentBidWorkflows();
    const isDrillWt = sel.includes('vertical_drilling') || sel.includes('horizontal_drilling') || bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling';
    const secLbl = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 };
    const ta = { width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical', background: jobInfoLocked ? '#f9fafb' : '#fff', fontFamily: 'inherit', color: '#111' };
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, alignItems: 'start' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px' }}>
          <div style={secLbl}>Included / Excluded <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· prints on the quote</span></div>
          <DrillScopeChecklist
            value={bid.drill_scope_items ? JSON.parse(bid.drill_scope_items) : null}
            isLocked={jobInfoLocked}
            onChange={scopeItems => updateBid('drill_scope_items', JSON.stringify(scopeItems))}
            defaults={isDrillWt ? DEFAULT_DRILL_SCOPE : DEFAULT_GENERAL_SCOPE}
          />
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px' }}>
          <div style={{ ...secLbl, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Additional Comments <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· prints on the quote</span></span>
            {!jobInfoLocked && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
                <input type="checkbox" checked={showAdditionalComments}
                  onChange={e => { setShowAdditionalComments(e.target.checked); if (!e.target.checked) updateBid('exclusions', ''); }} />
                <span style={{ fontSize: 11, color: '#6b7280' }}>Include</span>
              </label>
            )}
          </div>
          {showAdditionalComments
            ? <textarea value={bid.exclusions || ''} onChange={e => updateBid('exclusions', e.target.value)} rows={6} disabled={jobInfoLocked}
                placeholder="Anything the customer should know that isn't a line item…" style={ta} />
            : <div style={{ border: '1px dashed #e5e7eb', borderRadius: 7, padding: '9px 10px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Not included — check "Include" to add comments</div>}
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 8 }}>Saves as you type. The same list and comments show on Scope &amp; Bid Details if that tile is on the board.</div>
        </div>
      </div>
    );
  };

  const renderMarkups = () => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#064e3b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Labor Costs &amp; Mark Ups</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          ['Labor Rate ($/hr)', 'labor_rate'],
          ['Supervision %', 'supervision_pct'],
          ['Sales Tax %', 'sales_tax_pct'],
          ['Overhead – Mat %', 'overhead_material_pct'],
          ['Overhead – Labor %', 'overhead_labor_pct'],
          ['Profit %', 'profit_pct'],
        ].map(([label, key]) => (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{label}</label>
            <input type="number" step="0.001" value={bid[key] ?? ''} onFocus={selectOnFocus} onChange={e => updateBid(key, parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: isLocked ? '#f9fafb' : '#fff' }} />
          </div>
        ))}
      </div>
    </div>
  );

  // Daily Updates feed = office/board updates (project_daily_updates) + field notes
  // (daily_notes, which carry photos), newest first, in one list.
  const jobFeed = [
    ...(dailyUpdates || []).map(u => ({ id: 'u_' + u.id, who: u.author_name || 'Someone', text: u.notes, at: u.created_at, media: [] })),
    ...(fieldNotes || []).map(n => ({
      id: 'n_' + n.id,
      who: `${n.first_name || ''} ${n.last_name || ''}`.trim() || 'Field',
      text: n.note,
      at: n.created_at,
      media: Array.isArray(n.media) ? n.media : [],
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));
  const totalHours = items.reduce((sum, item) => sum + ((item.labor_hours || 0) * (1 + bid.supervision_pct / 100)), 0);

  const inputStyle = (disabled) => ({
    border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 12,
    width: '100%', boxSizing: 'border-box', background: disabled ? '#f9fafb' : '#fff',
    color: disabled ? '#9ca3af' : 'inherit',
  });

  // Percent fields step by a whole point: nobody has ever entered a fractional one
  // (348 profit values on record, not one with a decimal), so 0.01 steps meant
  // holding the arrow down to move a single percent. Typing a decimal still works.
  const pctInput = (item, field, placeholder) => (
    <input
      type="number" step="1"
      value={item[field] ?? ''}
      placeholder={placeholder}
      onChange={e => updateItem(item._key, field, e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ ...inputStyle(isLocked), width: 60 }}
      disabled={isLocked}
    />
  );

  return (
    <Wrap onBack={embedded ? () => navigate('/sup/bids') : undefined}>
      {/* Anything the caller wants above the page — currently the link across to
          the rebuilt five-tile layout, which nothing else routes to yet. */}
      {headerSlot}
      {/* Job Description save confirmation (project mode) */}
      {pendingJobDescSave && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700 }}>Update Active Project?</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
              You're changing Job Description information on an active project. This will update the record permanently. Are you sure you want to save these changes?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setPendingJobDescSave(null); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { saveBid(pendingJobDescSave.bidData); setPendingJobDescSave(null); }} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Yes, Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* The banner is shared with the rebuilt five-tile page — see JobBanner.js.
          Same markup on both, so they can't drift. */}
      {projectId && project && (
        <JobBanner
          project={project} bid={bid} projectId={projectId}
          statusMeta={statusMeta} addressee={submittalAddressee}
          currentUserRole={currentUserRole} isLocked={isLocked}
          onBack={() => navigate('/admin/new-projects')}
          onEditProject={() => { setShowJobDesc(true); setTimeout(() => document.getElementById('job-description-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); }}
          onDelete={() => setConfirmDeleteProject(true)}
          onWritePO={() => setShowPoModal(true)}
          onQuotePdf={async () => {
            if (!isLocked) { setExportRecipientModal('pdf'); return; }
            // Awarded: the saved Used Quote (Ryan, Oct 22 2026); nothing printed yet → the old ways.
            try { const r = await apiFetch(`/projects/${bid.project_id || bid.id}/used-quote`); window.open(`${API_BASE}/documents/${r.document.id}/file`, '_blank'); }
            catch { if (bid.used_quote_doc_id && !bid.used_quote_recipient_id) window.open(`${API_BASE}/documents/${bid.used_quote_doc_id}/file`, '_blank'); else setExportRecipientModal('pdf'); }
          }}
          onLocation={() => setShowLocationView(true)}
          onScheduleWorkflow={v => setScheduleWorkflow(v)}
        />
      )}
      {showLocationView && (
        <JobLocationModal projectId={projectId} address={bid?.job_location}
          label={project?.project_number || project?.name || 'Job Location'} onClose={() => setShowLocationView(false)} />
      )}

      {/* Field photos added from the drilling workflow card */}
      {projectId && <WorkflowPhotos projectId={projectId} />}

      {/* Same Trello-style drilling card as the Workflow board */}
      {drillCardSj && (
        <DrillCardModal card={drillCardFor(drillCardSj)} pmName={pmNameFor}
          onClose={() => setDrillCardSj(null)} onChanged={reloadSubJobs} />
      )}

      {/* Workflow category setup — auto-shows on an imported project with no tabs yet,
          and is reachable anytime via the "+ Add categories" button. */}
      {projectId && isAdminZone && (() => {
        const open = subJobs.length === 0 || showWorkflowSetup;
        const selCount = Object.values(wfSel).filter(Boolean).length;
        // Auto-opens on a brand-new job (no tabs). Otherwise show a button to
        // reopen the picker so categories (Submittals, Drilling, NVE…) can be
        // added to an existing project at any time.
        if (!open) return (
          <div style={{ marginBottom: 14 }}>
            <button onClick={() => setShowWorkflowSetup(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8, border: '1px dashed #a5b4fc', background: '#f5f7ff', color: '#4338ca', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              🧩 + Add workflow category
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>Submittals, Drilling, Loops, NVE, USA Digs…</span>
            </button>
          </div>
        );
        return (
          <div className="admin-card" style={{ marginBottom: 14, padding: 16, border: '1px solid #c7d2fe', background: '#f5f7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: '#3730a3', flex: 1 }}>🧩 Set up workflow categories</span>
              {subJobs.length > 0 && (
                <button onClick={() => { setShowWorkflowSetup(false); setWfSel({}); }}
                  style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Pick the categories this job needs — the crew works each one as a tab.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
              {ALL_WORKFLOW_OPTIONS.map(opt => {
                const already = subJobs.some(sj => sj.type === opt.key);
                const checked = !!wfSel[opt.key];
                return (
                  <button key={opt.key} disabled={already || addingWorkflow}
                    onClick={() => setWfSel(s => ({ ...s, [opt.key]: !s[opt.key] }))}
                    style={{ padding: '10px 8px', borderRadius: 8, border: `2px solid ${checked ? '#4f46e5' : '#e5e7eb'}`, background: already ? '#f3f4f6' : (checked ? '#eef2ff' : '#fff'), cursor: already ? 'default' : 'pointer', textAlign: 'center', opacity: already ? 0.65 : 1 }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{opt.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{opt.label}</div>
                    {already
                      ? <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2, fontWeight: 700 }}>✓ added</div>
                      : <div style={{ fontSize: 10, color: checked ? '#4f46e5' : '#9ca3af', marginTop: 2, fontWeight: 700 }}>{checked ? 'Selected' : 'Tap to add'}</div>}
                  </button>
                );
              })}
            </div>
            <button disabled={addingWorkflow || selCount === 0}
              onClick={() => addWorkflowTabs(Object.keys(wfSel).filter(k => wfSel[k]))}
              style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: selCount ? '#4f46e5' : '#c7d2fe', color: '#fff', fontWeight: 800, fontSize: 14, cursor: selCount ? 'pointer' : 'default' }}>
              {addingWorkflow ? 'Adding…' : `Add ${selCount || ''} ${selCount === 1 ? 'category' : 'categories'}`.replace('  ', ' ')}
            </button>
          </div>
        );
      })()}

      {confirmDeleteProject && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>🗑️</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700 }}>Delete this project?</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
              This will permanently delete the project record. The bid will be moved back to <strong>Upcoming</strong> in the Bids list so you can re-use it.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDeleteProject(false)} disabled={deletingProject}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={deleteProject} disabled={deletingProject}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: deletingProject ? 0.6 : 1 }}>
                {deletingProject ? 'Deleting…' : 'Yes, Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Write-a-PO from the project header (job fixed; vendor chosen) */}
      <CreatePoModal
        open={showPoModal}
        onClose={() => setShowPoModal(false)}
        projectId={projectId}
        projectLabel={`${project?.project_number ? project.project_number + ' — ' : ''}${bid.job_name || ''}`}
        vendors={vendors}
        onCreated={afterPoCreated}
      />
      {editingPO && (
        <PoDetailModal po={editingPO} vendors={vendors}
          onClose={() => setEditingPO(null)}
          onSaved={updated => { setProjectPOs(ps => ps.map(p => p.id === updated.id ? { ...p, ...updated } : p)); apiFetch(`/projects/${projectId}/purchase-orders`).then(setProjectPOs).catch(() => {}); }} />
      )}
      {/* Header (bid mode only — project mode uses the clean banner above) */}
      {!projectId && (
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {!embedded && (
            <button onClick={() => navigate(projectId ? '/admin/new-projects' : '/admin/bids')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 22, lineHeight: 1, padding: 0 }}>‹</button>
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{bid.job_name}</h1>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: statusMeta.color + '20', color: statusMeta.color }}>
                {statusMeta.label}
              </span>
            </div>
            {bid.client_name && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{bid.client_name}{bid.job_location ? ` · ${tidyAddress(bid.job_location)}` : ''}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {projectId && (
            <button onClick={() => setShowPoModal(true)}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              🧾 Write PO
            </button>
          )}
          <button onClick={() => (isLocked && bid.used_quote_doc_id && !bid.used_quote_recipient_id ? window.open(`${API_BASE}/documents/${bid.used_quote_doc_id}/file`, '_blank') : setExportRecipientModal('pdf'))}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #1e3a5f', color: '#1e3a5f', background: '#eff6ff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="1" width="10" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3" fill="none"/><path d="M4 4h6M4 6.5h6M4 9h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
            {isLocked ? 'View Used Quote PDF' : 'Export Quote PDF'}
          </button>
          {/* Email the quote straight from here — the PDF is drawn, filed and attached,
              and it goes out from the sender's own mailbox when they have one on file. */}
          <button onClick={() => mailStatus?.configured ? setExportRecipientModal('email') : showToast('Email isn’t set up on the server yet.', 'error')}
            title={mailStatus?.configured ? `Sends from ${mailStatus.from}` : (mailStatus === null ? 'Checking…' : 'Email isn’t set up on the server yet')}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: mailStatus?.configured ? '#15803d' : '#e5e7eb', color: mailStatus?.configured ? '#fff' : '#9ca3af', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            ✉️ Email Quote
          </button>
          <button onClick={() => setExportRecipientModal('excel')}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', color: '#374151', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v8M4 6l3 3 3-3M2 10v2a1 1 0 001 1h8a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Export Excel
          </button>
          {!isLocked && bid.status !== 'submitted' && (
            <button onClick={() => setStatus('submitted')}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #1d4ed8', color: '#1d4ed8', background: '#eff6ff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Mark Submitted
            </button>
          )}
          {/* Told we are low, nothing signed. Offered once it is out the door, since
              nobody is told they are low on a bid they have not sent. */}
          {!isLocked && bid.status === 'submitted' && (
            <button onClick={() => setStatus('told_low')}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #15803d', color: '#15803d', background: '#f0fdf4', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Mark Told Low
            </button>
          )}
          {!isLocked && bid.status !== 'potential' && bid.status !== 'submitted' && (
            <button onClick={() => setStatus('potential')}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #7c3aed', color: '#7c3aed', background: '#f5f3ff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Mark Potential
            </button>
          )}
          {!isLocked && bid.status === 'potential' && (
            <button onClick={() => setStatus('draft')}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #1e3a5f', color: '#1e3a5f', background: '#eff6ff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Move to Upcoming
            </button>
          )}
          {/* Rate Sheet PDF stays on the old job page; the bid header keeps to what's
              bid-specific. On a bid, an Update goes on the bid's thread — and rides
              along to the job at award. */}
          {projectId && (
          <button onClick={() => setShowRateSheetPdf(true)} title="Generate a rate sheet PDF addressed to this bid's customer"
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #7c3aed', color: '#7c3aed', background: '#f5f3ff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            📄 Rate Sheet
          </button>
          )}
          {!projectId && (
          <button onClick={() => setBidUpdateOpen(true)} title="Post an update on this bid — the PM is notified, and it carries to the job at award"
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #b45309', color: '#b45309', background: '#fffbeb', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            💬 Update{bidUpdates.length ? ` (${bidUpdates.length})` : ''}
          </button>
          )}
          {!isLocked && ['potential', 'draft', 'submitted', 'told_low'].includes(bid.status) && (
            <button onClick={() => setStatus('lost')}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', color: '#6b7280', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
              Mark Lost
            </button>
          )}
          {bid.old_system && !bid.project_id ? (
            <button onClick={() => { if (dirty) saveItems(items); setOldWizard(true); }} title="Open the old-system steps again: details, documents, Fieldwire, line items & scope, then make it"
              style={{ padding: '8px 14px', borderRadius: 8, border: '2px solid #b45309', background: '#fffbeb', color: '#92400e', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
              📦 Continue setting it up
            </button>
          ) : null}
          {oldWizard && <OldJobWizard bidId={bid.id} onClose={() => { setOldWizard(false); window.location.reload(); }} />}
          {!isLocked && (
            <button onClick={() => { if (dirty) saveItems(items); setShowAward(true); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Award Bid →
            </button>
          )}
          {isLocked && !bid.project_id && bid.moved_kind === 'co' && bid.moved_to_project_id && !projectId && (
            <button onClick={() => navigate(`/admin/jobs/${bid.moved_to_project_id}/changes?pillow=change_orders&item=${bid.moved_to_ticket_id || ''}`)}
              title="This bid was moved onto an existing job as a change order"
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              View the Change Order →
            </button>
          )}
          {isLocked && bid.project_id && !projectId && (
            <button onClick={() => navigate(`/admin/projects/${bid.project_id}`)}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              View Project →
            </button>
          )}
          {!isLocked && saving && (
            <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, alignSelf: 'center' }}>Saving…</span>
          )}
        </div>
      </div>
      )}

      {/* Headline tiles. Total Hours comes from the summary rollup — the old figure
          summed hours per unit and never multiplied by quantity, so it read 982.7
          on a bid carrying 24,207 hours of labor. */}
      {/* On a bid these four now live on the tiles (Line Items preview / Bid Summary
          tab), so the header is just the bid. The old job page keeps them. */}
      {projectId && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          ['Total Bid Value', fmt$(grandTotal)],
          ['Line Items', items.length],
          ['Total Hours', Math.round(bidSummary.hours).toLocaleString('en-US') + ' hrs'],
          ['Profit %', bid.profit_pct + '%'],
        ].map(([label, val]) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: 'var(--black)' }}>{val}</div>
          </div>
        ))}
      </div>
      )}
      {/* Bid mode: the sections below shown as a project-style tile hub. Click a tile to
          open that section; its own header bar (or this Back) returns to the grid. The old
          job page (project mode) keeps the stacked accordion and never sets openSection. */}
      {!projectId && (openSection === null ? (() => {
        // Each tile shows the few facts you'd open it for, so the grid reads as a
        // one-page brief of the bid before anything is clicked.
        const Fact = ({ label, children, wide }) => (
          <div style={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            {/* Two lines then an ellipsis — enough for a long owner name or a full date. */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: wide ? 3 : 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{children || <span style={{ color: '#c9ccd1', fontWeight: 500 }}>—</span>}</div>
          </div>
        );
        const clip = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n).trimEnd() + '…' : t; };
        const S = bidSummary;
        // vendor_quotes is a list of items (Material / Sub), each holding its quotes.
        let vqQuotes = []; try { vqQuotes = (bid.vendor_quotes ? JSON.parse(bid.vendor_quotes) : []).flatMap(c => Array.isArray(c.quotes) ? c.quotes : []); } catch {}
        const vq = vqQuotes;
        const vqUsed = vqQuotes.filter(q => q.status === 'Used').length;
        const vendorNames = [...new Set(vqQuotes.map(q => q.vendorName).filter(Boolean))];
        const bidTo = bid.recipient_is_owner && bid.client_name ? [`${bid.client_name} (owner)`] : recipients.filter(r => r.client_name).map(r => r.client_name);
        const tiles = [
          { key: 'job_desc', label: 'Job Description', accent: '#1e3a5f', open: () => setShowJobDesc(true), preview: (<>
              <Fact label="Owner">{bid.client_name}</Fact>
              <Fact label="Project Manager">{pmNameFor(bid.bid_pm_id) === 'Unassigned' ? (bid.bid_pm_name || null) : pmNameFor(bid.bid_pm_id)}</Fact>
              <Fact label="Bid Date & Time">{bidWhenText(bid)}</Fact>
              <Fact label="Pre-Bid">{preBidText(bid)}</Fact>
              {/* The New Bid form's Job Description — it lives here, not only on Scope & Bid (Ryan, Sep 30 2026). */}
              <Fact label="Job Description" wide>{clip(bid.scope, 160)}</Fact>
              <Fact label={bidTo.length ? 'Bidding To' : 'Type'} wide>{bidTo.length ? bidTo.join(', ') : [workTypeLabel(classifyBid(bid).work_type), bid.pricing_type === 'tm' ? 'Time & Material' : priceStructureLabel(classifyBid(bid).price_structure), bid.job_type, bid.job_sector].filter(Boolean).join(' · ')}</Fact>
            </>) },
          { key: 'scope', label: 'Scope & Bid Details', accent: '#4a1d50', open: () => setShowScope(true), preview: (<>
              <Fact label="Bid Total">{fmt$(S.total)}</Fact>
              <Fact label="Margin">{S.total > 0 ? `${S.margin.toFixed(1)}% · target ${bid.profit_pct ?? '—'}%` : null}</Fact>
              <Fact label="Labor Rate">{bid.labor_rate != null ? `$${bid.labor_rate}/hr` : null}</Fact>
              <Fact label="Total Hours">{S.hours ? `${Math.round(S.hours).toLocaleString('en-US')} hrs` : null}</Fact>
              <Fact label="Scope of Work" wide>{clip(bid.scope, 140)}</Fact>
            </>) },
          bid.pricing_type === 'tm'
          ? { key: 'line_items', label: 'T&M Quote Items', accent: '#6d28d9', open: () => setShowLineItems(true), preview: (<>
              <Fact label="Rate Lines">{tmQuoteItems.length ? `${tmQuoteItems.length} item${tmQuoteItems.length !== 1 ? 's' : ''}` : 'None yet'}</Fact>
              <Fact label={tmQuoteItems.some(t => Number(t.quantity) > 0) ? 'Estimated Total' : 'Not to Exceed'}>
                {tmQuoteItems.some(t => Number(t.quantity) > 0)
                  ? fmt$(tmQuoteItems.reduce((s, t) => s + (Number(t.quantity) || 0) * (Number(t.rate) || 0), 0))
                  : (bid.tm_nte ? fmt$(bid.tm_nte) : 'Open-ended')}
              </Fact>
              <Fact label="Rates" wide>{tmQuoteItems.length ? tmQuoteItems.slice(0, 4).map(t => `${t.name} ${fmt$(t.rate)}/${tmUnitOf(t)}`).join(' · ') + (tmQuoteItems.length > 4 ? ' · …' : '') : null}</Fact>
            </>) }
          : { key: 'line_items', label: 'Line Items', accent: '#1e3a5f', open: () => setShowLineItems(true), preview: (<>
              <Fact label="Line Items">{items.length ? items.length.toLocaleString('en-US') : null}</Fact>
              <Fact label="Bid Total">{fmt$(grandTotal)}</Fact>
              <Fact label="Material">{fmt$(S.materialTotal)}</Fact>
              <Fact label="Labor">{fmt$(S.labor)}</Fact>
              <Fact label="Alternates" wide>{alternates.length ? alternates.map(a => a.title).join(', ') : 'None — one base bid'}</Fact>
            </>) },
          { key: 'vendor_quotes', label: 'Vendor Quotes', accent: '#8a3b12', open: () => setShowVendorQuotes(true), preview: (<>
              <Fact label="Quotes">{vq.length ? `${vq.length} quote${vq.length !== 1 ? 's' : ''}${vqUsed ? ` · ${vqUsed} used` : ''}` : 'None yet'}</Fact>
              <Fact label="Files">{attachments.length ? `${attachments.length} on the bid` : null}</Fact>
              <Fact label="Vendors" wide>{vendorNames.length ? vendorNames.join(', ') : null}</Fact>
            </>) },
          { key: 'documents', label: 'Documents', accent: '#0f766e', open: () => {}, preview: (<>
              <Fact label="On File">{docCount == null ? '…' : docCount ? `${docCount} file${docCount !== 1 ? 's' : ''}` : 'Nothing filed yet'}</Fact>
              <Fact label="Includes" wide>Printed quotes and packages, plans, submittal files — with revisions</Fact>
            </>) },
          { key: 'updates', label: 'Bid Updates', accent: '#b45309', open: () => {}, preview: (<>
              <Fact label="Updates">{bidUpdates.length ? `${bidUpdates.length} post${bidUpdates.length !== 1 ? 's' : ''}` : 'None yet'}</Fact>
              <Fact label="Latest">{bidUpdates[0] ? `${bidUpdates[0].author_name || 'Someone'} · ${new Date(bidUpdates[0].created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : null}</Fact>
              <Fact label={bidUpdates[0] ? (bidUpdates[0].kind === 'system' ? '📅 Noted' : '💬 Said') : 'Use it for'} wide>
                {bidUpdates[0] ? clip(bidUpdates[0].notes, 120) : 'Ask the PM about a bid item, note who wants a price, see when the bid date moved'}
              </Fact>
            </>) },
          // Only once the bid has a Fieldwire project (plans sent from the Documents tile).
          ...(fwOv ? [
            { key: 'fw_sheets', label: 'Fieldwire Sheets', accent: '#0f766e', open: () => {}, preview: (<>
                <Fact label="Sheets">{fwOv.totals?.sheets ? `${fwOv.totals.sheets} plan sheet${fwOv.totals.sheets === 1 ? '' : 's'}` : 'None pulled yet'}</Fact>
                <Fact label="Project" wide>{fwOv.link?.name || 'Fieldwire'}</Fact>
              </>) },
            { key: 'fw_tasks', label: 'Fieldwire Tasks', accent: '#1e3a5f', open: () => {}, preview: (<>
                <Fact label="Pins">{fwOv.totals?.tasks ? `${fwOv.totals.tasks} · ${fwOv.totals.done || 0} done` : 'No pins yet'}</Fact>
                <Fact label="Categories" wide>{(fwOv.teams || []).map(t => t.name).join(', ') || 'None yet'}</Fact>
              </>) },
          ] : []),
        ];
        // Scope & Bid Details is gone from the bid board (Pat, Oct 8 2026: "not required any more") — its
        // scope is the Job Description's, its labor rate and mark-ups are Line Items → Mark-ups, its
        // margin and totals Line Items → Bid Summary, its exclusions Line Items → Exclusions.
        const shown = tiles.filter(t => t.key !== 'scope' && !hiddenTiles.includes(t.key));
        const offBoard = tiles.filter(t => t.key !== 'scope' && hiddenTiles.includes(t.key));
        const canArrangeTiles = isAdminZone && !isLocked;
        return (
        <>
        {/* 📌 Bid updates posted as a banner — addendums and the like that must not be missed (Pat, Oct 9 2026). */}
        {bidUpdates.filter(u => Number(u.banner) === 1 && !u.banner_off_at).map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10, padding: '12px 16px', borderRadius: 12, background: '#fef2f2', border: '2px solid #f87171', boxShadow: '0 2px 8px rgba(220,38,38,0.12)' }}>
            <span style={{ fontSize: 20 }}>📌</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#991b1b', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{u.notes}</div>
              <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 3 }}>{u.author_name || 'Someone'} · {new Date(u.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            </div>
            <button onClick={async () => { try { await apiFetch(`/bids/${id}/daily-updates/${u.id}/banner`, { method: 'PATCH', body: JSON.stringify({ on: false }) }); window.dispatchEvent(new CustomEvent('titan:bid-updates-changed')); } catch (e) { showToast(e?.error || 'Could not take it down', 'error'); } }}
              title="Take the banner down (the update stays in Bid Updates)"
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>Take down</button>
          </div>
        ))}
        {/* Three across like the project page, so each tile has room to read as a card. */}
        <div className="tile-grid-3" style={{ gap: 16, marginBottom: 8 }}>
          {shown.map(t => (
            <div key={t.key} role="button" tabIndex={0} onClick={() => { t.open(); setOpenSection(t.key); }}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); t.open(); setOpenSection(t.key); } }}
              style={{ position: 'relative', textAlign: 'left', cursor: 'pointer', font: 'inherit', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 220 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
              <div style={{ height: 5, background: t.accent }} />
              <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 17, fontWeight: 800, color: '#111', flex: 1 }}>{t.label}</span>
                  {/* Take this tile off the board for this bid; it's offered back below. */}
                  {canArrangeTiles && shown.length > 1 && (
                    <button title={`Take ${t.label} off this bid's board`}
                      onClick={e => { e.stopPropagation(); setHiddenTiles([...hiddenTiles, t.key]); }}
                      style={{ border: 'none', background: 'transparent', color: '#c9ccd1', fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '0 2px' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#b91c1c'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#c9ccd1'; }}>×</button>
                  )}
                  <span style={{ color: t.accent, fontSize: 18, lineHeight: 1 }}>›</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>{t.preview}</div>
              </div>
              {/* Says plainly that the tile opens (Pat, Oct 6 2026: "a banner on all the tiles"). */}
              <div style={{ padding: '9px 18px', background: `${t.accent}12`, borderTop: `1px solid ${t.accent}33`, color: t.accent, fontSize: 13.5, fontWeight: 800, textAlign: 'right' }}>See all ›</div>
            </div>
          ))}
        </div>
        {/* Anything taken off this bid's board, offered back — same idea as the project page. */}
        {canArrangeTiles && offBoard.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '6px 0 10px', fontSize: 12.5, color: '#9ca3af' }}>
            <span>Add a tile back:</span>
            {offBoard.map(t => (
              <button key={t.key} onClick={() => setHiddenTiles(hiddenTiles.filter(k => k !== t.key))}
                style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${t.accent}55`, background: '#fff', color: t.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                + {t.label}
              </button>
            ))}
          </div>
        )}
        </>
        );
      })() : (
        <button onClick={() => setOpenSection(null)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer', marginBottom: 12 }}>
          ‹ Back to bid sections
        </button>
      ))}
      {/* ═══ ZONE 1 — ADMIN divider (project mode only) ══════════════ */}
      {projectId && project && isAdminZone && (
        <div style={{ margin: '28px 0 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 2, background: '#1e3a5f', borderRadius: 1 }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 1.5, whiteSpace: 'nowrap' }}>Zone 1 — Admin</span>
          <div style={{ flex: 1, height: 2, background: '#1e3a5f', borderRadius: 1 }} />
        </div>
      )}

      {/* ── Section 1: Job Description ─────────────────────────────────────── */}
      {(projectId || openSection === 'job_desc') && (
      <div id="job-description-section" className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
        <div onClick={() => { if (projectId) setShowJobDesc(v => !v); else setOpenSection(null); }} style={{ background: '#1e3a5f', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2h10a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="#fff" strokeWidth="1.3" fill="none"/><path d="M3 5h8M3 7h5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/></svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>Job Description</span>
          </div>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: showJobDesc ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><path d="M2 4l4 4 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        {showJobDesc && <div style={{ padding: 20 }}>
          {/* Bid mode: the same read-first Job Information card the new job page uses
              (facts, ✎ Edit, saves as you go) — fed the bid-only facts and fields and
              wired to this page's autosave. Project mode keeps the old field grid. */}
          {!projectId ? (
            <JobHeaderPillow project={null} bid={bid} canEdit={!jobInfoLocked} compact={false} hideOwner
              saveBid={fields => updateBidFields(fields)}
              savePricing={v => setPricingType(v)}
              extraChips={[
                bid.pre_bid && bid.pre_bid_mandatory ? ['Mandatory Pre-Bid', '#b91c1c'] : null,
                bid.certified_payroll ? ['Certified Payroll', '#b45309'] : null,
              ].filter(Boolean)}
              extraFacts={[
                ['Bid Date & Time', bidWhenText(bid)],
                ['Pre-Bid Meeting', preBidText(bid)],
                ['Certified Payroll', bid.certified_payroll ? 'Required' : 'Not required'],
                bid.pricing_type === 'tm' ? ['T&M Rates', tmQuoteItems.length
                  ? tmQuoteItems.slice(0, 3).map(t => `${t.name} ${fmt$(t.rate)}/${tmUnitOf(t)}`).join(' · ') + (tmQuoteItems.length > 3 ? ' · …' : '')
                  : 'None yet — add them on T&M Quote Items'] : null,
                bid.recipient_is_owner && bid.client_name ? ['Bidding To', `${bid.client_name} (owner)`] : recipients.length ? ['Bidding To', recipients.filter(r => r.client_name).map(r => r.client_name).join(', ')] : null,
              ].filter(Boolean)}
              extraFields={<>
                <JhField label="Bid Date & Time">
                  <div style={{ display: 'flex', gap: 5 }}>
                    <input type="date" style={jhInp} value={bid.bid_date || ''} onChange={e => updateBid('bid_date', e.target.value)} />
                    <input type="time" style={{ ...jhInp, width: 120 }} value={bid.bid_time || ''} onChange={e => updateBid('bid_time', e.target.value)} />
                  </div>
                </JhField>
                <JhField label="Certified Payroll">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 0' }}>
                    <input type="checkbox" checked={!!bid.certified_payroll}
                      onChange={e => updateBid('certified_payroll', e.target.checked ? 1 : 0)}
                      style={{ width: 16, height: 16, accentColor: '#1e3a5f', cursor: 'pointer' }} />
                    <span style={{ fontSize: 13, color: bid.certified_payroll ? '#1e3a5f' : '#6b7280', fontWeight: bid.certified_payroll ? 600 : 400 }}>
                      {bid.certified_payroll ? 'Required' : 'Not Required'}
                    </span>
                  </label>
                </JhField>
                {/* The drill day/hour rate and mobilization used to be edited here; they
                    are quote items now (one rate source), and the drilling card's billing
                    reads the mirror the server keeps in step. */}
                {bid.pricing_type === 'tm' && (
                  <JhField label="T&M Rates" note="on the quote">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: '#374151', padding: '6px 0' }}>
                      <span>{tmQuoteItems.length ? `${tmQuoteItems.length} rate line${tmQuoteItems.length !== 1 ? 's' : ''}` : 'No rate lines yet'}</span>
                      <button type="button" onClick={() => { setShowLineItems(true); setOpenSection('line_items'); }}
                        style={{ background: 'none', border: '1px solid #ddd6fe', borderRadius: 999, padding: '3px 10px', color: '#6d28d9', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                        T&M Quote Items ›
                      </button>
                    </div>
                  </JhField>
                )}
                {bid.job_type === 'Prime' && <PreBidBlock bid={bid} isLocked={jobInfoLocked} updateBid={updateBid} updateBidFields={updateBidFields} />}
              </>} />
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {/* Job / Project Number — editable in project mode (the big number up top) */}
            {projectId && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Job / Project Number</label>
                <input type="text" value={projNumDraft} onChange={e => setProjNumDraft(e.target.value)} onBlur={saveProjectNumber}
                  disabled={jobInfoLocked} placeholder="e.g. 2534"
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }} />
              </div>
            )}
            {/* Awarded By — the contractor who awarded us the work (blank = we're the
                prime and documents address the Owner). Drives who change orders and
                submittals are addressed to. Quick-pick from the bid recipients. */}
            {projectId && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Awarded By (Contractor)</label>
                <input type="text" value={awardedByDraft} onChange={e => setAwardedByDraft(e.target.value)} onBlur={() => saveAwardedBy()}
                  disabled={jobInfoLocked} placeholder={`Blank = we're prime (${bid.client_name || 'Owner'})`}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }} />
                {project?.awarded_by_company && (
                  <div style={{ fontSize: 11, color: '#6b7280', background: '#f9fafb', border: '1px solid #eef2f7', borderRadius: 6, padding: '5px 8px', lineHeight: 1.45 }}>
                    <b style={{ color: '#111827' }}>{project.awarded_by_company}</b>
                    {project.awarded_by_name && project.awarded_by_name !== project.awarded_by_company && <> · Attn: {project.awarded_by_name}</>}
                    {project.awarded_by_address1 && <><br />{project.awarded_by_address1}</>}
                    {project.awarded_by_address2 && <><br />{project.awarded_by_address2}</>}
                  </div>
                )}
                {!jobInfoLocked && recipients.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 2 }}>
                    {recipients.filter(r => r.client_name).map(r => {
                      const label = r.contact_name || r.client_name;
                      return (
                        <button key={r.id} type="button" onClick={() => { setAwardedByDraft(label); saveAwardedBy(label, r); }}
                          style={{ fontSize: 10.5, fontWeight: 700, color: '#0f4c35', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 999, padding: '3px 9px', cursor: 'pointer' }}>
                          {r.client_name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {/* When it runs — read from the Schedule tiles (Ryan, Oct 1 2026); the typed
                completion date that used to live here drove the due pills and was wrong
                more often than the schedule. */}
            {projectId && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Schedule</label>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: '#f9fafb', color: project?.sched_start ? '#111' : '#9ca3af' }}>
                  {project?.sched_start
                    ? `${(d => new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))(project.sched_start)} → ${(d => new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))(project.sched_finish || project.sched_start)}`
                    : 'Not on the schedule yet — set it on the Schedule tile'}
                </div>
              </div>
            )}
            {/* Job Name — always top-left */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Job Name</label>
              <input type="text" value={bid.job_name || ''} onChange={e => updateBid('job_name', e.target.value)}
                disabled={jobInfoLocked}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }} />
            </div>
            {/* Location */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Location</label>
                <button type="button" onClick={() => setShowLocationMap(true)} disabled={jobInfoLocked}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: jobInfoLocked ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#1d4ed8', fontWeight: 600, opacity: jobInfoLocked ? 0.4 : 1 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 1C4.07 1 2.5 2.57 2.5 4.5c0 2.625 3.5 6.5 3.5 6.5s3.5-3.875 3.5-6.5C9.5 2.57 7.93 1 6 1z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                    <circle cx="6" cy="4.5" r="1.2" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                  </svg>
                  Pick on Map
                </button>
              </div>
              <input type="text" value={bid.job_location || ''} onChange={e => updateBidFields({ job_location: e.target.value, job_lat: null, job_lng: null })}
                disabled={jobInfoLocked}
                placeholder="Address or click Pick on Map"
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }} />
            </div>
            {/* Bid Date + Time */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Bid Date &amp; Time</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="date" value={bid.bid_date || ''} onChange={e => updateBid('bid_date', e.target.value)}
                  disabled={jobInfoLocked}
                  style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }} />
                <input type="time" value={bid.bid_time || ''} onChange={e => updateBid('bid_time', e.target.value)}
                  disabled={jobInfoLocked}
                  style={{ width: 110, border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }} />
              </div>
            </div>
            {/* Bid Type */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Bid Type</label>
              <select value={bid.bid_type || ''} disabled={jobInfoLocked} onChange={e => updateBid('bid_type', e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }}>
                <option value="">— Select type —</option>
                <option value="Unit Price">Unit Price</option>
                <option value="Lump Sum">Lump Sum</option>
                <option value="Vertical Drilling">Vertical Drilling</option>
                <option value="Directional Drilling">Directional Drilling</option>
                {/* Kept only for the handful of bids already saved this way, so opening
                    one does not show a blank box and quietly change it on the next save. */}
                {bid.bid_type === 'Time and Material' && <option value="Time and Material">Time and Material (old)</option>}
              </select>
            </div>
            {/* Pricing — Hard Bid vs Time & Material. Switchable after the bid flow so
                a job can be moved onto T&M billing without re-creating it. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Pricing</label>
              <select value={bid.pricing_type || 'hard'} disabled={jobInfoLocked} onChange={e => setPricingType(e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }}>
                <option value="hard">Hard Bid (fixed price)</option>
                <option value="tm">Time &amp; Material</option>
              </select>
              {bid.pricing_type === 'tm' && (
                <span style={{ fontSize: 11, color: '#6b7280' }}>T&amp;M Billing (labor, material, equipment) shows further down this page.</span>
              )}
            </div>
            {/* Time & Material price (editable after the bid flow) */}
            {bid.pricing_type === 'tm' && (
              <TMPriceBlock bid={bid} bidId={id} locked={jobInfoLocked}
                onSaved={updated => setBid(b => ({ ...b, ...updated }))} />
            )}
            {/* Job Type */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Job Type</label>
              <select value={bid.job_type || ''} disabled={jobInfoLocked} onChange={e => updateBid('job_type', e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }}>
                <option value="">— Select type —</option>
                <option value="Prime">Prime</option>
                <option value="Sub-Contractor">Sub-Contractor</option>
              </select>
            </div>
            {/* Job Sector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Public / Private</label>
              <select value={bid.job_sector || ''} disabled={jobInfoLocked} onChange={e => updateBid('job_sector', e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }}>
                <option value="">— Select —</option>
                <option value="Public">Public (Government)</option>
                <option value="Private">Private</option>
              </select>
            </div>
            {/* Project Manager */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Project Manager</label>
              <select value={bid.bid_pm_id || ''} disabled={jobInfoLocked} onChange={e => updateBid('bid_pm_id', e.target.value || null)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: jobInfoLocked ? '#f9fafb' : '#fff' }}>
                <option value="">— Select PM —</option>
                {/* The same people a new bid or a bid from email can have — PMs and Executives — and whoever it
                    is now, so the box never shows blank for a PM it can't list (Kaitlin, Oct 8 2026). */}
                {employees.filter(e => ['ProjectManager', 'Executive', 'Admin'].includes(e.role) || String(e.id) === String(bid.bid_pm_id || '')).map(e => (
                  <option key={e.id} value={e.id}>{e.firstName} {e.lastName}{e.role === 'Executive' ? ' (Executive)' : ''}</option>
                ))}
              </select>
            </div>
            {/* Pre-Bid (Prime only) */}
            {bid.job_type === 'Prime' && <PreBidBlock bid={bid} isLocked={jobInfoLocked} updateBid={updateBid} updateBidFields={updateBidFields} />}
            {/* Certified Payroll */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Certified Payroll</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: jobInfoLocked ? 'default' : 'pointer', padding: '7px 0' }}>
                <input type="checkbox" checked={!!bid.certified_payroll} disabled={jobInfoLocked}
                  onChange={e => updateBid('certified_payroll', e.target.checked ? 1 : 0)}
                  style={{ width: 16, height: 16, accentColor: '#1e3a5f', cursor: jobInfoLocked ? 'default' : 'pointer' }} />
                <span style={{ fontSize: 13, color: bid.certified_payroll ? '#1e3a5f' : '#6b7280', fontWeight: bid.certified_payroll ? 600 : 400 }}>
                  {bid.certified_payroll ? 'Required' : 'Not Required'}
                </span>
              </label>
            </div>
            {showLocationMap && (
              <LocationPickerModal
                value={bid.job_location}
                onConfirm={({ address, lat, lng }) => {
                  // Keep the pin, not just the words (2706, Oct 7 2026) — the job is placed from it.
                  updateBidFields({ job_location: address, job_lat: lat ?? null, job_lng: lng ?? null }); setDirty(true);
                  // In project mode, save the picked point to the project's
                  // location so the Equipment Locations map can plot it.
                  if (projectId && address) {
                    apiFetch(`/projects/${projectId}/locations`, { method: 'POST', body: JSON.stringify({ address, lat: lat ?? null, lng: lng ?? null, replace: true }) }).catch(() => {});
                  }
                }}
                onClose={() => setShowLocationMap(false)}
              />
            )}
          </div>
          )}

          {/* Project Owner card. Once this is a job, correspondence goes through the
              GC, so this is background rather than the headline — smaller, muted and
              labelled as coming from the bid. It stays fully usable: the owner still
              addresses some submittals and prints on the change-order letterhead. */}
          <div style={{ marginTop: 14, borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', opacity: projectId ? 0.92 : 1 }}>
            <div style={{ background: projectId ? '#4b5563' : '#0f4c35', padding: projectId ? '7px 12px' : '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 1C5.34 1 4 2.34 4 4s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zM1 13c0-3.31 2.69-6 6-6s6 2.69 6 6" stroke="#fff" strokeWidth="1.3" strokeLinecap="round"/></svg>
                <span style={{ fontSize: projectId ? 10 : 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Project Owner Contact{projectId && <span style={{ opacity: 0.7, fontWeight: 600 }}> · from the bid</span>}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* We are quoting the owner directly (Pat, Sep 2026) — in the green bar, left
                    of Change. Ticked, there is no separate list of contractors to quote, so
                    the Bid Recipients pillow below goes away and the quote is addressed to
                    the owner on purpose. Anything already on that list is kept. */}
                <label title={bid.recipient_is_owner ? 'The quote goes to the owner, and the Bid Recipients list is hidden. Untick to bring it back.' : 'Tick this when we are quoting the owner directly — the Bid Recipients list below goes away.'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: '#fff', cursor: jobInfoLocked ? 'default' : 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={!!bid.recipient_is_owner} disabled={jobInfoLocked}
                    onChange={e => updateBidFields({ recipient_is_owner: e.target.checked ? 1 : 0 })}
                    style={{ width: 15, height: 15, accentColor: '#22c55e', cursor: jobInfoLocked ? 'default' : 'pointer' }} />
                  Bid recipient is the Project Owner
                </label>
                {bid.client_name && !jobInfoLocked && (
                  <button onClick={() => {
                    updateBidFields({ client_id: null, client_name: '', client_address1: '', client_address2: '', client_phone: '', client_fax: '', client_contact: '', client_contact_id: null, client_email: '' });
                    setClientContacts([]);
                  }} style={{ padding: '3px 9px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.4)', background: 'transparent', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Change
                  </button>
                )}
              </div>
            </div>

            {!bid.client_name ? (
              /* Picker — no client selected yet */
              <div style={{ padding: '14px', background: '#f0fdf4', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>GC / Client / Vendor</label>
                  <CompanyCombo companies={companies} value={bid.client_name} disabled={jobInfoLocked}
                    placeholder="Type or select a company…"
                    allowCreate onCreated={foldCompany}
                    onSelect={co => {
                      updateBidFields({ client_id: co.clientId || null, client_name: co.name, client_address1: co.address1 || '', client_address2: co.address2 || '', client_phone: co.phone || '', client_fax: co.fax || '', client_contact: co.contact || '', client_contact_id: null, client_email: co.email || '' });
                      setClientContacts([]);
                      if (co.clientId) apiFetch(`/clients/${co.clientId}/contacts`).then(setClientContacts).catch(() => {});
                    }}
                    style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
            ) : (
              /* Card — client selected */
              <div style={{ padding: '12px 14px' }}>
                {/* Contact person — pick, add, or remove (people change over time) */}
                {!jobInfoLocked && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Contact Person</label>
                    <ContactManager clientId={bid.client_id} contacts={clientContacts} setContacts={setClientContacts}
                      selectedId={bid.client_contact_id} disabled={jobInfoLocked}
                      onSelect={cc => {
                        if (cc) updateBidFields({ client_contact_id: cc.id, client_contact: cc.name, client_phone: cc.phone || bid.client_phone || '', client_email: cc.email || '' });
                        else updateBidFields({ client_contact_id: null, client_contact: '', client_email: '' });
                      }} />
                  </div>
                )}
                {/* Info card */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{bid.client_name}</div>
                    {(bid.client_contact || bid.client_email || bid.client_phone) && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {bid.client_contact}{bid.client_email ? ` · ${bid.client_email}` : ''}{bid.client_phone ? ` · ${bid.client_phone}` : ''}
                      </div>
                    )}
                    {(bid.client_address1 || bid.client_address2) && (
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>
                        {[bid.client_address1, bid.client_address2].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Ticked with contractors already on the hidden list: say so, so nobody thinks
                they were thrown away. */}
            {!!bid.recipient_is_owner && recipients.length > 0 && (
              <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f1f1', background: '#fafafa', fontSize: 11.5, color: '#6b7280', lineHeight: 1.4 }}>
                The Bid Recipients list is hidden — the {recipients.length} already on it {recipients.length === 1 ? 'is' : 'are'} kept. Untick the box above to see {recipients.length === 1 ? 'it' : 'them'}.
              </div>
            )}
          </div>

          {/* Bid Recipients — who we quoted. History once the job exists, but the
              awarded one still decides who everything is addressed to, so it shrinks
              rather than hides. Hidden altogether when the recipient IS the owner. */}
          <div style={{ marginTop: 14, borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', opacity: projectId ? 0.92 : 1, display: bid.recipient_is_owner ? 'none' : undefined }}>
        <div style={{ background: projectId ? '#4b5563' : '#0f4c35', padding: projectId ? '7px 14px' : '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }} onClick={() => setShowRecipients(v => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="5" cy="4" r="2.5" stroke="#fff" strokeWidth="1.3"/><path d="M1 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#fff" strokeWidth="1.3" strokeLinecap="round"/><circle cx="10.5" cy="4" r="2" stroke="#fff" strokeWidth="1.2"/><path d="M12.5 12c0-1.7-1-3-2.5-3.5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/></svg>
            <span style={{ fontSize: projectId ? 10 : 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Bid Recipients ({recipients.length}){projectId && <span style={{ opacity: 0.7, fontWeight: 600 }}> · from the bid</span>}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!jobInfoLocked && showRecipients && (
              <button onClick={e => { e.stopPropagation(); setShowAddRecipient(v => !v); }}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.4)', background: 'transparent', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {showAddRecipient ? 'Cancel' : '+ Add GC / Client'}
              </button>
            )}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: showRecipients ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><path d="M2 4l4 4 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
        </div>

        {showRecipients && showAddRecipient && (
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', background: '#f0fdf4', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>GC / Client / Vendor</label>
              <CompanyCombo companies={companies} value={recipientCompany?.name || ''}
                placeholder="Type or select a company…"
                allowCreate onCreated={foldCompany}
                onSelect={co => {
                  setRecipientCompany(co);
                  setRecipientContactId('');
                  setRecipientContacts([]);
                  if (co.clientId) apiFetch(`/clients/${co.clientId}/contacts`).then(setRecipientContacts).catch(() => {});
                }}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            </div>
            {recipientCompany && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 260px' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Contact</label>
                <ContactManager clientId={recipientCompany.clientId} contacts={recipientContacts} setContacts={setRecipientContacts}
                  selectedId={recipientContactId}
                  onSelect={c => setRecipientContactId(c ? c.id : '')} />
              </div>
            )}
            <button onClick={addRecipient} disabled={!recipientCompany}
              style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: recipientCompany ? '#0f4c35' : '#d1d5db', color: '#fff', fontSize: 13, fontWeight: 600, cursor: recipientCompany ? 'pointer' : 'default' }}>
              Add
            </button>
          </div>
        )}

        {showRecipients && (recipients.length === 0 ? (
          <div style={{ padding: '20px 24px', color: '#9ca3af', fontSize: 13 }}>No recipients yet — add the GCs/Clients you'll be sending this bid to.</div>
        ) : (
          <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recipients.map(r => (
              <div key={r.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 14px', borderRadius: 8, border: r.low_bidder ? '1.5px solid #16a34a' : '1px solid #e5e7eb', background: r.low_bidder ? '#f0fdf4' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                      {r.client_name}
                      {r.low_bidder ? <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>🔒 Awarded</span> : null}
                    </div>
                    {/* Who the quote is addressed to. Editable — the contact that came
                        across with the company is often the wrong person, or missing. */}
                    <RecipientContact recipient={r} disabled={jobInfoLocked}
                      onChange={fields => patchRecipient(r.id, fields)} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => exportQuotePdf(bid, items, r, alternates, { onError: m => showToast(m, 'error') })}
                      title={`Export the bid PDF addressed to ${r.client_name} only (other GCs are hidden)`}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#1e3a5f', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      📄 Export PDF
                    </button>
                    {(r.revision > 0) && (
                      <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, background: '#eef2ff', padding: '3px 8px', borderRadius: 4, letterSpacing: 0.3 }}>
                        Rev {r.revision}
                      </span>
                    )}
                    {r.sent_at ? (
                      <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600, background: '#ecfdf5', padding: '3px 8px', borderRadius: 4 }}>
                        Sent {new Date(r.sent_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, background: '#fffbeb', padding: '3px 8px', borderRadius: 4 }}>Not sent</span>
                    )}
                    {!jobInfoLocked && !r.low_bidder && (
                      <button onClick={() => removeRecipient(r.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '0 2px' }}>×</button>
                    )}
                  </div>
                </div>
                {/* Award tracking. The GC's bid to the owner + whether Titan was
                    listed. Checking "Awarded" marks that GC as the winner and locks
                    the row. This does NOT create a project — the top Award button
                    handles that, once the GC awards us the subcontract. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Bid Amount</label>
                    <RecipientBidAmount value={r.price_to_owner} disabled={isLocked || !!r.low_bidder}
                      onSave={v => patchRecipient(r.id, { price_to_owner: v })} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: (isLocked || r.low_bidder) ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                    <input type="checkbox" disabled={isLocked || !!r.low_bidder} checked={!!r.used_us} onChange={e => patchRecipient(r.id, { used_us: e.target.checked })} />
                    Titan listed
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: isLocked ? 'default' : 'pointer', fontSize: 12, fontWeight: 700, color: r.low_bidder ? '#15803d' : '#374151' }}>
                    <input type="checkbox" disabled={isLocked} checked={!!r.low_bidder} onChange={e => patchRecipient(r.id, { low_bidder: e.target.checked })} />
                    Awarded {r.low_bidder ? '🔒' : ''}
                  </label>
                </div>
              </div>
            ))}
          </div>
        ))}
          </div>
        </div>}
      </div>
      )}

      {/* ── Section 3: Scope & Bid Details (Labor Costs & Mark Ups merged at top) ── */}
      {(projectId || openSection === 'scope') && (
        <div className="admin-card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          <div onClick={() => { if (projectId) setShowScope(v => !v); else setOpenSection(null); }} style={{ background: '#4a1d50', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 3h10M2 6h10M2 9h6" stroke="#fff" strokeWidth="1.3" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>Scope &amp; Bid Details</span>
            </div>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: showScope ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><path d="M2 4l4 4 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          {showScope && <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Bid summary — the cost stack rolled up from the line items. Reads top to
                bottom the way a bid is built up, and every subtotal ties to the next so the
                bid can be checked rather than just glanced at. */}
            <div style={{ marginBottom: 20 }}>{renderBidSummary()}</div>

            {/* Bid mode reads first: everything below the summary as a card of facts, with
                ✎ Edit opening the form. Project mode (the old job page) is always the form. */}
            {!projectId && !scopeEditing ? (() => {
              const Lbl = ({ children }) => <div style={{ ...jhLbl, marginBottom: 4 }}>{children}</div>;
              const chipStyle = tint => ({ fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 999, border: `1px solid ${tint}33`, background: `${tint}14`, color: tint, whiteSpace: 'nowrap' });
              const pct = v => v == null || v === '' ? '—' : `${v}%`;
              const markups = [
                [`Labor $${bid.labor_rate ?? '—'}/hr`, '#064e3b'], [`Supervision ${pct(bid.supervision_pct)}`, '#064e3b'],
                [`Sales tax ${pct(bid.sales_tax_pct)}`, '#064e3b'], [`OH material ${pct(bid.overhead_material_pct)}`, '#064e3b'],
                [`OH labor ${pct(bid.overhead_labor_pct)}`, '#064e3b'], [`Profit ${pct(bid.profit_pct)}`, '#15803d'],
              ];
              const isDrill = bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling';
              let holes = 0; try { holes = JSON.parse(bid.drill_hole_schedule || '[]').length; } catch {}
              let scopeItems = []; try { scopeItems = initScopeItems(bid.drill_scope_items ? JSON.parse(bid.drill_scope_items) : null, isDrill ? DEFAULT_DRILL_SCOPE : DEFAULT_GENERAL_SCOPE); } catch {}
              const included = scopeItems.filter(s => s.status === 'included'), excluded = scopeItems.filter(s => s.status === 'excluded');
              const wf = currentBidWorkflows().map(k => WORKFLOW_OPTIONS.find(o => o.key === k)).filter(Boolean);
              const text = { fontSize: 13, color: '#111', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
              const empty = <span style={{ color: '#9ca3af', fontWeight: 500 }}>—</span>;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Lbl>Labor Costs &amp; Mark Ups</Lbl>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{markups.map(([t, c]) => <span key={t} style={chipStyle(c)}>{t}</span>)}</div>
                    </div>
                    {!isLocked && (
                      <button onClick={() => setScopeEditing(true)}
                        style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid #fed7aa', background: '#fff7ed', color: '#c2410c', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ✎ Edit
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                    <div>
                      <Lbl>Workflows for this job</Lbl>
                      {wf.length ? <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{wf.map(o => <span key={o.key} style={chipStyle('#16a34a')}>{o.icon} {o.label}</span>)}</div> : <div style={text}>{empty}</div>}
                    </div>
                    {isDrill && (
                      <div>
                        <Lbl>Hole schedule</Lbl>
                        <div style={text}>{holes ? `${holes} hole${holes !== 1 ? 's' : ''} scheduled` : empty}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ height: 1, background: '#f1f1f1' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                    <div><Lbl>Scope of Work</Lbl><div style={text}>{bid.scope?.trim() ? bid.scope : empty}</div></div>
                    <div><Lbl>Additional Comments</Lbl><div style={text}>{bid.exclusions?.trim() ? bid.exclusions : <span style={{ color: '#9ca3af', fontWeight: 500 }}>Not included on the quote</span>}</div></div>
                  </div>
                  {(included.length > 0 || excluded.length > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                      <div>
                        <Lbl><span style={{ color: '#16a34a' }}>Included</span></Lbl>
                        <div style={{ ...text, columnCount: included.length > 6 ? 2 : 1, columnGap: 16 }}>{included.length ? included.map(s => <div key={s.id}>✓ {s.label}</div>) : empty}</div>
                      </div>
                      <div>
                        <Lbl><span style={{ color: '#dc2626' }}>Excluded</span></Lbl>
                        <div style={{ ...text, columnCount: excluded.length > 6 ? 2 : 1, columnGap: 16 }}>{excluded.length ? excluded.map(s => <div key={s.id}>✗ {s.label}</div>) : empty}</div>
                      </div>
                    </div>
                  )}
                  <div><Lbl>Notes</Lbl><div style={{ ...text, color: '#374151' }}>{bid.bid_notes?.trim() ? bid.bid_notes : empty}</div></div>
                  <div>
                    <Lbl>Bid Documents &amp; Plans</Lbl>
                    {attachments.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* The AI extract / import-old-quote actions stay one click away
                            here — they were the most-used part of this list. */}
                        {attachments.map(att => {
                          const pdf = att.mime_type === 'application/pdf' || /\.pdf$/i.test(att.original_name || '');
                          const sheet = /\.(xlsx|xls|xlsm|csv)$/i.test(att.original_name || '');
                          return (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <a href={`${API_BASE}/bids/${id}/attachments/${att.id}/download?token=${getToken()}`} target="_blank" rel="noreferrer"
                                style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 200px', minWidth: 0 }}>
                                📄 {att.original_name}
                              </a>
                              {pdf && !isLocked && (
                                <button onClick={() => extractFromAttachment(att)} disabled={extracting === att.id}
                                  style={{ background: 'none', border: '1px solid #7c3aed', borderRadius: 5, cursor: extracting === att.id ? 'default' : 'pointer', color: '#7c3aed', fontSize: 11, padding: '2px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {extracting === att.id ? '⏳ Extracting…' : '✦ Extract with AI'}
                                </button>
                              )}
                              {(pdf || sheet) && !isLocked && (
                                <button onClick={() => importOldQuote(att)} disabled={importingQuote === att.id}
                                  title="Read this as a quote we already sent — brings in each line and the price as quoted"
                                  style={{ background: 'none', border: '1px solid #0f766e', borderRadius: 5, cursor: importingQuote === att.id ? 'default' : 'pointer', color: '#0f766e', fontSize: 11, padding: '2px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {importingQuote === att.id ? '⏳ Reading…' : '📄 Import Old Quote'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                        <div style={{ fontSize: 11.5, color: '#9ca3af' }}>Upload or remove files under ✎ Edit; every file is also on the Documents tile.</div>
                      </div>
                    ) : <div style={text}>{empty}</div>}
                  </div>
                </div>
              );
            })() : (<>

            {/* ── Labor Costs & Mark Ups (moved to the top of Scope & Bid Details) ── */}
            {renderMarkups()}

            {/* ── Vertical / Directional Drilling fields ── */}
            {(bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: '#1e40af' }}>
                  🔀 On award, a <strong style={{ margin: '0 3px' }}>{bid.bid_type === 'Directional Drilling' ? 'Directional Drilling' : 'Vertical Drilling'}</strong> workflow tab is created for this job automatically.
                </div>
                <HoleScheduleTable
                  value={bid.drill_hole_schedule ? JSON.parse(bid.drill_hole_schedule) : []}
                  isLocked={isLocked}
                  onChange={updateHoleSchedule}
                  bidType={bid.bid_type}
                />
              </div>
            )}

            {/* ── Workflows for this job (pre-fills the Award window) ── */}
            {isAdminZone && (() => {
              const sel = currentBidWorkflows();
              return (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 3 }}>🔀 Workflows for this job</div>
                  <div style={{ fontSize: 11.5, color: '#9ca3af', marginBottom: 10 }}>Pick which workflows this job needs. These pre-fill the Award window and become tabs on the project once it’s awarded.</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 8 }}>
                    {WORKFLOW_OPTIONS.map(opt => {
                      const on = sel.includes(opt.key);
                      return (
                        <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${on ? '#16a34a' : '#e5e7eb'}`, background: on ? '#f0fdf4' : '#fff', cursor: jobInfoLocked ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, color: '#111827' }}>
                          <input type="checkbox" checked={on} disabled={jobInfoLocked} onChange={() => toggleBidWorkflow(opt.key)} style={{ width: 15, height: 15, accentColor: '#16a34a' }} />
                          <span style={{ fontSize: 15 }}>{opt.icon}</span>{opt.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── Scope & Additional Comments (all bid types) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Scope of Work</label>
                <textarea value={bid.scope || ''} onChange={e => updateBid('scope', e.target.value)} rows={4} disabled={jobInfoLocked}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, resize: 'vertical', background: jobInfoLocked ? '#f9fafb' : '#fff', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Additional Comments</label>
                  {!jobInfoLocked && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto' }}>
                      <input type="checkbox" checked={showAdditionalComments}
                        onChange={e => {
                          setShowAdditionalComments(e.target.checked);
                          if (!e.target.checked) updateBid('exclusions', '');
                        }} />
                      <span style={{ fontSize: 11, color: '#6b7280' }}>Include</span>
                    </label>
                  )}
                </div>
                {showAdditionalComments
                  ? <textarea value={bid.exclusions || ''} onChange={e => updateBid('exclusions', e.target.value)} rows={4} disabled={jobInfoLocked}
                      style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, resize: 'vertical', background: jobInfoLocked ? '#f9fafb' : '#fff', fontFamily: 'inherit' }} />
                  : <div style={{ border: '1px dashed #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic', minHeight: 88, display: 'flex', alignItems: 'center' }}>
                      Not included — check "Include" to add comments
                    </div>
                }
              </div>
            </div>

            {/* ── Inclusions & Exclusions — all bid types ── */}
            <DrillScopeChecklist
              value={bid.drill_scope_items ? JSON.parse(bid.drill_scope_items) : null}
              isLocked={jobInfoLocked}
              onChange={scopeItems => updateBid('drill_scope_items', JSON.stringify(scopeItems))}
              defaults={(bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling') ? DEFAULT_DRILL_SCOPE : DEFAULT_GENERAL_SCOPE}
            />

            {/* Notes */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Notes</div>
              <textarea value={bid.bid_notes || ''} onChange={e => updateBid('bid_notes', e.target.value)} rows={3} disabled={jobInfoLocked}
                placeholder="Internal notes, special conditions, reminders…"
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical', background: jobInfoLocked ? '#f9fafb' : '#fff', fontFamily: 'inherit', color: '#374151' }} />
            </div>

            {/* File upload area */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 10 }}>Bid Documents &amp; Plans</div>
              {!jobInfoLocked && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = '#f5f3ff'; }}
                  onDragLeave={e => { e.currentTarget.style.background = '#faf5ff'; }}
                  onDrop={async e => {
                    e.preventDefault();
                    e.currentTarget.style.background = '#faf5ff';
                    const files = Array.from(e.dataTransfer.files);
                    if (files.length === 0) return;
                    setUploading(true);
                    try {
                      for (const file of files) {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await fetch(`${API_BASE}/bids/${id}/attachments`, {
                          method: 'POST',
                          headers: { 'x-worker-token': getToken() },
                          body: fd,
                        });
                        if (res.ok) {
                          const att = await res.json();
                          setAttachments(a => [...a, att]);
                        } else {
                          const j = await res.json().catch(() => ({}));
                          showToast(`${file.name}: ${j.error || `upload failed (${res.status})`}`, 'error');
                        }
                      }
                    } finally { setUploading(false); }
                  }}
                  style={{ border: '2px dashed #c4b5fd', borderRadius: 10, padding: '20px 16px', textAlign: 'center', cursor: 'pointer', background: '#faf5ff', color: '#7c3aed', fontSize: 13, transition: 'background 0.15s' }}>
                  {uploading ? 'Uploading…' : (
                    <span>
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ verticalAlign: 'middle', marginRight: 6 }}><path d="M10 3v10M6 7l4-4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 17h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                      Drop files here or <strong>click to upload</strong> — PDFs, plans, and bid documents
                    </span>
                  )}
                </div>
              )}
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.dwg,.dxf"
                style={{ display: 'none' }}
                onChange={async e => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;
                  setUploading(true);
                  e.target.value = '';
                  try {
                    for (const file of files) {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await fetch(`${API_BASE}/bids/${id}/attachments`, {
                        method: 'POST',
                        headers: { 'x-worker-token': getToken() },
                        body: fd,
                      });
                      if (res.ok) {
                        const att = await res.json();
                        setAttachments(a => [...a, att]);
                      } else {
                        const j = await res.json().catch(() => ({}));
                        showToast(`${file.name}: ${j.error || `upload failed (${res.status})`}`, 'error');
                      }
                    }
                  } finally { setUploading(false); }
                }} />

              {attachments.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {attachments.map(att => {
                    const isPdf = att.mime_type === 'application/pdf' || att.original_name?.toLowerCase().endsWith('.pdf');
                    const sizeKb = att.size ? (att.size / 1024).toFixed(0) : null;
                    return (
                      <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: isPdf ? '#dc2626' : '#6b7280' }}><path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z" stroke="currentColor" strokeWidth="1.2" fill="none"/><path d="M9 1v4h4" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>
                        <span style={{ flex: 1, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_name}</span>
                        {sizeKb && <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{sizeKb} KB</span>}
                        {isPdf && (
                          <button onClick={() => extractFromAttachment(att)} disabled={extracting === att.id}
                            style={{ background: 'none', border: '1px solid #7c3aed', borderRadius: 5, cursor: extracting === att.id ? 'default' : 'pointer', color: '#7c3aed', fontSize: 11, padding: '2px 8px', flexShrink: 0, fontWeight: 600 }}>
                            {extracting === att.id ? '⏳ Extracting…' : '✦ Extract with AI'}
                          </button>
                        )}
                        {/* Bringing an old job into the system: read a quote we already
                            sent and take its prices as quoted, rather than re-costing it. */}
                        {(isPdf || /\.(xlsx|xls|xlsm|csv)$/i.test(att.original_name || '')) && (
                          <button onClick={() => importOldQuote(att)} disabled={importingQuote === att.id}
                            title="Read this as a quote we already sent — brings in each line and the price as quoted"
                            style={{ background: 'none', border: '1px solid #0f766e', borderRadius: 5, cursor: importingQuote === att.id ? 'default' : 'pointer', color: '#0f766e', fontSize: 11, padding: '2px 8px', flexShrink: 0, fontWeight: 600 }}>
                            {importingQuote === att.id ? '⏳ Reading…' : '📄 Import Old Quote'}
                          </button>
                        )}
                        <a href={`${API_BASE}/bids/${id}/attachments/${att.id}/download?token=${getToken()}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', flexShrink: 0 }}>Download</a>
                        {!jobInfoLocked && (
                          <button onClick={async () => {
                            if (!window.confirm(`Delete "${att.original_name}"?`)) return;
                            await apiFetch(`/bids/${id}/attachments/${att.id}`, { method: 'DELETE' });
                            setAttachments(a => a.filter(x => x.id !== att.id));
                          }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!projectId && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button onClick={() => setScopeEditing(false)}
                  style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                  Done
                </button>
                <span style={{ fontSize: 11.5, color: '#9ca3af' }}>Changes save as you go — Done just closes editing.</span>
              </div>
            )}
            </>)}
          </div>}
        </div>
      )}

      {/* Line items */}
      {(projectId || openSection === 'line_items') && (
      <div className="admin-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '16px 20px 12px', background: '#1e3a5f', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => { if (projectId) setShowLineItems(v => !v); else setOpenSection(null); }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {!projectId && bid.pricing_type === 'tm' ? `T&M Quote Items (${tmQuoteItems.length})` : `Line Items (${items.length})`}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Line items auto-save, but silently — so people couldn't tell whether their
                typing had landed. Say so, and offer a Save while anything is pending. */}
            {showLineItems && lineItemsForm && saveState !== 'idle' && (() => {
              const m = {
                pending: { t: '● Unsaved changes', c: '#b45309', b: '#fffbeb', br: '#fcd34d' },
                saving:  { t: '⟳ Saving…',         c: '#1d4ed8', b: '#eff6ff', br: '#bfdbfe' },
                saved:   { t: '✓ All changes saved', c: '#15803d', b: '#f0fdf4', br: '#bbf7d0' },
                error:   { t: '⚠ Not saved — retry', c: '#b91c1c', b: '#fef2f2', br: '#fecaca' },
              }[saveState];
              const clickable = saveState === 'pending' || saveState === 'error';
              return (
                <button onClick={e => { e.stopPropagation(); if (!clickable) return; clearTimeout(itemsSaveTimer.current); setSaveState('saving'); saveItems(itemsRef.current).then(ok => { if (ok) { pendingSaveRef.current = false; setSaveState('saved'); } else setSaveState('error'); }).catch(() => setSaveState('error')); }}
                  title={clickable ? 'Save now' : 'Line items save on their own a moment after you stop typing'}
                  style={{ padding: '5px 11px', borderRadius: 999, border: `1px solid ${m.br}`, background: m.b, color: m.c, fontSize: 11, fontWeight: 700, cursor: clickable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                  {m.t}
                </button>
              );
            })()}
            {/* Sorting stays available on an awarded bid. The rows themselves are still
                editable once a bid is awarded, so hiding sort there would mean you can
                change an item number but not put the list back in order. */}
            {showLineItems && lineItemsForm && items.length > 1 && (
              <div onClick={e => e.stopPropagation()}>
                <button onClick={sortItemsByNumber} title="Put the line items in bid item # order (click again to reverse)"
                  style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #93c5fd', background: itemSortDir ? '#2563eb' : '#fff', color: itemSortDir ? '#fff' : '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {itemSortDir === 'asc' ? '↑ Sorted by #' : itemSortDir === 'desc' ? '↓ Sorted by #' : '⇅ Sort by #'}
                </button>
              </div>
            )}
            {showLineItems && lineItemsForm && !isLocked && (
              <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                {/* The Rate Sheet picker only on the old job page now — on a bid, the
                    estimator asked for the Bid Summary and Mark-ups tabs in its place. */}
                {projectId && (
                <button onClick={() => setShowRateSheet(v => !v)}
                  style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #7c3aed', background: showRateSheet ? '#7c3aed' : '#fff', color: showRateSheet ? '#fff' : '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {showRateSheet ? '▲ Rate Sheet' : '▼ Rate Sheet'}
                </button>
                )}
                {/* Cut the bid into groups the owner picks from. Sits with Sort and
                    Add Item because it's the same kind of thing: how the sheet reads. */}
                <button onClick={() => setAltModal({ title: '', kind: 'additive', beforeKey: '' })}
                  title="Split this bid into a base bid plus alternates, each with its own subtotal"
                  style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #0f766e', background: hasAlternates ? '#0f766e' : '#fff', color: hasAlternates ? '#fff' : '#0f766e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ⌥ Bid Alternates{hasAlternates ? ` (${alternates.length})` : ''}
                </button>
                {/* Lump sum on the quote (Ryan, Oct 28 2026): the sheet keeps every line; the
                    quote prints the base bid as one line at its total. Press again to split back. */}
                {!projectId && bid.pricing_type !== 'tm' && (
                  <button onClick={() => {
                    if (bid.quote_lump_sum) {
                      if (window.confirm(`The quote prints one lump sum line:\n\n“${lumpDescOf(bid)}”\n\nSplit it back out so the quote lists every line again?`)) updateBidFields({ quote_lump_sum: 0 });
                      return;
                    }
                    const desc = window.prompt(`The quote will print the ${hasAlternates ? 'base bid' : 'whole bid'} as ONE lump sum line at its total. The lines here stay as they are.${hasAlternates ? ' Each alternate prints as one line at its own subtotal.' : ''}\n\nWording for that line:`, lumpDescOf(bid));
                    if (desc == null) return;
                    updateBidFields({ quote_lump_sum: 1, quote_lump_desc: desc.trim() });
                  }}
                    title={bid.quote_lump_sum ? `Quote prints one lump sum line: “${lumpDescOf(bid)}” — click to split it back out` : 'Print the quote as one lump sum price instead of every line (the lines here stay)'}
                    style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #9333ea', background: bid.quote_lump_sum ? '#9333ea' : '#fff', color: bid.quote_lump_sum ? '#fff' : '#9333ea', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {bid.quote_lump_sum ? '📦 Lump Sum Quote ✓ — Split back' : '📦 Lump Sum Quote'}
                  </button>
                )}
                {fwOv && !projectId && (
                  <button onClick={() => setFwLinesOpen(true)} title="One line per kind of Fieldwire task, its quantity the count — run it again to sync the quantities"
                    style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e87722', background: '#fff7ed', color: '#c2410c', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    📍 Lines from Fieldwire
                  </button>
                )}
                <button onClick={addItem}
                  style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  + Add Item
                </button>
              </div>
            )}
            {/* Bid Summary and Mark-ups as tabs, where the Rate Sheet toggle used to be —
                the estimator reads the cost stack and tunes the mark-ups without leaving
                the lines. Same figures and fields as Scope & Bid Details. */}
            {!projectId && bid.pricing_type !== 'tm' && [['summary', 'Bid Summary'], ['markups', 'Mark-ups'], ['exclusions', 'Exclusions']].map(([k, l]) => {
              // Exclusions reads red until something is marked in or out — a quote without them is usually a miss.
              const warn = k === 'exclusions' && !scopeChosen(bid);
              return (
              <button key={k} onClick={e => { e.stopPropagation(); setLineItemsPanel(p => p === k ? null : k); }}
                title={warn ? 'Nothing marked included or excluded yet' : undefined}
                style={{ padding: '5px 12px', borderRadius: 999, border: '1px solid ' + (warn ? '#fca5a5' : 'rgba(255,255,255,0.5)'), background: lineItemsPanel === k ? '#fff' : (warn ? '#dc2626' : 'transparent'), color: lineItemsPanel === k ? (warn ? '#dc2626' : '#1e3a5f') : '#fff', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {lineItemsPanel === k ? '▲' : '▼'} {l}{warn ? ' !' : ''}
              </button>
              );
            })}
            {/* ✦ Extract with AI, right where the items land. Lists the bid's PDFs (the
                plans / bid docs uploaded on Scope & Bid Details or the Documents tile)
                and runs the same extraction the file list offers. The guys used this
                constantly on the old form; it must never be more than a click away. */}
            {!projectId && bid.pricing_type !== 'tm' && !isLocked && (
              <div style={{ position: 'relative' }} data-extract-pick onClick={e => e.stopPropagation()}>
                <button onClick={e => { setExtractPickRect(e.currentTarget.getBoundingClientRect()); setExtractPick(v => !v); }}
                  style={{ padding: '5px 12px', borderRadius: 999, border: '1px solid #c4b5fd', background: extractPick ? '#7c3aed' : '#f5f3ff', color: extractPick ? '#fff' : '#6d28d9', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  ✦ Extract with AI
                </button>
                {extractPick && (() => {
                  const isReadable = (name, mime) => /pdf|spreadsheet|excel|csv/i.test(mime || '') || /\.(pdf|xlsx|xls|xlsm|csv)$/i.test(name || '');
                  // Classic attachments + the Documents tile's own uploads. The tile also
                  // lists the attachments as "att:" view rows — skip those so nothing doubles.
                  const fromAtt = attachments.filter(a => isReadable(a.original_name, a.mime_type)).map(a => ({ ...a, where: 'Plans' }));
                  const fromDocs = bidDocs
                    .filter(d => !String(d.id).includes(':') && !d.generated && isReadable(d.original_name || d.title, d.mime_type))
                    .map(d => ({ id: d.id, original_name: d.title || d.original_name, mime_type: d.mime_type, where: 'Documents' }));
                  const pdfs = [...fromDocs, ...fromAtt];
                  const r = extractPickRect || { bottom: 120, right: window.innerWidth - 16 };
                  const top = Math.min(r.bottom + 6, window.innerHeight - 160);
                  return createPortal(
                    <div data-extract-pick onClick={e => e.stopPropagation()}
                      style={{ position: 'fixed', top, right: Math.max(8, window.innerWidth - r.right), zIndex: 10000, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 10,
                        width: 'min(440px, calc(100vw - 16px))', maxHeight: Math.max(160, window.innerHeight - top - 16), overflowY: 'auto', textAlign: 'left' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Pick the file to read line items from</div>
                      {pdfs.length === 0 && (
                        <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.45 }}>No PDF or spreadsheet on this bid yet — drop the bid document on the <b>Documents</b> tile, then come back here.</div>
                      )}
                      {pdfs.map(a => (
                        <button key={a.id} onClick={() => { setExtractPick(false); extractFromAttachment(a); }} disabled={extracting === a.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '7px 9px', borderRadius: 7, border: '1px solid #eef0f2', background: '#fff', fontSize: 13, color: '#111', cursor: 'pointer', marginTop: 4, font: 'inherit' }}>
                          <span>📄</span><span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{a.original_name}</span>
                          <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>{a.where}</span>
                          <span style={{ color: '#7c3aed', fontSize: 11, fontWeight: 800 }}>{extracting === a.id ? '⏳' : '✦ Read'}</span>
                        </button>
                      ))}
                    </div>, document.body);
                })()}
              </div>
            )}
            {/* Bid mode: the estimating sheet is the default; 👁 Quote view shows the
                priced table as it prints (no cost columns), ✎ Sheet comes back. */}
            {!projectId && bid.pricing_type !== 'tm' && (
              <button onClick={e => { e.stopPropagation(); setLineItemsEditing(v => !v); }}
                title={lineItemsEditing ? 'Show the priced table as it prints on the quote' : 'Back to the estimating sheet'}
                style={{ padding: '5px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.5)', background: lineItemsEditing ? 'transparent' : '#fff', color: lineItemsEditing ? '#fff' : '#1e3a5f', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {lineItemsEditing ? '👁 Quote view' : '✎ Sheet'}
              </button>
            )}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: showLineItems ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><path d="M2 4l4 4 4-4" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
        </div>

        {addItemModal && (() => {
          const manualItems = items.filter(i => i.auto_source !== 'hole_schedule');
          return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setAddItemModal(false)}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, minWidth: 300, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: '#1f2937' }}>Where would you like to add the item?</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={() => doAddItem('top', '')}
                    style={{ padding: '10px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', color: '#374151' }}>
                    ⬆ Top of list
                  </button>
                  <button onClick={() => doAddItem('bottom', '')}
                    style={{ padding: '10px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', color: '#374151' }}>
                    ⬇ Bottom of list
                  </button>
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600 }}>After a specific item:</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <select value={addAfterKey} onChange={e => setAddAfterKey(e.target.value)}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: '1.5px solid #d1d5db', fontSize: 13, color: '#1f2937' }}>
                        {manualItems.map((it, idx) => (
                          <option key={it._key} value={it._key}>
                            {it.item_number ? `#${it.item_number}` : `Row ${idx + 1}`}{it.description ? ` — ${it.description.slice(0, 35)}${it.description.length > 35 ? '…' : ''}` : ''}
                          </option>
                        ))}
                      </select>
                      <button onClick={() => doAddItem('after', addAfterKey)}
                        style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Add Here
                      </button>
                    </div>
                  </div>
                </div>
                <button onClick={() => setAddItemModal(false)}
                  style={{ marginTop: 14, width: '100%', padding: '8px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}

        {/* Bid mode tabs: the cost stack and the mark-ups, right above the lines. */}
        {showLineItems && !projectId && bid.pricing_type !== 'tm' && lineItemsPanel && (
          <div style={{ padding: '16px 20px', background: '#fafafa', borderBottom: '1px solid #e5e7eb' }}>
            {lineItemsPanel === 'summary' ? renderBidSummary() : lineItemsPanel === 'exclusions' ? renderExclusions() : renderMarkups()}
          </div>
        )}
        {showLineItems && lineItemsForm && projectId && showRateSheet && !isLocked && (
          <RateSheetPanel onAdd={(rate) => {
            const item = {
              ...newItem(items.length),
              description: rate.description,
              unit: 'HR',
              quantity: 1,
              labor_hours: rate.category === 'Labor' ? rate.rate : 0,
              dje_standard: rate.category !== 'Labor' ? rate.rate : 0,
            };
            setItems(its => [...its, item]);
            setDirty(true);
          }} />
        )}

        {showLineItems && (!projectId && bid.pricing_type === 'tm' ? (
          // A T&M bid quotes rates, not a cost build-up: the list of rate lines IS the
          // quote, and carries onto the job's T&M Rates tile at award. Ryan wants this
          // one tile to be all a T&M quote needs, so it also carries the scope of work
          // and the job's workflows above the lines, and the exclusions below them —
          // the SAME fields the Scope & Bid Details tile edits (bid.scope, bid_workflows,
          // bid.exclusions, drill_scope_items), so the two tiles never disagree.
          (() => {
            const secLbl = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 };
            const ta = { width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical', background: jobInfoLocked ? '#f9fafb' : '#fff', fontFamily: 'inherit', color: '#111' };
            const sel = currentBidWorkflows();
            // A drilling workflow on the job means the scope has holes in it — ask the
            // hole schedule (size, count, depth) right under the scope text. Same table
            // and same saved schedule as the Scope & Bid Details tile.
            const drillingSelected = sel.includes('vertical_drilling') || sel.includes('horizontal_drilling');
            const isDrillWt = drillingSelected || bid.bid_type === 'Vertical Drilling' || bid.bid_type === 'Directional Drilling';
            const holeBidType = sel.includes('vertical_drilling') ? 'Vertical Drilling' : sel.includes('horizontal_drilling') ? 'Directional Drilling' : bid.bid_type;
            return (
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* 1. What the work is — prints on the quote as Scope of Work. */}
                <div>
                  <div style={secLbl}>Scope of Work <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· prints on the quote</span></div>
                  <textarea value={bid.scope || ''} onChange={e => updateBid('scope', e.target.value)} rows={3} disabled={jobInfoLocked}
                    placeholder="What we're being asked to perform…" style={ta} />
                </div>

                {/* 1b. Drilling scope — the holes, when a drilling workflow is on the job. */}
                {drillingSelected && (
                  <div>
                    <div style={secLbl}>Hole Schedule <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· size, how many, how deep — seeds the drilling card at award</span></div>
                    <HoleScheduleTable
                      value={bid.drill_hole_schedule ? JSON.parse(bid.drill_hole_schedule) : []}
                      isLocked={isLocked}
                      onChange={updateHoleSchedule}
                      bidType={holeBidType}
                    />
                  </div>
                )}

                {/* 1c. Loops scope — how many and what size, when the Loops workflow is on
                    the job. Locations come later, on the job's Loops tab. */}
                {sel.includes('loops') && (
                  <div>
                    <div style={secLbl}>Loops <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· how many and what size — prints with the scope</span></div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 130 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Number of loops</span>
                        <input type="number" min="0" step="1" value={bid.loop_count ?? ''} disabled={jobInfoLocked}
                          onChange={e => updateBid('loop_count', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                          style={{ ...ta, resize: 'none' }} />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 200px', maxWidth: 320 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Loop size</span>
                        <input type="text" value={bid.loop_size || ''} disabled={jobInfoLocked} placeholder={"e.g. 6'×6' or 6'×40'"}
                          onChange={e => updateBid('loop_size', e.target.value)} style={{ ...ta, resize: 'none' }} />
                      </label>
                    </div>
                  </div>
                )}

                {/* 2. The quote itself — the rate lines. */}
                <div>
                  <div style={secLbl}>Quote Items <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· the rates this quote goes out with</span></div>
                  <TmTile bidId={id} bid={bid} canEdit={!isLocked} quoteMode />
                </div>

                {/* 3. What's in and what's out — the checklist and the comments that print
                    under the rates. */}
                <div style={{ borderTop: '1px solid #f1f1f1', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <div style={{ ...secLbl, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>Additional Comments <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· prints on the quote</span></span>
                      {!jobInfoLocked && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
                          <input type="checkbox" checked={showAdditionalComments}
                            onChange={e => { setShowAdditionalComments(e.target.checked); if (!e.target.checked) updateBid('exclusions', ''); }} />
                          <span style={{ fontSize: 11, color: '#6b7280' }}>Include</span>
                        </label>
                      )}
                    </div>
                    {showAdditionalComments
                      ? <textarea value={bid.exclusions || ''} onChange={e => updateBid('exclusions', e.target.value)} rows={3} disabled={jobInfoLocked}
                          placeholder="Anything the customer should know that isn't a line item…" style={ta} />
                      : <div style={{ border: '1px dashed #e5e7eb', borderRadius: 7, padding: '9px 10px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Not included — check "Include" to add comments</div>}
                  </div>
                  <DrillScopeChecklist
                    value={bid.drill_scope_items ? JSON.parse(bid.drill_scope_items) : null}
                    isLocked={jobInfoLocked}
                    onChange={scopeItems => updateBid('drill_scope_items', JSON.stringify(scopeItems))}
                    defaults={isDrillWt ? DEFAULT_DRILL_SCOPE : DEFAULT_GENERAL_SCOPE}
                  />
                </div>

                {/* 4. Which workflows the job will run — one compact row at the foot; it
                    pre-fills the Award window. Ticking a drilling one brings the hole
                    schedule up top. */}
                {isAdminZone && (
                  <div style={{ borderTop: '1px solid #f1f1f1', paddingTop: 14 }}>
                    <div style={{ ...secLbl, marginBottom: 8 }}>Workflows for this job <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· become tabs on the project at award</span></div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {WORKFLOW_OPTIONS.map(opt => {
                        const on = sel.includes(opt.key);
                        return (
                          <button key={opt.key} type="button" disabled={jobInfoLocked} onClick={() => toggleBidWorkflow(opt.key)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, border: `1.5px solid ${on ? '#16a34a' : '#e5e7eb'}`, background: on ? '#f0fdf4' : '#fff', color: on ? '#166534' : '#6b7280', fontSize: 12.5, fontWeight: 700, cursor: jobInfoLocked ? 'default' : 'pointer' }}>
                            <span style={{ fontSize: 13 }}>{on ? '✓' : opt.icon}</span>{opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        ) : !lineItemsForm ? (() => {
          // The estimate as it prints on the quote: number, description, qty, unit,
          // unit price, extended — base bid and alternates each with a subtotal. No
          // cost columns, no inputs; the editor is one ✎ Edit away.
          const inGroup = gid => items.filter(i => (i.alternate_id || null) === gid);
          const sumOf = list => list.reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
          const groups = hasAlternates
            ? [{ title: 'Base Bid', items: inGroup(null) }, ...alternates.map(a => ({ title: a.title, kind: a.kind, items: inGroup(a.id) }))]
            : [{ items }];
          const th = extra => ({ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'left', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', ...extra });
          const td = extra => ({ padding: '8px 10px', fontSize: 13, color: '#111', borderBottom: '1px solid #f1f1f1', verticalAlign: 'top', ...extra });
          return (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th({ width: 60, textAlign: 'center' })}>Item #</th>
                    <th style={th()}>Description</th>
                    <th style={th({ width: 70, textAlign: 'center' })}>Qty</th>
                    <th style={th({ width: 70, textAlign: 'center' })}>Unit</th>
                    <th style={th({ width: 120, textAlign: 'right' })}>Unit Price</th>
                    <th style={th({ width: 130, textAlign: 'right' })}>Extended</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: 36, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No line items yet — press ✎ Sheet to add the first one.</td></tr>
                  )}
                  {groups.map((g, gi) => (
                    <React.Fragment key={gi}>
                      {g.title && (
                        <tr>
                          <td colSpan={6} style={{ padding: '12px 10px 6px', fontSize: 12, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '2px solid #1e3a5f' }}>
                            {g.title}
                            {g.kind && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: g.kind === 'deductive' ? '#b91c1c' : '#6b7280', textTransform: 'none', letterSpacing: 0 }}>{g.kind === 'deductive' ? 'Deductive — subtracts from the base' : 'Additive — adds to the base'}</span>}
                          </td>
                        </tr>
                      )}
                      {g.items.map((it, idx) => (
                        <tr key={it._key || it.id || idx}>
                          <td style={td({ textAlign: 'center', color: '#6b7280' })}>{it.item_number || (idx + 1)}</td>
                          <td style={td()}>{it.description || <span style={{ color: '#c9ccd1' }}>—</span>}</td>
                          <td style={td({ textAlign: 'center' })}>{it.quantity ?? 1}</td>
                          <td style={td({ textAlign: 'center', color: '#6b7280' })}>{it.unit || ''}</td>
                          <td style={td({ textAlign: 'right', fontVariantNumeric: 'tabular-nums' })}>{fmt$(itemEffectiveUnitPrice(it, bid))}</td>
                          <td style={td({ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' })}>{fmt$(itemEffectiveTotal(it, bid))}</td>
                        </tr>
                      ))}
                      {g.title && (
                        <tr style={{ background: '#f9fafb' }}>
                          <td colSpan={5} style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#374151' }}>{g.title} subtotal</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 13, fontWeight: 800, color: g.kind === 'deductive' ? '#b91c1c' : '#111', whiteSpace: 'nowrap' }}>
                            {g.kind === 'deductive' ? `(${fmt$(sumOf(g.items))})` : fmt$(sumOf(g.items))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {items.length > 0 && (
                    <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                      <td colSpan={5} style={{ padding: '10px', fontWeight: 700, fontSize: 13, textAlign: 'right', color: '#374151' }}>
                        {!hasAlternates ? 'Grand Total' : (alternatesDecidable ? 'Base bid + accepted alternates' : 'Base bid — the number you submit')}
                      </td>
                      <td style={{ padding: '10px', fontWeight: 800, fontSize: 15, textAlign: 'right', color: 'var(--orange)', whiteSpace: 'nowrap' }}>
                        {fmt$(hasAlternates ? altTotals.contract : grandTotal)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div style={{ padding: '10px 16px 14px', fontSize: 11.5, color: '#9ca3af' }}>Prices as they print on the quote. Press ✎ Sheet for quantities, costs, hours, mark-ups, alternates and the rate sheet.</div>
            </div>
          );
        })() : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            No line items yet.{!isLocked && <> <button onClick={addItem} style={{ color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Add the first item</button>.</>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {(() => {
              // Only Vertical Drilling keeps the short sheet (its lines come off the hole
              // schedule as one price each). Directional Drilling is priced like any other
              // bid — material, labor, sub, mark-ups — so it gets the full sheet (Pat, Sep 25 2026).
              const isDrilling = bid.bid_type === 'Vertical Drilling';
              const th = (extra = {}) => ({ padding: '8px 10px', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap', ...extra });
              const hc = hiddenCols;
              const thS = (key) => (
                <th key={key} onClick={() => toggleCol(key)} title="Click to expand"
                  style={{ width: 8, padding: '4px 3px', cursor: 'pointer', background: '#f3f4f6', color: '#9ca3af', fontSize: 9, textAlign: 'center', userSelect: 'none', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>+</th>
              );
              const thC = (key, label, extra = {}) => (
                <th key={key} onClick={() => toggleCol(key)} title="Click to hide"
                  style={{ ...th({ ...extra, cursor: 'pointer', userSelect: 'none' }) }}>{label}</th>
              );
              const tdS = (key) => (
                <td key={key} onClick={e => { e.stopPropagation(); toggleCol(key); }}
                  style={{ width: 8, padding: 0, cursor: 'pointer', background: '#f3f4f6', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }} />
              );
              return (<>
            {/* A line-item cell is a plain <input>, and browsers let you select text
                inside one and drag it into another — which MOVES it, emptying the cell
                you took it from. A value walks one column across and its own cell goes
                blank, with nothing in the app aware it happened. Cancelling the drag at
                source stops it; the row-reorder handle is a span, not an input, so
                dragging rows to reorder still works. */}
            {/* No up/down arrows on the number boxes (Ryan, Oct 21 2026), and a scroll over a box you're in
                scrolls the page instead of nudging the number. */}
            <style>{'.bid-li input[type=number]::-webkit-outer-spin-button, .bid-li input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; } .bid-li input[type=number] { -moz-appearance: textfield; appearance: textfield; }'}</style>
            <table className="bid-li"
              onWheel={e => { if (e.target.tagName === 'INPUT' && e.target.type === 'number' && document.activeElement === e.target) e.target.blur(); }}
              onDragStart={e => { const t = e.target.tagName; if (t === 'INPUT' || t === 'TEXTAREA') e.preventDefault(); }}
              onDrop={e => { const t = e.target.tagName; if (t === 'INPUT' || t === 'TEXTAREA') e.preventDefault(); }}
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: isDrilling ? 600 : 1100 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {/* Flag filter + the # sort share this cell. The dots show only the
                      colours actually used on this bid, so the header stays quiet until
                      someone flags something. Clicking a dot narrows the list to that
                      colour; clicking it again clears. Filtering only — the bid's own
                      order is never touched and nothing is saved. */}
                  <th style={th({ textAlign: 'left', userSelect: 'none' })}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {(() => {
                        const used = ITEM_FLAGS.filter(f => items.some(i => i.flag === f.key));
                        if (!used.length) return null;
                        return (
                          <span style={{ display: 'inline-flex', gap: 3, marginRight: 2 }}>
                            {used.map(f => {
                              const on = flagFilter === f.key;
                              const count = items.filter(i => i.flag === f.key).length;
                              return (
                                <button key={f.key} onClick={e => { e.stopPropagation(); setFlagFilter(on ? null : f.key); }}
                                  title={on ? 'Show all lines again' : 'Show only these (' + count + ')'}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 1 }}>
                                  <span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: f.color,
                                    outline: on ? '2px solid ' + f.color : 'none', outlineOffset: 1, opacity: (on || !flagFilter) ? 1 : 0.35 }} />
                                </button>
                              );
                            })}
                          </span>
                        );
                      })()}
                      <span onClick={sortItemsByNumber} title="Sort by bid item # (click again to reverse)" style={{ cursor: 'pointer' }}>
                        # <span style={{ color: itemSortDir ? '#2563eb' : '#c7cdd6' }}>{itemSortDir === 'asc' ? '↑' : itemSortDir === 'desc' ? '↓' : '⇅'}</span>
                      </span>
                    </div>
                  </th>
                  {!isDrilling && (hc.has('pay_item') ? thS('pay_item') : thC('pay_item', 'Pay Item', { textAlign: 'left' }))}
                  <th style={th({ textAlign: 'left', minWidth: 200 })}>Description</th>
                  <th style={th({ textAlign: 'right' })}>Qty</th>
                  <th style={th({ textAlign: 'left' })}>Unit</th>
                  {!isDrilling && (hc.has('material') ? thS('material') : thC('material', 'Material $', { textAlign: 'right' }))}
                  {!isDrilling && (hc.has('labor_hrs') ? thS('labor_hrs') : thC('labor_hrs', 'Labor Hrs', { textAlign: 'right' }))}
                  {!isDrilling && (hc.has('dje_std') ? thS('dje_std') : thC('dje_std', 'Sub $', { textAlign: 'right' }))}
                  {!isDrilling && (hc.has('oh_mat') ? thS('oh_mat') : thC('oh_mat', 'OH Mat %', { textAlign: 'right' }))}
                  {!isDrilling && (hc.has('profit') ? thS('profit') : thC('profit', 'Profit %', { textAlign: 'right' }))}
                  {!isDrilling && (hc.has('rounding') ? thS('rounding') : thC('rounding', 'Round', { textAlign: 'center' }))}
                  {isDrilling && <th style={th({ textAlign: 'right' })}>Price/Unit $</th>}
                  <th style={th({ textAlign: 'right' })}>Unit Price</th>
                  <th style={th({ textAlign: 'right' })}>Item Total</th>
                  {!isLocked && <th style={{ padding: '8px 6px' }} />}
                </tr>
              </thead>
              <tbody onClick={() => partsSuggestKey && setPartsSuggestKey(null)}>
                {orderedItems.filter(i => !flagFilter || i.flag === flagFilter).map((item, idx, shown) => {
                  // Group headings and subtotals ride along with the rows rather than
                  // being a separate pass, so a filtered view can't strand a subtotal
                  // under items it doesn't cover.
                  const gid = item.alternate_id || null;
                  const prevGid = idx > 0 ? (shown[idx - 1].alternate_id || null) : undefined;
                  const nextGid = idx < shown.length - 1 ? (shown[idx + 1].alternate_id || null) : '__end__';
                  const grp = gid ? altById[gid] : null;
                  const startsGroup = hasAlternates && gid !== prevGid;
                  const endsGroup = hasAlternates && nextGid !== gid;
                  const groupTotal = gid ? (altTotals.perGroup[gid] || 0) : altTotals.ungrouped;
                  const isDeduct = grp?.kind === 'deductive';
                  const headerRow = startsGroup && (
                    <tr key={`h_${gid || 'base'}`}
                      onDragOver={e => { if (!isLocked && dragItemKey.current) { e.preventDefault(); if (dragOverKey !== `hdr_${gid || 'base'}`) setDragOverKey(`hdr_${gid || 'base'}`); } }}
                      onDragLeave={() => { if (dragOverKey === `hdr_${gid || 'base'}`) setDragOverKey(null); }}
                      onDrop={e => { e.preventDefault(); dropItemOnAlternate(dragItemKey.current, gid); dragItemKey.current = null; setDragOverKey(null); }}
                      style={{ background: dragOverKey === `hdr_${gid || 'base'}` ? '#fde68a' : (grp ? (isDeduct ? '#fef2f2' : '#f0fdfa') : '#f8fafc') }}>
                      <td colSpan={99} style={{ padding: '9px 10px', borderTop: dragOverKey === `hdr_${gid || 'base'}` ? '2px solid #d97706' : '2px solid #cbd5e1' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          {!grp ? (
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>Base Bid</span>
                          ) : (<>
                            <input value={grp.title} disabled={isLocked}
                              onChange={e => setAlternates(prev => prev.map(a => a.id === grp.id ? { ...a, title: e.target.value } : a))}
                              onBlur={e => patchAlternate(grp.id, { title: e.target.value })}
                              style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a', border: '1px solid transparent', background: 'transparent', borderRadius: 5, padding: '3px 6px', minWidth: 240 }} />
                            <button type="button" disabled={isLocked}
                              onClick={() => patchAlternate(grp.id, { kind: isDeduct ? 'additive' : 'deductive' })}
                              title={isDeduct ? 'Taking this alternate reduces the price by its subtotal' : 'Taking this alternate adds its subtotal to the price'}
                              style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '3px 11px', cursor: isLocked ? 'default' : 'pointer', border: 'none', background: isDeduct ? '#dc2626' : '#0f766e', color: '#fff' }}>
                              {isDeduct ? '− DEDUCTIVE' : '+ ADDITIVE'}
                            </button>
                            {alternatesDecidable && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: grp.accepted ? '#15803d' : '#64748b', cursor: isLocked ? 'default' : 'pointer' }}>
                                <input type="checkbox" checked={!!grp.accepted} disabled={isLocked}
                                  onChange={e => patchAlternate(grp.id, { accepted: e.target.checked })} />
                                Accepted
                              </label>
                            )}
                            {!isLocked && (
                              <button type="button" onClick={() => { if (window.confirm(`Remove the group "${grp.title}"?\n\nIts line items are NOT deleted — they move back into the base bid.`)) removeAlternate(grp.id); }}
                                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 15, cursor: 'pointer', lineHeight: 1 }} title="Remove this group (keeps the line items)">×</button>
                            )}
                            {!isLocked && (<>
                              {/* Two ways in, because they're different jobs: type a
                                  new line here, or pull existing ones across. */}
                              <button type="button" onClick={() => addItemToAlternate(grp.id)}
                                title="Add a new blank line item inside this alternate"
                                style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', border: 'none', background: '#0f766e', color: '#fff' }}>
                                ＋ Add line
                              </button>
                              <button type="button" onClick={() => setAltPickModal({ altId: grp.id, keys: items.filter(i => i.alternate_id === grp.id).map(i => i._key) })}
                                title="Move existing line items into this alternate"
                                style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', border: '1px solid #0f766e', background: '#fff', color: '#0f766e' }}>
                                Move lines in
                              </button>
                            </>)}
                            {isDeduct && <span style={{ fontSize: 11, color: '#991b1b' }}>subtracted from the total if the owner takes it</span>}
                          </>)}
                        </div>
                      </td>
                    </tr>
                  );
                  const subtotalRow = endsGroup && (
                    <tr key={`s_${gid || 'base'}`} style={{ background: '#f8fafc' }}>
                      <td colSpan={98} style={{ padding: '7px 10px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                        {grp ? `${grp.title} subtotal` : 'Base bid line items'}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', color: isDeduct ? '#dc2626' : '#0f172a' }}>
                        {isDeduct ? `(${fmt$(groupTotal)})` : fmt$(groupTotal)}
                      </td>
                      {!isLocked && <td />}
                    </tr>
                  );
                  // An empty group: heading, and a line telling you how to fill it.
                  if (item._placeholder) {
                    return (
                      <React.Fragment key={`g_${item._key}`}>
                        {headerRow}
                        <tr>
                          <td colSpan={99} style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8', fontStyle: 'italic', background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                            Nothing in this alternate yet — drag a line onto the heading above, or use <strong style={{ fontStyle: 'normal', color: '#0f766e' }}>＋ Add line</strong> and <strong style={{ fontStyle: 'normal', color: '#0f766e' }}>Move lines in</strong>.
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  }
                  return (<React.Fragment key={`g_${item._key}`}>{headerRow}{(() => {
                  const calc = calcItem(item, bid);
                  const isActive = activeItemKey === item._key;
                  const isAuto = item.auto_source === 'hole_schedule';
                  const subs = Array.isArray(item.sub_items) ? item.sub_items : [];
                  const subsOpen = expandedSubs.has(item._key);
                  const subTotal = subs.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                  const colCount = isDrilling ? (isLocked ? 6 : 7) : (isLocked ? 13 : 14);
                  // A line with sub-items leaves its cost columns blank (Ryan, Oct 27 2026 — adding up
                  // the subs' per-each numbers read wrong once the quantities extended). The assembly
                  // total row under the subs shows the extended material, hours and sub $ instead.
                  const extSub = field => subs.reduce((s, x) => s + (parseFloat(x[field]) || 0) * pricedQty(x), 0);
                  const totCell = v => <span style={{ fontSize: 12, fontWeight: 800, color: '#4c1d95' }}>{v ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'}</span>;
                  return (
                    <React.Fragment key={item._key}>
                      <tr
                        onClick={() => !isAuto && setActiveItemKey(isActive ? null : item._key)}
                        onDragOver={e => { if (!isAuto && !isLocked && dragItemKey.current && dragItemKey.current !== item._key) { e.preventDefault(); if (dragOverKey !== item._key) setDragOverKey(item._key); } }}
                        onDragLeave={() => { if (dragOverKey === item._key) setDragOverKey(null); }}
                        onDrop={e => { e.preventDefault(); reorderItems(dragItemKey.current, item._key); dragItemKey.current = null; setDragOverKey(null); }}
                        style={{ borderBottom: '1px solid #f3f4f6', borderTop: dragOverKey === item._key ? '2px solid #e87722' : undefined, cursor: isAuto ? 'default' : 'pointer', background: isAuto ? '#faf5ff' : isActive ? '#fef9f0' : (idx % 2 === 0 ? '#fff' : '#fafafa') }}
                        title={isAuto ? 'Auto-generated from Hole Schedule — edit in Scope section' : ''}
                      >
                        <td style={{ padding: '6px 6px 6px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {/* Colour flag — leftmost, before the drag handle. Click cycles
                                through the palette and back to unset, so it's one tap to
                                mark and one more to clear without opening a menu. */}
                            {!isAuto && (
                              <button onClick={e => { e.stopPropagation(); cycleFlag(item._key); }}
                                title={item.flag ? `Flagged ${item.flag} — click for the next colour` : 'Flag this line'}
                                style={{ background: 'none', border: 'none', padding: '1px 2px', cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>
                                <span style={{ display: 'block', width: 11, height: 11, borderRadius: '50%',
                                  background: flagColor(item.flag) || 'transparent',
                                  border: `1.5px solid ${flagColor(item.flag) || '#d1d5db'}` }} />
                              </button>
                            )}
                            {/* Comment pad, next to the flag. Grey outline when empty,
                                filled amber with a count once someone has written
                                something, so a line carrying a note is obvious at a glance. */}
                            {!isAuto && (() => {
                              const cs = itemComments(item);
                              const has = cs.length > 0;
                              return (
                                <button onClick={e => { e.stopPropagation(); setCommentDraft(''); setCommentsFor(commentsFor === item._key ? null : item._key); }}
                                  title={has ? `${cs.length} comment${cs.length === 1 ? '' : 's'} — ${cs[cs.length - 1].author}: ${cs[cs.length - 1].text.slice(0, 60)}` : 'Add a comment'}
                                  style={{ background: 'none', border: 'none', padding: '1px 2px', cursor: 'pointer', lineHeight: 1, flexShrink: 0, position: 'relative' }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill={has ? '#f59e0b' : 'none'} stroke={has ? '#b45309' : '#d1d5db'} strokeWidth="2.2">
                                    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.8-.8L3 21l1.9-5.1A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                  {cs.length > 1 && <span style={{ position: 'absolute', top: -3, right: -3, fontSize: 8, fontWeight: 800, color: '#b45309' }}>{cs.length}</span>}
                                </button>
                              );
                            })()}
                            {!isAuto && !isLocked && (
                              <span draggable
                                onDragStart={e => { dragItemKey.current = item._key; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('application/x-bid-item', item._key); }}
                                onDragEnd={() => { dragItemKey.current = null; setDragOverKey(null); }}
                                onClick={e => e.stopPropagation()}
                                title={hasAlternates ? 'Drag to reorder — or drop on another heading to move it into that group' : 'Drag to reorder this line item'}
                                style={{ cursor: 'grab', color: '#c4b5c9', fontSize: 13, lineHeight: 1, padding: '0 1px', userSelect: 'none', flexShrink: 0 }}>⠿</span>
                            )}
                            {!isAuto && (
                              <button onClick={e => { e.stopPropagation(); toggleSubs(item._key); }}
                                title={subsOpen ? 'Collapse breakdown' : subs.length ? `${subs.length} sub-item${subs.length !== 1 ? 's' : ''} · ${fmt$(subTotal)}` : 'Add cost breakdown'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 2px', color: subs.length ? '#6366f1' : '#d1d5db', fontSize: 13, lineHeight: 1, flexShrink: 0, transition: 'transform 0.15s', transform: subsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</button>
                            )}
                            <input value={item.item_number || ''} onChange={e => { e.stopPropagation(); updateItem(item._key, 'item_number', e.target.value); }}
                              onClick={e => e.stopPropagation()} style={{ ...inputStyle(isLocked || isAuto), width: 42 }} disabled={isLocked || isAuto} />
                          </div>
                          {!subsOpen && subs.length > 0 && (
                            <div style={{ fontSize: 9, color: '#6366f1', marginTop: 2, marginLeft: 18, whiteSpace: 'nowrap', fontWeight: 600 }}>
                              {subs.length} sub · {fmt$(subTotal)}
                            </div>
                          )}
                        </td>
                        {!isDrilling && (hc.has('pay_item') ? tdS('pay_item') : (
                          <td style={{ padding: '6px 10px' }}>
                            <input value={item.pay_item_number || ''} onChange={e => { e.stopPropagation(); updateItem(item._key, 'pay_item_number', e.target.value); }}
                              onClick={e => e.stopPropagation()} style={{ ...inputStyle(isLocked), width: 90 }} disabled={isLocked} />
                          </td>
                        ))}
                        <td style={{ padding: '6px 10px', position: 'relative' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input value={item.description || ''} onChange={e => { e.stopPropagation(); updateItem(item._key, 'description', e.target.value); }}
                              onClick={e => e.stopPropagation()} style={{ ...inputStyle(isLocked || isAuto), minWidth: 160, flex: 1 }} disabled={isLocked || isAuto} />
                            {!isLocked && !isAuto && (
                              <button
                                title="Look up from Parts Directory"
                                onClick={e => { e.stopPropagation(); partsSuggestKey === item._key ? setPartsSuggestKey(null) : openPartsSuggest(item._key, item.description || ''); }}
                                style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 10, border: 'none', background: partsSuggestKey === item._key ? 'var(--orange)' : '#e0e7ff', color: partsSuggestKey === item._key ? '#fff' : '#4f46e5', fontSize: 14, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: 0 }}>
                                +
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <MoneyInput value={item.quantity ?? 1} disabled={isLocked || isAuto} baseStyle={inputStyle(isLocked || isAuto)}
                            minDecimals={0} maxDecimals={4} minWidth={60}
                            onCommit={v => updateItem(item._key, 'quantity', v)} />
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <UnitSelect value={item.unit} onChange={v => updateItem(item._key, 'unit', v)}
                            style={{ ...inputStyle(isLocked || isAuto), width: 78 }} disabled={isLocked || isAuto} />
                        </td>
                        {!isDrilling && (hc.has('material') ? tdS('material') : (
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {subs.length ? null : (
                            <MoneyInput value={item.material_cost} disabled={isLocked} baseStyle={inputStyle(isLocked)}
                              onCommit={v => updateItem(item._key, 'material_cost', v)} />
                            )}
                          </td>
                        ))}
                        {!isDrilling && (hc.has('labor_hrs') ? tdS('labor_hrs') : (
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {subs.length ? null : (
                            <input type="number" step="0.5" value={item.labor_hours || ''} onChange={e => { e.stopPropagation(); updateItem(item._key, 'labor_hours', parseFloat(e.target.value) || 0); }}
                              onClick={e => e.stopPropagation()} style={{ ...inputStyle(isLocked), width: 70, textAlign: 'right' }} disabled={isLocked} />
                            )}
                          </td>
                        ))}
                        {!isDrilling && (hc.has('dje_std') ? tdS('dje_std') : (
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {subs.length ? null : (
                            <input type="number" value={item.dje_standard || ''} onChange={e => { e.stopPropagation(); updateItem(item._key, 'dje_standard', parseFloat(e.target.value) || 0); }}
                              onClick={e => e.stopPropagation()} style={{ ...inputStyle(isLocked), width: 80, textAlign: 'right' }} disabled={isLocked} />
                            )}
                          </td>
                        ))}
                        {!isDrilling && (hc.has('oh_mat') ? tdS('oh_mat') : (
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {subs.length ? null : pctInput(item, 'overhead_material_pct', bid.overhead_material_pct + '%')}
                          </td>
                        ))}
                        {!isDrilling && (hc.has('profit') ? tdS('profit') : (
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {subs.length ? null : pctInput(item, 'profit_pct', bid.profit_pct + '%')}
                          </td>
                        ))}
                        {!isDrilling && (hc.has('rounding') ? tdS('rounding') : (
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                            <select value={isRoundNone(item.rounding) ? ROUND_NONE : item.rounding} disabled={isLocked}
                              onChange={e => { e.stopPropagation(); updateItem(item._key, 'rounding', parseInt(e.target.value)); }}
                              onClick={e => e.stopPropagation()}
                              style={{ ...inputStyle(isLocked), width: 80, fontSize: 11, padding: '3px 4px', textAlign: 'center' }}>
                              <option value={ROUND_NONE}>None</option>
                              <option value={-2}>0.01</option>
                              <option value={-1}>0.1</option>
                              <option value={0}>1</option>
                              <option value={1}>10</option>
                              <option value={2}>100</option>
                              <option value={3}>1,000</option>
                              <option value={4}>10,000</option>
                            </select>
                          </td>
                        ))}
                        {isDrilling && (
                          <td style={{ padding: '6px 10px' }}>
                            <input type="number" value={item.dje_standard || ''}
                              onChange={e => { e.stopPropagation(); updateItem(item._key, 'dje_standard', parseFloat(e.target.value) || 0); }}
                              onClick={e => e.stopPropagation()}
                              style={{ ...inputStyle(isLocked), width: 100, textAlign: 'right', background: isAuto ? '#f3f0ff' : '#fff' }}
                              disabled={isLocked}
                              placeholder="0.00" />
                          </td>
                        )}
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--orange)', whiteSpace: 'nowrap' }}>
                          {fmt$(subs.length ? itemEffectiveUnitPrice(item, bid) : calc.unitPrice)}
                          {subs.length > 0 && <div style={{ fontSize: 9, color: '#a78bfa', fontWeight: 400 }}>({subs.length} sub)</div>}
                          {priceOverrideOf(item) != null && <div title="This unit price was typed in on the job's Line Items tile — cost and mark-ups no longer move it" style={{ fontSize: 9, color: '#b45309', fontWeight: 700 }}>set on the job</div>}
                        </td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {fmt$(subs.length ? itemEffectiveTotal(item, bid) : calc.itemTotal)}
                        </td>
                        {!isLocked && (
                          <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                            {isAuto ? (
                              <span title="Edit in Scope section" style={{ fontSize: 11, color: '#a78bfa' }}>auto</span>
                            ) : (
                              <button onClick={e => { e.stopPropagation(); removeItem(item._key); }}
                                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
                            )}
                          </td>
                        )}
                      </tr>
                      {/* Comment thread, opened from the pad. Sits under its own line so
                          it reads as belonging to it, and spans the table so long
                          comments have room to breathe. */}
                      {commentsFor === item._key && (
                        <tr>
                          <td colSpan={40} style={{ padding: 0, background: '#fffbeb', borderBottom: '2px solid #fde68a' }}>
                            <div style={{ padding: '10px 14px 12px 34px', maxWidth: 780 }}>
                              {itemComments(item).length === 0 && (
                                <div style={{ fontSize: 12, color: '#a16207', marginBottom: 8 }}>No comments on this line yet.</div>
                              )}
                              {itemComments(item).map(c => (
                                <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e' }}>
                                      {c.author}
                                      <span style={{ fontWeight: 500, color: '#b45309', marginLeft: 6 }}>
                                        {c.at ? new Date(c.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                      </span>
                                    </div>
                                    <div style={{ fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', marginTop: 1 }}>{c.text}</div>
                                  </div>
                                  {/* Your own comment, or an owner/Executive tidying up. */}
                                  {(c.authorId === me?.id || ['admin', 'Admin', 'Executive'].includes(currentUserRole)) && (
                                    <button onClick={e => { e.stopPropagation(); deleteComment(item._key, c.id); }}
                                      title="Delete this comment"
                                      style={{ background: 'none', border: 'none', color: '#d97706', fontSize: 14, cursor: 'pointer', lineHeight: 1, padding: 2 }}>×</button>
                                  )}
                                </div>
                              ))}
                              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                <textarea value={commentDraft} autoFocus rows={2}
                                  onChange={e => setCommentDraft(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addComment(item._key); } }}
                                  placeholder={`Comment as ${myName}…`}
                                  style={{ flex: 1, border: '1.5px solid #fcd34d', borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', background: '#fff' }} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  <button onClick={e => { e.stopPropagation(); addComment(item._key); }} disabled={!commentDraft.trim()}
                                    style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: commentDraft.trim() ? '#b45309' : '#e5e7eb', color: '#fff', fontSize: 12, fontWeight: 800, cursor: commentDraft.trim() ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>Add</button>
                                  <button onClick={e => { e.stopPropagation(); setCommentsFor(null); setCommentDraft(''); }}
                                    style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Close</button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Sub-item rows — aligned with parent columns */}
                      {subsOpen && !isAuto && subs.map(sub => {
                        const subCalc = calcItem(sub, bid);
                        const subInput = (field, opts = {}) => (
                          <input type={opts.type || 'text'} step={opts.step}
                            value={opts.type === 'number' ? (sub[field] || '') : (sub[field] || '')}
                            placeholder={opts.placeholder || ''}
                            disabled={isLocked}
                            onChange={e => updateSubItem(item._key, sub.id, field, opts.type === 'number' ? (e.target.value === '' ? (opts.nullable ? null : 0) : parseFloat(e.target.value)) : e.target.value)}
                            style={{ ...inputStyle(isLocked), width: opts.width || 80, textAlign: opts.align || 'left' }} />
                        );
                        return (
                          <tr key={sub.id} style={{ borderBottom: '1px solid #ede9fe', background: '#faf5ff', borderLeft: '3px solid #a78bfa' }}>
                            {/* # column: remove-sub-item button, left-aligned so it's always reachable */}
                            <td style={{ padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {!isLocked ? (
                                <button onClick={() => updateSubItems(item._key, subs.filter(s => s.id !== sub.id))}
                                  title="Remove this sub-item"
                                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', fontWeight: 700 }}>×</button>
                              ) : (
                                <span style={{ color: '#c4b5fd', fontSize: 14, userSelect: 'none' }}>└</span>
                              )}
                            </td>
                            {/* Pay Item: empty */}
                            {!isDrilling && (hc.has('pay_item') ? tdS('pay_item') : <td />)}
                            <td style={{ padding: '4px 6px', position: 'relative' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input value={sub.description || ''} placeholder="Sub-item description…" disabled={isLocked}
                                  onChange={e => updateSubItem(item._key, sub.id, 'description', e.target.value)}
                                  style={{ ...inputStyle(isLocked), width: 180 }} />
                                {!isLocked && (
                                  <button title="Look up from Parts Directory"
                                    onClick={e => { e.stopPropagation(); partsSuggestKey === 'sub:' + sub.id ? setPartsSuggestKey(null) : openSubPartsSuggest(sub.id, sub.description || ''); }}
                                    style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 10, border: 'none', background: partsSuggestKey === 'sub:' + sub.id ? 'var(--orange)' : '#e0e7ff', color: partsSuggestKey === 'sub:' + sub.id ? '#fff' : '#4f46e5', fontSize: 14, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: 0 }}>
                                    +
                                  </button>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '4px 6px' }}><MoneyInput value={sub.quantity ?? 1} disabled={isLocked} baseStyle={inputStyle(isLocked)} minDecimals={0} maxDecimals={4} minWidth={60} onCommit={v => updateSubItem(item._key, sub.id, 'quantity', v)} /></td>
                            <td style={{ padding: '4px 6px' }}><UnitSelect value={sub.unit} onChange={v => updateSubItem(item._key, sub.id, 'unit', v)} disabled={isLocked} style={{ ...inputStyle(isLocked), width: 78 }} /></td>
                            {!isDrilling && (hc.has('material') ? tdS('material') : <td style={{ padding: '4px 6px' }}><MoneyInput value={sub.material_cost} disabled={isLocked} baseStyle={inputStyle(isLocked)} onCommit={v => updateSubItem(item._key, sub.id, 'material_cost', v)} /></td>)}
                            {!isDrilling && (hc.has('labor_hrs') ? tdS('labor_hrs') : <td style={{ padding: '4px 6px' }}>{subInput('labor_hours', { type: 'number', step: '0.5', width: 70, align: 'right' })}</td>)}
                            {!isDrilling && (hc.has('dje_std') ? tdS('dje_std') : <td style={{ padding: '4px 6px' }}>{subInput('dje_standard', { type: 'number', width: 80, align: 'right' })}</td>)}
                            {!isDrilling && (hc.has('oh_mat') ? tdS('oh_mat') : <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <input type="number" step="1" value={sub.overhead_material_pct ?? ''} placeholder={bid.overhead_material_pct + '%'} disabled={isLocked}
                                onChange={e => updateSubItem(item._key, sub.id, 'overhead_material_pct', e.target.value === '' ? null : parseFloat(e.target.value))}
                                style={{ ...inputStyle(isLocked), width: 60 }} />
                            </td>)}
                            {!isDrilling && (hc.has('profit') ? tdS('profit') : <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <input type="number" step="1" value={sub.profit_pct ?? ''} placeholder={bid.profit_pct + '%'} disabled={isLocked}
                                onChange={e => updateSubItem(item._key, sub.id, 'profit_pct', e.target.value === '' ? null : parseFloat(e.target.value))}
                                style={{ ...inputStyle(isLocked), width: 60 }} />
                            </td>)}
                            {!isDrilling && (hc.has('rounding') ? tdS('rounding') : (
                              <td title="Rounding is set on the main line, not sub-items" style={{ padding: '4px 6px', textAlign: 'center', color: '#c4b5fd', fontSize: 12 }}>—</td>
                            ))}
                            {isDrilling && <td style={{ padding: '4px 6px' }}>{subInput('dje_standard', { type: 'number', width: 100, align: 'right' })}</td>}
                            <td style={{ padding: '4px 6px', textAlign: 'right', color: '#7c3aed', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 11 }}>{fmt$(subCalc.selling)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', fontSize: 11 }}>{fmt$(subCalc.selling * pricedQty(sub))}</td>
                            {!isLocked && (
                              <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                                <button onClick={() => updateSubItems(item._key, subs.filter(s => s.id !== sub.id))}
                                  style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {/* Assembly total — every sub extended by its quantity, lined up under its column. */}
                      {subsOpen && !isAuto && subs.length > 0 && (
                        <tr style={{ background: '#ede9fe', borderBottom: isLocked ? '2px solid #ddd6fe' : '1px solid #ddd6fe', borderLeft: '3px solid #a78bfa' }}>
                          <td />
                          {!isDrilling && (hc.has('pay_item') ? tdS('pay_item') : <td />)}
                          <td style={{ padding: '6px 6px', fontSize: 11.5, fontWeight: 800, color: '#4c1d95', whiteSpace: 'nowrap' }}
                            title="Each sub-item times its quantity. The main line's Round shapes the total into the quote unit price (hidden from the owner).">
                            Assembly total · {subs.length} sub-item{subs.length === 1 ? '' : 's'}
                          </td>
                          <td /><td />
                          {!isDrilling && (hc.has('material') ? tdS('material') : <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{totCell(extSub('material_cost'))}</td>)}
                          {!isDrilling && (hc.has('labor_hrs') ? tdS('labor_hrs') : <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{totCell(extSub('labor_hours'))}{extSub('labor_hours') ? <span style={{ fontSize: 10, fontWeight: 600, color: '#7c3aed' }}> hrs</span> : null}</td>)}
                          {!isDrilling && (hc.has('dje_std') ? tdS('dje_std') : <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{totCell(extSub('dje_standard'))}</td>)}
                          {!isDrilling && (hc.has('oh_mat') ? tdS('oh_mat') : <td />)}
                          {!isDrilling && (hc.has('profit') ? tdS('profit') : <td />)}
                          {!isDrilling && (hc.has('rounding') ? tdS('rounding') : <td />)}
                          {isDrilling && <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{totCell(extSub('dje_standard'))}</td>}
                          <td />
                          <td style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap', fontSize: 11.5, color: '#4c1d95' }}>{fmt$(subs.reduce((s, sub) => s + calcItem(sub, bid).selling * pricedQty(sub), 0))}</td>
                          {!isLocked && <td />}
                        </tr>
                      )}
                      {/* Add sub-item row */}
                      {subsOpen && !isAuto && !isLocked && (
                        <tr style={{ background: '#faf5ff', borderBottom: '2px solid #ddd6fe', borderLeft: '3px solid #a78bfa' }}>
                          <td colSpan={colCount} style={{ padding: '4px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <button onClick={() => updateSubItems(item._key, [...subs, newSubItem()])}
                                style={{ background: 'none', border: '1px dashed #a78bfa', borderRadius: 5, color: '#6366f1', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '3px 12px' }}>
                                + Add sub-item
                              </button>
                              {subs.length > 0 && (
                                <span style={{ fontSize: 11, color: '#9ca3af' }}>The main line’s Round shapes the assembly total into the quote unit price (hidden from owner)</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })()}{subtotalRow}</React.Fragment>);
                })}
                {/* The bid read as the owner reads it: the base, then each alternate
                    they can take, then what the contract becomes. A deductive group is
                    already inside the base figure, which is why accepting it subtracts. */}
                {hasAlternates && (
                  <>
                    <tr style={{ background: '#f1f5f9' }}>
                      <td colSpan={98} style={{ padding: '9px 10px', textAlign: 'right', fontSize: 12.5, fontWeight: 800, color: '#0f172a', borderTop: '2px solid #cbd5e1' }}>
                        BASE BID
                      </td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap', borderTop: '2px solid #cbd5e1' }}>{fmt$(altTotals.base)}</td>
                      {!isLocked && <td style={{ borderTop: '2px solid #cbd5e1' }} />}
                    </tr>
                    {alternates.map(a => {
                      const v = altTotals.perGroup[a.id] || 0;
                      const signed = a.kind === 'deductive' ? -v : v;
                      // Until the bid is out these are options on offer, not things
                      // anyone has turned down — so don't grey them or label them
                      // "not accepted".
                      const decided = alternatesDecidable && a.accepted;
                      const pending = alternatesDecidable && !a.accepted;
                      return (
                        <tr key={`t_${a.id}`} style={{ background: decided ? '#f0fdf4' : '#fff' }}>
                          <td colSpan={98} style={{ padding: '6px 10px', textAlign: 'right', fontSize: 12, color: decided ? '#15803d' : (pending ? '#94a3b8' : '#475569'), fontWeight: decided ? 700 : 500 }}>
                            {decided ? '✓ ' : ''}{a.title} · {a.kind === 'deductive' ? 'deduct' : 'add'}
                            {pending && <span style={{ fontStyle: 'italic' }}> — not accepted</span>}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', color: decided ? (signed < 0 ? '#dc2626' : '#15803d') : (pending ? '#cbd5e1' : (signed < 0 ? '#dc2626' : '#475569')) }}>
                            {signed < 0 ? `(${fmt$(-signed)})` : `+${fmt$(signed)}`}
                          </td>
                          {!isLocked && <td />}
                        </tr>
                      );
                    })}
                  </>
                )}
                {/* Grand total row */}
                <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                  <td colSpan={isDrilling ? (isLocked ? 5 : 5) : (isLocked ? 12 : 12)} style={{ padding: '10px 10px', fontWeight: 700, fontSize: 13, textAlign: 'right', color: '#374151' }}>
                    {/* Say so when a flag filter is on. The total is always the WHOLE bid —
                        without this it reads as if the two lines on screen come to $10M. */}
                    {flagFilter && (
                      <span style={{ fontWeight: 600, color: '#6b7280', fontSize: 12, marginRight: 10 }}>
                        showing {items.filter(i => i.flag === flagFilter).length} of {items.length} — total below is the whole bid
                      </span>
                    )}
                    {!hasAlternates ? 'Grand Total' : (alternatesDecidable ? 'Base bid + accepted alternates' : 'Base bid — the number you submit')}
                  </td>
                  <td style={{ padding: '10px 10px', fontWeight: 800, fontSize: 15, textAlign: 'right', color: 'var(--orange)', whiteSpace: 'nowrap' }}>
                    {fmt$(hasAlternates ? altTotals.contract : grandTotal)}
                  </td>
                  {!isLocked && <td />}
                </tr>
              </tbody>
            </table>
            </>);
            })()}
          </div>
        ))}
      </div>
      )}

      {/* ── Vendor Quotes ──────────────────────────────────────────────────────── */}
      {(projectId || openSection === 'vendor_quotes') && (
      <VendorQuotesSection
        bid={bid}
        isLocked={projectId ? false : isLocked}
        show={projectId ? showVendorQuotes : true}
        readFirst={!projectId}
        onToggle={() => { if (projectId) setShowVendorQuotes(v => !v); else setOpenSection(null); }}
        attachments={attachments}
        onQuotesChange={newItems => updateBid('vendor_quotes', JSON.stringify(newItems))}
        onFileUpload={async (file, quoteId, currentItems) => {
          const fd = new FormData();
          fd.append('file', file);
          const res = await fetch(`${API_BASE}/bids/${id}/attachments`, {
            method: 'POST',
            headers: { 'x-worker-token': getToken() },
            body: fd,
          });
          if (res.ok) {
            const att = await res.json();
            setAttachments(a => [...a, att]);
            const updated = currentItems.map(it => ({
              ...it, quotes: it.quotes.map(q =>
                q.id === quoteId ? { ...q, files: [...(q.files || []), att.id] } : q
              )
            }));
            updateBid('vendor_quotes', JSON.stringify(updated));
            return att;
          }
        }}
        onFileRemove={(attId, quoteId, currentItems) => {
          const updated = currentItems.map(it => ({
            ...it, quotes: it.quotes.map(q =>
              q.id === quoteId ? { ...q, files: (q.files || []).filter(f => f !== attId) } : q
            )
          }));
          updateBid('vendor_quotes', JSON.stringify(updated));
        }}
        getToken={getToken}
        API_BASE={API_BASE}
        bidId={id}
        projectId={projectId}
        onPOCreated={po => setProjectPOs(p => [...p, po])}
        showToast={showToast}
      />
      )}

      {/* ── Documents ledger (bid mode) — every file on this bid, printed or uploaded ── */}
      {!projectId && openSection === 'documents' && (
        <div className="admin-card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          <div onClick={() => setOpenSection(null)} style={{ background: '#0f766e', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>Documents{docCount ? ` (${docCount} file${docCount !== 1 ? 's' : ''})` : ''}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isAdminZone && (
                <button onClick={e => { e.stopPropagation(); setDocsEditing(v => !v); }}
                  style={{ padding: '4px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.5)', background: docsEditing ? '#fff' : 'transparent', color: docsEditing ? '#0f766e' : '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {docsEditing ? 'Done' : '✎ Edit'}
                </button>
              )}
              <span style={{ fontSize: 11, color: '#fff' }}>▲</span>
            </div>
          </div>
          <div style={{ padding: 20 }}>
            <DocumentsPillow bidId={id} canEdit={isAdminZone} manage={docsEditing} />
          </div>
        </div>
      )}

      {/* ── Fieldwire Sheets / Tasks (bid mode) — the bid's own Fieldwire project ── */}
      {!projectId && (openSection === 'fw_sheets' || openSection === 'fw_tasks') && (
        <div className="admin-card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          <div onClick={() => setOpenSection(null)} style={{ background: openSection === 'fw_sheets' ? '#0f766e' : '#1e3a5f', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>{openSection === 'fw_sheets' ? 'Fieldwire Sheets' : 'Fieldwire Tasks'}{fwOv?.link?.name ? ` · ${fwOv.link.name}` : ''}</span>
            <span style={{ fontSize: 11, color: '#fff' }}>▲</span>
          </div>
          <div style={{ padding: 20 }}>
            <BidFieldwirePillows bidId={id} isAdminZone={isAdminZone} which={openSection === 'fw_sheets' ? 'sheets' : 'tasks'} />
          </div>
        </div>
      )}

      {/* Quick update composer from the header button (bid mode). */}
      {!projectId && bidUpdateOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setBidUpdateOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 22, width: 520, maxWidth: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 4 }}>💬 Update on {bid.job_name || 'this bid'}</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
              {pmNameFor(bid.bid_pm_id) !== 'Unassigned' ? `${pmNameFor(bid.bid_pm_id)} gets a notification.` : 'The bid’s PM is notified once one is assigned.'} It stays with the bid and reads as a Daily Update on the job after award.
            </div>
            <textarea autoFocus value={bidUpdateText} onChange={e => setBidUpdateText(e.target.value)} rows={4}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postBidUpdateFromHeader(); if (e.key === 'Escape') setBidUpdateOpen(false); }}
              placeholder='"Do we have a price on the pull boxes?" · "Sierra Nevada wants a number by Friday" · "Bid date moved to the 2nd"'
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 9, padding: '10px 12px', fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <button onClick={postBidUpdateFromHeader} disabled={!bidUpdateText.trim() || postingBidUpdate}
                style={{ padding: '9px 20px', borderRadius: 9, border: 'none', background: bidUpdateText.trim() ? '#b45309' : '#cbd5e1', color: '#fff', fontSize: 14, fontWeight: 800, cursor: bidUpdateText.trim() ? 'pointer' : 'default' }}>
                {postingBidUpdate ? 'Posting…' : 'Post Update'}
              </button>
              <button onClick={() => setBidUpdateOpen(false)} style={{ padding: '9px 14px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>Cancel</button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: bidUpdateBanner ? '#b91c1c' : '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={bidUpdateBanner} onChange={e => setBidUpdateBanner(e.target.checked)} /> 📌 Banner on this bid
              </label>
              <button onClick={() => { setBidUpdateOpen(false); setOpenSection('updates'); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#b45309', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
                See all {bidUpdates.length ? `(${bidUpdates.length})` : ''} ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bid Updates (bid mode) ─────────────────────────────────────────────── */}
      {!projectId && openSection === 'updates' && (
        <div className="admin-card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          <div onClick={() => setOpenSection(null)} style={{ background: '#b45309', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>Bid Updates{bidUpdates.length ? ` (${bidUpdates.length})` : ''}</span>
            <span style={{ fontSize: 11, color: '#fff' }}>▲</span>
          </div>
          <div style={{ padding: 20 }}>
            <BidUpdatesPillow bidId={id} pmName={pmNameFor(bid.bid_pm_id) === 'Unassigned' ? (bid.bid_pm_name || null) : pmNameFor(bid.bid_pm_id)} canRemove={isAdminZone} />
          </div>
        </div>
      )}

      {!isLocked && saving && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 100, background: '#111', color: '#fff', padding: '9px 16px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, boxShadow: '0 4px 12px rgba(0,0,0,0.18)' }}>
          Saving…
        </div>
      )}

      {partsSuggestKey && (
        <PartsPickerModal
          onPick={(p, tab) => applyPickedPart(p, tab)}
          onClose={() => setPartsSuggestKey(null)}
          initialQuery={partsSearch}
          tabs={[
            { key: 'material', label: 'Material', title: 'Material Database', options: partsPickerOptions, emptyText: 'No parts in the directory yet.', onManage: () => setShowPartsDir(true) },
            { key: 'equipment', label: 'Equipment', title: 'Equipment — the hourly rate goes in Sub $', emptyText: 'No equipment with a rate yet.',
              options: pickLists.equipment.filter(e => !/retired|sold/i.test(e.status || '') && !e.rental_returned_at).map(e => ({ key: e.id, label: [e.asset_number, e.name].filter(Boolean).join(' · '), category: e.category || '', sub: [e.make, e.model].filter(Boolean).join(' ') || e.category || '', price: Number(e.hourly_rate) || Number(e.billing_rate) || null, original: e })) },
            { key: 'labor', label: 'Labor', title: 'Labor — one hour at the trade\'s rate', options: EW_LABOR_ROLES.map(r => ({ key: r.role, label: r.role, sub: 'per hour', price: r.rate, original: r })) },
            { key: 'common', label: 'Common Prices', title: 'Common Prices (Price Book) — the rate goes in Sub $', emptyText: 'Nothing in the Price Book yet.',
              options: pickLists.common.map(c => ({ key: c.key, label: c.name, category: c.group || '', sub: c.unit || '', price: c.rate ?? null, original: c })) },
            ...(String(partsSuggestKey).startsWith('sub:') ? [] : [{ key: 'assembly', label: 'Bid Assemblies', title: 'Bid Assemblies — its lines drop in under this line, times its quantity', emptyText: 'No assemblies yet — add them in Settings → Bid Assemblies.',
              options: pickLists.assemblies.map(a => ({ key: a.id, label: a.name, sub: `${a.items.length} line${a.items.length === 1 ? '' : 's'} · per ${a.unit}${a.description ? ' · ' + a.description : ''}`, original: a })),
              onManage: () => window.open('/admin/bid-assemblies', '_blank') }]),
            // Trenching on main lines AND sub-lines (Ryan, Oct 26 2026) — a sub-line gets one summed line.
            { key: 'trenching', label: '⛏ Trenching', title: 'Trenching — pick a production setup, then the pit, dump and trucks', emptyText: 'No production setups yet — add them in Settings → Trenching.',
              options: pickLists.trench.map(t => ({ key: t.id, label: t.name, sub: `${t.data.width_in}" × ${t.data.depth_in}" · ${t.data.ft_per_day} ft/day`, original: t })),
              onManage: () => window.open('/admin/trenching', '_blank') },
          ]} />
      )}

      {/* The directory itself, for adding and editing parts. It can be picked from
          too: it used to open with no onSelect at all, which left every row dead. */}
      {trenchFor && (
        <TrenchCalc apiFetch={apiFetch} bid={bid} line={String(trenchFor.key).startsWith('sub:') ? items.flatMap(i => i.sub_items || []).find(x => x.id === String(trenchFor.key).slice(4)) : items.find(i => i._key === trenchFor.key)} setupId={trenchFor.setupId} saved={trenchFor.saved || null}
          onApply={(lines, lf, inputs, name, summary, note) => applyTrench(trenchFor.key, lines, lf, inputs, name, summary, note)} onClose={() => setTrenchFor(null)}
          isSub={String(trenchFor.key).startsWith('sub:')}
          onOther={() => { const k = trenchFor.key; setTrenchFor(null); loadParts(); setPartsSearch(''); setPartsSuggestKey(k); }} />
      )}
      {fwLinesOpen && (
        <FieldwireLines bidId={id} items={items} makeItem={newItem} onClose={() => setFwLinesOpen(false)}
          onApply={(next, r) => {
            setItems(next); setDirty(true); scheduleItemsSave(); setFwLinesOpen(false);
            showToast(r.removed != null ? `Fieldwire lines: ${r.removed} taken out, ${r.added} put in fresh` : `Fieldwire lines: ${r.added} new, ${r.updated} updated${r.zeroed ? `, ${r.zeroed} to zero (flagged red)` : ''}`);
          }} />
      )}
      {showPartsDir && (
        <PartsDirectoryModal
          API_BASE={API_BASE}
          getToken={getToken}
          onClose={() => setShowPartsDir(false)}
          onSelect={partsSuggestKey ? (p => { applyPickedPart(p); setShowPartsDir(false); }) : null}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'error' ? '#ef4444' : '#111', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 200 }}>
          {toast.msg}
        </div>
      )}

      {altPickModal && (() => {
        const grp = altById[altPickModal.altId];
        const chosen = new Set(altPickModal.keys);
        const toggle = k => setAltPickModal(m => {
          const s = new Set(m.keys);
          s.has(k) ? s.delete(k) : s.add(k);
          return { ...m, keys: [...s] };
        });
        // Every line is offerable: the ones already here, the base lines, and lines
        // sitting in another alternate — moving one between groups is the same action
        // as putting it in, and hiding them would make that impossible.
        const rows = items.filter(i => i.auto_source !== 'hole_schedule');
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, width: 640, maxWidth: '94vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Which lines belong to “{grp?.title}”?</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', margin: '4px 0 12px' }}>
                Tick a line to move it into this alternate; untick to send it back to the base bid.
                {grp?.kind === 'deductive' && <> This is a deductive alternate — its subtotal comes off the price if the owner takes it.</>}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button type="button" onClick={() => setAltPickModal(m => ({ ...m, keys: rows.map(i => i._key) }))}
                  style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Select all</button>
                <button type="button" onClick={() => setAltPickModal(m => ({ ...m, keys: [] }))}
                  style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Clear</button>
                <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#0f766e', alignSelf: 'center' }}>{chosen.size} selected</span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                {rows.length === 0 && <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>This bid has no line items yet.</div>}
                {rows.map(i => {
                  const on = chosen.has(i._key);
                  const elsewhere = i.alternate_id && i.alternate_id !== altPickModal.altId ? altById[i.alternate_id] : null;
                  return (
                    <label key={i._key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: on ? '#f0fdfa' : '#fff' }}>
                      <input type="checkbox" checked={on} onChange={() => toggle(i._key)} />
                      <span style={{ fontSize: 12, color: '#6b7280', minWidth: 42 }}>{i.item_number || '—'}</span>
                      <span style={{ flex: 1, fontSize: 13, color: '#111827' }}>{i.description || '(no description)'}</span>
                      {elsewhere && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '2px 6px' }}>in {elsewhere.title}</span>}
                      <span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt$(itemEffectiveTotal(i, bid) || 0)}</span>
                    </label>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={() => setAltPickModal(null)}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => {
                  // One pass: ticked lines join this group, lines that were in it and
                  // are no longer ticked go back to the base. Nothing else moves.
                  const next = items.map(i => {
                    if (chosen.has(i._key)) return { ...i, alternate_id: altPickModal.altId };
                    if (i.alternate_id === altPickModal.altId) return { ...i, alternate_id: null };
                    return i;
                  });
                  saveItems(next);
                  setAltPickModal(null);
                }}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Save — {chosen.size} line{chosen.size === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {altModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 520, maxWidth: '94vw', padding: 26, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>⌥ Insert a bid alternate</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>
              Cuts the bid into groups. Everything from the line you pick downwards joins this group and gets its own subtotal.
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Group title</label>
            <input autoFocus value={altModal.title} onChange={e => setAltModal(m => ({ ...m, title: e.target.value }))}
              placeholder="e.g. Alternate 1 — Fiber Conduit"
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 11px', fontSize: 14, margin: '5px 0 16px' }} />

            <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Additive or deductive?</label>
            <div style={{ display: 'flex', gap: 8, margin: '6px 0 16px' }}>
              {[['additive', 'Additive', 'Taking this alternate adds its subtotal to the price.'],
                ['deductive', 'Deductive', 'Taking this alternate subtracts its subtotal from the price.']].map(([val, label, hint]) => {
                const on = altModal.kind === val;
                return (
                  <button key={val} type="button" onClick={() => setAltModal(m => ({ ...m, kind: val }))}
                    style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      border: `1.5px solid ${on ? (val === 'deductive' ? '#dc2626' : '#0f766e') : '#e5e7eb'}`,
                      background: on ? (val === 'deductive' ? '#fef2f2' : '#f0fdfa') : '#fff' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: on ? (val === 'deductive' ? '#991b1b' : '#0f766e') : '#374151' }}>{label}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.35 }}>{hint}</div>
                  </button>
                );
              })}
            </div>

            <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Start the group at</label>
            <select value={altModal.beforeKey} onChange={e => setAltModal(m => ({ ...m, beforeKey: e.target.value }))}
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 11px', fontSize: 13, margin: '5px 0 6px' }}>
              <option value="">Start it empty — I'll move lines in later</option>
              {orderedItems.filter(i => !i.alternate_id).map(i => (
                <option key={i._key} value={i._key}>{(i.item_number ? `${i.item_number} · ` : '') + (i.description || '(no description)').slice(0, 60)}</option>
              ))}
            </select>
            <div style={{ fontSize: 11.5, color: '#9ca3af', marginBottom: 18 }}>
              Every base line from there to the bottom moves into the group. Nothing is deleted — and you can change which lines
              are in it any time with <strong style={{ color: '#0f766e' }}>＋ Add line</strong> and <strong style={{ color: '#0f766e' }}>Move lines in</strong> on the group heading.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setAltModal(null)}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button disabled={!altModal.title.trim()}
                onClick={async () => {
                  try { await addAlternate({ title: altModal.title.trim(), kind: altModal.kind, beforeKey: altModal.beforeKey }); setAltModal(null); showToast('Alternate added'); }
                  catch (e) { showToast(e?.error || e?.message || 'Could not add that alternate', 'error'); }
                }}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: altModal.title.trim() ? '#0f766e' : '#e5e7eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: altModal.title.trim() ? 'pointer' : 'default' }}>
                Insert alternate
              </button>
            </div>
          </div>
        </div>
      )}

      {exportRecipientModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 460, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              {exportRecipientModal === 'pdf' ? '📄 Export Quote PDF' : exportRecipientModal === 'email' ? '✉️ Email Quote' : '📊 Export Excel'} — Select Recipient
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: recipients.length ? 20 : 12 }}>
              {exportRecipientModal === 'email' ? 'Choose who the quote is addressed to — the email goes to their contact.' : "Choose which GC/Client's information appears on this export."}
            </div>
            {/* Say which version is coming out. Before this, an awarded bid re-rendered
                live data under a "View Used Quote PDF" label, so there was no way to
                tell you were looking at today's numbers rather than what was sent. */}
            {isLocked && recipients.some(r => r.quote_snapshot) && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 12.5, color: '#14532d', lineHeight: 1.5 }}>
                <strong>This job is awarded.</strong> A recipient with a recorded quote prints <strong>the quote as it was sent</strong>, not the bid as it stands now.
              </div>
            )}
            {/* With no recipients this used to skip the picker and quietly print the
                owner in the title block — so a quote went out addressed to the wrong
                company and looked perfectly normal. Now the substitution is a choice
                somebody makes on purpose. */}
            {recipients.length === 0 && !!bid?.recipient_is_owner && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '11px 13px', marginBottom: 16, fontSize: 12.5, color: '#14532d', lineHeight: 1.5 }}>
                This bid&rsquo;s recipient is the <strong>Project Owner</strong>{bid?.client_name ? <> — <strong>{bid.client_name}</strong></> : ' — but no owner is picked on the Job Description yet'}. The quote goes out in their name.
              </div>
            )}
            {recipients.length === 0 && !bid?.recipient_is_owner && (
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '11px 13px', marginBottom: 16, fontSize: 12.5, color: '#7c2d12', lineHeight: 1.5 }}>
                <strong>This bid has no recipients yet.</strong> Add the contractor you&rsquo;re quoting on the
                Bid Recipients tab and the quote will be addressed to them. Otherwise it goes out with the
                owner&rsquo;s details{bid?.client_name ? <> — <strong>{bid.client_name}</strong></> : ''}.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {recipients.map(r => (
                <button key={r.id} onClick={() => {
                  if (exportRecipientModal === 'email') { openQuoteEmail(r); setExportRecipientModal(false); return; }
                  // On an awarded bid the locked quote is the point — render what was
                  // sent, not what the bid says now. Falls back to live data only when
                  // there's no snapshot (quoted before this existed).
                  const snap = lockedQuoteFor(r.id);
                  if (exportRecipientModal === 'pdf') exportQuotePdf(snap ? { ...bid, ...snap.bid } : bid, snap ? snap.items : items, r, alternates, { onError: m => showToast(m, 'error') });
                  else exportBidToExcel(bid, items, r);
                  markRecipientSent(r.id);
                  setExportRecipientModal(false);
                }} style={{ padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.client_name}</div>
                  {r.contact_name && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{r.contact_name}{r.contact_email ? ` · ${r.contact_email}` : ''}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center' }}>
                    {r.revision > 0 && <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, background: '#eef2ff', padding: '2px 6px', borderRadius: 3 }}>Rev {r.revision} → Rev {r.revision + 1}</span>}
                    {r.sent_at && <span style={{ fontSize: 11, color: '#10b981' }}>✓ Last sent {new Date(r.sent_at).toLocaleDateString()}</span>}
                  </div>
                </button>
              ))}
              <button onClick={() => {
                if (exportRecipientModal === 'email') { openQuoteEmail(null); setExportRecipientModal(false); return; }
                if (exportRecipientModal === 'pdf') exportQuotePdf(bid, items, null, alternates, { onError: m => showToast(m, 'error') });
                else exportBidToExcel(bid, items);
                setExportRecipientModal(false);
              }} style={{ padding: '12px 16px', borderRadius: 8, border: '1px dashed #d1d5db', background: '#fff', cursor: 'pointer', textAlign: 'left', color: '#6b7280', fontSize: 13 }}>
                {/* Name the company it will actually print, so nobody picks this
                    thinking it means "no addressee". */}
                Use the owner&rsquo;s details{bid?.client_name ? ` — ${bid.client_name}` : ''}
              </button>
            </div>
            <button onClick={() => setExportRecipientModal(false)} style={{ width: '100%', padding: '9px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* The quote email composer — the PDF is drawn fresh and attached when it goes. */}
      {quoteEmail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !quoteEmail.busy && setQuoteEmail(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 620, maxWidth: '100%', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>✉️ Email Quote{quoteEmail.recipient ? ` — ${quoteEmail.recipient.client_name}` : bid?.client_name ? ` — ${bid.client_name}` : ''}</div>
            <div style={{ fontSize: 12.5, color: '#6b7280' }}>From <strong>{mailStatus?.from || 'the company mailbox'}</strong>{mailStatus?.personal ? '' : ' — set your own mailbox on your employee card to send as yourself'}.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <AddressPicker value={quoteEmail.to} onChange={v => setQuoteEmail(q => ({ ...q, to: v }))} placeholder="To — pick a bid recipient, or type an address" bidId={id}
                inputStyle={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              <AddressPicker value={quoteEmail.cc} onChange={v => setQuoteEmail(q => ({ ...q, cc: v }))} placeholder="Cc (optional)" bidId={id}
                inputStyle={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            </div>
            <input value={quoteEmail.subject} onChange={e => setQuoteEmail(q => ({ ...q, subject: e.target.value }))} placeholder="Subject"
              style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            <textarea value={quoteEmail.message} rows={6} onChange={e => setQuoteEmail(q => ({ ...q, message: e.target.value }))}
              style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>Attached: the bid proposal PDF{quoteEmail.recipient ? ` addressed to ${quoteEmail.recipient.client_name}` : ''} — drawn fresh, filed on the Documents tile, and the recipient is marked sent.</div>
            {quoteEmail.err && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{quoteEmail.err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => setQuoteEmail(null)} disabled={quoteEmail.busy} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={previewQuoteEmail} disabled={quoteEmail.busy} title="Open the quote PDF that will be attached"
                style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>👁 Preview PDF</button>
              <button onClick={sendQuoteEmail} disabled={quoteEmail.busy || !quoteEmail.to.trim()}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: quoteEmail.to.trim() ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: quoteEmail.to.trim() ? 'pointer' : 'default' }}>
                {quoteEmail.busy ? 'Sending…' : '✉️ Send quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {extractModal && (
        <ExtractModal
          items={extractModal.items}
          fromQuote={extractModal.fromQuote}
          onClose={() => setExtractModal(null)}
          onAdd={addExtractedItems}
        />
      )}

      {showRateSheetPdf && (
        <RateSheetModal
          initial={{
            customer_name: bid.client_name || '',
            attn: bid.client_contact || '',
            address: [bid.client_address1, bid.client_address2].filter(Boolean).join('\n'),
          }}
          onClose={() => setShowRateSheetPdf(false)}
        />
      )}

      {showAward && (
        <AwardModal
          bid={bid}
          items={items}
          alternates={alternates}
          employees={employees}
          onClose={() => setShowAward(false)}
          onAward={({ project: awardedProject, bid: updatedBid }) => {
            setBid(b => ({ ...b, ...updatedBid }));
            setShowAward(false);
            showToast('Project created! Opening in New Projects…');
            setTimeout(() => navigate(`/admin/new-projects/${awardedProject.id}`), 1000);
          }}
        />
      )}

      {/* ── PROJECT MODE ZONES ───────────────────────────────────── */}
      {projectId && project && (<>

        {/* ═══ ZONE 1 — ADMIN (PM, Office, Admin only) ══════════════ */}
        {isAdminZone && (<>
          {/* Bid & Estimate — collapsed by default */}
          <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
            <div onClick={() => setShowBidZone(v => !v)}
              style={{ background: '#374151', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>📋 Bid &amp; Estimate Details</span>
              <span style={{ fontSize: 11, opacity: 0.7 }}>{showBidZone ? '▲ Hide' : '▼ Show'}</span>
            </div>
            {showBidZone && (
              <div style={{ padding: '8px 0', fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                ↑ All bid information is displayed above this section ↑
              </div>
            )}
          </div>

        </>)}

        {/* Zone 1 — Admin-only workflow tabs (Submittals, Subcontracts, Payroll) */}
        {subJobs.filter(sj => OFFICE_WORKFLOW_TYPES.includes(sj.type)).map(sj => {
          const meta = WORKFLOW_COLORS[sj.type] || WORKFLOW_COLORS.Custom;
          const isOpen = showSubJobs[sj.id] !== false;
          return (
            <div key={sj.id} className="admin-card"
              draggable={isAdminZone && !isOpen}
              onDragStart={() => { if (isAdminZone && !isOpen) dragSjRef.current = sj.id; }}
              onDragOver={e => { if (dragSjRef.current && dragSjRef.current !== sj.id) e.preventDefault(); }}
              onDrop={() => { const d = dragSjRef.current; dragSjRef.current = null; if (d) moveSubJob(d, sj.id); }}
              onDragEnd={() => { dragSjRef.current = null; }}
              style={{ marginBottom: 10, overflow: 'hidden' }}>
              <div style={{ background: meta.color, color: '#fff', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div onClick={() => setShowSubJobs(s => ({ ...s, [sj.id]: !isOpen }))} style={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{sj.name || meta.label}</span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.2)', fontWeight: 600, textTransform: 'capitalize' }}>{(sj.status || 'pending').replace('_', ' ')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isAdminZone && (
                    <button onClick={e => { e.stopPropagation(); deleteWorkflowTab(sj.id); }}
                      title="Remove this tab"
                      style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 5, color: '#fff', fontSize: 12, padding: '2px 8px', cursor: 'pointer', fontWeight: 600 }}>
                      ✕ Remove
                    </button>
                  )}
                  {!isOpen && isAdminZone && <span title="Drag to reorder" style={{ fontSize: 13, opacity: 0.6, cursor: 'grab', marginRight: 4 }}>⠿</span>}<span onClick={() => setShowSubJobs(s => ({ ...s, [sj.id]: !isOpen }))} style={{ fontSize: 11, opacity: 0.7, cursor: 'pointer' }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>
              {isOpen && (
                <div style={{ padding: 16 }}>
                  {sj.type === 'contract' ? (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                        {[
                          ['Contract #', 'contract_number', 'text'],
                          ['Contract Value', 'contract_value', 'number'],
                          ['Signed Date', 'contract_signed_date', 'date'],
                          ['GC / Owner Contact', 'contract_contact', 'text'],
                        ].map(([label, field, type]) => (
                          <div key={field}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{label}</div>
                            <input type={type} value={contract[field] || ''} disabled={!isAdminZone}
                              onChange={e => setContract(c => ({ ...c, [field]: e.target.value }))}
                              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input type="checkbox" id={`cert-payroll-${sj.id}`} checked={!!contract.certified_payroll} disabled={!isAdminZone}
                          onChange={e => setContract(c => ({ ...c, certified_payroll: e.target.checked ? 1 : 0 }))} style={{ width: 16, height: 16 }} />
                        <label htmlFor={`cert-payroll-${sj.id}`} style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Certified Payroll Required</label>
                      </div>
                      {contract.certified_payroll ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14, background: '#eff6ff', borderRadius: 8, padding: 14 }}>
                          {[['Prevailing Wage Notes', 'prevailing_wage_notes'], ['Payroll Submission Schedule', 'payroll_schedule']].map(([label, field]) => (
                            <div key={field}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8', marginBottom: 4 }}>{label}</div>
                              <textarea value={contract[field] || ''} disabled={!isAdminZone} onChange={e => setContract(c => ({ ...c, [field]: e.target.value }))}
                                rows={3} style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #bfdbfe', borderRadius: 6, padding: '7px 10px', fontSize: 12, resize: 'vertical' }} />
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Compliance Notes</div>
                        <textarea value={contract.compliance_notes || ''} disabled={!isAdminZone} onChange={e => setContract(c => ({ ...c, compliance_notes: e.target.value }))}
                          rows={3} placeholder="Any compliance requirements, permits, bonding notes…"
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13, resize: 'vertical' }} />
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Contract Notes</div>
                        <textarea value={contract.contract_notes || ''} disabled={!isAdminZone} onChange={e => setContract(c => ({ ...c, contract_notes: e.target.value }))}
                          rows={3} placeholder="Scope clarifications, special terms, change order notes…"
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13, resize: 'vertical' }} />
                      </div>
                      {isAdminZone && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={saveContract} disabled={contractSaving}
                            style={{ padding: '8px 20px', borderRadius: 8, background: '#1e3a5f', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: contractSaving ? 0.6 : 1 }}>
                            {contractSaving ? 'Saving…' : 'Save Contract Info'}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : sj.type === 'submittals' ? (
                    <SubmittalsTab sjId={sj.id} projectId={projectId} project={project} bidItems={items} isAdminZone={isAdminZone} addressee={submittalAddressee} addresseeOptions={submittalAddresseeOptions} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                  ) : sj.type === 'payroll' ? (
                    <PayrollTab sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                  ) : sj.type === 'change_orders' ? (
                    // Same engine as Extra Work (T&M / Unit Price lines + the PDF),
                    // but office-side: priced, and fed by converting field tickets.
                    <ExtraWorkTab sjId={sj.id} projectId={projectId} project={project} isAdminZone={isAdminZone} bid={bid} isChangeOrders API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                  ) : (
                    <div style={{ fontSize: 13, color: '#6b7280' }}>Subcontracts tab — coming soon.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Close Out Checklist — bottom of Zone 1 */}
        <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
          <div onClick={() => setShowCloseout(v => !v)}
            style={{ background: '#1e4d3a', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              🏁 Close Out ({closeout.filter(x => x.checked).length}/{closeout.length} complete)
            </span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>{showCloseout ? '▲' : '▼'}</span>
          </div>
          {showCloseout && (
            <div style={{ padding: 16 }}>
              {isAdminZone && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <input value={newCloseoutItem} onChange={e => setNewCloseoutItem(e.target.value)}
                    placeholder="Add close out item…" onKeyDown={e => e.key === 'Enter' && addCloseoutItem()}
                    style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  <button onClick={addCloseoutItem} disabled={!newCloseoutItem.trim()}
                    style={{ padding: '7px 16px', borderRadius: 8, background: '#1e4d3a', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !newCloseoutItem.trim() ? 0.5 : 1 }}>
                    + Add
                  </button>
                </div>
              )}
              {closeout.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>No close out items yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {closeout.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: item.checked ? '#f0fdf4' : '#fafafa', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                      <input type="checkbox" checked={!!item.checked} onChange={e => toggleCloseoutItem(item.id, e.target.checked)}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1e4d3a' }} />
                      <span style={{ flex: 1, fontSize: 13, color: item.checked ? '#9ca3af' : '#111827', textDecoration: item.checked ? 'line-through' : 'none' }}>{item.description}</span>
                      {item.checked && item.checked_by && (
                        <span style={{ fontSize: 11, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 10 }}>✓ {item.checked_by}</span>
                      )}
                      {isAdminZone && (
                        <button onClick={() => deleteCloseoutItem(item.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Office Workflow (Zone 1) — below Close Out */}
        {isAdminZone && (
          <div style={{ marginBottom: 10 }}>
            {!showAddZone1 ? (
              <button onClick={() => setShowAddZone1(true)}
                style={{ width: '100%', padding: '9px', borderRadius: 8, border: '2px dashed #d1d5db', background: 'transparent', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                + Add Office Workflow (Zone 1)
              </button>
            ) : (
              <div className="admin-card" style={{ padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Add a Zone 1 (office) workflow:</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
                  {OFFICE_WORKFLOW_OPTIONS.map(opt => (
                    <button key={opt.key} onClick={() => { addWorkflowTab(opt.key, opt.label); setShowAddZone1(false); }} disabled={addingWorkflow}
                      style={{ padding: '10px 8px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{opt.icon}</div>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowAddZone1(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              </div>
            )}
          </div>
        )}

        {/* ═══ ZONE 2 — FIELD (everyone) ════════════════════════════ */}
        <div style={{ margin: '28px 0 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 2, background: '#064e3b', borderRadius: 1 }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: '#064e3b', textTransform: 'uppercase', letterSpacing: 1.5, whiteSpace: 'nowrap' }}>Zone 2 — Field</span>
          <div style={{ flex: 1, height: 2, background: '#064e3b', borderRadius: 1 }} />
        </div>

        {/* Workflow Tabs (Zone 2 — field workflows only) */}
        <div style={{ marginBottom: 12 }}>
            {subJobs.filter(sj => !OFFICE_WORKFLOW_TYPES.includes(sj.type)).map(sj => {
              const meta = WORKFLOW_COLORS[sj.type] || WORKFLOW_COLORS.Custom;
              const isOpen = showSubJobs[sj.id] !== false;
              return (
                <div key={sj.id} className="admin-card"
              draggable={isAdminZone && !isOpen}
              onDragStart={() => { if (isAdminZone && !isOpen) dragSjRef.current = sj.id; }}
              onDragOver={e => { if (dragSjRef.current && dragSjRef.current !== sj.id) e.preventDefault(); }}
              onDrop={() => { const d = dragSjRef.current; dragSjRef.current = null; if (d) moveSubJob(d, sj.id); }}
              onDragEnd={() => { dragSjRef.current = null; }}
              style={{ marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ background: meta.color, color: '#fff', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div onClick={() => setShowSubJobs(s => ({ ...s, [sj.id]: !isOpen }))} style={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{sj.name || meta.label}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.2)', fontWeight: 600, textTransform: 'capitalize' }}>{(sj.status || 'pending').replace('_', ' ')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isAdminZone && (
                        <button onClick={e => { e.stopPropagation(); deleteWorkflowTab(sj.id); }}
                          title="Remove this tab"
                          style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 5, color: '#fff', fontSize: 12, padding: '2px 8px', cursor: 'pointer', fontWeight: 600 }}>
                          ✕ Remove
                        </button>
                      )}
                      {!isOpen && isAdminZone && <span title="Drag to reorder" style={{ fontSize: 13, opacity: 0.6, cursor: 'grab', marginRight: 4 }}>⠿</span>}<span onClick={() => setShowSubJobs(s => ({ ...s, [sj.id]: !isOpen }))} style={{ fontSize: 11, opacity: 0.7, cursor: 'pointer' }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{ padding: 16 }}>
                      {/* Status row */}
                      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>Status:</span>
                        <select value={sj.status || 'pending'}
                          onChange={async e => {
                            const updated = subJobs.map(s => s.id === sj.id ? { ...s, status: e.target.value } : s);
                            setSubJobs(updated);
                            await apiFetch(`/projects/${projectId}/sub-jobs/${sj.id}`, { method: 'PATCH', body: JSON.stringify({ notes: subJobNotes[sj.id], status: e.target.value }) });
                          }}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="complete">Complete</option>
                        </select>
                      </div>

                      {/* ── Vertical Drilling special layout ── */}
                      {sj.type === 'vertical_drilling' ? (() => {
                        const bidHoles = (() => { try { return JSON.parse(bid.drill_hole_schedule || '[]'); } catch { return []; } })();
                        const saved = (() => { try { const p = JSON.parse(subJobNotes[sj.id] || '{}'); return p; } catch { return {}; } })();
                        const extraHoles = saved.extraHoles || [];
                        const fieldNotes = saved.notes || '';
                        const drillOverrides = saved.drillOverrides || {};

                        function saveDrillData(patch) {
                          const next = JSON.stringify({ ...saved, ...patch });
                          setSubJobNotes(n => ({ ...n, [sj.id]: next }));
                        }

                        const th = { padding: '7px 10px', fontWeight: 700, fontSize: 11, color: '#fff', textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4, background: '#4a1d50', whiteSpace: 'nowrap' };
                        const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };

                        return (
                          <div>
                            {/* Same Trello-style drilling card as the Workflow board */}
                            <div onClick={() => setDrillCardSj(sj)}
                              style={{ background: '#1e3a5f', color: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                              <div>
                                <div style={{ fontSize: 15, fontWeight: 800 }}>🕳️ Drilling Card</div>
                                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ background: sj.drill_rig ? '#fef3c7' : 'rgba(255,255,255,0.15)', color: sj.drill_rig ? '#92400e' : '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>🛠️ {sj.drill_rig || 'No drill set'}</span>
                                  {/* The drill date belongs here too — the job's due date is not the day the
                                      rig is coming, and until now that day only existed on the card. */}
                                  <span style={{ background: sj.drill_date ? '#fff' : 'rgba(255,255,255,0.15)', color: sj.drill_date ? '#1e3a5f' : '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                                    📅 {sj.drill_date ? new Date(`${sj.drill_date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No date set'}
                                  </span>
                                  {/* "Scheduled", not "Active" — the same word the boards use. */}
                                  <span style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>{sj.status === 'active' ? 'Scheduled' : (sj.status || 'pending') === 'pending' ? 'Not Started' : (sj.status || '').replace(/_/g, ' ').replace(/w/g, c => c.toUpperCase())}</span>
                                </div>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.4)', padding: '8px 14px', borderRadius: 8, whiteSpace: 'nowrap' }}>Open card ›</span>
                            </div>
                            {/* Whatever the driller wrote on the card. It only existed there, so a PM
                                reading the job never saw it. */}
                            {sj.drill_comment && (
                              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 13px', marginTop: -8, marginBottom: 16 }}>
                                <div style={{ fontSize: 10.5, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>Drilling comment</div>
                                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{sj.drill_comment}</div>
                              </div>
                            )}
                            {/* Bid holes — read only qty/size/depth/soil, editable drill */}
                            {bidHoles.length > 0 && (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#4a1d50', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Holes from Bid</div>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                      <tr>
                                        <th style={th}>QTY</th>
                                        <th style={th}>Diameter</th>
                                        <th style={th}>Depth</th>
                                        <th style={th}>Soil</th>
                                        <th style={th}>Drill Rig</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {bidHoles.filter(r => r.qty).map((r, i) => (
                                        <tr key={r.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#faf5ff' }}>
                                          <td style={td}><strong>{r.qty}</strong></td>
                                          <td style={td}>{r.diameter ? `${r.diameter}"` : '—'}</td>
                                          <td style={td}>{r.depth || '—'}</td>
                                          <td style={td}>{r.soil || '—'}</td>
                                          <td style={td}>
                                            <select value={drillOverrides[r.id] || r.drill || ''}
                                              onChange={e => saveDrillData({ drillOverrides: { ...drillOverrides, [r.id]: e.target.value } })}
                                              style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', fontSize: 12, background: '#fff', cursor: 'pointer' }}>
                                              <option value="">— Select Drill —</option>
                                              <option value="Volvo">Volvo</option>
                                              <option value="Ella">Ella</option>
                                              <option value="Harold">Harold</option>
                                            </select>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Additional holes added in the field */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#4a1d50', textTransform: 'uppercase', letterSpacing: 0.5 }}>Additional Holes</div>
                                <button onClick={() => saveDrillData({ extraHoles: [...extraHoles, { id: Date.now().toString(), qty: '', diameter: '', depth: '', soil: '', drill: '' }] })}
                                  style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#4a1d50', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                  + Add Hole
                                </button>
                              </div>
                              {extraHoles.length === 0 ? (
                                <div style={{ padding: 12, textAlign: 'center', color: '#9ca3af', fontSize: 12, background: '#f9fafb', borderRadius: 8, border: '1px dashed #e5e7eb' }}>No additional holes</div>
                              ) : (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                      <tr>
                                        <th style={th}>QTY</th>
                                        <th style={th}>Diameter</th>
                                        <th style={th}>Depth</th>
                                        <th style={th}>Soil</th>
                                        <th style={th}>Drill Rig</th>
                                        <th style={th} />
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {extraHoles.map((r, i) => {
                                        const upd = (field, val) => saveDrillData({ extraHoles: extraHoles.map(x => x.id === r.id ? { ...x, [field]: val } : x) });
                                        const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', fontSize: 12, width: '100%', boxSizing: 'border-box' };
                                        return (
                                          <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#faf5ff' }}>
                                            <td style={td}><input type="text" inputMode="numeric" value={r.qty} onChange={e => upd('qty', e.target.value)} style={{ ...inp, width: 55 }} placeholder="0" /></td>
                                            <td style={td}>
                                              <select value={r.diameter} onChange={e => upd('diameter', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                                                <option value="">—</option>
                                                {DIAMETERS.map(d => <option key={d} value={d}>{d}"</option>)}
                                              </select>
                                            </td>
                                            <td style={td}><input type="text" value={r.depth} onChange={e => upd('depth', e.target.value)} style={{ ...inp, width: 75 }} placeholder="e.g. 20'" /></td>
                                            <td style={td}>
                                              <select value={r.soil} onChange={e => upd('soil', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                                                <option value="">—</option>
                                                {SOIL_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                              </select>
                                            </td>
                                            <td style={td}>
                                              <select value={r.drill} onChange={e => upd('drill', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                                                <option value="">— Select —</option>
                                                <option value="Volvo">Volvo</option>
                                                <option value="Ella">Ella</option>
                                                <option value="Harold">Harold</option>
                                              </select>
                                            </td>
                                            <td style={td}>
                                              <button onClick={() => saveDrillData({ extraHoles: extraHoles.filter(x => x.id !== r.id) })}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 18, lineHeight: 1 }}>×</button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Field notes */}
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Field Notes</div>
                              <textarea value={fieldNotes} onChange={e => saveDrillData({ notes: e.target.value })}
                                placeholder="Daily progress, issues, observations…" rows={3}
                                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
                            </div>

                            {/* Billable equipment hours */}
                            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#4a1d50', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Equipment Hours</div>
                              <EquipmentHoursTab sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                            </div>
                          </div>
                        );
                      })() : sj.type === 'nve' ? (
                        <NVETab sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                      ) : sj.type === 'loops' ? (
                        <LoopsTab sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                      ) : sj.type === 'submittals' ? (
                        <SubmittalsTab sjId={sj.id} projectId={projectId} project={project} bidItems={items} isAdminZone={isAdminZone} addressee={submittalAddressee} addresseeOptions={submittalAddresseeOptions} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                      ) : sj.type === 'usa_digs' ? (
                        <UsaDigsTab sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                      ) : sj.type === 'shop' ? (
                        <ShopTab projectId={projectId} sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                      ) : sj.type === 'horizontal_drilling' ? (
                        <div>
                          <div onClick={() => setDrillCardSj(sj)}
                            style={{ background: '#4a1d50', color: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 800 }}>↔️ Drilling Card</div>
                              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ background: sj.drill_rig ? '#fef3c7' : 'rgba(255,255,255,0.15)', color: sj.drill_rig ? '#92400e' : '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>🛠️ {sj.drill_rig || 'No drill set'}</span>
                                {/* The drill date belongs here too — the job's due date is not the day the
                                    rig is coming, and until now that day only existed on the card. */}
                                <span style={{ background: sj.drill_date ? '#fff' : 'rgba(255,255,255,0.15)', color: sj.drill_date ? '#1e3a5f' : '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                                  📅 {sj.drill_date ? new Date(`${sj.drill_date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No date set'}
                                </span>
                                {/* "Scheduled", not "Active" — the same word the boards use. */}
                                <span style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>{sj.status === 'active' ? 'Scheduled' : (sj.status || 'pending') === 'pending' ? 'Not Started' : (sj.status || '').replace(/_/g, ' ').replace(/w/g, c => c.toUpperCase())}</span>
                              </div>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.4)', padding: '8px 14px', borderRadius: 8, whiteSpace: 'nowrap' }}>Open card ›</span>
                          </div>
                          {/* Whatever the driller wrote on the card. It only existed there, so a PM
                              reading the job never saw it. */}
                          {sj.drill_comment && (
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 13px', marginTop: -8, marginBottom: 16 }}>
                              <div style={{ fontSize: 10.5, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>Drilling comment</div>
                              <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{sj.drill_comment}</div>
                            </div>
                          )}
                          <EquipmentHoursTab sjId={sj.id} isAdminZone={isAdminZone} API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                        </div>
                      ) : sj.type === 'shop_order' ? (
                        <ShopOrdersBoard projectId={projectId} />
                      ) : sj.type === 'extra_work' ? (
                        <ExtraWorkTab sjId={sj.id} projectId={projectId} project={project} isAdminZone={isAdminZone} bid={bid}
                          onWorkflowsChanged={() => apiFetch(`/projects/${projectId}/sub-jobs`).then(s => setSubJobs(Array.isArray(s) ? s : [])).catch(() => {})}
                          API_BASE={API_BASE} getToken={getToken} showToast={showToast} />
                      ) : (
                        /* Generic notes for all other workflow types */
                        <textarea value={subJobNotes[sj.id] || ''} onChange={e => setSubJobNotes(n => ({ ...n, [sj.id]: e.target.value }))}
                          placeholder="Notes for this work type…" rows={4}
                          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
                      )}

                      {sj.type !== 'nve' && sj.type !== 'loops' && sj.type !== 'submittals' && sj.type !== 'usa_digs' && sj.type !== 'shop' && sj.type !== 'horizontal_drilling' && sj.type !== 'shop_order' && sj.type !== 'extra_work' && (
                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={() => saveSubJob(sj)} disabled={subJobSaving[sj.id]}
                            style={{ padding: '6px 16px', borderRadius: 7, background: meta.color, color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: subJobSaving[sj.id] ? 0.6 : 1 }}>
                            {subJobSaving[sj.id] ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Daily Updates */}
        <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
          <div onClick={() => setShowDailyUpdates(v => !v)}
            style={{ background: '#064e3b', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>📝 Daily Updates ({jobFeed.length})</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>{showDailyUpdates ? '▲' : '▼'}</span>
          </div>
          {showDailyUpdates && (
            <div style={{ padding: 16 }}>
              <div style={{ marginBottom: 14 }}>
                <textarea value={newUpdate} onChange={e => setNewUpdate(e.target.value)}
                  placeholder="Enter today's project update…" rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical', marginBottom: 8 }} />
                <button onClick={postDailyUpdate} disabled={postingUpdate || !newUpdate.trim()}
                  style={{ padding: '7px 18px', borderRadius: 8, background: '#064e3b', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!newUpdate.trim() || postingUpdate) ? 0.5 : 1 }}>
                  {postingUpdate ? 'Posting…' : 'Post Update'}
                </button>
              </div>
              {jobFeed.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>No updates yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {jobFeed.map(u => (
                    <div key={u.id} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', borderLeft: `3px solid ${u.media.length ? '#1d4ed8' : '#064e3b'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                          {u.who}
                          {u.media.length > 0 && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', borderRadius: 10, padding: '1px 7px' }}>📷 {u.media.length}</span>}
                        </span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(u.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{u.text}</div>
                      {u.media.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                          {u.media.map((src, i) => (
                            <a key={i} href={src} target="_blank" rel="noreferrer" title="Open full size">
                              <img src={src} alt="" loading="lazy"
                                style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* T&M Billing — only on Time & Material jobs */}
        {bid?.pricing_type === 'tm' && <TMBillingSection projectId={projectId} bid={bid} />}

        {/* Equipment on this job (days-on-job + drill hours) */}
        <EquipmentJobCard projectId={projectId} />

        {/* Work Orders — call-outs under an on-call contract. Each is its own small
            job, so its POs and billing stay on it. Hidden until the first one. */}
        {projectId && (workOrders.length > 0 || showWoAdd) && (
          <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
            <div onClick={() => setShowWOs(v => !v)}
              style={{ background: '#1d4ed8', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>🧾 Work Orders ({workOrders.length})</span>
              <span style={{ fontSize: 11, opacity: 0.7 }}>{showWOs ? '▲' : '▼'}</span>
            </div>
            {showWOs && (
              <div style={{ padding: 14 }}>
                {workOrders.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {workOrders.map(w => (
                      <div key={w.id} onClick={() => navigate(`/admin/new-projects/${w.id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f9fafb', border: '1px solid #eef2f7', borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 12.5, color: '#1d4ed8', whiteSpace: 'nowrap' }}>{w.project_number}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {String(w.name || '').replace(w.project_number, '').trim() || w.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                            {(WORKFLOW_COLORS[w.schedule_workflow]?.label) || 'Small Jobs'}
                            {w.pm_name ? ` · ${w.pm_name}` : ''}
                            {w.change_orders ? ` · ${w.change_orders} CO` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{fmt$(w.po_total || 0)}</div>
                          <div style={{ fontSize: 10.5, color: '#9ca3af' }}>{w.po_count} PO{w.po_count === 1 ? '' : 's'}</div>
                        </div>
                        <span style={{ color: '#d1d5db' }}>›</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, fontSize: 12.5, color: '#374151', paddingTop: 4, borderTop: '1px dashed #e5e7eb' }}>
                      <span style={{ color: '#6b7280' }}>Across {workOrders.length} work order{workOrders.length === 1 ? '' : 's'}</span>
                      <b>{fmt$(workOrders.reduce((s, w) => s + (Number(w.po_total) || 0), 0))}</b>
                    </div>
                  </div>
                )}
                {showWoAdd ? (
                  <div style={{ border: '1px dashed #bfdbfe', background: '#eff6ff', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', marginBottom: 6 }}>New work order</div>
                    <input value={woName} onChange={e => setWoName(e.target.value)} placeholder="What's the call-out for? e.g. Knocked-down pole at Virginia & 4th"
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 7, padding: '9px 11px', fontSize: 14 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Track on</span>
                      <select value={woBoard} onChange={e => setWoBoard(e.target.value)}
                        style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, background: '#fff' }}>
                        {WO_BOARD_OPTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                      </select>
                      <span style={{ flex: 1 }} />
                      <button onClick={createWorkOrder} disabled={!woName.trim() || creatingWo}
                        style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: woName.trim() ? '#1d4ed8' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: woName.trim() ? 'pointer' : 'default' }}>
                        {creatingWo ? 'Creating…' : 'Create Work Order'}
                      </button>
                      <button onClick={() => { setShowWoAdd(false); setWoName(''); }}
                        style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                      Numbered automatically as {(project?.project_number || '')}-WO### · inherits the customer, PM and contractor from this job.
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setShowWoAdd(true)}
                    style={{ fontSize: 12.5, fontWeight: 700, color: '#1d4ed8', background: 'none', border: '1px dashed #bfdbfe', borderRadius: 7, padding: '7px 14px', cursor: 'pointer' }}>+ New Work Order</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Purchase Orders */}
        <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
          <div onClick={() => setShowPOs(v => !v)}
            style={{ background: '#92400e', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>📦 Purchase Orders ({projectPOs.length})</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>{showPOs ? '▲' : '▼'}</span>
          </div>
          {showPOs && (
            <div style={{ padding: 16 }}>
              {projectPOs.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>
                  No POs linked to this project yet.
                  <div style={{ marginTop: 8 }}>
                    <a href="/admin/purchase-orders" style={{ color: '#92400e', fontWeight: 600, fontSize: 12 }}>Go to Purchase Orders →</a>
                  </div>
                </div>
              ) : (() => {
                const majorCount = projectPOs.filter(p => p.is_major).length;
                const shownPOs = poMajorOnly ? projectPOs.filter(p => p.is_major) : projectPOs;
                return (
                <>
                  {projectPOs.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                      <button onClick={() => setPoMajorOnly(false)}
                        style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${!poMajorOnly ? '#92400e' : '#e5e7eb'}`, background: !poMajorOnly ? '#92400e' : '#fff', color: !poMajorOnly ? '#fff' : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        All ({projectPOs.length})
                      </button>
                      <button onClick={() => setPoMajorOnly(true)} title="Show only this job's Major POs"
                        style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${poMajorOnly ? '#b91c1c' : '#fca5a5'}`, background: poMajorOnly ? '#b91c1c' : '#fef2f2', color: poMajorOnly ? '#fff' : '#b91c1c', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        ⭐ Major only ({majorCount})
                      </button>
                    </div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#6b7280' }}>PO #</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#6b7280' }}>Description</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 11, color: '#6b7280' }}>Value</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#6b7280' }}>Status</th>
                    </tr></thead>
                    <tbody>
                      {shownPOs.map(po => (
                        <tr key={po.id} onClick={() => setEditingPO(po)} title="Open / edit this PO"
                          style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#fdf6ec'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                          <td style={{ padding: '7px 8px', fontWeight: 700, fontFamily: 'monospace', color: '#92400e', whiteSpace: 'nowrap' }}>
                            {po.po_number}
                            {!!po.is_major && <span title="Major PO" style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', padding: '1px 5px', borderRadius: 5 }}>⭐</span>}
                          </td>
                          <td style={{ padding: '7px 8px', color: '#374151' }}>{po.description || '—'}</td>
                          <td style={{ padding: '7px 8px', textAlign: 'right' }}>{(po.cost ?? po.estimated_value) != null ? '$' + Number(po.cost ?? po.estimated_value).toLocaleString() : '—'}</td>
                          <td style={{ padding: '7px 8px', color: '#6b7280', fontSize: 12 }}>{po.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Punch List */}
        <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
          <div onClick={() => setShowPunchList(v => !v)}
            style={{ background: '#7c2d12', color: '#fff', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              ✅ Punch List ({punchList.filter(x => x.status === 'done').length}/{punchList.length} done)
            </span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>{showPunchList ? '▲' : '▼'}</span>
          </div>
          {showPunchList && (
            <div style={{ padding: 16 }}>
              {isAdminZone && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  <input value={newPunchItem} onChange={e => setNewPunchItem(e.target.value)}
                    placeholder="New punch list item…" onKeyDown={e => e.key === 'Enter' && addPunchItem()}
                    style={{ flex: 2, minWidth: 160, border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  <input value={newPunchAssignee} onChange={e => setNewPunchAssignee(e.target.value)}
                    placeholder="Assign to…"
                    style={{ flex: 1, minWidth: 120, border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  <button onClick={addPunchItem} disabled={!newPunchItem.trim()}
                    style={{ padding: '7px 16px', borderRadius: 8, background: '#7c2d12', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !newPunchItem.trim() ? 0.5 : 1 }}>
                    + Add
                  </button>
                </div>
              )}
              {punchList.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>No punch list items yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {punchList.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: item.status === 'done' ? '#f0fdf4' : '#fafafa', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                      <select value={item.status} onChange={e => updatePunchStatus(item.id, e.target.value)}
                        style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 6px', fontSize: 11, background: item.status === 'done' ? '#dcfce7' : item.status === 'in_progress' ? '#fef9c3' : '#fff' }}>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="done">Done</option>
                      </select>
                      <span style={{ flex: 1, fontSize: 13, color: item.status === 'done' ? '#9ca3af' : '#111827', textDecoration: item.status === 'done' ? 'line-through' : 'none' }}>{item.description}</span>
                      {item.assigned_to && <span style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 10 }}>{item.assigned_to}</span>}
                      {isAdminZone && (
                        <button onClick={() => deletePunchItem(item.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Field Workflow (Zone 2) — bottom of Zone 2 */}
        {isAdminZone && (
          <div style={{ marginBottom: 12 }}>
            {!showAddWorkflow ? (
              <button onClick={() => setShowAddWorkflow(true)}
                style={{ width: '100%', padding: '9px', borderRadius: 8, border: '2px dashed #d1d5db', background: 'transparent', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                + Add Field Workflow (Zone 2)
              </button>
            ) : (
              <div className="admin-card" style={{ padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Add a Zone 2 (field) workflow:</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
                  {FIELD_WORKFLOW_OPTIONS.map(opt => (
                    <button key={opt.key} onClick={() => addWorkflowTab(opt.key, opt.label)} disabled={addingWorkflow}
                      style={{ padding: '10px 8px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{opt.icon}</div>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input id="custom-workflow-name" placeholder="Or type a custom name…"
                    style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
                  <button onClick={() => { const v = document.getElementById('custom-workflow-name').value.trim(); if (v) addWorkflowTab('Custom', v); }} disabled={addingWorkflow}
                    style={{ padding: '7px 14px', borderRadius: 8, background: '#374151', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Add Custom
                  </button>
                  <button onClick={() => setShowAddWorkflow(false)}
                    style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </>)}
    </Wrap>
  );
}
